const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

// [ERP-ENV-VISIBILITY-BANNER-DEV-20260626-001]
// 환경 배지(EnvBadge)용 — API base/실해석 URL/health 환경 조회. read-only.
/** 설정된 API base (상대경로 "/api" 또는 절대 URL) 그대로 반환. */
export function getApiBase(): string {
  return API_URL;
}
/** 상대경로면 현재 origin 을 붙여 실제 해석된 API URL 반환. 브라우저 한정(SSR 시 base 그대로). */
export function getResolvedApiUrl(): string {
  if (typeof window === "undefined") return API_URL;
  if (/^https?:\/\//i.test(API_URL)) return API_URL;
  return `${window.location.origin}${API_URL}`;
}
export interface HealthEnv {
  status?: string;
  database?: string;
  db_name?: string;
  app_env?: string;
}
/** GET /health — db_name/app_env 포함(backend 신버전). 구버전이면 해당 필드 없음. */
export function getHealthEnv(): Promise<HealthEnv> {
  return request<HealthEnv>("/health");
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

// 26차 후속 #82 Phase 1 — 구조화된 에러 정보를 호출자에게 노출.
// 기존 호출자(.message 만 읽음)는 그대로 동작하고,
// 신규 호출자는 .status / .errorCode / .detail 로 분기 가능.
export class ApiError extends Error {
  status: number;
  errorCode: string | null;
  detail: unknown;
  constructor(message: string, status: number, errorCode: string | null, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.detail = detail;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: Record<string, string> = {
    // FormData 인 경우 boundary 자동 부여를 위해 Content-Type 미설정
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new ApiError("인증이 만료되었습니다. 다시 로그인해주세요.", 401, null, null);
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류가 발생했습니다." }));
    // FastAPI validation 에러 detail 이 배열인 경우를 평문화
    let detailMsg: string;
    let errorCode: string | null = null;
    if (Array.isArray(err.detail)) {
      detailMsg = err.detail
        .map((d: any) => {
          if (typeof d === "string") return d;
          const loc = Array.isArray(d?.loc) ? d.loc.slice(-1).join(".") : "";
          return [loc, d?.msg].filter(Boolean).join(": ");
        })
        .join(" / ");
    } else if (typeof err.detail === "string") {
      detailMsg = err.detail;
    } else if (err.detail && typeof err.detail === "object") {
      // 26차 후속 #82 Phase 1 — 백엔드가 detail={code, message, ...} 로 내려주는 케이스.
      const d = err.detail as Record<string, unknown>;
      if (typeof d.code === "string") errorCode = d.code;
      detailMsg =
        (typeof d.message === "string" ? d.message : null) ||
        (typeof d.msg === "string" ? d.msg : null) ||
        JSON.stringify(err.detail);
    } else {
      detailMsg = "요청 처리 중 오류가 발생했습니다.";
    }
    throw new ApiError(`[${res.status}] ${detailMsg}`, res.status, errorCode, err.detail);
  }

  // 204 No Content 등 빈 응답은 json 파싱하지 않음
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return res.json();
}

// ─── Auth ───────────────────────────────────

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_id: number;
  name: string;
  role: string;
  // [ERP-ORDER-DETAIL-SKU-CHANGE-LV2-PERMISSION-HOTFIX-20260615-001] 권한등급 lv1/lv2/lv3.
  permission_level?: string;
  company_id: number | null;
  company_code?: string | null;
  company_name?: string | null;
}

export async function login(login_id: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ login_id, password }),
  });
}

export function changeMyPassword(body: {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}): Promise<{ message: string }> {
  return request<{ message: string }>("/auth/me/password", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function resetUserPassword(
  userId: number,
  body: { new_password: string; new_password_confirm: string },
): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/password`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface UserResponse {
  id: number;
  login_id: string;
  name: string;
  role: string;
  // [ERP-USER-PERMISSION-LEVEL-SCHEMA-AND-UI-20260604-001] 권한등급 lv1/lv2/lv3.
  permission_level?: string;
  status: string;
  is_active: boolean;
  birth_date: string | null;
  phone: string | null;
  hire_date?: string | null;
  company_id: number | null;
  company_code?: string | null;
  company_name?: string | null;
  created_at: string;
  // [ERP-USER-ACCOUNT-LEAVE-20260519-001] Phase 1 — 잔여 휴가 정보.
  // /auth/users 목록 응답에서만 채워짐. /auth/me 는 null.
  remaining_leave_days?: number | null;
  leave_accrued_days?: number | null;
  leave_used_days?: number | null;
  leave_period_start?: string | null;
  leave_period_end?: string | null;
}

export async function signup(body: {
  login_id: string;
  password: string;
  name: string;
  birth_date?: string;
  phone?: string;
  hire_date?: string;
}): Promise<{ message: string }> {
  return request<{ message: string }>("/auth/signup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getUsers(): Promise<UserResponse[]> {
  return request<UserResponse[]>("/auth/users");
}

// 담당자 드롭다운용 — 활성(approved + is_active) 사용자의 id/name만 반환.
export function getAssignees(): Promise<{ id: number; name: string }[]> {
  return request<{ id: number; name: string }[]>("/auth/assignees");
}

export function approveUser(userId: number): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/approve`, { method: "POST" });
}

export function rejectUser(userId: number): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/reject`, { method: "POST" });
}

export function deactivateUser(userId: number): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/deactivate`, { method: "POST" });
}

// [ERP-USER-ACCOUNT-LEAVE-20260519-001] role / company 변경 helper.
export function updateUserRole(
  userId: number,
  role: string,
): Promise<{ message: string; user_id: number; role: string }> {
  return request(`/auth/users/${userId}/role`, {
    method: "POST",
    body: JSON.stringify({ role }),
  });
}

// [ERP-USER-PERMISSION-LEVEL-SCHEMA-AND-UI-20260604-001] 권한등급(permission_level) 변경.
export function updateUserPermissionLevel(
  userId: number,
  permissionLevel: string,
): Promise<{ message: string; user_id: number; permission_level: string }> {
  return request(`/auth/users/${userId}/permission-level`, {
    method: "POST",
    body: JSON.stringify({ permission_level: permissionLevel }),
  });
}

export function updateUserCompany(
  userId: number,
  companyId: number | null,
): Promise<{ message: string; user_id: number; company_id: number | null }> {
  return request(`/auth/users/${userId}/company`, {
    method: "POST",
    body: JSON.stringify({ company_id: companyId }),
  });
}

// ─── Products ───────────────────────────────

export interface Product {
  id: number;
  sku: string;
  product_name: string;
  // [ERP-PRODUCT-LAST-INBOUND-DATE-DISPLAY-20260618-001] 상세 최근 입고일
  last_inbound_at?: string | null;
  last_inbound_source?: string | null;
  name_cn: string | null;                 // 품명(중문) — 제조사 공유 양식 "中文" SoT
  // [EX-1 보완] 통관 2필드(서버 ProductResponse 기존 필드) — 중국수입 필수값 입력(등록 폼)용 타입 보완.
  english_name?: string | null;
  customs_hs_code?: string | null;
  brand: string | null;
  category: string | null;
  option1: string | null;
  option1_cn: string | null;
  option2: string | null;
  option2_cn: string | null;
  option3: string | null;
  option3_cn: string | null;
  model_name: string | null;
  cost_price: number | null;
  barcode: string | null;
  // [ERP-BARCODE-20260513-002] 바코드 스티커 표시 텍스트 전용.
  barcode_label_name?: string | null;
  barcode_option_name?: string | null;
  // [ERP-BARCODE-PREFERENCE-PHASE-A-PRODUCT-DEFAULT-STYLE-20260616-001]
  // SKU별 기본 바코드 출력 스타일. null fallback: size→small, code→(small=qr/large=code128).
  barcode_label_size?: BarcodeLabelSize | null;
  barcode_code_type?: BarcodeCodeType | null;
  image_url: string | null;
  purchase_url: string | null;
  supplier_name: string | null;
  supplier_product_code: string | null;
  purchase_type: string | null;
  shipping_alias: string | null;
  as_available: boolean;
  is_active: boolean;
  product_status?: string;
  safety_stock_qty?: number;
  part_type?: string;
  default_labor_fee?: number;
  // 치수/무게
  width_cm: number | null;
  depth_cm: number | null;
  height_cm: number | null;
  weight_kg: number | null;
  // 원가 계산기
  cost_price_cny: number | null;
  cost_factor: number | null;
  // 판매가 / 원가 유형
  sale_price?: number | null;
  cost_currency?: string;              // legacy — 판정·저장에 쓰지 않음([FW-005-COST-PHASE2])
  // [FW-005-COST-PHASE2] 원가 관리유형(null = 미분류 legacy · cny_calc · krw_direct)
  cost_mode?: string | null;
  memo: string | null;
  // [ERP-PO-...-111] SKU 전역 발주 참고 비고.
  purchase_note?: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Cost Calculator ─────────────────────────────────────────────────────────

export interface CustomsCategory { label: string; customs_rate: number; vat_rate: number; }
export interface CostPreset {
  exchange_rate: number;
  cbm_unit_price: number;
  handling_fee: number;
  lcl_customs_fee: number;
  lcl_coo_fee: number;
  bl_fee: number;
  /** 외국인결제 수수료 % — CNY 원가에 복리 곱 */
  foreign_payment_fee_rate: number;
  /** 카드 수수료 % */
  card_fee_rate: number;
  /** 기타 수수료 % */
  etc_fee_rate: number;
  customs_categories: CustomsCategory[];
}

export interface ProductSearchItem {
  sku: string;
  product_name: string;
  cost_price_cny: number | null;
  cost_price: number | null;
  // [ERP-AS-PARTS-SEARCH-RETAIL-PRICE-HOTFIX-20260701-008]
  // 소비자 판매가(smartstore 기준가 → sale_price fallback). AS 파츠 견적 표시/채움에 사용.
  selling_price?: number | null;
  brand: string | null;
  default_labor_fee: number | null;
  // 26차 후속 #14(2026-04-24) — 중국 수입 PO 품목 추가 UX 에서 옵션/중문옵션/구매링크를
  // 선택 즉시 폼에 채우기 위해 응답 확장.
  name_cn?: string | null;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null; // [FW-032] additive
  option1_cn?: string | null;
  option2_cn?: string | null;
  purchase_url?: string | null;
  image_url?: string | null;
  // [ERP-WARRANTY-POLICY-SKU-SEARCH-UX-FIX-20260617-001]
  // sku_priority/include_inactive 모드에서 상품상태/활성여부 배지 표시용(옵션 — 기존 호출부 무영향).
  product_status?: string | null;
  is_active?: boolean;
  // [AS-001-AS-PART-MULTISELECT-CURRENT-STOCK-DIFF1-DEV] include_stock=true 호출에서만 존재.
  // inventory_by_location.available_quantity 활성위치 합(재고행 없으면 0). 표시 전용.
  available_quantity?: number;
  // [ERP-AS-ENTITLEMENT-SKU-PICKER-PAGINATION-OPTION-MULTISELECT-DEV-20260804-027]
  // AS 권리 SKU picker 완제품/AS부품 분류·배지용(옵션 — 기존 호출부 무영향).
  part_type?: string | null;
  as_available?: boolean | null;
  category?: string | null;
  // [ERP-PRODUCT-BUNDLE-COMPONENT-SEARCH-EXCLUDE-BUNDLE-SKU-20260619-001]
  // true 면 이미 세트상품(product_bundles.bundle_sku) — 다른 세트 구성품 후보에서 제외.
  is_bundle?: boolean;
  // [ERP-PO-...-111B] SKU 발주 참고 비고 (Product.purchase_note, 표시 전용).
  purchase_note?: string | null;
}

export function searchProducts(
  q: string,
  limit = 10,
  part_type?: string,
  excludeBrands?: string[],
  offset = 0,
): Promise<ProductSearchItem[]> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  if (offset) qs.set("offset", String(offset));
  if (part_type) qs.set("part_type", part_type);
  if (excludeBrands && excludeBrands.length > 0) {
    qs.set("exclude_brands", excludeBrands.join(","));
  }
  return request<ProductSearchItem[]>(`/products/search?${qs}`);
}

// [ERP-WARRANTY-POLICY-SKU-SEARCH-UX-FIX-20260617-001]
// 무상 AS 정책 / 대상 SKU 지정 전용 검색. /products/search 를 sku_priority+include_inactive
// 옵션으로 재사용 — exact SKU 최상단 + 비활성 상품도 배지용으로 포함. 다른 화면 무영향.
export function searchProductsForWarranty(q: string, limit = 20): Promise<ProductSearchItem[]> {
  const qs = new URLSearchParams({
    q,
    limit: String(limit),
    sku_priority: "true",
    include_inactive: "true",
  });
  return request<ProductSearchItem[]>(`/products/search?${qs}`);
}

// [ERP-WARRANTY-POLICY-SKU-SEARCH-PAGINATION-20260617-001]
// 보증정책 대상 SKU "더보기" 전용 페이지네이션 검색. /products/search 를 with_meta=true 로
// 호출해 {items, has_more, next_offset} 를 받는다. sku_priority/include_inactive 옵션은
// 기존 searchProductsForWarranty 와 동일. with_meta 는 백엔드 opt-in 이라 기본 배열 호출(다른 화면)은 무영향.
export interface WarrantySkuSearchPage {
  items: ProductSearchItem[];
  has_more: boolean;
  next_offset: number | null;
}

export function searchProductsForWarrantyPaged(
  q: string,
  offset = 0,
  limit = 20,
): Promise<WarrantySkuSearchPage> {
  const qs = new URLSearchParams({
    q,
    limit: String(limit),
    offset: String(offset),
    sku_priority: "true",
    include_inactive: "true",
    with_meta: "true",
  });
  return request<WarrantySkuSearchPage>(`/products/search?${qs}`);
}

// [ERP-PRODUCT-DETAIL-BUNDLE-SKU-SEARCH-UNIFY-20260617-001]
// 공용 SKU picker 페이지네이션 검색. /products/search 를 sku_priority+with_meta 로 호출해
// {items, has_more, next_offset} 반환. 상품상세 번들 SKU 검색 등에서 재사용.
// (warranty 전용 searchProductsForWarrantyPaged 는 그대로 두고, 공용 흐름은 이 함수로.)
export interface SkuSearchPage {
  items: ProductSearchItem[];
  has_more: boolean;
  next_offset: number | null;
}

export function searchProductsForSkuPicker(
  q: string,
  offset = 0,
  limit = 20,
  includeInactive = true,
  // [ERP-AS-PARTS-SKU-SEARCH-PAGINATION-UNIFY-20260618-001]
  // AS 파츠 검색 등 part_type 필터가 필요한 경우 전달(예: "repair_part").
  // 미전달 시 기존 동작 그대로 — 번들/보증정책 호출 회귀 0.
  partType?: string,
): Promise<SkuSearchPage> {
  const qs = new URLSearchParams({
    q,
    limit: String(limit),
    offset: String(offset),
    sku_priority: "true",
    include_inactive: includeInactive ? "true" : "false",
    with_meta: "true",
  });
  if (partType) qs.set("part_type", partType);
  return request<SkuSearchPage>(`/products/search?${qs}`);
}

// [ERP-GLOBAL-SKU-SEARCH-PAGINATION-PO-ITEM-ADD-FIRST-DEV-20260622-001]
// 전역 SKU 검색 페이지네이션 공용 helper. /products/search 를 with_meta+with_total 로 호출해
// page/page_size/total/total_pages/has_more 까지 받는다(이전/다음/총 N건/전체 페이지 UX).
// 발주서 품목 추가부터 적용. 다른 검색창은 기존 helper 유지(점진 이관 — backend opt-in 이라 회귀 0).
export interface SkuSearchPagedResult {
  items: ProductSearchItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_more: boolean;
  next_offset: number | null;
}

export function searchProductsPaged(
  q: string,
  opts: {
    page?: number;          // 1-base
    pageSize?: number;
    skuPriority?: boolean;   // exact>prefix>contains 우선 정렬 + 브랜드 contains 매칭
    includeInactive?: boolean;
    partType?: string;
    excludeBrands?: string[];
    // [AS-001-AS-PART-MULTISELECT-CURRENT-STOCK-DIFF1-DEV] true 일 때만 include_stock=true 전송
    // → item.available_quantity(현재고) 동봉. 기본 false = 재고 쿼리·필드 없음(다른 화면 무변경).
    includeStock?: boolean;
  } = {},
): Promise<SkuSearchPagedResult> {
  const {
    page = 1, pageSize = 20, skuPriority = true, includeInactive = true, partType, excludeBrands, includeStock = false,
  } = opts;
  const offset = (Math.max(1, page) - 1) * pageSize;
  const qs = new URLSearchParams({
    q,
    limit: String(pageSize),
    offset: String(offset),
    with_meta: "true",
    with_total: "true",
  });
  if (skuPriority) qs.set("sku_priority", "true");
  if (includeInactive) qs.set("include_inactive", "true");
  if (partType) qs.set("part_type", partType);
  if (excludeBrands && excludeBrands.length > 0) qs.set("exclude_brands", excludeBrands.join(","));
  if (includeStock) qs.set("include_stock", "true");
  return request<SkuSearchPagedResult>(`/products/search?${qs}`);
}

export interface AsSettings {
  default_labor_fee: number;
  default_shipping_fee: number;
}

export function getAsSettings(): Promise<AsSettings> {
  return request<AsSettings>("/as/settings");
}

export function updateAsSettings(body: Partial<AsSettings>): Promise<AsSettings> {
  return request<AsSettings>("/as/settings", { method: "PUT", body: JSON.stringify(body) });
}

export interface ApplyCostFactorRequest {
  cost_factor: number;
  cost_price_cny?: number | null;
}

export interface ApplyCostFactorResponse {
  sku: string;
  cost_factor: number;
  cost_price_cny: number | null;
  cost_price: number | null;
}

export function getCostPreset(): Promise<CostPreset> {
  return request<CostPreset>("/cost-calculator/preset");
}

export function saveCostPreset(preset: CostPreset): Promise<CostPreset> {
  return request<CostPreset>("/cost-calculator/preset", { method: "PUT", body: JSON.stringify(preset) });
}

export function applyCostFactor(sku: string, body: ApplyCostFactorRequest): Promise<ApplyCostFactorResponse> {
  return request<ApplyCostFactorResponse>(`/cost-calculator/products/${sku}/apply-cost-factor`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface ProductPrice {
  id: number;
  product_id: number;
  sku: string;
  platform: string;
  price: number;
  is_base_price: boolean;
  fee_rate: number | null;
  margin_rate: number | null;
  memo: string | null;
}

export async function getProducts(params?: { is_active?: boolean }): Promise<Product[]> {
  const query = params?.is_active !== undefined ? `?is_active=${params.is_active}` : "";
  return request<Product[]>(`/products${query}`);
}

export async function getProduct(sku: string): Promise<Product> {
  return request<Product>(`/products/${sku}`);
}

export async function createProduct(data: Partial<Product>): Promise<Product> {
  return request<Product>("/products", { method: "POST", body: JSON.stringify(data) });
}

export async function updateProduct(sku: string, data: Partial<Product>): Promise<Product> {
  return request<Product>(`/products/${sku}`, { method: "PATCH", body: JSON.stringify(data) });
}

// ─────────────────────────────────────────────
// [FW-005-PHASE2] SKU 상세 직접편집 — 기존 GET/PATCH /products/{sku} 의 opt-in 모드(신규 operation 0).
//   GET  ?include_edit_meta=true → 상품 + edit_meta(fingerprint · 플랫폼 메타 · 최근 수정 · 검증 규칙)
//   PATCH { expected_fingerprint, edit_source:"detail", 변경 필드만, platform_meta? }
//     → 200 상품 + edit_meta / 409 product_edit_stale / 422 product_edit_invalid(errors: 필드별 사유)
// ─────────────────────────────────────────────
export type ProductEditFieldRule = {
  label: string;
  kind: "text" | "int" | "number" | "bool" | "enum" | "url";
  required: boolean;
  max_length: number | null;
  choices: string[];
  min: number | null;
  max: number | null;
};

export type ProductEditRules = {
  fields: Record<string, ProductEditFieldRule>;
  sale_price: ProductEditFieldRule;
  platform_fields: Record<"is_listed" | "listing_url" | "price_multiplier", ProductEditFieldRule>;
  platforms: string[];
  read_only: string[];
};

export type ProductEditPlatformMeta = {
  platform: string;
  is_listed: boolean;
  listing_url: string | null;
  price_multiplier: number | null;
};

export type ProductLastEdit = {
  changed_by: string | null;
  changed_at: string | null;
  source: string | null;
  action_type: string;
  summary: string | null;
};

export type ProductEditMeta = {
  fingerprint: string;
  smartstore_price: number | null;
  platform_meta: ProductEditPlatformMeta[];
  last_edit: ProductLastEdit | null;
  updated_at: string | null;
  rules: ProductEditRules;
};

export type ProductWithEditMeta = Product & {
  english_name?: string | null;
  customs_hs_code?: string | null;
  daiwa_stock_location?: string | null;
  edit_meta: ProductEditMeta;
};

export type ProductDetailEditPayload = Record<string, unknown> & {
  expected_fingerprint: string;
  edit_source: "detail";
  platform_meta?: Array<Record<string, unknown>>;   // { platform, 변경된 is_listed/listing_url/price_multiplier }
};

export async function getProductForEdit(sku: string): Promise<ProductWithEditMeta> {
  return request<ProductWithEditMeta>(`/products/${encodeURIComponent(sku)}?include_edit_meta=true`);
}

export async function saveProductDetailEdit(sku: string, payload: ProductDetailEditPayload): Promise<ProductWithEditMeta> {
  return request<ProductWithEditMeta>(`/products/${encodeURIComponent(sku)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// ─────────────────────────────────────────────
// [FW-005-EXCEL] 상품 엑셀로 편집 — POST /products/bulk-edit (mode: load · review · apply)
//   load/review 는 DB write 0. apply 는 단일 atomic batch(오류·stale·가격권한 문제가 하나라도 있으면 전체 거부).
// ─────────────────────────────────────────────
export type BulkEditLoadRow = {
  sku: string;
  fingerprint: string;
  values: Record<string, unknown>;
  sale_price: number | null;
  smartstore_price: number | null;
  platform_meta: ProductEditPlatformMeta[];
  readonly: Record<string, unknown>;
};

export type BulkEditLoadResponse = {
  rows: BulkEditLoadRow[];
  missing: string[];
  rules: ProductEditRules;
  max_rows: number;
};

export type BulkEditRowPayload = {
  sku: string;
  expected_fingerprint: string;
  fields: Record<string, unknown>;
  platform_meta?: Array<Record<string, unknown>>;
};

export type BulkEditSummary = {
  ok: boolean;
  sku_count: number;
  field_count: number;
  unchanged_skus: string[];
  changes: {
    sku: string;
    fields: { field: string; before: unknown; after: unknown }[];
    // [FW-005-COST-PHASE4] 원가 변경 행 — 관리유형 · 서버 계산 원가(KRW)(cny_calc) · 계산 완성 여부
    cost?: { cost_mode: string | null; calculated_cost_price: number | null; calc_complete: boolean | null };
  }[];
  errors: Record<string, Record<string, string>>;
  stale: { sku: string; current: BulkEditLoadRow; last_edit: ProductLastEdit | null }[];
  price_permission_required: boolean;
  price_permission_ok: boolean;
  cost_permission_required?: boolean;   // [FW-005-COST-PHASE2]
  cost_permission_ok?: boolean;
  cost_mode_required_skus?: string[];   // [FW-005-COST-PHASE4] 원가 변경 대상에 포함된 미분류 SKU(전체 거부)
};

/** [FW-005-COST-PHASE2] 다건 편집 감사 경로 — excel(엑셀로 편집) · list_cost_factor(목록 선택 일괄 원가팩터). */
export type BulkEditSource = "excel" | "list_cost_factor";

export type BulkEditApplyResponse = BulkEditSummary & {
  applied: number;
  batch_id: string | null;
  rows: BulkEditLoadRow[];
};

export async function bulkEditLoad(skus: string[]): Promise<BulkEditLoadResponse> {
  return request<BulkEditLoadResponse>("/products/bulk-edit", { method: "POST", body: JSON.stringify({ mode: "load", skus }) });
}

export async function bulkEditReview(rows: BulkEditRowPayload[]): Promise<BulkEditSummary> {
  return request<BulkEditSummary>("/products/bulk-edit", { method: "POST", body: JSON.stringify({ mode: "review", rows }) });
}

export async function bulkEditApply(rows: BulkEditRowPayload[], source: BulkEditSource = "excel"): Promise<BulkEditApplyResponse> {
  return request<BulkEditApplyResponse>("/products/bulk-edit", { method: "POST", body: JSON.stringify({ mode: "apply", rows, source }) });
}

// ─────────────────────────────────────────────
// [FW-005-EXCEL-ALL] 전체상품 Excel 편집 — 같은 POST /products/bulk-edit 의 추가 mode(신규 operation 0)
//   page(전체 조회) · sku_preview(신규 행 예상 SKU) 는 write 0. review/apply 에 신규 행(new_rows)을 함께 보낸다.
// ─────────────────────────────────────────────
export type ExcelLoadRow = BulkEditLoadRow & { china_missing: string[] };

export type ExcelPageQuery = {
  page: number;
  page_size: number;
  q?: string;
  brand?: string;
  category?: string;
  active?: "all" | "Y" | "N";
  purchase_type?: string;
  cost_mode?: string;
  china_missing?: boolean;
  sort?: string;
  dir?: "asc" | "desc";
  skus?: string[];
};

export type ExcelPageResponse = {
  rows: ExcelLoadRow[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  rules: ProductEditRules & { china_import_required?: [string, string][]; page_max?: number };
  max_rows: number;
  // [FW-005-EX2] 신규 행 브랜드 선택지 — 브랜드 SKU 설정이 있는 브랜드(자동채번 가능)
  numbering_brands?: { brand: string; pattern: string; digit_count: number }[];
};

export type ExcelNewRowPayload = {
  temp_id: string;
  sku?: string | null;
  fields: Record<string, unknown>;
  expected_sku?: string | null;
  numbering_fingerprint?: string | null;
};

export type ExcelNewRowReview = {
  temp_id: string;
  sku: string | null;
  sku_source: "auto" | "manual";
  numbering: { pattern: string; source: string; fingerprint: string } | null;
  fields: { field: string; before: unknown; after: unknown }[];
  cost?: { cost_mode: string | null; calculated_cost_price: number | null; calc_complete: boolean | null };
};

export type ExcelEditSummary = BulkEditSummary & {
  new_count: number;
  new_rows: ExcelNewRowReview[];
  new_errors: Record<string, Record<string, string>>;
  new_stale: { temp_id: string; expected_sku: string | null; current_sku: string | null; message: string }[];
  create_permission_required: boolean;
  create_permission_ok: boolean;
  audit_kinds: string[];
};

export type ExcelEditApplyResponse = ExcelEditSummary & {
  applied: number;
  created: { temp_id: string; sku: string }[];
  batch_id: string | null;
  rows: ExcelLoadRow[];
};

export type SkuPreviewRow = { temp_id: string; sku?: string; sku_source: "auto" | "manual"; pattern?: string;
  source?: string; fingerprint?: string; error?: string };

export async function excelEditPage(q: ExcelPageQuery): Promise<ExcelPageResponse> {
  return request<ExcelPageResponse>("/products/bulk-edit", { method: "POST", body: JSON.stringify({ mode: "page", ...q }) });
}

export async function excelEditSkuPreview(newRows: ExcelNewRowPayload[]): Promise<{ rows: SkuPreviewRow[] }> {
  return request<{ rows: SkuPreviewRow[] }>("/products/bulk-edit", {
    method: "POST", body: JSON.stringify({ mode: "sku_preview", new_rows: newRows }) });
}

export async function excelEditReview(rows: BulkEditRowPayload[], newRows: ExcelNewRowPayload[]): Promise<ExcelEditSummary> {
  return request<ExcelEditSummary>("/products/bulk-edit", {
    method: "POST", body: JSON.stringify({ mode: "review", source: "excel", rows, new_rows: newRows }) });
}

export async function excelEditApply(rows: BulkEditRowPayload[], newRows: ExcelNewRowPayload[]): Promise<ExcelEditApplyResponse> {
  return request<ExcelEditApplyResponse>("/products/bulk-edit", {
    method: "POST", body: JSON.stringify({ mode: "apply", source: "excel", rows, new_rows: newRows }) });
}

// ─────────────────────────────────────────────
// [FW-005-HTML] 상품그룹 · 상품 HTML 원문/버전/복원
//   조회 2(GET /products/{sku}/html · GET /products/groups) · 쓰기 2(POST /products/{sku}/html · PATCH /products/{sku}/product-group).
//   적용 HTML 판정(effective_type)은 backend 가 내려준 값만 쓴다. 원문은 저장된 문자열 그대로 주고받는다.
// ─────────────────────────────────────────────
export type ProductHtmlEffectiveType = "group" | "sku_override" | "none";
export type ProductHtmlTarget = "group" | "sku_override";
export type ProductHtmlAction = "save" | "restore" | "clear";

export type ProductHtmlVersionMeta = {
  version_id: number;
  owner_type: "product_group" | "sku_override";
  version_no: number;
  action: ProductHtmlAction;
  has_source: boolean;
  source_length: number;
  source_sha256: string | null;
  restored_from_version_id: number | null;
  memo: string | null;
  created_by_user_id: number | null;
  created_by: string | null;
  created_at: string | null;
  is_current: boolean;
  html_source?: string | null;
};

export type ProductGroupMember = {
  sku: string;
  product_name: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  has_sku_override: boolean;
  is_self: boolean;
};

export type ProductHtmlBundle = {
  sku: string;
  product_name: string;
  product_group: { id: number; name: string; member_count: number; members: ProductGroupMember[] } | null;
  group_current: ProductHtmlVersionMeta | null;
  sku_override_current: ProductHtmlVersionMeta | null;
  effective_type: ProductHtmlEffectiveType;
  effective_version_id: number | null;
  effective_source: string | null;
  group_history: ProductHtmlVersionMeta[];
  group_history_total: number;
  sku_history: ProductHtmlVersionMeta[];
  sku_history_total: number;
  selected_version: ProductHtmlVersionMeta | null;
  limits: { max_source_bytes: number; group_name_max: number; memo_max: number };
  result?: { changed: boolean; version_id: number | null; action: ProductHtmlAction };
};

export type ProductGroupSummary = {
  id: number;
  name: string;
  member_count: number;
  has_html: boolean;
  updated_at: string | null;
};

export type ProductHtmlWritePayload = {
  target: ProductHtmlTarget;
  action: ProductHtmlAction;
  expected_product_group_id: number | null;
  expected_version_id: number | null;
  html_source?: string;
  restore_version_id?: number;
  memo?: string | null;
};

export type ProductGroupAssignPayload = {
  mode: "create" | "attach" | "detach";
  expected_product_group_id: number | null;
  product_group_id?: number;
  name?: string;
};

export async function getProductHtml(sku: string, versionId?: number): Promise<ProductHtmlBundle> {
  const q = versionId != null ? `?version_id=${encodeURIComponent(String(versionId))}` : "";
  return request<ProductHtmlBundle>(`/products/${encodeURIComponent(sku)}/html${q}`);
}

export async function searchProductGroups(q: string, limit = 20): Promise<ProductGroupSummary[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (q.trim()) params.set("q", q.trim());
  return request<ProductGroupSummary[]>(`/products/groups?${params.toString()}`);
}

export async function writeProductHtml(sku: string, payload: ProductHtmlWritePayload): Promise<ProductHtmlBundle> {
  return request<ProductHtmlBundle>(`/products/${encodeURIComponent(sku)}/html`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function assignProductGroup(sku: string, payload: ProductGroupAssignPayload): Promise<ProductHtmlBundle> {
  return request<ProductHtmlBundle>(`/products/${encodeURIComponent(sku)}/product-group`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getProductPrices(sku: string): Promise<ProductPrice[]> {
  return request<ProductPrice[]>(`/products/${sku}/prices`);
}

export async function upsertPrice(sku: string, data: Partial<ProductPrice>): Promise<ProductPrice> {
  return request<ProductPrice>(`/products/${sku}/prices`, { method: "PUT", body: JSON.stringify(data) });
}

// ─── Import ─────────────────────────────────

// ─── Import Preview ─────────────────────────
export interface ImportPreviewResponse {
  detected_import_type: string;
  is_valid: boolean;
  validation_errors: string[];
  warnings: string[];
  file_name: string;
  detected_company_hint: string | null;
  selected_company_code: string;
  matched_company_confidence: string;
  row_count: number;
  sample_rows: Record<string, string>[];
  detected_headers: string[];
  header_row_idx: number;
  duplicate_order_nos_count: number | null;
}

// ─────────────────────────────────────────────
// [ETC-001-PHASE4B4A] 업로드(FormData) 전용 실패 변환기.
// FormData 는 boundary 때문에 request() 를 쓸 수 없어 raw fetch 를 유지하되,
// 실패 시 status·detail 을 잃지 않도록 ApiError 로 통일한다.
// 이전 구현들은 본문만 Error 로 감싸서 403 을 파싱 오류와 구분할 수 없었다.
// ─────────────────────────────────────────────
async function uploadApiError(res: Response, fallback: string): Promise<ApiError> {
  const raw = await res.text();
  let detail: unknown = raw;
  let msg = raw || fallback;
  let errorCode: string | null = null;
  try {
    const parsed = JSON.parse(raw);
    detail = parsed?.detail ?? parsed;
    if (typeof detail === "string") {
      msg = detail;
    } else if (detail && typeof detail === "object") {
      const d = detail as Record<string, unknown>;
      if (typeof d.code === "string") errorCode = d.code;
      if (typeof d.error_code === "string") errorCode = d.error_code;
      msg = (typeof d.message === "string" ? d.message : null) ?? fallback;
    }
  } catch {
    /* JSON 이 아니면 원문을 그대로 쓴다 */
  }
  return new ApiError(`[${res.status}] ${msg}`, res.status, errorCode, detail);
}

async function uploadFilePreview(path: string, file: File, companyCode: string): Promise<ImportPreviewResponse> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}${path}?company_code=${encodeURIComponent(companyCode)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    throw await uploadApiError(res, "미리보기 처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

export function previewProductMaster(file: File, companyCode = "HITCAMP"): Promise<ImportPreviewResponse> {
  return uploadFilePreview("/products/import/product-master/preview", file, companyCode);
}

export function previewToggleOrders(file: File, companyCode = "HITCAMP"): Promise<ImportPreviewResponse> {
  return uploadFilePreview("/orders/import/toggle/preview", file, companyCode);
}

export interface ImportErrorRow {
  row: number;
  sku: string | null;
  error: string;
}

export interface ImportResult {
  total_rows: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  errors: ImportErrorRow[];
}

// ─── Order Import ────────────────────────────

export interface OrderImportResult {
  total_rows: number;
  inserted_orders: number;
  updated_orders: number;
  inserted_items: number;
  updated_items: number;
  skipped_rows: number;
  sku_matched: number;
  sku_unmatched: number;
  new_customers: number;
  existing_customers: number;
  errors: Array<{ order_no?: string; error: string }>;
  header_row_idx: number;
  detected_headers: string[];
  sku_samples: Array<{ order_no: string; seller_codes: string[]; matched_sku: string | null; product_name: string }>;
  customer_samples: Array<{ action: string; order_no: string; customer_name: string; phone_normalized: string | null; customer_id?: number }>;
  status_samples: Array<{ order_no: string; raw_status: string; order_status: string; is_cancelled: boolean; is_returned: boolean }>;
  // [ERP-HOLD-FLOW-20260518-007] 출고중지 완료 이력 주문 자동 덮어쓰기 차단 결과.
  protected_hold_skipped_count?: number;
  protected_hold_skipped_orders?: Array<{
    order_no: string;
    receiver_name?: string | null;
    invoice_no?: string | null;
    reason?: string;
  }>;
  // [ORDER-001-PHASE2] 재업로드 충돌 보류(원자 롤백, DB 미반영) 결과.
  conflict_order_count?: number;
  conflict_orders?: Array<{ order_no: string; reason: string }>;
  reupload_reconciled_orders?: number;
  // [ORDER-REUPLOAD-PARTIAL-CANCEL-CONFIRM-A] 부분취소 확인 필요(review) 생성/교체 · 대기 중 미적용 재업로드.
  //   이 값은 이번 업로드 요약일 뿐 — 지속 목록은 listPartialCancelReviews() 로 다시 조회한다.
  partial_cancel_review_count?: number;
  partial_cancel_reviews?: Array<{ order_no: string; review_id: number; action: string; missing_items: PartialCancelItem[] }>;
  partial_cancel_held_count?: number;
  partial_cancel_held?: Array<{ order_no: string; review_id: number }>;
  // [TOGGLE-REUPLOAD-PACKED-INVOICE-CHANGE-WARN-20261103] 포장완료 주문의 송장이 이번 업로드로 바뀐 건(반영됨 · 경고만).
  packed_invoice_changed_count?: number;
  packed_invoice_changed_orders?: Array<{
    order_no: string;
    receiver_name?: string | null;
    packed_invoice_no: string;
    prev_invoice_no?: string | null;
    new_invoice_no?: string | null;
  }>;
}

// ─── Order Review ────────────────────────────

export interface DaiwaItemInfo {
  sku: string;
  product_name: string;
  stock_location: string;
}

export interface ImportBatchSummary {
  id: number;
  file_name: string | null;
  uploaded_at: string | null;
  order_count: number;
  uploaded_by: string | null;
}

export interface OrderReviewItem {
  // [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001] hide 대상 식별 + 숨김 표시.
  order_id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_type: string;
  order_date: string | null;
  total_product_amount: number | null;
  paid_amount: number | null;
  item_count: number;
  sku_matched_count: number;
  sku_unmatched_count: number;
  receiver_name: string | null;
  order_status: string | null;
  // [FW-004] 배송중 계열 status 가 포장완료 자동전이로만 생긴 택배사 인계 전 상태(표시 `포장완료(인계 전)`).
  packing_handover_pending?: boolean;
  is_cancelled: boolean;
  is_returned: boolean;
  daiwa_direct_eligible: boolean;
  is_daiwa_direct_ship: boolean;
  daiwa_invoice_no: string | null;
  daiwa_stock_location?: string | null;
  daiwa_fulfillment_choice?: string | null;
  daiwa_items?: DaiwaItemInfo[];
  // 26차 후속 #65 — 운영 판단용 보강 필드 (펼침 행 / 인라인 표시)
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  invoice_no?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  raw_status?: string | null;
  memo?: string | null;
  first_product_name?: string | null;
  first_option_name?: string | null;
  has_active_hold?: boolean;
  // [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001] 당일주문검토 큐 숨김 상태.
  review_hidden_at?: string | null;
  review_hidden_reason?: string | null;
}

export interface OrderReviewResponse {
  total: number;
  items: OrderReviewItem[];
  order_type_counts: Record<string, number>;
  import_batches: ImportBatchSummary[];
  // 26차 후속 #64 — 미출고/합포장 통계 (해당 날짜 업로드 전체 기준, 검색/필터 무관)
  pending_total: number;
  package_count: number;
  // 26차 후속 #67 — 페이지네이션 메타
  page: number;
  page_size: number;
  total_pages: number;
}

// 26차 후속 #67 — 전체 주문 검색 (고객 DB 관리 주문 검색 탭용)

export interface OrderSearchItem {
  order_id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_date: string | null;
  order_status: string | null;
  raw_status: string | null;
  is_cancelled: boolean;
  is_returned: boolean;
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  invoice_no: string | null;
  paid_amount: number | null;
  item_count: number;
  total_quantity: number;
  first_product_name: string | null;
  first_option_name: string | null;
  other_distinct_count: number;
  // [CUSTOMER-001] 유효 OrderItem SKU별 행. 비어 있으면(legacy/AS 배송 등) 주문정보 1행 fallback.
  sku_rows?: OrderSearchSkuRow[];
  // [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001] 숨김 상태.
  review_hidden_at?: string | null;
  review_hidden_reason?: string | null;
  // [AS-002] AS권 소유권(원구매자=customer_name / 현재 소유자 / 과거 승계고객 판정용). 매출/집계 무변경.
  as_transferred?: boolean;
  as_current_owners?: { customer_id: number; customer_name: string | null }[];
  // [AS-002 DIFF2] 과거 승계고객(현재 소유자·원구매자 제외, 중복 제거).
  as_past_owners?: { customer_id: number; customer_name: string | null }[];
  as_transfer_participant_ids?: number[];
  // [FW-004] 표시용 — 포장완료 자동전이로만 생긴 배송중 계열 status(택배사 인계 전).
  packing_handover_pending?: boolean;
  // [FW-004] with_linked=true 일 때만 채워진다.
  //   addon_rows    = 이 주문의 추가 합배송 품목(같은 주문번호)
  //   linked_orders = 이 주문이 대표인 합배송 그룹의 추가 주문(별도 주문번호) — 최상위 행에는 다시 나오지 않는다.
  //   linked_parent = 이 주문이 추가 주문이고 대표 주문이 같은 결과에 없을 때 대표 주문.
  addon_rows?: OrderSearchAddonRow[];
  linked_orders?: OrderSearchLinkedOrder[];
  linked_parent?: { order_no: string; group_no: string } | null;
}

// [FW-004] 고객 주문 검색 — 원주문 아래 추가 합배송 품목.
export interface OrderSearchAddonRow {
  addon_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number;
  total_price: number | null;
  payment_status: string | null;
  packed_at: string | null;
  created_at: string | null;
}

// [FW-004] 고객 주문 검색 — 대표 주문 아래 합배송 추가 주문(별도 주문번호).
export interface OrderSearchLinkedOrder {
  order_id: number;
  order_no: string;
  group_no: string;
  group_status: string | null;
  order_date: string | null;
  order_status: string | null;
  raw_status: string | null;
  is_cancelled: boolean;
  paid_amount: number | null;
  invoice_no: string | null;
  packing_handover_pending: boolean;
  sku_rows: OrderSearchSkuRow[];
  addon_rows: OrderSearchAddonRow[];
}

// [CUSTOMER-001] 유효 OrderItem(비취소·비dedup) SKU별 행.
export interface OrderSearchSkuRow {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  option1: string | null;
  option2: string | null;
  quantity: number;
  unit_price: number | null;
  line_amount: number | null;
}

export interface OrderSearchResponse {
  items: OrderSearchItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  // [ERP-...-113] "all_time"(검색어 있음, 날짜무시) | "date_range".
  search_scope?: "all_time" | "date_range";
}

export function searchOrders(params: {
  q?: string;
  date_from?: string;
  date_to?: string;
  platform?: string;
  order_status?: string;
  sku_unmatched_only?: boolean;
  customer_id?: number;
  page?: number;
  page_size?: number;
  // [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001] 숨김 필터.
  include_hidden?: boolean;
  hidden_only?: boolean;
  // [CUSTOMER-001] 취소 주문·취소 품목 제외(고객 주문내역 가시성).
  exclude_cancelled?: boolean;
  // [FW-004] 원주문 아래 추가 합배송·합배송 추가 주문 묶음(고객 DB 관리 화면 전용).
  with_linked?: boolean;
}): Promise<OrderSearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.platform) qs.set("platform", params.platform);
  if (params.order_status) qs.set("order_status", params.order_status);
  if (params.sku_unmatched_only) qs.set("sku_unmatched_only", "true");
  if (params.customer_id != null) qs.set("customer_id", String(params.customer_id));
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  if (params.include_hidden) qs.set("include_hidden", "true");
  if (params.hidden_only) qs.set("hidden_only", "true");
  if (params.exclude_cancelled) qs.set("exclude_cancelled", "true");
  if (params.with_linked) qs.set("with_linked", "true");
  return request<OrderSearchResponse>(`/orders/search?${qs}`);
}

// [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001]
// 일반 주문 숨김(soft hide, LV2 이상). 주문 상세/고객주문 내역 공용. 원본/고객/재고 무변경.
export function hideOrder(orderId: number, reason?: string): Promise<OrderReviewHideResponse> {
  return request<OrderReviewHideResponse>(`/orders/${orderId}/hide`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

// [ERP-ORDER-ADMIN-UNHIDE-AND-ONEOFF-RESTORE-20260825] 관리자(lv3) 주문 숨김 해제.
// 일반 주문 review_hidden_* 만 해제(재노출). 분할자식은 backend 409. 권한 SoT=backend lv3.
export function unhideOrder(orderId: number): Promise<OrderReviewHideResponse> {
  return request<OrderReviewHideResponse>(`/orders/${orderId}/unhide`, { method: "POST" });
}

export function setDaiwaFulfillmentChoice(
  orderNo: string,
  choice: "hitcamp" | "daiwa",
): Promise<{ ok: boolean }> {
  return request(`/orders/${encodeURIComponent(orderNo)}/daiwa-fulfillment?choice=${choice}`, {
    method: "POST",
  });
}

export function getOrderReview(params?: {
  order_type?: string;
  sku_unmatched_only?: boolean;
  platform?: string;
  search?: string;
  // 26차 후속 #67 — page/page_size 페이지네이션
  page?: number;
  page_size?: number;
  review_date?: string;
  pending_only?: boolean;
  // [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001] 숨김 포함 조회.
  include_hidden?: boolean;
}): Promise<OrderReviewResponse> {
  const qs = new URLSearchParams();
  if (params?.order_type) qs.set("order_type", params.order_type);
  if (params?.sku_unmatched_only) qs.set("sku_unmatched_only", "true");
  if (params?.platform) qs.set("platform", params.platform);
  if (params?.search) qs.set("search", params.search);
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.page_size != null) qs.set("page_size", String(params.page_size));
  if (params?.review_date) qs.set("review_date", params.review_date);
  if (params?.pending_only != null) qs.set("pending_only", String(params.pending_only));
  if (params?.include_hidden) qs.set("include_hidden", "true");
  return request<OrderReviewResponse>(`/orders/review?${qs}`);
}

// [ERP-LONG-UNSHIPPED-HOLD-MANAGEMENT-PHASE-B2-FRONTEND-20260714-087]
// 장기 미출고/보류/상태확인 4-큐 (Phase B-2A 운영 API 연결).
export type UnshippedQueueType = "general" | "long_unshipped" | "hold" | "needs_review";
export type ShipmentHoldReason =
  | "awaiting_stock" | "customer_agreed_wait" | "platform_cancel_check" | "invoice_reissue"
  | "carrier_pickup_delay" | "packing_pending" | "employee_followup" | "other";

export interface UnshippedQueueItem {
  id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_date: string | null;
  order_status: string | null;
  raw_status: string | null;
  customer_name: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  invoice_no: string | null;
  paid_amount: number | null;
  item_count: number;
  first_product_name: string | null;
  invoice_registered_at: string | null;
  unshipped_business_days: number | null;
  unshipped_warning_level: string | null;   // normal|warning|urgent|overdue_expected_date|invoice_time_unknown
  shipment_hold_reason: ShipmentHoldReason | null;
  expected_ship_date: string | null;        // date-only (YYYY-MM-DD)
  customer_notified: boolean;
  customer_notified_at: string | null;
  platform_delay_processed: boolean;
  platform_delay_processed_at: string | null;
  shipment_last_checked_at: string | null;
  shipment_last_checked_by_user_id: number | null;
  shipment_last_checked_by_name: string | null;
  shipment_hold_memo: string | null;
  shipment_mismatch_reasons: string[];
  review_hidden_at: string | null;
}

export interface UnshippedQueueResponse {
  total: number;
  page: number;
  page_size: number;
  items: UnshippedQueueItem[];
  count_general: number;
  count_long_unshipped: number;
  count_hold: number;
  count_needs_review: number;
}

export interface ShipmentHoldUpdatePayload {
  shipment_hold_reason?: ShipmentHoldReason | null;
  expected_ship_date?: string | null;
  customer_notified?: boolean;
  platform_delay_processed?: boolean;
  shipment_hold_memo?: string | null;
  clear?: boolean;
}

export function getUnshippedQueue(params: {
  queue: UnshippedQueueType;
  page?: number;
  page_size?: number;
  platform?: string;
  store?: string;
  hold_reason?: string;
  q?: string;
}): Promise<UnshippedQueueResponse> {
  const qs = new URLSearchParams();
  qs.set("queue", params.queue);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  if (params.platform) qs.set("platform", params.platform);
  if (params.store) qs.set("store", params.store);
  if (params.hold_reason) qs.set("hold_reason", params.hold_reason);
  if (params.q) qs.set("q", params.q);
  return request<UnshippedQueueResponse>(`/orders/unshipped-queue?${qs}`);
}

export function updateShipmentHold(orderNo: string, payload: ShipmentHoldUpdatePayload): Promise<{ ok: boolean; order_no: string; cleared: boolean }> {
  return request(`/orders/${encodeURIComponent(orderNo)}/shipment-hold`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function markShipmentChecked(orderNo: string, body?: { memo?: string; platform_cancel_checked?: boolean }): Promise<{ ok: boolean; order_no: string; shipment_last_checked_at: string }> {
  return request(`/orders/${encodeURIComponent(orderNo)}/shipment-check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
}

// [ERP-TOGGLE-DAILY-PACKING-DASHBOARD-ROLLOVER-AND-HOLD-WORKFLOW-20260715-106]
// 당일 포장 업무 현황판 (read-only). packed_at 날짜 기준 일별 포장완료 + 이월 포장대기 + 고객대기/보류.
export type PackingBoardTab = "all" | "pending" | "rollover" | "packed" | "hold";
export type PackingRowStatus =
  | "pending" | "rollover" | "completed" | "hold" | "awaiting_stock" | "platform_cancel_check";
export type OrderKind = "as" | "manual" | "online";

export interface PackingBoardItem {
  order_id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_date: string | null;
  created_at: string | null;
  order_status: string | null;
  raw_status: string | null;
  order_type: string | null;
  order_kind: OrderKind;
  receiver_name: string | null;
  invoice_no: string | null;
  item_count: number;
  first_product_name: string | null;
  first_option_name: string | null;
  memo: string | null;
  packing_status: PackingRowStatus;
  is_rollover: boolean;
  rollover_days: number | null;
  packed_at: string | null;
  is_packed: boolean;
  hold_reason: ShipmentHoldReason | null;
  expected_ship_date: string | null;
  customer_notified: boolean;
  platform_delay_processed: boolean;
  shipment_hold_memo: string | null;
  invoice_state: "has_invoice" | "print_pending";
  // [ORDER-003 Phase 5] 표시용 출처·수령 방법(read-only · additive). 운영 backend 미반영 시 없음.
  order_source?: string | null;
  receipt_method?: ReceiptMethod | null;
}

export interface PackingBoardResponse {
  date: string;
  is_today: boolean;
  tab: PackingBoardTab;
  new_packing_target_count: number;
  rollover_pending_count: number;
  packed_on_selected_date_count: number;
  hold_count: number;
  current_pending_count: number;
  all_target_count: number;
  // [ERP-...-108] 포장수(합포장 단위) additive.
  current_pending_package_count: number;
  rollover_package_count: number;
  packed_package_count: number;
  hold_package_count: number;
  new_packing_target_package_count: number;
  // [ERP-...-110] 주문수(상품·옵션 주문행 수) additive. 운영 backend reload 전에는 미포함될 수 있어 optional.
  current_pending_order_line_count?: number;
  rollover_order_line_count?: number;
  packed_order_line_count?: number;
  hold_order_line_count?: number;
  new_packing_target_order_line_count?: number;
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  items: PackingBoardItem[];
  // [ORDER-003 Phase 5] 택배 지정 POS 주문 중 포장 대상에서 빠진 주문 + 사유(read-only · additive).
  pos_courier_attention?: PosCourierAttention[];
}

export function getPackingDailyBoard(params: {
  review_date?: string;
  tab?: PackingBoardTab;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<PackingBoardResponse> {
  const qs = new URLSearchParams();
  if (params.review_date) qs.set("review_date", params.review_date);
  if (params.tab) qs.set("tab", params.tab);
  if (params.search) qs.set("search", params.search);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  return request<PackingBoardResponse>(`/orders/packing/daily-board?${qs}`);
}

// [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001]
// 당일주문검토 큐에서 개별 주문 숨김(soft hide, LV2 이상). 원본 주문/고객 데이터 삭제 아님.
export interface OrderReviewHideResponse {
  order_id: number;
  order_no: string;
  review_hidden_at: string | null;
  review_hidden_reason: string | null;
  already_hidden: boolean;
  message: string;
}
export function hideOrderFromReview(orderId: number, reason?: string): Promise<OrderReviewHideResponse> {
  return request<OrderReviewHideResponse>(`/orders/review/${orderId}/hide`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export async function updateOrderType(orderNo: string, orderType: string, memo?: string): Promise<void> {
  await request(`/orders/order-type/${encodeURIComponent(orderNo)}?order_type=${orderType}${memo ? `&memo=${encodeURIComponent(memo)}` : ""}`, {
    method: "PATCH",
  });
}

export async function setDaiwaDirctShip(
  orderNo: string,
  params: { invoice_no?: string; requested_by?: string },
): Promise<{ order_no: string; is_daiwa_direct_ship: boolean; daiwa_invoice_no: string | null }> {
  const qs = new URLSearchParams();
  if (params.invoice_no) qs.set("invoice_no", params.invoice_no);
  if (params.requested_by) qs.set("requested_by", params.requested_by);
  return request(`/orders/${encodeURIComponent(orderNo)}/set-direct-ship?${qs}`, { method: "POST" });
}

// ─── Order Detail ────────────────────────────

export interface OrderItemDetail {
  id: number;
  order_item_key: string | null;
  product_name: string | null;
  option_name: string | null;
  // [ERP-ORDER-DETAIL-ITEM-OPTION2-DISPLAY-20260527-001]
  // products.option1/option2 join 결과. 매칭 SKU 가 없으면 null.
  option1?: string | null;
  option2?: string | null;
  // [FW-032] products.option3 (additive). product_matched = sku 가 현재 products 에 있음(교환 '회수 품목 복사' 기준).
  option3?: string | null;
  product_matched?: boolean | null;
  sku: string | null;
  order_type: string;
  quantity: number | null;
  unit_price: number | null;
  line_amount: number | null;
  // [ERP-STORE-POS-ADDITIONAL-DISCOUNT-PERSIST-DEV-20260922-002] 행별 할인 SoT(additive).
  line_discount_amount?: number | null;
  line_discount_rate?: number | null;           // = 상품결제금액
  // raw 원본값 (합산 금지)
  raw_shipping_fee: number | null;
  raw_additional_shipping_fee: number | null;
  raw_order_amount: number | null;
  raw_fee_amount: number | null;
  raw_settlement_amount: number | null;
  item_status: string | null;
  raw_item_status: string | null;
  is_cancelled: boolean;
  is_returned: boolean;
  is_exchange: boolean;
  memo: string | null;
  // SKU 매칭 힌트 (Tier 2 수동 매칭 UI 분류)
  // matched | no_catalog_match | ambiguous_multi_match | bundle_set
  // | as_parts_notation | as_repair_notes | numeric_vendor_id | unknown
  match_hint?: string | null;
  // [ERP-ORDER-INVOICE-SPLIT-ITEM-MOVE-REDESIGN-20260529-001]
  // moved_out 항목은 분할자식으로 이동된 이력 → 원주문 스캔/재고차감 제외.
  split_status?: string;             // none | moved_out
  split_child_order_no?: string | null;
  split_child_invoice_no?: string | null;
  split_moved_at?: string | null;
  // [ORDER-003 Phase 5] 최종 출고 identity(read-only). 활성 ORDER-003 연결행이 있으면 그 SKU·상품명·옵션.
  //   sku/product_name/option_name 은 원 주문 품목 identity(불변) 그대로다.
  effective_sku?: string | null;
  effective_product_name?: string | null;
  effective_option_name?: string | null;
  linked_change_id?: number | null;
}

// ─── [ORDER-003] 수령 방법 · 출고 전 주문정보 수정 (backend canonical 판정의 표시용 형태) ───
export type ReceiptMethod = "store_pickup" | "courier";

/** 주문 상세 additive — 수정 가능 상태 요약(판정 SoT = backend order_shipping_edit). */
export interface ShippingEditCapability {
  effective_receipt_method: ReceiptMethod;
  receipt_method: ReceiptMethod | null;           // DB raw(NULL = legacy 미지정)
  effective_customer_name?: string | null;
  delivery_request_note: string | null;            // orders.memo 와 분리된 배송 요청사항
  shipping_ready: boolean;
  missing_shipping_fields: string[];
  edit_allowed: boolean;
  edit_block_code: string | null;
  edit_block_reason: string | null;
  purchase_edit_allowed?: boolean;
  purchase_edit_block_code?: string | null;
  purchase_edit_block_reason?: string | null;
  exported: boolean;
  invoice_registered: boolean;
  packing_started: boolean;
  shipped: boolean;
  active_amendment_no: number | null;
  active_amendment_status: string | null;
  purchase_amendment_supported?: boolean;
}

export interface PurchaseAmendmentLineView {
  order_item_id: number | null;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  effective_sku: string | null;
  effective_product_name: string | null;
  effective_option_name: string | null;
  quantity: number | null;
  unit_price: string | null;
  discount_rate: string | null;
  discount_amount: string | null;
  line_amount: string | null;
}

export interface PurchaseAmendmentStateView {
  items: PurchaseAmendmentLineView[];
  additional_discount: string | null;
  shipping_fee: string | null;
  memo: string | null;
  total_product_amount: string | null;
  discount_amount: string | null;
  paid_amount: string | null;
}

/** 서버 재고 변화 계획(요청 생성 시 고정). mode: immediate(POS · 정산 확인 시 반영) | deferred_to_packing(비-POS · 포장완료 시). */
export interface StockDeltaView {
  mode?: string;
  skus?: { sku: string; before_qty: number; after_qty: number; delta: number; location: string | null }[];
}

/** 진행 중 구매정보 수정 요청(read-only). 금액·차액은 서버가 요청 생성 시 고정한 값. */
export interface ActivePurchaseAmendmentView {
  amendment_no: number;
  status: "pending" | "awaiting_settlement";
  amount_before: string;
  amount_after: string;
  diff_amount: string;
  settlement_type: "equal" | "additional_payment" | "refund";
  settlement_status: string | null;
  expected_settled_amount: string;
  changed_fields: string[];
  stock_delta?: StockDeltaView | null;
  before: PurchaseAmendmentStateView;
  after: PurchaseAmendmentStateView;
  reason: string | null;
  admin_memo: string | null;
  created_at: string | null;
  created_by_name: string | null;
  stale: boolean | null;
  gate_allowed: boolean;
  gate_block_code: string | null;
  gate_block_reason: string | null;
}

/** 택배 지정 POS 주문 중 포장·Export 대상에서 빠진 주문(표시 전용 · 사유 = backend fulfillment_block). */
export interface PosCourierAttention {
  order_no: string;
  order_status: string | null;
  receiver_name: string | null;
  created_at: string | null;
  block_code: string | null;
  block_reason: string | null;
  missing_fields: string[];
  amendment_no: number | null;
}

export interface OrderDetail {
  id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_type: string;
  order_date: string | null;
  paid_date: string | null;
  order_status: string | null;
  raw_status: string | null;
  // [FW-004] 배송중 계열 status 가 포장완료 자동전이로만 생긴 택배사 인계 전 상태(표시 `포장완료(인계 전)`).
  packing_handover_pending?: boolean;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  // 정규화 금액 (대시보드 집계용)
  total_product_amount: number | null;
  shipping_fee: number | null;
  additional_shipping_fee: number | null;
  // [ERP-STORE-POS-AMOUNT-HARDENING-DEV-20260923-001]
  // 저장된 주문 레벨 할인. POS 신규 주문은 "추가 할인", 그 외(과거 POS 포함)는 일반 주문 할인이며
  // 컬럼만으로는 둘을 구분할 provenance 가 없다 → 화면 공통 라벨은 "주문 할인".
  discount_amount: number | null;
  paid_amount: number | null;
  fee_amount: number | null;
  settlement_amount: number | null;
  is_cancelled: boolean;
  is_returned: boolean;
  is_exchange: boolean;
  source_file_name: string | null;
  memo: string | null;
  // [ERP-STORE-POS-ADDITIONAL-DISCOUNT-PERSIST-DEV-20260922-002] 금액 표기 분기용(additive).
  order_source?: string | null;
  // [ERP-PACKING-REQUEST-NOTE-COLUMNS-AND-HIGHLIGHT-20260527-001]
  packing_request_note?: string | null;
  packing_request_updated_at?: string | null;
  packing_request_updated_by_name?: string | null;
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: OrderItemDetail[];
  // [CUSTOMER-001] 송장번호 목록(dedup). 비어 있으면 '-' 표시.
  invoice_nos?: string[];
  // [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001] 숨김 상태(배지).
  review_hidden_at?: string | null;
  review_hidden_reason?: string | null;
  // [ORDER-003] 배송 상세주소 · 수정 가능 상태 · 진행 중 구매정보 수정 요청(additive).
  address2?: string | null;
  shipping_edit?: ShippingEditCapability | null;
  active_purchase_amendment?: ActivePurchaseAmendmentView | null;
}

export async function getOrderDetail(orderNo: string): Promise<OrderDetail> {
  return request<OrderDetail>(`/orders/${encodeURIComponent(orderNo)}`);
}

// ─── [ORDER-003 Phase 2] 고객·배송정보 수정 — 단일 원자 endpoint (금액 불변) ───
export interface ShippingInfoUpdateRequest {
  customer_name?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  delivery_request_note?: string | null;
  receipt_method?: ReceiptMethod | null;
  reason?: string | null;
}

export interface ShippingInfoUpdateResponse {
  order_no: string;
  no_op: boolean;
  changed_fields: string[];
  amendment_no: number | null;
  amendment_id: number | null;
  settlement_id: number | null;
  capability: ShippingEditCapability;
}

export function updateOrderShippingInfo(
  orderNo: string, body: ShippingInfoUpdateRequest,
): Promise<ShippingInfoUpdateResponse> {
  return request<ShippingInfoUpdateResponse>(`/orders/${encodeURIComponent(orderNo)}/shipping-info`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// ─── [ORDER-003 Phase 3] 구매정보 수정 — 요청(pending) → 확인 → 정산 확인(원자 적용) ───
//   단계 전이·금액·재고는 전부 서버가 판정한다. 화면은 응답 값을 그대로 표시한다.
export interface PurchaseAmendmentItemInput {
  order_item_id: number;
  sku?: string;
  quantity?: number;
  unit_price?: number;
  discount_rate?: number | null;
  discount_amount?: number;
  stock_location?: "office" | "warehouse";
}

export interface PurchaseAmendmentCreateRequest {
  items?: PurchaseAmendmentItemInput[];
  additional_discount?: number;
  shipping_fee?: number;
  memo?: string | null;
  reason?: string | null;
}

export interface PurchaseAmendmentResponse {
  order_no: string;
  no_op: boolean;
  idempotent: boolean;
  amendment_id: number | null;
  amendment_no: number | null;
  status: string | null;
  amount_before: string | null;
  amount_after: string | null;
  diff_amount: string | null;
  changed_fields: string[];
  stock_delta: StockDeltaView | null;
  settlement_id: number | null;
  settlement_type: string | null;
  settlement_status: string | null;
  settled_amount: string | null;
}

// ─── [ORDER-REUPLOAD-PARTIAL-CANCEL-CONFIRM-A] 재업로드 부분취소 확인 ───────────
// 같은 주문번호 재업로드에서 기존 활성 품목이 파일에 없으면 자동 취소하지 않고 review(pending)로 보류한다.
// pending 동안 포장·출고는 backend 가 409(ORDER_PARTIAL_CANCEL_REVIEW_PENDING)로 차단한다.
export interface PartialCancelItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number;
  unit_price: number | null;
  line_amount: number | null;
  is_cancelled?: boolean;
  item_status?: string | null;
  raw_item_status?: string | null;
}

export interface PartialCancelReuploadItem {
  row_idx: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number;
  unit_price?: number | null;
  line_amount: number | null;
  raw_item_status?: string | null;
  is_cancelled?: boolean;
  matched_order_item_id?: number | null;
}

export interface PartialCancelBlocker {
  code: string;
  label: string;
  count: number;
}

export interface PartialCancelReviewView {
  id: number;
  order_id: number;
  order_no: string;
  status: "pending" | "partial_cancel_confirmed" | "original_kept";
  import_batch_id: number | null;
  source_file_name: string | null;
  first_detected_at: string;
  last_detected_at: string;
  detect_count: number;
  candidate_replaced: boolean;
  existing_snapshot: {
    order?: Record<string, unknown>;
    items?: Array<{
      id: number; sku: string | null; product_name: string | null; option_name: string | null;
      quantity: number; unit_price: number | null; line_amount: number | null;
      is_cancelled: boolean; item_status: string | null;
    }>;
  };
  missing_items: PartialCancelItem[];
  reupload_items: PartialCancelReuploadItem[];
  payload_hash: string;
  payload_schema_version: number;
  last_held_upload: {
    at?: string; source_file_name?: string | null; import_batch_id?: number | null; reason?: string;
  } | null;
  decided_by_user_id: number | null;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_memo: string | null;
  apply_result: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  block_code: string | null;
  block_message: string | null;
  blockers: PartialCancelBlocker[];
  stale: boolean | null;
  shipping_decision?: PartialCancelShippingDecision | null;
}

export type PartialCancelDecision = "partial_cancel_confirmed" | "original_kept" | "deferred";

// [ORDER-REUPLOAD-PARTIAL-CANCEL-SHIPPING-CHOICE] 부분취소 확정 시 배송비 처리 선택(정책 선택만 — 금액은 서버 계산).
export type PartialCancelShippingChoice = "keep_existing" | "use_reupload";

export interface PartialCancelHeaderAmounts {
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  discount_amount: number;
  fee_amount: number;
  settlement_amount: number;
}

export interface PartialCancelShippingDecision {
  required: boolean;
  /** 재업로드가 필드별로 헤더 금액을 덮어쓰는지(false = import 보호 필드 — 두 선택지 모두 기존 값). */
  updated_fields?: Partial<Record<keyof PartialCancelHeaderAmounts, boolean>>;
  before: PartialCancelHeaderAmounts;
  candidate: PartialCancelHeaderAmounts & { raw_shipping_fee: number };
  options: Record<PartialCancelShippingChoice, PartialCancelHeaderAmounts>;
}

export function listPartialCancelReviews(
  params: { status?: "pending" | "partial_cancel_confirmed" | "original_kept" | "all"; order_no?: string; limit?: number } = {},
): Promise<{ items: PartialCancelReviewView[]; total: number }> {
  const q = new URLSearchParams();
  q.set("status", params.status ?? "pending");
  if (params.order_no) q.set("order_no", params.order_no);
  if (params.limit) q.set("limit", String(params.limit));
  return request(`/orders/reupload-partial-cancel-reviews?${q.toString()}`);
}

export function decidePartialCancelReview(
  reviewId: number,
  body: {
    decision: PartialCancelDecision;
    payload_hash?: string | null;
    memo?: string | null;
    shipping_choice?: PartialCancelShippingChoice | null;
  },
): Promise<{ review: PartialCancelReviewView; idempotent: boolean }> {
  return request(`/orders/reupload-partial-cancel-reviews/${reviewId}/decision`, {
    method: "POST", body: JSON.stringify(body),
  });
}

const _amdPath = (orderNo: string, tail = "") =>
  `/orders/${encodeURIComponent(orderNo)}/purchase-amendments${tail}`;

export function createPurchaseAmendment(
  orderNo: string, body: PurchaseAmendmentCreateRequest,
): Promise<PurchaseAmendmentResponse> {
  return request<PurchaseAmendmentResponse>(_amdPath(orderNo), {
    method: "POST", body: JSON.stringify(body),
  });
}

export function confirmPurchaseAmendment(
  orderNo: string, amendmentNo: number, memo?: string | null,
): Promise<PurchaseAmendmentResponse> {
  return request<PurchaseAmendmentResponse>(_amdPath(orderNo, `/${amendmentNo}/confirm`), {
    method: "POST", body: JSON.stringify({ memo: memo ?? null }),
  });
}

export function confirmPurchaseAmendmentSettlement(
  orderNo: string, amendmentNo: number,
  body: { settled_amount: number; payment_method?: string | null; reference_note?: string | null; admin_memo?: string | null },
): Promise<PurchaseAmendmentResponse> {
  return request<PurchaseAmendmentResponse>(_amdPath(orderNo, `/${amendmentNo}/settlement/confirm`), {
    method: "POST", body: JSON.stringify(body),
  });
}

export function cancelPurchaseAmendment(
  orderNo: string, amendmentNo: number, reason?: string | null,
): Promise<PurchaseAmendmentResponse> {
  return request<PurchaseAmendmentResponse>(_amdPath(orderNo, `/${amendmentNo}/cancel`), {
    method: "POST", body: JSON.stringify({ reason: reason ?? null }),
  });
}

// [ERP-ORDER-HARD-DELETE-ADMIN-DEV-20260727-001] 주문 영구삭제(admin 전용)
export interface OrderDeletionBlocker {
  code: string;
  label: string;
  count: number;
}
export interface OrderDeletionCheck {
  order_id: number;
  order_no: string;
  order_type: string | null;
  order_status: string | null;
  is_cancelled: boolean;
  can_delete: boolean;
  blockers: OrderDeletionBlocker[];
  related_counts: Record<string, number>;
}

/** 주문 영구삭제 가능여부 + 차단사유 조회 (admin 전용, read-only). order_id(PK) 기준. */
export async function getOrderDeletionCheck(orderId: number): Promise<OrderDeletionCheck> {
  return request<OrderDeletionCheck>(`/orders/${orderId}/deletion-check`);
}

/** 주문 영구삭제 (admin 전용). confirmOrderNo 는 서버에서 order_no 와 재확인. */
export async function hardDeleteOrder(
  orderId: number,
  confirmOrderNo: string,
): Promise<{ ok: boolean; deleted_order_id: number; deleted_order_no: string }> {
  return request(`/orders/${orderId}`, {
    method: "DELETE",
    body: JSON.stringify({ confirm_order_no: confirmOrderNo }),
  });
}

// [ERP-PACKING-REQUEST-NOTE-COLUMNS-AND-HIGHLIGHT-20260527-001]
export interface PackingRequestNoteUpdateResponse {
  order_no: string;
  packing_request_note: string | null;
  packing_request_updated_at: string | null;
  packing_request_updated_by_name: string | null;
}

export async function updatePackingRequestNote(
  orderNo: string,
  note: string | null,
): Promise<PackingRequestNoteUpdateResponse> {
  return request<PackingRequestNoteUpdateResponse>(
    `/orders/${encodeURIComponent(orderNo)}/packing-request`,
    {
      method: "PATCH",
      body: JSON.stringify({ note }),
    },
  );
}

// [ERP-PACKING-REQUEST-TOP-BANNER-COMPLETE-AND-MANUAL-FINISH-20260528-001]
export interface PackingRequestCompleteResponse {
  ok: boolean;
  order_no: string;
  packing_request_completed_at: string;
  packing_request_completed_by_name: string;
}

export async function completePackingRequest(
  orderNo: string,
  note?: string | null,
): Promise<PackingRequestCompleteResponse> {
  return request<PackingRequestCompleteResponse>(
    `/orders/${encodeURIComponent(orderNo)}/packing-request/complete`,
    {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    },
  );
}

// ─── Product Search ──────────────────────────

export interface ProductSearchResult {
  sku: string;
  product_name: string;
  option1: string | null;
  // [ERP-MANUAL-ORDER-OPTION2-AND-SPECIAL-PART-SKU-20260527-001]
  option2?: string | null;
  option3?: string | null; // [FW-032] additive
  brand: string | null;
  selling_price?: number | null;
  is_bundle?: boolean;
  components?: { component_sku: string; quantity: number; component_type: string }[];
  // [ERP-STORE-BARCODE-PRODUCT-NAME-DISPLAY-FIX-DEV-20260626-001]
  // /store addToCart 어댑터에서 바코드상품명 전달용(옵셔널). product-search 응답엔 없음.
  barcode_product_name?: string | null;
}

export async function searchProductsForSku(q: string, limit = 20, offset = 0): Promise<ProductSearchResult[]> {
  const off = offset ? `&offset=${offset}` : "";
  return request<ProductSearchResult[]>(`/orders/product-search?q=${encodeURIComponent(q)}&limit=${limit}${off}`);
}

// ─── [ORDER-002] 구매상품 SKU 변경 · 차액정산 ──────────────────────
export interface SkuChangePreview {
  order_item_id: number; order_no: string;
  original_sku: string | null; original_product_name: string | null; original_option_name: string | null;
  original_quantity: number; already_changed_quantity: number; remaining_changeable: number;
  changed_quantity: number;
  new_sku: string; new_product_name: string | null; new_option1: string | null; new_option2: string | null;
  new_option3?: string | null; // [FW-032] additive
  new_product_exists: boolean; new_product_active?: boolean; original_stock: number; new_stock: number;
  original_amount_portion: number; recommended_amount: number | null; agreed_amount: number; diff_amount: number;
  settlement_type: "equal" | "additional" | "refund"; settlement_status_if_confirmed: string;
  block_reason: string | null; over_quantity: boolean; can_confirm: boolean;
  // [FW-040] 송장 발행(Export·송장번호) 후 변경 — 이전 상품명 인쇄 경고 + 사유 필수(서버 강제).
  invoice_issued?: boolean; reason_required?: boolean;
}
export interface SkuChangeRow {
  id: number; order_id: number; order_item_id: number;
  original_sku: string | null; original_product_name: string | null; original_option_name: string | null;
  original_quantity: number; changed_quantity: number;
  new_sku: string; new_product_name: string | null; new_option_name: string | null;
  original_amount_portion: number; recommended_amount: number | null; agreed_amount: number; diff_amount: number;
  settlement_type: string; settlement_status: string; settled_amount: number | null;
  processed_at: string | null; is_active: boolean; reason: string | null; memo: string | null; created_at: string | null;
  // [ORDER-003 Phase 3B] 금액 소유권(서버 SoT). order003_amendment = 구매정보 수정 정산으로 확정된 품목 전체 교체
  //   연결행 — 차액이 이미 결제액에 포함돼 조정금 합계에서 제외되고 ORDER-002 정산/취소 대상이 아니다.
  order_amendment_id: number | null;
  superseded_by_amendment_id: number | null;
  amount_owner: "order002" | "order003_amendment";
  counts_in_adjustment: boolean;
}
export interface SkuChangeList {
  order_no: string; settlement_block_reason: string | null;
  adjustment_sum: number;   // [ORDER-003 Phase 3B] 활성 독립 ORDER-002 행 diff 합(연결행 제외)
  items: SkuChangeRow[];
}
export function previewSkuChange(orderNo: string, orderItemId: number, changedQuantity: number, newSku: string, agreedAmount?: number | null): Promise<SkuChangePreview> {
  const qs = new URLSearchParams({ order_item_id: String(orderItemId), changed_quantity: String(changedQuantity), new_sku: newSku });
  if (agreedAmount != null) qs.set("agreed_amount", String(agreedAmount));
  return request<SkuChangePreview>(`/orders/${encodeURIComponent(orderNo)}/sku-change/preview?${qs}`);
}
export function listSkuChanges(orderNo: string, includeInactive = false): Promise<SkuChangeList> {
  const qs = includeInactive ? "?include_inactive=true" : "";
  return request<SkuChangeList>(`/orders/${encodeURIComponent(orderNo)}/sku-changes${qs}`);
}
// [ORDER-002 DIFF9] agreed_amount(합의금액)은 필수. 화면이 권장 그대로든 수정이든 항상 숫자로 전송한다.
// 서버 자동 대입 없음 — 누락 시 기록 생성 없이 422.
export function confirmSkuChange(orderNo: string, body: { order_item_id: number; changed_quantity: number; new_sku: string; agreed_amount: number; reason?: string | null; memo?: string | null }): Promise<SkuChangeRow> {
  return request<SkuChangeRow>(`/orders/${encodeURIComponent(orderNo)}/sku-change/confirm`, { method: "POST", body: JSON.stringify(body) });
}
export function settleSkuChange(changeId: number, memo?: string | null): Promise<SkuChangeRow> {
  return request<SkuChangeRow>(`/orders/sku-changes/${changeId}/settle`, { method: "POST", body: JSON.stringify({ memo: memo ?? null }) });
}
export function cancelSkuChange(changeId: number, reason?: string | null): Promise<SkuChangeRow> {
  return request<SkuChangeRow>(`/orders/sku-changes/${changeId}/cancel`, { method: "POST", body: JSON.stringify({ reason: reason ?? null }) });
}

// [ERP-SKU-PRODUCT-SEARCH-MODAL-INFINITE-SCROLL-PHASE2-20260603-001]
// 더보기/무한스크롤용 메타 응답 helper. with_meta=true → {items,has_more,next_offset,...}.
export interface ProductSearchPagedResponse {
  items: ProductSearchResult[];
  limit: number;
  offset: number;
  next_offset: number | null;
  has_more: boolean;
  // [ERP-STORE-POS-SALES-UX-DEV-20260922-001] 전체 결과 수(with_meta 응답에만 포함).
  // 구버전 backend 호환을 위해 optional — 없으면 화면이 건수 표시를 생략한다.
  total?: number;
  query: string;
}

export interface ProductSearchPagedOptions {
  /** true 면 option1/option2 도 검색 대상에 포함(opt-in). 기본 false = 기존 SKU/상품명만. */
  includeOptions?: boolean;
}

export async function searchProductsForSkuPaged(
  q: string,
  limit = 30,
  offset = 0,
  opts: ProductSearchPagedOptions = {},
): Promise<ProductSearchPagedResponse> {
  // [ERP-STORE-POS-SALES-UX-DEV-20260922-001]
  // include_options 는 opt-in 이라 기존 호출부(단건 주문접수·SKU 변경·교환접수·추가상품)는 무변경.
  const withOptions = opts.includeOptions ? "&include_options=true" : "";
  return request<ProductSearchPagedResponse>(
    `/orders/product-search?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}&with_meta=true${withOptions}`,
  );
}

// ─── Naver price lookup (오프라인 매장 자동 단가 채움) ───────────────
export async function getNaverPrice(sku: string): Promise<{ sku: string; naver_price: number | null }> {
  return request<{ sku: string; naver_price: number | null }>(
    `/products/${encodeURIComponent(sku)}/naver-price`,
  );
}

// ─── SKU Match ───────────────────────────────

export async function matchSku(itemId: number, sku: string, memo?: string): Promise<void> {
  await request(`/orders/order-items/${itemId}/match-sku`, {
    method: "PATCH",
    body: JSON.stringify({ sku, memo: memo || null }),
  });
}

// ─── SKU Suggestions (Tier 3 — 추천만, 자동 확정 없음) ───────────────

export interface SkuSuggestion extends ProductSearchResult {
  reason: string;
}

export interface SkuSuggestionsResponse {
  item_id: number;
  product_name: string | null;
  reason: string;
  count: number;
  suggestions: SkuSuggestion[];
}

export async function getSkuSuggestions(itemId: number): Promise<SkuSuggestionsResponse> {
  return request<SkuSuggestionsResponse>(`/orders/order-items/${itemId}/sku-suggestions`);
}

export async function importToggleOrders(file: File, companyCode = "HITCAMP"): Promise<OrderImportResult> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}/orders/import/toggle?company_code=${encodeURIComponent(companyCode)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류가 발생했습니다." }));
    throw new Error(err.detail || "Import 처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

// ─── Shipment Holds ──────────────────────────

export interface RestockCandidate {
  sku: string;
  location_code: string;
  qty: number;
  movement_id: number;
  movement_type: string;
  source_ref: string | null;
  created_at: string | null;
}

// 26차 후속 #71 — 출고중지 row 에 동봉되는 주문 요약 (목록 응답에서만 채워짐)
export interface HoldOrderItemSummary {
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
}

export interface HoldOrderSummary {
  order_id: number | null;
  invoice_no?: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  display_customer_name: string | null;
  display_phone: string | null;
  address1: string | null;
  address2: string | null;
  platform: string | null;
  platform_store_name: string | null;
  order_status: string | null;
  order_date: string | null;
  paid_amount: number | null;
  memo: string | null;
  item_count: number;
  total_qty: number;
  product_summary: string | null;
  items: HoldOrderItemSummary[];
}

export interface ShipmentHoldRequest {
  id: number;
  order_no: string;
  invoice_no: string | null;
  request_type: string;
  status: string;
  request_reason: string | null;
  requested_by: string;
  seen_by: string | null;
  memo: string | null;
  requested_at: string;
  seen_at: string | null;
  stopped_at: string | null;
  label_discarded_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // cancel_completed 전이 응답에만 포함
  restock_candidates?: RestockCandidate[] | null;
  // /shipment-holds 목록 응답에서만 채워짐. 단건 GET/POST/PATCH 응답은 null.
  order_summary?: HoldOrderSummary | null;
}

export interface ShipmentHoldListResponse {
  total: number;
  items: ShipmentHoldRequest[];
  status_counts: Record<string, number>;
}

export async function createShipmentHold(data: {
  order_no: string;
  invoice_no?: string;
  request_type?: string;
  request_reason?: string;
  requested_by: string;
  memo?: string;
}): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>("/shipment-holds", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getShipmentHolds(params?: {
  status?: string;
  order_no?: string;
  request_type?: string;
  limit?: number;
  offset?: number;
}): Promise<ShipmentHoldListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.order_no) qs.set("order_no", params.order_no);
  if (params?.request_type) qs.set("request_type", params.request_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<ShipmentHoldListResponse>(`/shipment-holds?${qs}`);
}

export async function updateShipmentHoldStatus(
  holdId: number,
  data: { status: string; seen_by?: string; memo?: string }
): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>(`/shipment-holds/${holdId}/status`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// [ERP-SHIPMENT-HOLD-FORCE-CLOSE-20260520-001]
// 이미 배송이 진행/완료된 주문의 출고중지 요청을 안전하게 종료한다.
// finalize_order_cancellation 호출 없음 — 주문 상태는 보존, hold.status 만
// cancel_completed 로 마킹 + audit_logs 기록. 권한: require_admin.
export async function forceCloseShippedShipmentHold(
  holdId: number,
): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>(
    `/shipment-holds/${holdId}/force-close-shipped`,
    { method: "POST" },
  );
}

// [ERP-SHIPMENT-HOLD-REQUEST-CANCEL-FLOW-20260527-001]
// 출고중지 "요청 취소" — 잘못 등록되었거나 더 이상 필요 없게 된 요청을 정리.
// "출고중지 완료(cancel_completed)" 와 명확히 구분되며 주문 상태는 보존된다.
// 응답 hold.status = "cancelled", memo prefix prepend.
export async function cancelShipmentHoldRequest(
  holdId: number,
  reason?: string,
): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>(
    `/shipment-holds/${holdId}/cancel-request`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    },
  );
}

// 출고중지 force-close 대상 order_status 화이트리스트 (backend 와 동일).
export const HOLD_BLOCKED_ORDER_STATUSES = new Set<string>([
  "shipped",
  "in_transit",
  "shipping",
  "out_for_delivery",
  "delivery_area_arrived",
  "delivered",
  "completed",
]);

export async function importProductMaster(file: File, companyCode = "HITCAMP"): Promise<ImportResult> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}/products/import/product-master?company_code=${encodeURIComponent(companyCode)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    throw await uploadApiError(res, "Import 처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

// [ERP-PRODUCT-MASTER-EXPORT-VERSIONED-XLSX-PHASE1-WITH-COST-POLICY-20260611-001]
// 상품DB → PRODUCT MASTER V2 xlsx 다운로드 + 컬럼 정의 조회.
export interface MasterColumnDef {
  key: string; title: string; sheet: string; required: boolean;
  import: boolean; export: boolean; kind: string;
  db_source: string | null; note: string;
}
export interface MasterColumnsResponse {
  column_version: string;
  export_version: string;
  columns: MasterColumnDef[];
  import_enabled: string[];
  export_enabled: string[];
  reference_only: string[];
  deferred: string[];
  required: string[];
  cost_policy: Record<string, string>;
}

export async function getMasterColumns(): Promise<MasterColumnsResponse> {
  return request<MasterColumnsResponse>("/products/master-columns");
}

export async function downloadMasterExport(opts: {
  scope?: "active" | "all";
  include_as?: boolean;
  include_discontinued?: boolean;
  include_stock_ref?: boolean;
}): Promise<void> {
  const token = getToken();
  const qs = new URLSearchParams();
  qs.set("scope", opts.scope ?? "active");
  qs.set("include_as", String(opts.include_as ?? true));
  qs.set("include_discontinued", String(opts.include_discontinued ?? false));
  qs.set("include_stock_ref", String(opts.include_stock_ref ?? true));
  const res = await fetch(`${API_URL}/products/master-export?${qs.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "다운로드 오류" }));
    throw new Error(err.detail || "PRODUCT MASTER 다운로드 중 오류가 발생했습니다.");
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename=([^;]+)/);
  const filename = m ? m[1].trim() : `PRODUCT_MASTER_V2_${Date.now()}.xlsx`;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 일부 브라우저는 click 직후 동기 revoke 시 다운로드가 중단될 수 있어 지연 해제.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// [ERP-PRODUCT-MASTER-PHASE2-META-GUARD-IMPORT-HISTORY-20260612-001]
// safe master-import (preview/apply/history) — 구 /products/import/product-master 와 별개 경로.
export interface MasterFileIdentity {
  has_meta: boolean;
  is_official_export?: boolean;
  file_purpose?: string;
  export_id?: string;
  export_version?: string;
  column_version?: string;
  exported_at?: string;
  exported_by?: string;
  source_db?: string;
  product_master_count?: string;
  as_spare_parts_count?: string;
  is_current_column_version?: boolean;
  is_same_source_db?: boolean;
}
export interface MasterRiskSummary {
  new_count: number;
  // [ERP-PRODUCT-MASTER-PREVIEW-TERMS-ACTUAL-CHANGE-CLARITY-20260615-001]
  // existing_matched_count = 기존 상품 확인(파일에 포함된 기존 SKU 수),
  // actual_changed_count = 실제 변경 예정(값이 실제로 달라지는 row 수).
  existing_matched_count: number;
  actual_changed_count: number;
  update_count: number; // 하위호환 별칭 = actual_changed_count
  price_change_count: number;
  large_price_drop_count: number;
  large_price_increase_count: number;
  duplicate_sku_count: number;
  ignored_reference_columns: string[];
  ignored_deferred_columns: string[];
  errors: number;
  blocking_warnings: number;
}
export interface MasterApplyGuard {
  can_apply: boolean;
  requires_admin_confirm: boolean;
  requires_strong_confirm_text: boolean;
  confirm_text: string;
}
// safe preview 응답 (sheet_results 는 그대로 통과, Phase2 필드만 타입화)
export interface MasterPreviewResponse {
  mode: string;
  ignored_sheets: string[];
  sheet_results: Record<string, unknown>[];
  error?: string;
  file_identity: MasterFileIdentity;
  risk_level: "no_change" | "low" | "medium" | "high" | "blocked";
  risk_warnings: string[];
  risk_summary: MasterRiskSummary;
  apply_guard: MasterApplyGuard;
}
export interface MasterImportHistoryItem {
  id: number;
  file_name: string | null;
  imported_by: string | null;
  row_count: number;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  warning_count: number;
  error_count: number;
  status: string;
  error_summary: string | null;
  created_at: string | null;
  completed_at: string | null;
}

async function _masterUpload<T>(path: string, file: File): Promise<T> {
  const token = getToken();
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    // [ETC-001-PHASE4B4A] 이전에는 detail 이 **객체일 때만** ApiError 였다.
    // 권한 거부 detail 은 문자열이라 status 가 소실됐다 — 이제 항상 ApiError 로 통일한다.
    throw await uploadApiError(res, "처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

export function previewMasterImport(file: File): Promise<MasterPreviewResponse> {
  return _masterUpload<MasterPreviewResponse>("/products/master-import/preview", file);
}
export function applyMasterImport(file: File): Promise<Record<string, unknown>> {
  return _masterUpload<Record<string, unknown>>("/products/master-import/apply", file);
}
export async function getMasterImportHistory(limit = 20): Promise<MasterImportHistoryItem[]> {
  return request<MasterImportHistoryItem[]>(`/products/master-import/history?limit=${limit}`);
}

// ─── Returns (반품/교환 검수) ──────────────────────────────────

export interface ReturnInspectionResponse {
  id: number;
  return_receipt_id: number;
  order_no: string;
  order_item_id: number | null;
  inspection_result: string;
  restock_decision: string;
  inspected_qty: number;
  missing_qty: number;
  damaged_qty: number;
  opened_box: boolean;
  used_trace: boolean;
  missing_parts: boolean;
  notes: string | null;
  inspected_by: string;
  inspected_at: string;
  created_at: string;
  updated_at: string;
}

// 회수 유형 (교환/반품/AS회수 검수장 공통 흐름)
export type RecoveryType =
  | "courier_pickup"
  | "customer_ships"
  | "walkin_repair_same_day_pickup"
  | "walkin_repair_ship_later";

// 검수 후 처리결정
export type RestockDecision =
  | "restock"
  | "quarantine"
  | "as_transfer"
  | "exchange_resend"
  | "discard"
  | "hold";

export interface LinkedStockMovement {
  id: number;
  sku: string;
  location_code: string;
  movement_type: string;
  quantity_delta: number;
  memo: string | null;
  created_at: string;
}

// [ERP-RETURN-EXCHANGE-INTAKE-UX-CLEANUP-DEV-20260710-037]
// 같은 주문(order_no)의 전체 주문상품 (read-only 표시). is_target = 이 회수건이 가리키는 상품.
export interface ReturnReceiptOrderItem {
  order_item_id: number | null;
  product_name: string | null;
  option_name: string | null;
  sku: string | null;
  order_quantity: number | null;
  is_target: boolean;
}

export interface ReturnReceiptResponse {
  id: number;
  order_no: string | null;
  order_item_id: number | null;
  as_request_id: number | null;
  return_type: string;
  recovery_type: RecoveryType;
  recovery_courier: string | null;
  return_invoice_no: string | null;
  exchange_resend_invoice_no: string | null;
  visit_date: string | null;
  onsite_received_at: string | null;
  collected_at: string | null;
  received_at: string | null;
  status: string;
  received_by: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  inspections: ReturnInspectionResponse[];
  // server-join inline 주문/고객 정보
  customer_name: string | null;
  customer_phone: string | null;
  customer_address: string | null;
  // [ERP-RETURN-INSPECTION-ORDERER-RECIPIENT-DISPLAY-20260527-001]
  orderer_name?: string | null;
  orderer_phone?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  order_date: string | null;
  product_name: string | null;
  option_name: string | null;
  item_quantity: number | null;
  sku: string | null;
  order_memo: string | null;
  order_invoice_no: string | null;
  // [ERP-RETURN-EXCHANGE-INTAKE-UX-CLEANUP-DEV-20260710-037] 같은 주문의 전체 주문상품
  order_items?: ReturnReceiptOrderItem[];
  // 검수창고 drill-down
  linked_movements: LinkedStockMovement[];
  quarantine_reflected_qty: number;
  restock_reflected_qty: number;
  // 교환 재발송 링크 (3차)
  exchange_resend_order_id: number | null;
  exchange_resend_order_no: string | null;
  exchange_resend_order_status: string | null;
  exchange_resend_pick_status: string | null;
  exchange_resend_pick_invoice_no: string | null;
  exchange_resend_shipped_at: string | null;
  // AS 회수 링크 (5차)
  as_no: string | null;
  as_symptom: string | null;
  as_status: string | null;
  as_pickup_type: string | null;
  // 26차 후속 — 입금 확인 큐 통합 Phase 1.
  expected_payment_amount: number | null;
  payment_reason: ReturnPaymentReason | null;
  payment_status: ReturnPaymentStatus;
  payment_requested_at: string | null;
  payment_confirmed_at: string | null;
  payment_confirmed_by_user_id: number | null;
}

// 26차 후속 — 입금 확인 큐 통합 Phase 1.
export type ReturnPaymentReason =
  | "additional_charge"
  | "exchange_diff"
  | "round_trip_shipping"
  | "return_shipping"
  | "reshipping_fee"
  | "other";
export type ReturnPaymentStatus =
  | "none"
  | "pending"
  | "confirmed"
  | "waived"
  | "cancelled";

export interface ReturnReceiptListResponse {
  total: number;
  items: ReturnReceiptResponse[];
  status_counts: Record<string, number>;
  recovery_type_counts: Record<string, number>;
  decision_counts: Record<string, number>;
  quarantine_total_qty: number;
  return_type_counts: Record<string, number>;
}

export async function createReturnReceipt(data: {
  order_no: string;
  order_item_id?: number | null;
  return_type?: string;
  recovery_type?: RecoveryType;
  recovery_courier?: string | null;
  return_invoice_no?: string | null;
  visit_date?: string | null;
  received_by?: string | null;
  memo?: string | null;
  // 26차 후속 — 입금 확인 큐 통합 Phase 1 (선택 입력).
  expected_payment_amount?: number | null;
  payment_reason?: ReturnPaymentReason | null;
}): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>("/returns/receipts", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// 26차 후속 — 반품/교환 입금 확인 / 면제 helper.
export function confirmReturnReceiptPayment(
  receiptId: number,
  memo?: string,
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(
    `/returns/receipts/${receiptId}/confirm-payment`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}
export function waiveReturnReceiptPayment(
  receiptId: number,
  memo?: string,
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(
    `/returns/receipts/${receiptId}/waive-payment`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}

// [ERP-RETURN-RECEIPT-CANCEL-FLOW-20260527-001]
// 회수대기 상태 회수건 취소. reason 은 선택, memo 에 prefix 로 기록된다.
export function cancelReturnReceipt(
  receiptId: number,
  reason?: string,
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(
    `/returns/receipts/${receiptId}/cancel`,
    { method: "POST", body: JSON.stringify({ reason: reason ?? null }) },
  );
}

export async function getReturnReceipts(params?: {
  status?: string;
  order_no?: string;
  return_type?: string;
  recovery_type?: RecoveryType;
  decision?: RestockDecision;
  quarantine_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<ReturnReceiptListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.order_no) qs.set("order_no", params.order_no);
  if (params?.return_type) qs.set("return_type", params.return_type);
  if (params?.recovery_type) qs.set("recovery_type", params.recovery_type);
  if (params?.decision) qs.set("decision", params.decision);
  if (params?.quarantine_only) qs.set("quarantine_only", "true");
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const query = qs.toString();
  return request<ReturnReceiptListResponse>(`/returns/receipts${query ? "?" + query : ""}`);
}

export async function getReceiptStockMovements(receiptId: number): Promise<LinkedStockMovement[]> {
  return request<LinkedStockMovement[]>(`/returns/receipts/${receiptId}/stock-movements`);
}

// 17차 1단계 — 반품/교환회수 검수창고 SKU별 원천 회수건 조회 (read-only)
export interface QuarantineLotItem {
  movement_id: number;
  receipt_id: number;
  return_type: string;
  order_no: string | null;
  return_receipt_memo: string | null;
  customer_name: string | null;
  product_name: string | null;
  option_name: string | null;
  sku: string;
  quantity: number;
  inspected_at: string | null;
  restock_decision: string | null;
  created_at: string;
  // [ERP-RETURN-QUARANTINE-LOT-REMAINING-QTY-DEV-20260709-034]
  // 회수건 단위 재배치 잔여 추적(백엔드 stock_movements 집계 파생). 구버전 응답 호환 위해 optional.
  original_quantity?: number;
  transferred_quantity?: number;
  remaining_quantity?: number;
  is_fully_transferred?: boolean;
}

export interface QuarantineLotListResponse {
  sku: string;
  total_quantity: number;
  items: QuarantineLotItem[];
}

export async function getQuarantineLotsBySku(sku: string): Promise<QuarantineLotListResponse> {
  return request<QuarantineLotListResponse>(
    `/returns/quarantine-lots?sku=${encodeURIComponent(sku)}`
  );
}

export interface ExchangeResendItemIn {
  sku: string;
  product_name: string;
  option_name?: string | null;
  quantity?: number;
  unit_price?: number;
}

export async function createExchangeResendOrder(
  receiptId: number,
  data: {
    receiver_name?: string | null;
    receiver_phone?: string | null;
    zipcode?: string | null;
    address1?: string | null;
    address2?: string | null;
    items?: ExchangeResendItemIn[];
    shipping_fee?: number;
    memo?: string | null;
  } = {},
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(`/returns/receipts/${receiptId}/exchange-resend`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateReturnReceiptStatus(
  receiptId: number,
  data: {
    status: string;
    received_by?: string | null;
    recovery_type?: RecoveryType | null;
    recovery_courier?: string | null;
    return_invoice_no?: string | null;
    exchange_resend_invoice_no?: string | null;
    visit_date?: string | null;
    onsite_received_at?: string | null;
    collected_at?: string | null;
    received_at?: string | null;
    memo?: string | null;
  }
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(`/returns/receipts/${receiptId}/status`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function createAsFromReturn(receiptId: number): Promise<{ as_no: string; as_id: number }> {
  return request<{ as_no: string; as_id: number }>(`/returns/receipts/${receiptId}/create-as`, {
    method: "POST",
  });
}

/** AS 원본 → 교환/반품/AS회수 검수장 허브로 ingest (5차 정리). */
export async function createReceiptFromAs(
  asId: number,
  data: {
    recovery_type?: RecoveryType;
    recovery_courier?: string | null;
    return_invoice_no?: string | null;
    visit_date?: string | null;
    memo?: string | null;
  } = {},
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(`/returns/receipts/from-as?as_id=${asId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function createReturnInspection(data: {
  return_receipt_id: number;
  order_no: string;
  order_item_id?: number | null;
  inspection_result?: string;
  restock_decision?: string;
  inspected_qty?: number;
  missing_qty?: number;
  damaged_qty?: number;
  opened_box?: boolean;
  used_trace?: boolean;
  missing_parts?: boolean;
  notes?: string | null;
  inspected_by: string;
}): Promise<ReturnInspectionResponse> {
  return request<ReturnInspectionResponse>("/returns/inspections", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateReturnInspection(
  inspectionId: number,
  data: Partial<{
    inspection_result: string;
    restock_decision: string;
    inspected_qty: number;
    missing_qty: number;
    damaged_qty: number;
    opened_box: boolean;
    used_trace: boolean;
    missing_parts: boolean;
    notes: string | null;
    inspected_by: string;
  }>
): Promise<ReturnInspectionResponse> {
  return request<ReturnInspectionResponse>(`/returns/inspections/${inspectionId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ─── Daiwa / Vendors ──────────────────────────────────────────

export interface DaiwaImportResult {
  sheet_type: string;
  total_rows: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  duplicate_jan_count?: number;
  errors: { row?: number; error: string }[];
}

export interface DaiwaSummary {
  inventory_snapshot_count: number;
  latest_snapshot_date: string | null;
  available_product_count: number;
  price_policy_count: number;
  markup_rule_count: number;
  category_mapping_count: number;
  origin_mapping_count: number;
  supply_source_count: number;
  unprocessed_direct_ship_count: number;
  unregistered_product_count: number;
  stock_zero_sku_count: number;
  // [ERP-DAIWA-ORDER-QUEUE-RESTOCKED-CANDIDATE-AND-NAVER-DK-ZERO-FILTER-20260605-001]
  restocked_candidate_count: number;
}

export interface DaiwaPriceCalc {
  jan_code: string;
  product_group: string | null;
  base_cost: number | null;
  online_min_price_krw: number | null;
  calculated_price: number | null;
  price_source: string;
  kake_rate: number | null;
  margin_rate_applied: number | null;
}

async function uploadFile<T>(path: string, file: File, extraParams?: Record<string, string>): Promise<T> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const formData = new FormData();
  formData.append("file", file);
  let url = `${API_URL}${path}`;
  if (extraParams) {
    const qs = new URLSearchParams(extraParams);
    url += `?${qs}`;
  }
  const res = await fetch(url, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "업로드 실패" }));
    throw new Error(err.detail || "업로드 실패");
  }
  return res.json();
}

export async function getDaiwaSummary(): Promise<DaiwaSummary> {
  return request<DaiwaSummary>("/vendors/daiwa/summary");
}

export interface DaiwaUnregisteredProduct {
  jan_code: string;
  product_name: string | null;
  product_group: string | null;
  base_cost: number | null;
}

export async function getDaiwaUnregisteredProducts(limit = 50): Promise<{ total: number; items: DaiwaUnregisteredProduct[] }> {
  return request(`/vendors/daiwa/unregistered-products?limit=${limit}`);
}

export async function importDaiwaInventory(file: File): Promise<DaiwaImportResult> {
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/inventory", file);
}

export async function importDaiwaPricePolicies(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/price-policies", file, params);
}

export async function importDaiwaKakeRules(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/kake-rules", file, params);
}

export async function importDaiwaCategoryMappings(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/category-mappings", file, params);
}

export async function importDaiwaOriginMappings(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/origin-mappings", file, params);
}

export async function getDaiwaPriceCalc(
  janCode: string,
  productGroup?: string,
  baseCost?: number
): Promise<DaiwaPriceCalc> {
  const qs = new URLSearchParams();
  if (productGroup) qs.set("product_group", productGroup);
  if (baseCost != null) qs.set("base_cost", String(baseCost));
  return request<DaiwaPriceCalc>(`/vendors/daiwa/price/${encodeURIComponent(janCode)}?${qs}`);
}

export interface DaiwaNaverBulkPreview {
  total_rows: number;
  price_source_counts: Record<string, number>;
  category_mapped_count: number;
  origin_mapped_count: number;
  internet_floor_applied_count?: number;
  sample_rows: Record<string, unknown>[];
}

export async function getDaiwaNaverBulkPreview(limit = 10): Promise<DaiwaNaverBulkPreview> {
  return request<DaiwaNaverBulkPreview>(`/vendors/daiwa/export/naver-bulk/preview?limit=${limit}`);
}

// ── Daiwa Direct Ship Export ───────────────────────────────────

export type FulfillmentClass = "hitcamp" | "daiwa" | "decide" | "needs_match";

export interface DirectShipRow {
  order_no: string;
  product_name: string;
  receiver_name: string;
  phone: string;
  zipcode: string;
  address: string;
  jan_code: string;
  quantity: number;
  delivery_message: string;
  eligible_reason: string;
  /** 분류 (백엔드 추가, 신규 필드라 optional 로 받아 기존 사용처 깨지지 않게 함) */
  fulfillment_class?: FulfillmentClass;
  hitcamp_qty?: number;
  daiwa_qty?: number;
  /** 운영자 결정값 (orders.daiwa_fulfillment_choice) — 미결정시 null */
  daiwa_fulfillment_choice?: "hitcamp" | "daiwa" | null;
  /** 같은 주문 내 다이와 후보 아이템 수. >1 이면 결정 잠금. */
  daiwa_eligible_item_count?: number;
  /** 결정 저장 가능 여부 (단일 후보 아이템 주문). */
  is_decision_writable?: boolean;
  /** [ERP-DAIWA-ACTION-QUEUE-SKU-UNMATCHED-NEEDS-MATCH-FIX-20260528-001]
   *  sku NULL/empty 인 다이와 후보 (option_name JAN + master 매칭으로 식별). */
  sku_missing?: boolean;
  candidate_jan_code?: string | null;
  candidate_source?: string | null;
  match_reason?: string | null;
  option_name?: string;
}

// [ERP-DAIWA-SKU-MISSING-CANDIDATE-PRODUCT-MAPPING-PREVIEW-20260529-001]
// SKU 미매칭 다이와 후보 → products 등록/매핑 Preview (Preview-only).
export interface DaiwaJanMasterInfo {
  jan_code: string;
  supplier_product_code?: string | null;
  name_ko?: string | null;
  name_en?: string | null;
  dk_stock_qty?: number | null;
}
export interface DaiwaNaverListingInfo {
  jan_code: string;
  seller_product_code?: string | null;
  product_name?: string | null;
  listing_status?: string | null;
}
export interface SuggestedProductPreview {
  sku: string;
  barcode?: string | null;
  product_name: string;
  brand: string;
  source: string;
  as_available: boolean;
  product_status: string;
}
export interface ExistingProductMatch {
  sku: string;
  product_name?: string | null;
  barcode?: string | null;
  is_active: boolean;
}
export interface SkuMissingCandidateItem {
  order_no: string;
  order_item_id: number;
  product_name: string;
  option_name?: string | null;
  quantity: number;
  candidate_jan_code?: string | null;
  candidate_source?: string | null;
  daiwa_jan_master?: DaiwaJanMasterInfo | null;
  naver_listing?: DaiwaNaverListingInfo | null;
  snapshot_qty?: number | null;
  products_match?: ExistingProductMatch | null;
  candidate_products: ExistingProductMatch[];
  suggested_product?: SuggestedProductPreview | null;
  recommended_action: "create_product_preview" | "map_existing_product_preview" | "manual_review";
  warning?: string | null;
}
export interface SkuMissingCandidatesResponse {
  total: number;
  items: SkuMissingCandidateItem[];
}
export function getDaiwaSkuMissingCandidates(limit = 50): Promise<SkuMissingCandidatesResponse> {
  return request(`/vendors/daiwa/sku-missing-candidates?limit=${limit}`);
}

// [ERP-PACKING-REQUEST-CARRYOVER-HOLD-ORDER-VISIBILITY-20260529-001]
// 미완료 포장요청 carryover — 주문일 무관. /picking 상단 amber 배너용.
export interface PendingPackingRequestItem {
  order_no: string;
  invoice_no: string | null;
  order_date: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  packing_request_note: string | null;
  packing_request_updated_at: string | null;
  packing_request_updated_by_name: string | null;
  packing_request_completed_at: string | null;
  order_status: string | null;
  shipment_pick_status: string | null;
  can_open_by_invoice: boolean;
}
export interface PendingPackingRequestsResponse {
  total: number;
  items: PendingPackingRequestItem[];
}
export function getPendingPackingRequests(limit = 100): Promise<PendingPackingRequestsResponse> {
  return request(`/orders/packing/pending-requests?limit=${limit}`);
}

export interface DirectShipClassCounts {
  hitcamp: number;
  daiwa: number;
  decide: number;
  needs_match: number;
}

export interface DirectShipPreview {
  total_orders: number;
  total_rows: number;
  sample_rows: DirectShipRow[];
  class_counts?: DirectShipClassCounts;
}

export interface DirectShipFilters {
  order_nos?: string;
  order_date_from?: string;
  order_date_to?: string;
  platform?: string;
  fulfillment_class?: FulfillmentClass;
  /** [ERP-DAIWA-ORDER-ACTION-QUEUE-REDEFINE-20260522-001]
   *  'all' (기본, Export 호환) | 'action_queue' (미처리 액션 큐 카운트) */
  scope?: "all" | "action_queue";
}

export async function getDaiwaDirectShipPreview(
  filters: DirectShipFilters = {},
  limit = 10
): Promise<DirectShipPreview> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (filters.order_nos) qs.set("order_nos", filters.order_nos);
  if (filters.order_date_from) qs.set("order_date_from", filters.order_date_from);
  if (filters.order_date_to) qs.set("order_date_to", filters.order_date_to);
  if (filters.platform) qs.set("platform", filters.platform);
  if (filters.fulfillment_class) qs.set("fulfillment_class", filters.fulfillment_class);
  if (filters.scope) qs.set("scope", filters.scope);
  return request<DirectShipPreview>(`/vendors/daiwa/export/direct-ship/preview?${qs}`);
}

export async function downloadDaiwaDirectShip(
  filters: DirectShipFilters = {},
  includeDecide = false,
): Promise<void> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const qs = new URLSearchParams();
  if (filters.order_nos) qs.set("order_nos", filters.order_nos);
  if (filters.order_date_from) qs.set("order_date_from", filters.order_date_from);
  if (filters.order_date_to) qs.set("order_date_to", filters.order_date_to);
  if (filters.platform) qs.set("platform", filters.platform);
  if (includeDecide) qs.set("include_decide", "true");
  // [ERP-DAIWA-ORDER-MANAGEMENT-TABS-DAILY-SCOPE-20260527-001]
  if (filters.scope) qs.set("scope", filters.scope);
  const res = await fetch(`${API_URL}/vendors/daiwa/export/direct-ship?${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Export 실패" }));
    throw new Error(err.detail || "Export 실패");
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const today = new Date();
  const yyyymmdd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
  const filename = match?.[1] || `daiwa_direct_orders_${yyyymmdd}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Daiwa Inventory Search (탭1: 재고 조회) ─────────────────────

export interface DaiwaLatestSnapshotInfo {
  snapshot_date: string | null;
  row_count: number;
  latest_created_at: string | null;
}

export interface DaiwaInventorySummaryCard {
  total: number;
  in_stock: number;
  out_of_stock: number;
  category_mapped: number;
  origin_mapped: number;
  kake_mapped: number;
}

export interface DaiwaInventorySearchItem {
  id: number;
  jan_code: string;
  product_name: string | null;          // 다이와 스냅샷 product_name (vendor 원본)
  vendor_category: string | null;
  available_qty: number;
  is_available: boolean;
  snapshot_date: string;
  sku_registered: boolean;
  erp_product_name: string | null;      // Product.product_name (#74)
  model_name: string | null;
  option1: string | null;
  option2: string | null;
  current_price: number | null;         // Product.sale_price (#74)
  naver_category_code: string | null;
  naver_category_name: string | null;
  naver_origin_code: string | null;
  kake_rate: number | null;
  policy_price: number | null;
  // 정책가 비교 상태 (#74): "normal" | "violation" | "no_policy" | "no_product"
  policy_status: string;
  price_diff: number | null;
}

export interface DaiwaInventorySearchResponse {
  latest_snapshot: DaiwaLatestSnapshotInfo;
  summary: DaiwaInventorySummaryCard;
  items: DaiwaInventorySearchItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaInventorySearchParams {
  q?: string;
  in_stock_only?: boolean;
  unmapped_category_only?: boolean;
  unmapped_origin_only?: boolean;
  unmapped_kake_only?: boolean;
  // 정책가 비교 필터 (#74)
  with_policy_only?: boolean;
  violation_only?: boolean;
  no_policy_only?: boolean;
  page?: number;
  page_size?: number;
}

export async function searchDaiwaInventory(
  params: DaiwaInventorySearchParams = {},
): Promise<DaiwaInventorySearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.in_stock_only) qs.set("in_stock_only", "true");
  if (params.unmapped_category_only) qs.set("unmapped_category_only", "true");
  if (params.unmapped_origin_only) qs.set("unmapped_origin_only", "true");
  if (params.unmapped_kake_only) qs.set("unmapped_kake_only", "true");
  if (params.with_policy_only) qs.set("with_policy_only", "true");
  if (params.violation_only) qs.set("violation_only", "true");
  if (params.no_policy_only) qs.set("no_policy_only", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 100));
  return request<DaiwaInventorySearchResponse>(`/vendors/daiwa/inventory/search?${qs}`);
}

// ── Daiwa JAN Master (Phase 3, #79) — current table 기반 재고 조회 ─

export interface DaiwaJanMasterItem {
  id: number;
  jan_code: string;
  supplier_product_code: string | null;
  name_en: string | null;
  name_ko: string | null;
  min_order_qty: number | null;
  catalog_page: string | null;
  price_jpy: number | null;
  msrp_krw: number | null;
  product_group: string | null;
  barcode: string | null;
  category_large: string | null;
  category_middle: string | null;
  category_small: string | null;
  origin_country: string | null;
  dk_stock_qty: number;
  preorder_qty: number;
  expected_arrival_date: string | null;
  source_file_name: string | null;
  last_uploaded_at: string | null;
  is_new_jan: boolean;
  new_jan_detected_at: string | null;
  new_jan_acknowledged_at: string | null;
  new_jan_acknowledged_by_user_id: number | null;
  direct_ship_eligible: boolean;
  // [ERP-DAIWA-HITCAMP-STOCK-COLUMN-20260520-001] 히트캠프 출고 가능 재고 합산.
  // 매칭: products.sku == jan_code (Q1=a). 합산 location: office/warehouse_401402/
  // warehouse/rocket_growth (Q2=Q). quantity 컬럼 SUM (Q3=X), reserved 차감 X (Q4=M).
  hitcamp_stock_qty: number;
  // [ERP-DAIWA-INVENTORY-OPS-FILTERS-20260520-001] 네이버 등록 여부 + URL.
  naver_listed: boolean;
  naver_listing_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface DaiwaJanMasterListResponse {
  items: DaiwaJanMasterItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  new_jan_count: number;
  last_uploaded_at_max: string | null;
}

export interface DaiwaJanMasterListParams {
  q?: string;
  dk_in_stock_only?: boolean;
  dk_zero_only?: boolean;
  direct_ship_only?: boolean;
  new_jan_only?: boolean;
  // [ERP-DAIWA-CATEGORY-FILTER-20260520-001] 대/중/소 카테고리 계층 필터.
  major_category?: string;
  middle_category?: string;
  minor_category?: string;
  // [ERP-DAIWA-HITCAMP-STOCK-FILTER-20260520-001] 히트캠프 실재고 있는 JAN 만.
  hitcamp_stock_only?: boolean;
  // [ERP-DAIWA-INVENTORY-OPS-FILTERS-20260520-001] 운영 조합 필터 + 정렬.
  dk_stock_hitcamp_zero_only?: boolean;
  dk_zero_hitcamp_stock_only?: boolean;
  sort_by?: "hitcamp_stock_qty";
  sort_dir?: "asc" | "desc";
  // [ERP-DAIWA-JAN-MASTER-BAD-ROW-DEFAULT-HIDE-UI-20260604-001]
  // true 면 leading-zero bad duplicate(앞자리 0 손실 중복) row 포함. 기본 false=숨김.
  include_bad_duplicates?: boolean;
  page?: number;
  page_size?: number;
}

// [ERP-DAIWA-CATEGORY-FILTER-20260520-001] 카테고리 옵션 트리.
export interface DaiwaCategoryOptions {
  major_categories: string[];
  middle_by_major: Record<string, string[]>;
  minor_by_middle: Record<string, string[]>;
}

export async function getDaiwaJanMasterCategoryOptions(): Promise<DaiwaCategoryOptions> {
  return request<DaiwaCategoryOptions>("/vendors/daiwa/jan-master/category-options");
}

export interface DaiwaJanMasterUploadResult {
  sheet_type: string;
  mode: string;
  seed_mode: boolean;
  source_file_name: string | null;
  parsed_rows: number;
  skipped_empty_jan_rows: number;
  invalid_jan_rows: number;
  unusual_jan_rows: number;
  normalized_jan_count: number;
  duplicate_jans_in_file: number;
  duplicate_rows_in_file: number;
  inserted_count: number;
  updated_count: number;
  missing_zeroed_count: number;
  new_jan_count: number;
  acknowledged_preserved_count: number;
  total_current_rows_after: number;
  // [ERP-DAIWA-ZERO-HISTORY-RESPONSE-FIELD-20260526-001]
  full_zero_history_appended_count?: number;
  normalize_samples: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

// [ERP-DAIWA-JAN-UPLOAD-UI-PREVIEW-SAFETY-20260526-001]
export interface DaiwaJanMasterUploadPreviewResult {
  sheet_type: string;
  mode: string;
  seed_mode: boolean;
  source_file_name: string | null;
  parsed_rows: number;
  skipped_empty_jan_rows: number;
  invalid_jan_rows: number;
  unusual_jan_rows: number;
  normalized_jan_count: number;
  duplicate_jans_in_file: number;
  duplicate_rows_in_file: number;
  estimated_insert_count: number;
  estimated_update_count: number;
  estimated_missing_zeroed_count: number;
  estimated_full_zero_history_appended_count: number;
  estimated_new_jan_count: number;
  total_current_rows_before: number;
  total_current_rows_after_estimated: number;
  zero_out_samples: Array<{
    jan_code: string;
    supplier_product_code?: string | null;
    name_ko?: string | null;
    old_dk_stock_qty: number;
    new_dk_stock_qty: number;
  }>;
  zero_out_confirm_threshold: number;
  zero_out_confirm_required: boolean;
  normalize_samples: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

export interface DaiwaJanMasterAckResponse {
  id: number;
  jan_code: string;
  is_new_jan: boolean;
  new_jan_acknowledged_at: string | null;
  new_jan_acknowledged_by_user_id: number | null;
}

export async function getDaiwaJanMaster(
  params: DaiwaJanMasterListParams = {},
): Promise<DaiwaJanMasterListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.dk_in_stock_only) qs.set("dk_in_stock_only", "true");
  if (params.dk_zero_only) qs.set("dk_zero_only", "true");
  if (params.direct_ship_only) qs.set("direct_ship_only", "true");
  if (params.new_jan_only) qs.set("new_jan_only", "true");
  // [ERP-DAIWA-CATEGORY-FILTER-20260520-001]
  if (params.major_category) qs.set("major_category", params.major_category);
  if (params.middle_category) qs.set("middle_category", params.middle_category);
  if (params.minor_category) qs.set("minor_category", params.minor_category);
  if (params.hitcamp_stock_only) qs.set("hitcamp_stock_only", "true");
  if (params.dk_stock_hitcamp_zero_only) qs.set("dk_stock_hitcamp_zero_only", "true");
  if (params.dk_zero_hitcamp_stock_only) qs.set("dk_zero_hitcamp_stock_only", "true");
  if (params.sort_by) qs.set("sort_by", params.sort_by);
  if (params.sort_dir) qs.set("sort_dir", params.sort_dir);
  if (params.include_bad_duplicates) qs.set("include_bad_duplicates", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 100));
  return request<DaiwaJanMasterListResponse>(`/vendors/daiwa/jan-master?${qs}`);
}

export async function uploadDaiwaJanMaster(
  file: File,
  opts: { mode?: "full" | "partial"; seed_mode?: boolean; confirm_zero_out?: boolean } = {},
): Promise<DaiwaJanMasterUploadResult> {
  const params: Record<string, string> = {
    mode: opts.mode ?? "partial",
    seed_mode: String(Boolean(opts.seed_mode)),
    confirm_zero_out: String(Boolean(opts.confirm_zero_out)),
  };
  return uploadFile<DaiwaJanMasterUploadResult>(
    "/vendors/daiwa/jan-master/upload",
    file,
    params,
  );
}

// [ERP-DAIWA-JAN-UPLOAD-UI-PREVIEW-SAFETY-20260526-001]
export async function previewDaiwaJanMaster(
  file: File,
  opts: { mode?: "full" | "partial"; seed_mode?: boolean } = {},
): Promise<DaiwaJanMasterUploadPreviewResult> {
  const params: Record<string, string> = {
    mode: opts.mode ?? "partial",
    seed_mode: String(Boolean(opts.seed_mode)),
  };
  return uploadFile<DaiwaJanMasterUploadPreviewResult>(
    "/vendors/daiwa/jan-master/preview",
    file,
    params,
  );
}

export async function acknowledgeDaiwaNewJan(id: number): Promise<DaiwaJanMasterAckResponse> {
  return request<DaiwaJanMasterAckResponse>(
    `/vendors/daiwa/jan-master/${id}/acknowledge-new`,
    { method: "POST" },
  );
}

export interface DaiwaJanMasterAckAllResponse {
  ok: boolean;
  updated_count: number;
  acknowledged_by_user_id: number;
  acknowledged_at: string;
}

export async function acknowledgeAllDaiwaNewJan(): Promise<DaiwaJanMasterAckAllResponse> {
  return request<DaiwaJanMasterAckAllResponse>(
    "/vendors/daiwa/jan-master/acknowledge-all-new",
    { method: "POST" },
  );
}

// ── Daiwa Naver Listing Master (Phase B/C/D, #80) ─────────────

export interface DaiwaNaverListingUploadResult {
  sheet_type: string;
  mode: "partial" | "full";
  source_file_name: string | null;
  header_row_index: number | null;
  data_start_row_index: number | null;
  parsed_rows: number;
  skipped_empty_jan_rows: number;
  invalid_jan_rows: number;
  normalized_jan_count: number;
  duplicate_jans_in_file: number;
  duplicate_rows_in_file: number;
  inserted_count: number;
  updated_count: number;
  ended_count: number;
  seller_code_conflict_count: number;
  matched_daiwa_jan_count: number;
  unmatched_daiwa_jan_count: number;
  total_current_rows_after: number;
  canonical_jan_redirect_count: number;
  canonical_jan_conflict_count: number;
  canonical_jan_redirect_examples: Record<string, unknown>[];
  canonical_jan_conflict_examples: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

export interface DaiwaNaverListingSummary {
  total_listings: number;
  active_count: number;
  paused_count: number;
  ended_count: number;
  unknown_count: number;
  matched_daiwa_jan_count: number;
  unmatched_daiwa_jan_count: number;
  daiwa_master_total_count: number;
  daiwa_master_not_listed_count: number;
  candidate_count: number;
  last_uploaded_at_max: string | null;
}

export async function uploadDaiwaNaverListing(
  file: File,
  mode: "partial" | "full" = "partial",
): Promise<DaiwaNaverListingUploadResult> {
  return uploadFile<DaiwaNaverListingUploadResult>(
    "/vendors/daiwa/naver-listing/upload",
    file,
    { mode },
  );
}

export async function getDaiwaNaverListingSummary(): Promise<DaiwaNaverListingSummary> {
  return request<DaiwaNaverListingSummary>("/vendors/daiwa/naver-listing/summary");
}

// ── Daiwa Naver Listing — 목록 (Phase D 2차, #80) ──────────────

export interface DaiwaNaverListingItem {
  id: number;
  jan_code: string;
  seller_product_code: string | null;
  naver_product_no: string | null;
  listing_url: string | null;
  listing_status: string;
  product_name: string | null;
  category_path: string | null;
  sale_price_krw: number | null;
  source_file_name: string | null;
  last_uploaded_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
  // daiwa_jan_master LEFT JOIN 파생
  daiwa_master_matched: boolean;
  dk_stock_qty: number | null;
  min_order_qty: number | null;
  direct_ship_eligible: boolean;
  name_ko: string | null;
  product_group: string | null;
  // [ERP-DAIWA-NAVER-DIRECT-SOLDOUT-RESTOCK-ACTION-STATE-20260605-001]
  action_status: string | null;
  soldout_done_at: string | null;
  soldout_done_by_name: string | null;
  restock_done_at: string | null;
  restock_done_by_name: string | null;
}

export interface DaiwaNaverListingListResponse {
  items: DaiwaNaverListingItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaNaverListingListParams {
  q?: string;
  listing_status?: "active" | "paused" | "ended" | "unknown";
  matched_daiwa_only?: boolean;
  unmatched_daiwa_only?: boolean;
  // [ERP-DAIWA-ORDER-QUEUE-RESTOCKED-CANDIDATE-AND-NAVER-DK-ZERO-FILTER-20260605-001]
  dk_stock_filter?: "all" | "zero" | "in_stock";
  page?: number;
  page_size?: number;
}

export async function getDaiwaNaverListings(
  params: DaiwaNaverListingListParams = {},
): Promise<DaiwaNaverListingListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.listing_status) qs.set("listing_status", params.listing_status);
  if (params.matched_daiwa_only) qs.set("matched_daiwa_only", "true");
  if (params.unmatched_daiwa_only) qs.set("unmatched_daiwa_only", "true");
  if (params.dk_stock_filter && params.dk_stock_filter !== "all")
    qs.set("dk_stock_filter", params.dk_stock_filter);
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaNaverListingListResponse>(
    `/vendors/daiwa/naver-listing?${qs}`,
  );
}

// ── Daiwa Naver Listing — 후보 (Phase D 2차, #80) ──────────────

export interface DaiwaNaverListingCandidateItem {
  id: number;
  jan_code: string;
  supplier_product_code: string | null;
  name_en: string | null;
  name_ko: string | null;
  min_order_qty: number | null;
  direct_ship_eligible: boolean;
  product_group: string | null;
  barcode: string | null;
  category_large: string | null;
  category_middle: string | null;
  category_small: string | null;
  origin_country: string | null;
  dk_stock_qty: number;
  preorder_qty: number;
  expected_arrival_date: string | null;
  price_jpy: number | null;
  msrp_krw: number | null;
  listed: boolean;
  candidate_reason: string | null;
}

export interface DaiwaNaverListingCandidateResponse {
  items: DaiwaNaverListingCandidateItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaNaverListingCandidateParams {
  q?: string;
  dk_stock_only?: boolean;
  direct_ship_only?: boolean;
  product_group?: string;
  category_large?: string;
  category_middle?: string;
  category_small?: string;
  page?: number;
  page_size?: number;
}

export async function getDaiwaNaverListingCandidates(
  params: DaiwaNaverListingCandidateParams = {},
): Promise<DaiwaNaverListingCandidateResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  // dk_stock_only / direct_ship_only 는 백엔드 기본이 True 라서 false 일 때만 명시
  if (params.dk_stock_only === false) qs.set("dk_stock_only", "false");
  if (params.direct_ship_only === false) qs.set("direct_ship_only", "false");
  if (params.product_group) qs.set("product_group", params.product_group);
  if (params.category_large) qs.set("category_large", params.category_large);
  if (params.category_middle) qs.set("category_middle", params.category_middle);
  if (params.category_small) qs.set("category_small", params.category_small);
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaNaverListingCandidateResponse>(
    `/vendors/daiwa/naver-listing/candidates?${qs}`,
  );
}

// ── [ERP-DAIWA-STOCK-ZERO-TRANSITION-LIST-20260522-001] 재고 0 전환 SKU 상세 ──

export interface DaiwaStockZeroTransitionItem {
  jan_code: string;
  supplier_product_code: string | null;
  name_ko: string | null;
  name_en: string | null;
  prev_stock_qty: number;
  current_stock_qty: number;
  prev_snapshot_at: string;
  current_snapshot_at: string;
  min_order_qty: number | null;
  direct_ship_eligible: boolean;
  naver_listed: boolean;
  naver_listing_url: string | null;
  // [ERP-DAIWA-NAVER-DIRECT-SOLDOUT-RESTOCK-ACTION-STATE-20260605-001]
  naver_listing_id: number | null;
  action_status: string | null;
  soldout_done_at: string | null;
  soldout_done_by_name: string | null;
  restock_done_at: string | null;
  restock_done_by_name: string | null;
}

export interface DaiwaStockZeroTransitionResponse {
  items: DaiwaStockZeroTransitionItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaStockZeroTransitionParams {
  q?: string;
  direct_ship_only?: boolean;
  naver_listed_only?: boolean;
  page?: number;
  page_size?: number;
}

export async function getDaiwaStockZeroTransitions(
  params: DaiwaStockZeroTransitionParams = {},
): Promise<DaiwaStockZeroTransitionResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.direct_ship_only) qs.set("direct_ship_only", "true");
  if (params.naver_listed_only) qs.set("naver_listed_only", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaStockZeroTransitionResponse>(
    `/vendors/daiwa/stock-zero-transitions?${qs}`,
  );
}

// ── [ERP-DAIWA-ORDER-QUEUE-RESTOCKED-CANDIDATE-AND-NAVER-DK-ZERO-FILTER-20260605-001]
// 네이버 직배 재입고/품절해제 후보 (prev=0 & latest>0). 응답 스키마는 품절 후보와 동일.
export async function getDaiwaStockRestockTransitions(
  params: DaiwaStockZeroTransitionParams = {},
): Promise<DaiwaStockZeroTransitionResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.direct_ship_only) qs.set("direct_ship_only", "true");
  if (params.naver_listed_only) qs.set("naver_listed_only", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaStockZeroTransitionResponse>(
    `/vendors/daiwa/stock-restock-transitions?${qs}`,
  );
}

// ── [ERP-DAIWA-NAVER-DIRECT-SOLDOUT-RESTOCK-ACTION-STATE-20260605-001]
// 네이버 직배 품절/재고복구 수동 작업 상태 (직원 확인 기록, 네이버 자동변경 없음).

export interface DaiwaNaverStockActionState {
  id: number;
  jan_code: string;
  seller_product_code_snapshot: string | null;
  naver_listing_id: number | null;
  naver_product_no_snapshot: string | null;
  product_name_snapshot: string | null;
  supplier_product_code_snapshot: string | null;
  status: string;
  soldout_done_at: string | null;
  soldout_done_by_user_id: number | null;
  soldout_done_by_name_snapshot: string | null;
  soldout_dk_stock_qty_snapshot: number | null;
  restock_detected_at: string | null;
  restock_dk_stock_qty_snapshot: number | null;
  restock_done_at: string | null;
  restock_done_by_user_id: number | null;
  restock_done_by_name_snapshot: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export async function daiwaNaverSoldoutDone(
  listingId: number,
  memo?: string,
): Promise<DaiwaNaverStockActionState> {
  return request<DaiwaNaverStockActionState>(
    `/vendors/daiwa/naver-listing/${listingId}/soldout-done`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}

export async function daiwaNaverRestockDone(
  listingId: number,
  memo?: string,
): Promise<DaiwaNaverStockActionState> {
  return request<DaiwaNaverStockActionState>(
    `/vendors/daiwa/naver-listing/${listingId}/restock-done`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}

// ── Daiwa Reference Tables (탭2: 기준표 관리) ──────────────────

export interface DaiwaKakeRule {
  id: number;
  vendor_name: string;
  product_group: string;
  kake_rate: number;
  is_active: boolean;
  updated_at: string;
}

export interface DaiwaKakeRuleListResponse {
  items: DaiwaKakeRule[];
  total: number;
  updated_at_max: string | null;
}

export interface DaiwaKakeRuleUpdateRequest {
  kake_rate?: number;
  is_active?: boolean;
}

export async function getDaiwaKakeRules(): Promise<DaiwaKakeRuleListResponse> {
  return request<DaiwaKakeRuleListResponse>("/vendors/daiwa/reference-tables/kake");
}

export async function updateDaiwaKakeRule(
  id: number,
  body: DaiwaKakeRuleUpdateRequest,
): Promise<DaiwaKakeRule> {
  return request<DaiwaKakeRule>(`/vendors/daiwa/reference-tables/kake/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface DaiwaCategoryMapping {
  id: number;
  vendor_name: string;
  vendor_category_name: string;
  naver_category_code: string | null;
  naver_category_name: string | null;
  is_active: boolean;
  updated_at: string;
}

export interface DaiwaCategoryMappingListResponse {
  items: DaiwaCategoryMapping[];
  total: number;
  updated_at_max: string | null;
}

export interface DaiwaCategoryMappingUpdateRequest {
  naver_category_code?: string;
  naver_category_name?: string;
  is_active?: boolean;
}

export async function getDaiwaCategoryMappings(): Promise<DaiwaCategoryMappingListResponse> {
  return request<DaiwaCategoryMappingListResponse>("/vendors/daiwa/reference-tables/category");
}

export async function updateDaiwaCategoryMapping(
  id: number,
  body: DaiwaCategoryMappingUpdateRequest,
): Promise<DaiwaCategoryMapping> {
  return request<DaiwaCategoryMapping>(`/vendors/daiwa/reference-tables/category/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface DaiwaOriginMapping {
  id: number;
  origin_name: string;
  naver_origin_code: string | null;
  is_active: boolean;
  updated_at: string;
}

export interface DaiwaOriginMappingListResponse {
  items: DaiwaOriginMapping[];
  total: number;
  updated_at_max: string | null;
}

export interface DaiwaOriginMappingUpdateRequest {
  naver_origin_code?: string;
  is_active?: boolean;
}

export async function getDaiwaOriginMappings(): Promise<DaiwaOriginMappingListResponse> {
  return request<DaiwaOriginMappingListResponse>("/vendors/daiwa/reference-tables/origin");
}

export async function updateDaiwaOriginMapping(
  id: number,
  body: DaiwaOriginMappingUpdateRequest,
): Promise<DaiwaOriginMapping> {
  return request<DaiwaOriginMapping>(`/vendors/daiwa/reference-tables/origin/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// ── 다이와 인터넷 가격리스트 (#72) ─────────────────────────────

export interface DaiwaPriceListItem {
  id: number;
  jan_code: string;
  product_name: string | null;
  online_min_price_krw: number | null;
  policy_active: boolean;
  effective_month: string | null;
  memo: string | null;
  updated_at: string;
  // 세부스펙 참고표 원본 컬럼 (#76)
  grade: string | null;
  sub_category: string | null;
  product_group: string | null;
  price_jpy: number | null;
  msrp_krw: number | null;
  sales_flag: string | null;
  // 카케 매칭 + 파생 계산 (응답 시점)
  matched_kake_rate: number | null;
  kake_matched: boolean;
  expected_cost: number | null;
  margin_amount: number | null;
  margin_rate: number | null;
  is_not_for_sale: boolean;
  // ERP 등록상품 + 현재 네이버 판매가 비교 (#77)
  has_erp_product: boolean;
  current_naver_price: number | null;
  policy_price_diff: number | null;  // current_naver_price - online_min_price_krw
  price_policy_status: "normal" | "violation" | "no_product" | "no_price" | "no_policy";
}

export interface DaiwaPriceListResponse {
  items: DaiwaPriceListItem[];
  total: number;
  updated_at_max: string | null;
  page: number;
  page_size: number;
  total_pages: number;
}

export async function getDaiwaPriceList(params: {
  q?: string;
  page?: number;
  page_size?: number;
  active_only?: boolean;
  no_kake_only?: boolean;
  not_for_sale_only?: boolean;
  low_margin_only?: boolean;
  erp_registered_only?: boolean;
} = {}): Promise<DaiwaPriceListResponse> {
  const qs = new URLSearchParams();
  if (params.q && params.q.trim()) qs.set("q", params.q.trim());
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 100));
  if (params.active_only === false) qs.set("active_only", "false");
  if (params.no_kake_only) qs.set("no_kake_only", "true");
  if (params.not_for_sale_only) qs.set("not_for_sale_only", "true");
  if (params.low_margin_only) qs.set("low_margin_only", "true");
  if (params.erp_registered_only) qs.set("erp_registered_only", "true");
  return request<DaiwaPriceListResponse>(`/vendors/daiwa/reference-tables/price-list?${qs}`);
}

// ── 다이와 직배 운영자 결정 (decide → hitcamp/daiwa/reset) ──────

export interface DirectShipDecisionResponse {
  order_no: string;
  daiwa_fulfillment_choice: "hitcamp" | "daiwa" | null;
  fulfillment_class: FulfillmentClass;
  daiwa_eligible_item_count: number;
}

export async function setDaiwaDirectShipDecision(
  order_no: string,
  choice: "hitcamp" | "daiwa" | "reset",
): Promise<DirectShipDecisionResponse> {
  return request<DirectShipDecisionResponse>(`/vendors/daiwa/direct-ship/decision`, {
    method: "POST",
    body: JSON.stringify({ order_no, choice }),
  });
}

export async function downloadDaiwaNaverBulk(): Promise<void> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const res = await fetch(`${API_URL}/vendors/daiwa/export/naver-bulk`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Export 실패" }));
    throw new Error(err.detail || "Export 실패");
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || "daiwa_naver_bulk.xlsx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Inventory ──────────────────────────────────────────────────

export interface StockImportResult {
  sheet_type: string;
  total_rows: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  errors: { row?: number; error: string }[];
}

export interface InventorySummary {
  total_skus: number;
  total_quantity: number;
  location_counts: Record<string, number>;
  location_quantities: Record<string, number>;
  inhouse_supply_source_count: number;
  recent_movement_count: number;
}

export interface InventoryItem {
  sku: string;
  jan_code: string | null;
  location_code: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  is_active: boolean;
  updated_at: string;
}

export interface InventoryByLocationResponse {
  total: number;
  items: InventoryItem[];
}

export interface StockMovementItem {
  id: number;
  sku: string;
  jan_code: string | null;
  location_code: string;
  movement_type: string;
  quantity_delta: number;
  source_type: string;
  source_ref: string | null;
  memo: string | null;
  created_at: string;
}

export async function getInventorySummary(): Promise<InventorySummary> {
  return request<InventorySummary>("/inventory/summary");
}

export async function getInventoryByLocation(params?: {
  location_code?: string;
  sku?: string;
  only_in_stock?: boolean;
  limit?: number;
  offset?: number;
}): Promise<InventoryByLocationResponse> {
  const qs = new URLSearchParams();
  if (params?.location_code) qs.set("location_code", params.location_code);
  if (params?.sku) qs.set("sku", params.sku);
  if (params?.only_in_stock) qs.set("only_in_stock", "true");
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<InventoryByLocationResponse>(`/inventory/by-location?${qs}`);
}

export async function getStockMovements(params?: {
  sku?: string;
  location_code?: string;
  movement_type?: string;
  limit?: number;
}): Promise<StockMovementItem[]> {
  const qs = new URLSearchParams();
  if (params?.sku) qs.set("sku", params.sku);
  if (params?.location_code) qs.set("location_code", params.location_code);
  if (params?.movement_type) qs.set("movement_type", params.movement_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  return request<StockMovementItem[]>(`/inventory/movements?${qs}`);
}

export async function importHitcampStock(
  file: File,
  locationCode = "hitcamp_main",
  receiptDate?: string
): Promise<StockImportResult> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const formData = new FormData();
  formData.append("file", file);
  const qs = new URLSearchParams({ location_code: locationCode });
  if (receiptDate) qs.set("receipt_date", receiptDate);
  const res = await fetch(`${API_URL}/vendors/daiwa/import/hitcamp-stock?${qs}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "업로드 실패" }));
    throw new Error(err.detail || "업로드 실패");
  }
  return res.json();
}

// ── 멀티 로케이션 재고 고도화 ──────────────────────────────────

export interface LocationSummaryItem {
  location_code: string;
  label: string;
  sku_count: number;
  total_quantity: number;
  quarantine: boolean;
  rocket: boolean;
}

export interface SkuLocationRow {
  location_code: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  updated_at: string;
}

export interface SkuLocationResponse {
  sku: string;
  total_quantity: number;
  locations: SkuLocationRow[];
}

export interface TransferRequest {
  sku: string;
  from_location_code: string;
  to_location_code: string;
  quantity: number;
  memo?: string;
  transferred_by?: string;
  // [ERP-RETURN-QUARANTINE-TRANSFER-SOURCE-TRACE-DEV-20260709-033]
  // 검수창고 lot 단위 재배치 시 원 회수건 추적용(미전달 시 기존 manual_adjustment 유지).
  source_type?: string;
  source_ref?: string;
  source_label?: string;
}

// 12차 — 백엔드는 `require_or_request_approval` 을 거쳐
// {mode: "applied"|"pending", approval_id, result} 형태로 응답한다.
// `result` 는 admin 일 때 `_apply_transfer` 의 반환값, pending 일 땐 null.
export interface TransferResult {
  sku: string;
  from_location_code: string;
  to_location_code: string;
  quantity: number;
  from_quantity_after: number;
  to_quantity_after: number;
  movement_out_id: number;
  movement_in_id: number;
}

export interface TransferResponse {
  mode: "applied" | "pending";
  approval_id: number;
  result: TransferResult | null;
}

export function getLocationsSummary(): Promise<LocationSummaryItem[]> {
  return request<LocationSummaryItem[]>("/inventory/locations/summary");
}

export function getSkuLocations(sku: string): Promise<SkuLocationResponse> {
  return request<SkuLocationResponse>(`/inventory/sku/${encodeURIComponent(sku)}`);
}

export function transferStock(body: TransferRequest): Promise<TransferResponse> {
  return request<TransferResponse>("/inventory/transfer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 상품+재고 통합 화면 ────────────────────────────────────────────────────

export interface ProductWithInventoryItem {
  id: number;
  sku: string;
  product_name: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  brand: string | null;
  category: string | null;
  image_url: string | null;
  product_status: string;
  safety_stock_qty: number;
  part_type: string;
  as_available: boolean;
  is_active: boolean;
  sale_price: number | null;
  cost_factor: number | null;
  cost_mode?: string | null;             // [FW-005-COST-PHASE2] 목록 원가팩터 편집은 cny_calc 만
  // [ERP-PRODUCT-PRICE-SOT-SMARTSTORE-DISPLAY-CONSISTENCY-PHASE1-20260608-001]
  // 기준가 SoT = product_prices.smartstore. effective_base_price 우선 표시.
  base_price?: number | null;            // product_prices.smartstore (없으면 null)
  effective_base_price?: number | null;  // base_price ?? sale_price (표시용 최종 기준가)
  sale_price_mismatch?: boolean;         // base_price ≠ sale_price 면 true (불일치 경고)
  total_quantity: number;
  available_quantity: number;
  reserved_from_po: number;
  inventory_by_location: Record<string, number>;
  // [ERP-BUNDLE-AVAILABLE-STOCK-SEPARATION-20260618-001] 일반/번들 가용 분리
  general_available_stock?: number | null;
  bundle_available_stock?: number | null;   // 번들이면 값, 일반이면 null
  effective_available_stock?: number | null;
  is_bundle?: boolean;
  bundle_component_count?: number;
  // [ERP-PRODUCT-LAST-INBOUND-DATE-DISPLAY-20260618-001] 최근 입고일(PO 입고/입고검수, 수동조정 제외)
  last_inbound_at?: string | null;
  last_inbound_source?: string | null;
  // [ERP-PRODUCT-INVENTORY-LAST-PO-INBOUND-QUANTITY-DISPLAY-20260713-082]
  // 최근 PO 입고 이벤트의 실제 입고수량(동일 이벤트 합산). 이력 없으면 null. 누적/현재고 아님.
  last_inbound_quantity?: number | null;
  // [ERP-PRODUCTS-STOCK-VISIBILITY-BUSINESS-FLOW-SOURCE-ALIGNMENT-20260608-001]
  // 업무화면 source 기준 대기 수량 + location 잔여(확인필요).
  purchase_inbound_waiting?: number;   // 발주/입고검수장 기준 입고검수 대기
  return_inspection_waiting?: number;  // 교환반품 회수검수장 기준 검수 대기
  incoming_orphan_qty?: number;        // hitcamp_incoming 잔여(입고검수장 미연결, 관리자 확인필요)
  quarantine_orphan_qty?: number;      // quarantine 잔여(회수검수장 미연결, 관리자 확인필요)
  safety_stock_recommended: number;
  listed_platforms: string[];
  // [ERP-PRODUCT-LIST-PLATFORM-LINKS-20260519-001] 표준 platform key 의 listing_url.
  // 구 key 제외, http/https 시작 url 만 채택. 키 없으면 미등록 또는 url 없음.
  listing_urls?: Record<string, string>;
  // [ERP-PRODUCT-PURCHASE-URL-QUICKLINK-20260521-001] 매입처(구매) URL — 빠른 링크용
  purchase_url?: string | null;
}

export interface ProductWithInventoryResponse {
  total: number;
  items: ProductWithInventoryItem[];
}

export interface DashboardCardsResponse {
  total_skus: number;
  safety_stock_alert_count: number;
  stockout_count: number;
  stale_3m: number;
  stale_6m: number;
  stale_12m: number;
  recently_moved_7d_count: number;
}

// 상단 카드 drill-down 프리셋 — /products/with-inventory?stock_flag=<v> 와 1:1 매핑
export type StockFlag =
  | "safety_alert"
  | "stockout"
  | "stale_3m"
  | "stale_6m"
  | "stale_12m"
  | "recently_moved_7d";

export interface BatchTransferItem {
  sku: string;
  quantity: number;
}

export interface BatchTransferRequest {
  transfers: BatchTransferItem[];
  from_location_code: string;
  to_location_code: string;
  transferred_by?: string;
  memo?: string;
}

export interface BatchTransferResultItem {
  sku: string;
  quantity: number;
  success: boolean;
  error?: string | null;
}

// 12차 — 배치 이동도 require_or_request_approval 래핑된 응답 사용.
// admin → {mode: "applied", result: {succeeded_count, failed_count, results}}
// 비-admin → {mode: "pending", result: null}
export interface BatchTransferInner {
  succeeded_count: number;
  failed_count: number;
  results: BatchTransferResultItem[];
}

export interface BatchTransferResponse {
  mode: "applied" | "pending";
  approval_id: number;
  result: BatchTransferInner | null;
  // 호환 — 기존 호출부가 평탄 필드를 직접 참조하는 경우 대비
  succeeded_count?: number;
  failed_count?: number;
  results?: BatchTransferResultItem[];
}

export function getDashboardCards(): Promise<DashboardCardsResponse> {
  return request<DashboardCardsResponse>("/inventory/dashboard-cards");
}

export function getProductsWithInventory(params?: {
  page?: number;
  page_size?: number;
  brand?: string;
  category?: string;
  search?: string;
  part_type?: string;
  product_status?: string;
  location_filter?: string;
  unlisted_platform?: string;
  stock_flag?: StockFlag;
  // [ERP-PRODUCT-INVENTORY-PO-INBOUND-HISTORY-FILTER-20260713-081]
  // 실제 PO 발주·입고 이력 유무 필터. "all"(기본)|"yes"|"no". 미전달 시 backend 기본 all.
  po_inbound_history?: "all" | "yes" | "no";
  // 관리자 "숨김 포함" 보기 — true 면 is_active=false(숨김/중복 SKU 정리) 상품도 노출.
  include_inactive?: boolean;
  // [PURCHASE-001][ERP-PURCHASE-001-WORKFLOW-OPERATIONS-PHASE2-DEV-20260819]
  // 이 component SKU 가 포함된 세트(parent bundle)만 표시. 발주 상세 [세트 N] deep-link.
  used_in_bundle_sku?: string;
}, init?: { signal?: AbortSignal }): Promise<ProductWithInventoryResponse> {
  // [FIX-DB-POOL-EXHAUSTION-20260928] init.signal — 실시간 검색에서 이전 요청 취소용(선택).
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.page_size) qs.set("page_size", String(params.page_size));
  if (params?.brand) qs.set("brand", params.brand);
  if (params?.category) qs.set("category", params.category);
  if (params?.search) qs.set("search", params.search);
  if (params?.part_type) qs.set("part_type", params.part_type);
  if (params?.product_status) qs.set("product_status", params.product_status);
  if (params?.location_filter) qs.set("location_filter", params.location_filter);
  if (params?.unlisted_platform) qs.set("unlisted_platform", params.unlisted_platform);
  if (params?.stock_flag) qs.set("stock_flag", params.stock_flag);
  if (params?.po_inbound_history && params.po_inbound_history !== "all") qs.set("po_inbound_history", params.po_inbound_history);
  if (params?.include_inactive) qs.set("include_inactive", "true");
  if (params?.used_in_bundle_sku) qs.set("used_in_bundle_sku", params.used_in_bundle_sku);
  return request<ProductWithInventoryResponse>(`/products/with-inventory?${qs}`, { signal: init?.signal });
}

export interface LocationInventorySummaryItem {
  location_code: string;
  sku_count: number;
  total_quantity: number;
}
export interface LocationInventorySummaryResponse {
  items: LocationInventorySummaryItem[];
  total_sku_count: number;
  total_quantity: number;
}

/**
 * 위치 탭 카운트 전용. `getProductsWithInventory` 와 동일한 필터(`brand`, `category`, `search`,
 * `part_type`, `product_status`, `unlisted_platform`)를 받아 위치별 집계를 반환한다.
 * `location_filter` 파라미터는 **받지 않음** — 이 응답 자체가 모든 위치의 집계이기 때문.
 */
export function getProductInventorySummary(params?: {
  brand?: string;
  category?: string;
  search?: string;
  part_type?: string;
  product_status?: string;
  unlisted_platform?: string;
  stock_flag?: StockFlag;
  // [ERP-PRODUCT-INVENTORY-PO-INBOUND-HISTORY-FILTER-20260713-081] with-inventory 와 동일 필터.
  po_inbound_history?: "all" | "yes" | "no";
  include_inactive?: boolean;
}, init?: { signal?: AbortSignal }): Promise<LocationInventorySummaryResponse> {
  // [FIX-DB-POOL-EXHAUSTION-20260928] init.signal — 실시간 검색에서 이전 요청 취소용(선택).
  const qs = new URLSearchParams();
  if (params?.brand) qs.set("brand", params.brand);
  if (params?.category) qs.set("category", params.category);
  if (params?.search) qs.set("search", params.search);
  if (params?.part_type) qs.set("part_type", params.part_type);
  if (params?.product_status) qs.set("product_status", params.product_status);
  if (params?.unlisted_platform) qs.set("unlisted_platform", params.unlisted_platform);
  if (params?.stock_flag) qs.set("stock_flag", params.stock_flag);
  if (params?.po_inbound_history && params.po_inbound_history !== "all") qs.set("po_inbound_history", params.po_inbound_history);
  if (params?.include_inactive) qs.set("include_inactive", "true");
  const q = qs.toString();
  return request<LocationInventorySummaryResponse>(
    `/products/inventory-summary${q ? `?${q}` : ""}`,
    { signal: init?.signal },
  );
}

export function transferStockBatch(body: BatchTransferRequest): Promise<BatchTransferResponse> {
  return request<BatchTransferResponse>("/inventory/transfer/batch", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function adjustInventory(body: {
  sku: string;
  location_code: string;
  quantity: number;
  reason?: string;
}): Promise<{ sku: string; location_code: string; quantity: number; delta: number }> {
  return request("/inventory/adjust", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface AdjustDeltaRequest {
  sku: string;
  location_code: string;
  delta: number;
  reason?: string;
  memo?: string;
}

export function adjustInventoryDelta(body: AdjustDeltaRequest): Promise<unknown> {
  return request("/inventory/adjust-delta", { method: "POST", body: JSON.stringify(body) });
}

// ── 상품 등록 모달 보조 ────────────────────────────────────────────────────────

export function getNextSku(brand: string): Promise<{ next_sku: string; prefix: string; next_num: number }> {
  return request(`/products/next-sku?brand=${encodeURIComponent(brand)}`);
}

export function getCategories(): Promise<string[]> {
  return request("/products/categories");
}

export function getBrands(): Promise<string[]> {
  return request<string[]>("/products/brands");
}

export function searchProductNames(q: string, limit = 10): Promise<{ product_name: string; sku: string }[]> {
  return request(`/products/names?q=${encodeURIComponent(q)}&limit=${limit}`);
}

export interface PlatformMeta {
  id?: number;
  sku?: string;
  platform: string;
  is_listed: boolean;
  listing_url?: string | null;
  price_multiplier?: number | null;
}

export function getPlatformMeta(sku: string): Promise<PlatformMeta[]> {
  return request<PlatformMeta[]>(`/products/${encodeURIComponent(sku)}/platform-meta`);
}

// [ERP-DAIWA-BARCODE-SCAN-20260519-001] 실물 바코드 등록 helper.
export interface RegisterBarcodeWarning {
  code: string;
  message: string;
}
export interface RegisterBarcodeResponse {
  success: boolean;
  sku: string;
  barcode: string;
  warnings: RegisterBarcodeWarning[];
  matched_by: string;
}
export function registerProductBarcode(
  sku: string,
  barcode: string,
  source: string = "packing_scan",
): Promise<RegisterBarcodeResponse> {
  return request<RegisterBarcodeResponse>(
    `/products/${encodeURIComponent(sku)}/register-barcode`,
    {
      method: "POST",
      body: JSON.stringify({ barcode, source }),
    },
  );
}

export function upsertPlatformMeta(
  sku: string,
  data: { platform: string; is_listed: boolean; listing_url?: string | null; price_multiplier?: number | null },
): Promise<{ ok: boolean }> {
  return request(`/products/${encodeURIComponent(sku)}/platform-meta`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── 브랜드 SKU 설정 ────────────────────────────────────────────────────────────

export interface BrandSkuPrefix {
  id: number;
  brand: string;
  prefix: string;
  digit_count: number;
  separator: string;
}

export function getBrandSkuPrefixes(): Promise<BrandSkuPrefix[]> {
  return request<BrandSkuPrefix[]>("/settings/brand-sku-prefixes");
}

export function upsertBrandSkuPrefix(data: {
  brand: string;
  prefix: string;
  digit_count: number;
  separator: string;
}): Promise<BrandSkuPrefix> {
  return request<BrandSkuPrefix>("/settings/brand-sku-prefixes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deleteBrandSkuPrefix(id: number): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/settings/brand-sku-prefixes/${id}`, { method: "DELETE" });
}

// ── AS 관리 ──────────────────────────────────────────────────────────────────

// 26차 후속 #22 — AS 5단계 (2026-04-27)
export const AS_STATUS_LABELS: Record<string, string> = {
  received: "접수",
  recovered: "회수완료",
  repaired: "수리완료",
  payment_confirmed: "수리비용입금확인",
  shipped: "출고완료",
  cancelled: "취소",
};

export const AS_STATUS_COLORS: Record<string, string> = {
  received: "bg-blue-100 text-blue-800",
  recovered: "bg-yellow-100 text-yellow-800",
  repaired: "bg-purple-100 text-purple-800",
  payment_confirmed: "bg-emerald-100 text-emerald-800",
  shipped: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
};

// 5단계 정방향 다음-단계 매핑 (AS 상세 페이지의 "다음 단계" 버튼용).
// cancelled / shipped 는 종료 상태.
export const AS_NEXT_STATUS: Record<string, string | null> = {
  received: "recovered",
  recovered: "repaired",
  repaired: "payment_confirmed",
  payment_confirmed: "shipped",
  shipped: null,
  cancelled: null,
};

export interface AsPartsUsedItem {
  id: number;
  as_request_id: number;
  part_name: string;
  part_sku: string | null;
  option1?: string | null;
  option2?: string | null;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  customer_price: number | null;   // 소비자가
  labor_fee: number | null;        // 파츠별 기술료
  memo: string | null;
  created_at: string;
  // [ERP-AS-WARRANTY-FREE-PARTS-ACTUAL-LOGIC-20260618-001] 파츠비 무상 적용 상태
  is_warranty_free?: boolean;
  warranty_usage_id?: number | null;
  original_part_price?: number | null;  // 무상 적용 전 파츠비
  warranty_policy_name?: string | null;
}

export interface AsCostItem {
  id: number;
  as_request_id: number;
  parts_cost: number;
  labor_cost: number;
  shipping_cost: number;
  extra_cost: number;
  total_cost: number;
  customer_charge: number;
  charge_confirmed: boolean;
  charge_confirmed_at: string | null;
  charge_confirmed_by: string | null;
  // 26차 후속 #22 — 입금확인 큐 (none | pending | confirmed)
  deposit_status: string;
  deposit_confirmed_at: string | null;
  memo: string | null;
  updated_at: string;
}

// [ERP-AS-ADDITIONAL-SALES-ITEMS-WITH-REPAIR-SHIPMENT-DEV-20260622-001]
// AS 고객 추가구매 상품(일반 판매상품). 파츠와 분리 — 재고차감/포장검수 대상.
export interface AsAdditionalItem {
  id: number;
  as_request_id: number;
  product_id: number | null;
  sku: string | null;
  product_name: string;   // 등록 시점 snapshot
  option1: string | null;
  option2: string | null;
  barcode: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  memo: string | null;
  created_at: string;
}

export interface AsShipmentRequest {
  id: number;
  as_request_id: number;
  as_no: string;
  status: string;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  requested_by: string | null;
  memo: string | null;
  created_at: string;
}

export interface AsRequest {
  id: number;
  as_no: string;
  order_no: string | null;
  customer_id: number | null;
  customer_name: string;
  customer_phone: string | null;
  sku: string | null;
  product_name: string;
  symptom: string | null;
  // [054B-2] AS 처리방식(self_repair/inbound_repair/onsite_repair). legacy AS 는 null.
  as_mode?: string | null;
  order_item_id?: number | null;
  request_channel: string;
  received_date: string;
  status: string;
  assignee: string | null;
  memo: string | null;
  company_code: string | null;
  free_repair_eligible: boolean;
  pickup_type: string | null;
  pickup_tracking_no: string | null;
  symptom_match: boolean | null;
  mismatch_comment: string | null;
  repair_comment: string | null;
  out_tracking_no: string | null;
  // [ERP-TOGGLE-EXPORT-AS-DETAIL-ADDRESS-EDIT-HOTFIX-20260618-001] 회수/출고 주소
  // [AS-001-AS-PICKUP-RECIPIENT-CONTACT] 회수지 수취인·연락처(배송지 return_name/phone 대칭).
  pickup_name?: string | null;
  pickup_phone?: string | null;
  pickup_zipcode?: string | null;
  pickup_address1?: string | null;
  pickup_address2?: string | null;
  return_name?: string | null;
  return_phone?: string | null;
  return_zipcode?: string | null;
  return_address1?: string | null;
  return_address2?: string | null;
  created_at: string;
  updated_at: string;
  parts: AsPartsUsedItem[];
  cost: AsCostItem | null;
  // [ERP-AS-ADDITIONAL-SALES-ITEMS-WITH-REPAIR-SHIPMENT-DEV-20260622-001]
  additional_items: AsAdditionalItem[];
  // 7차 정리 — AS 회수 허브 연계 상태 파생
  recovery_receipt_id: number | null;
  recovery_receipt_status: string | null;
  recovery_arrived: boolean | null;
  // [ERP-AS-054C-3D] 자가수리 배송 read model (054B-5-1). 파생값 — 신규 상태머신 아님.
  self_repair_shipment?: SelfRepairShipmentInfo | null;
  // [AS-001-AS-INTAKE-PRODUCT-OPTION-DISPLAY] 접수 상품 옵션(OrderItem 구매당시 → Product fallback).
  intake_option1?: string | null;
  intake_option2?: string | null;
  // [AS-001-AS-EFFECTIVE-ADDRESS-CONSISTENCY] 실제 사용 주소 파생(단건 AS Export 와 동일 기준).
  //   *_source: "return"|"pickup"|"order"|"customer"|null → 화면 출처 표기.
  effective_ship_name?: string | null;
  effective_ship_phone?: string | null;
  effective_ship_zipcode?: string | null;
  effective_ship_address1?: string | null;
  effective_ship_address2?: string | null;
  effective_ship_source?: string | null;
  effective_pickup_name?: string | null;
  effective_pickup_phone?: string | null;
  // [AS-002] 승계건: AS 고객=현재 소유자, 원주문자는 연결 주문에서 read-only 파생(신규 SoT 0).
  is_transfer_in?: boolean;
  original_order_customer_name?: string | null;
  original_order_customer_phone?: string | null;
  effective_pickup_zipcode?: string | null;
  effective_pickup_address1?: string | null;
  effective_pickup_address2?: string | null;
  effective_pickup_source?: string | null;
  // [AS-002 Phase 2b-2D 보완] 실물 연결(product_unit_id)이 있는 AS 의 read-only 파생.
  //   과거 product_unit_id=NULL AS 는 전부 null(backfill 없음) — 기존 표시를 그대로 유지한다.
  product_unit_id?: number | null;
  unit_seq?: number | null;
  product_owner_customer_id?: number | null;
  product_owner_customer_name?: string | null;
  product_owner_phone?: string | null;
  product_owner_zipcode?: string | null;
  product_owner_address1?: string | null;
  product_owner_address2?: string | null;
  as_customer_is_product_owner?: boolean | null;
  // 제품 소유권 판정 불가 — true 면 화면은 `판정 불가`를 명시하고 AS 접수 고객으로 대체 표시하지 않는다.
  product_owner_unresolved?: boolean;
  product_owner_unresolved_reason?: string | null;
}

// [ERP-AS-054B-5-1] 자가수리 배송 현황 파생 read model.
export interface SelfRepairShipmentInfo {
  shipment_state: string; // shipment_not_created | awaiting_packing | shipped
  order_id?: number | null;
  order_no?: string | null;
  order_status?: string | null;
  invoice_no?: string | null;
  packing_completed?: boolean;
  out_tracking_no?: string | null;
  // [FW-038] 배송 주문 취소 여부 · Export 파일 출력 시각 · 수정/취소 잠금(송장 발급/포장 완료)
  is_cancelled?: boolean;
  exported_at?: string | null;
  edit_locked?: boolean;
  edit_lock_reason?: "invoiced" | "packed" | null;
}

export interface AsRequestListItem {
  id: number;
  as_no: string;
  order_no: string | null;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  sku?: string | null;
  as_mode?: string | null;
  status: string;
  assignee: string | null;
  received_date: string;
  pickup_type: string | null;
  pickup_tracking_no: string | null;
  out_tracking_no?: string | null;
  updated_at: string;
  // [ERP-AS-...-20260816] 목록 정산 요약
  customer_charge?: number | null;
  charge_confirmed?: boolean;
  deposit_status?: string | null;
  entitlement_used_count?: number;
  // [AS-001-AS-LIST-IDENTITY-CUSTOMER-SUMMARY-DEV] 통합고객 상태
  customer_id?: number | null;
  identity_group_id?: number | null;
  identity_member_count?: number;
  identity_status?: "unlinked" | "unmerged" | "confirmed" | "inactive";
}

export interface AsRequestListResponse {
  total: number;
  items: AsRequestListItem[];
}

// [AS-001-AS-LIST-IDENTITY-CUSTOMER-SUMMARY-DEV] 고객 요약 펼침 read-model
export interface AsCustomerPartsAgg {
  part_name: string;
  part_sku?: string | null;
  total_quantity: number;
  free_quantity: number;
}
export interface AsCustomerSummaryAsRow {
  as_id: number;
  as_no: string;
  received_date: string;
  status: string;
  product_name: string;
  sku?: string | null;
  customer_charge?: number | null;
  charge_confirmed: boolean;
  deposit_status?: string | null;
  entitlement_used_count: number;
  parts: AsCustomerPartsAgg[];
}
export interface AsCustomerSummary {
  customer_id: number;
  identity_group_id?: number | null;
  is_grouped: boolean;
  member_count: number;
  member_customer_ids: number[];
  purchase_order_count: number;
  purchased_product_count: number;
  past_as_count: number;
  entitlement_available_total: number;
  entitlement_groups: Array<Record<string, unknown>>;
  charge_confirmed_total: number;
  deposit_confirmed_total: number;
  parts_summary: AsCustomerPartsAgg[];
  as_list: AsCustomerSummaryAsRow[];
}
export function getAsCustomerAsSummary(customerId: number): Promise<AsCustomerSummary> {
  return request<AsCustomerSummary>(`/as/customer-as-summary?customer_id=${customerId}`);
}

export function getAsRequests(params?: {
  status?: string;
  assignee?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<AsRequestListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.assignee) qs.set("assignee", params.assignee);
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<AsRequestListResponse>(`/as/requests?${qs}`);
}

export function getAsRequest(id: number): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${id}`);
}

// [AS-001-AS-BILLING-EXPORT-CUSTOMER-MESSAGE-DEV-20260828]
// 고객 안내문(공통 SoT). AS 상세 · 단건 AS Export 두 화면이 이 한 함수를 재사용한다.
export interface AsCustomerMessage { as_no: string; message: string }
export function getAsCustomerMessage(id: number): Promise<AsCustomerMessage> {
  return request<AsCustomerMessage>(`/as/requests/${id}/customer-message`);
}

// [ERP-AS-REQUEST-LV2-SOFT-DELETE-20260619-001]
// AS 접수건 soft delete (lv2 이상). 물리 삭제 아님 — deleted_at 기록 후 목록에서 숨김.
export function deleteAsRequest(
  id: number,
  reason?: string,
): Promise<{ id: number; as_no: string; deleted_at: string | null; already_deleted: boolean }> {
  return request(`/as/requests/${id}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export function createAsRequest(body: {
  customer_name: string;
  product_name: string;
  received_date: string;
  order_no?: string;
  customer_id?: number;
  customer_phone?: string;
  sku?: string;
  symptom?: string;
  request_channel?: string;
  assignee?: string;
  memo?: string;
  // [054C-2] AS 처리방식(self_repair/inbound_repair/onsite_repair) + 원구매 OrderItem SoT.
  as_mode?: string;
  order_item_id?: number;
  // [AS-002 Phase 2b-2D] 실물 번호. 동일 OrderItem 의 실물이 서로 다른 고객 소유면 서버가 **필수**로 요구한다.
  unit_seq?: number | null;
  // company_id 우선, company_code 는 호환 fallback. 백엔드는 둘 중 하나 필수.
  company_id?: number;
  company_code?: string;
  pickup_type?: string;
  pickup_tracking_no?: string;
  // [ERP-TOGGLE-EXPORT-AS-DETAIL-ADDRESS-EDIT-HOTFIX-20260618-001] 회수/출고 주소
  // [AS-001-AS-PICKUP-RECIPIENT-CONTACT] 회수지 수취인·연락처.
  pickup_name?: string;
  pickup_phone?: string;
  pickup_zipcode?: string;
  pickup_address1?: string;
  pickup_address2?: string;
  return_name?: string;
  return_phone?: string;
  return_zipcode?: string;
  return_address1?: string;
  return_address2?: string;
}): Promise<AsRequest> {
  return request<AsRequest>("/as/requests", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateAsRequest(
  id: number,
  body: Partial<{
    customer_name: string;
    customer_phone: string;
    order_no: string;
    sku: string;
    product_name: string;
    symptom: string;
    status: string;
    assignee: string;
    memo: string;
    pickup_type: string;
    pickup_tracking_no: string;
    symptom_match: boolean;
    mismatch_comment: string;
    repair_comment: string;
    out_tracking_no: string;
    // [ERP-TOGGLE-EXPORT-AS-DETAIL-ADDRESS-EDIT-HOTFIX-20260618-001] 회수/출고 주소
    // [AS-001-AS-PICKUP-RECIPIENT-CONTACT] 회수지 수취인·연락처.
    pickup_name: string;
    pickup_phone: string;
    pickup_zipcode: string;
    pickup_address1: string;
    pickup_address2: string;
    return_name: string;
    return_phone: string;
    return_zipcode: string;
    return_address1: string;
    return_address2: string;
  }>
): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// [ERP-AS-054C-3A-INBOUND-RECOVERY-METHOD-SELECTION-DEV-20260814]
// 수리입고(inbound_repair) 회수방식 선택 → pickup_type + recovery receipt 서버 생성.
//   method: courier(택배 회수) | customer_ships(고객 직접 발송) | visit(방문 맡김)
export function setAsRecoveryMethod(id: number, method: "courier" | "customer_ships" | "visit"): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${id}/recovery-method`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ method }),
  });
}

export interface CustomerOrderItem {
  product_name: string;
  sku: string | null;
  quantity: number | null;
}

export interface CustomerOrder {
  order_no: string;
  order_date: string | null;
  platform: string | null;
  paid_amount: number;
  items: CustomerOrderItem[];
}

export function getAsCustomerOrders(params: {
  customer_id?: number;
  phone?: string;
}): Promise<{ orders: CustomerOrder[] }> {
  const qs = new URLSearchParams();
  if (params.customer_id) qs.set("customer_id", String(params.customer_id));
  if (params.phone) qs.set("phone", params.phone);
  return request<{ orders: CustomerOrder[] }>(`/as/customer-orders?${qs}`);
}

// [ERP-AS-054C-1-AS-INTAKE-CUSTOMER-SEARCH-PURCHASE-SKU-DEV-20260814]
// AS 접수 1단계 — 고객 구매 SKU(개별 order_item) 목록. 선택 SoT = order_item_id.
export interface CustomerPurchaseItem {
  order_item_id: number;
  order_id: number;
  order_no: string;
  order_date: string | null;
  product_name: string | null;
  option_name: string | null;
  sku: string | null;
  quantity: number | null;
  entitlement_available: number;
  entitlement_group_name: string | null;
  effective_owner_customer_id: number | null;
  as_history_count: number;
  // [AS-002] 승계받은 상품(다른 고객 원주문을 whole-lot 승계·현재 이 고객 소유). 원 주문/집계 무변경.
  is_transferred_in?: boolean;
  original_owner_customer_id?: number | null;
  // [AS-002 Phase 2b-2D 보완] 실물 선택용(additive).
  //   quantity = 원주문 전체수량 / owned_unit_count = 이 고객이 **현재 소유한 실물 수**.
  //   2개 이상이면 AS 접수 시 unit_seq 가 필수다(서버가 422 unit_seq_required 로 강제).
  owned_units?: OwnedProductUnit[] | null;
  owned_unit_count?: number | null;
}
export interface OwnedProductUnit {
  unit_seq: number;
  product_unit_id: number | null;
  current_owner_customer_id: number | null;
  materialized: boolean;
}
export interface CustomerPurchasesResponse {
  customer_id: number;
  member_customer_ids: number[];
  is_grouped: boolean;
  items: CustomerPurchaseItem[];
}
export function getAsCustomerPurchases(customerId: number): Promise<CustomerPurchasesResponse> {
  return request<CustomerPurchasesResponse>(`/as/customer-purchases?customer_id=${customerId}`);
}

// [ERP-AS-001-CUSTOMER-CONTEXT-COMPLETE-DEV] AS 접수 고객 상황 파악 통합 조회(READ-ONLY).
export interface ContextCustomer {
  customer_id: number;
  customer_name: string | null;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  total_order_count: number;
  is_base: boolean;
}
export interface ContextOrderItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  selectable: boolean;
  entitlement_available: number;          // 고객 공용 그룹 잔액(상품별 분할 아님)
  entitlement_group_name: string | null;
  as_history_count: number;
  as_status_summary: { status: string; count: number }[];  // 이 order_item AS 상태요약
}
export interface ContextProductSummary {
  sku: string;
  product_name: string | null;
  option_name: string | null;
  total_quantity: number;                 // 이 SKU 총 구매수량
  order_count: number;                    // 이 SKU 구매 주문 수
  entitlement_issued: number;             // 이 구매로 발급된 AS권 수(source_order_item_id)
  entitlement_used: number;               // 이 상품 AS에서 사용된 AS권 수(as_request→order_item)
  entitlement_available: number;          // 고객 공용 그룹 잔액(상품별 아님)
  entitlement_group_id: number | null;
  entitlement_group_name: string | null;
  as_intake_count: number;                // 이 상품으로 접수된 AS 건수
  as_status_summary: { status: string; count: number }[];
}
export interface ContextOrder {
  order_id: number;
  order_no: string;
  order_date: string | null;
  platform: string | null;
  platform_store_name: string | null;
  order_type: string | null;
  order_source: string | null;
  customer_id: number | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  address1: string | null;
  address2: string | null;
  paid_amount: number | null;
  items: ContextOrderItem[];
}
export interface ContextAsHistory {
  as_request_id: number;
  as_no: string;
  received_date: string | null;
  status: string | null;
  as_mode: string | null;
  product_name: string | null;
  sku: string | null;
  symptom: string | null;
  repair_comment: string | null;
  out_tracking_no: string | null;
  order_item_id: number | null;
  customer_id: number | null;
  parts: { part_name: string | null; part_sku: string | null }[];
  entitlement_used_count: number;   // 이 AS 가 사용한 AS권 횟수(consume·as_request_id 명시연결)
}
export interface CustomerContextResponse {
  customer_id: number;
  identity_group_id: number | null;
  is_grouped: boolean;
  member_customer_ids: number[];
  customers: ContextCustomer[];
  entitlement_summary: IdentityEntitlementSummary[];
  product_summary: ContextProductSummary[];
  orders: ContextOrder[];
  as_history: ContextAsHistory[];
  order_total: number;
  order_item_total: number;
  as_total: number;
}
export function getAsCustomerContext(customerId: number): Promise<CustomerContextResponse> {
  return request<CustomerContextResponse>(`/as/customer-context?customer_id=${customerId}`);
}

export function getAsStatusCounts(): Promise<Record<string, number>> {
  return request<Record<string, number>>("/as/status-counts");
}

export function addAsPart(body: {
  as_request_id: number;
  part_name: string;
  part_sku?: string;
  quantity?: number;
  unit_cost?: number;
  customer_price?: number;
  labor_fee?: number;
  memo?: string;
}): Promise<AsPartsUsedItem> {
  return request<AsPartsUsedItem>("/as/parts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteAsPart(partId: number): Promise<void> {
  return request<void>(`/as/parts/${partId}`, { method: "DELETE" });
}

// [AS-001-AS-PART-MULTISELECT-BULK-ADD] 파츠 다중선택 일괄추가.
// item 은 SKU 기준(상품명·원가·옵션·part_type 은 서버가 Product 로 재검증/보충). part_type=repair_part 만 허용.
export interface AsPartBulkItem {
  part_sku: string;
  quantity: number;
  customer_price?: number | null;
  labor_fee?: number | null;
  memo?: string | null;
}
export interface AsPartBulkSkipped { sku: string; reason: string }
export interface AsPartBulkResult {
  created_sku_count: number;
  created_row_count: number;
  created: AsPartsUsedItem[];
  skipped: AsPartBulkSkipped[];
}
export function bulkAddAsParts(asRequestId: number, items: AsPartBulkItem[]): Promise<AsPartBulkResult> {
  return request<AsPartBulkResult>("/as/parts/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ as_request_id: asRequestId, items }),
  });
}

// ── [ERP-AS-ADDITIONAL-SALES-ITEMS-WITH-REPAIR-SHIPMENT-DEV-20260622-001] AS 추가구매 상품 ──
export function addAsAdditionalItem(
  asId: number,
  body: {
    product_id?: number | null;
    sku?: string | null;
    product_name: string;
    option1?: string | null;
    option2?: string | null;
    barcode?: string | null;
    quantity?: number;
    unit_price?: number;
    memo?: string | null;
  },
): Promise<AsAdditionalItem> {
  return request<AsAdditionalItem>(`/as/requests/${asId}/additional-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateAsAdditionalItem(
  asId: number,
  itemId: number,
  body: { quantity?: number; unit_price?: number; memo?: string | null },
): Promise<AsAdditionalItem> {
  return request<AsAdditionalItem>(`/as/requests/${asId}/additional-items/${itemId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteAsAdditionalItem(asId: number, itemId: number): Promise<void> {
  return request<void>(`/as/requests/${asId}/additional-items/${itemId}`, { method: "DELETE" });
}

// ── [ERP-AS-WARRANTY-FREE-PARTS-ACTUAL-LOGIC-20260618-001] 파츠비 무상 적용 ──
export interface WarrantyEligibilityResponse {
  status: string;   // available / already_used / already_applied / no_purchase / not_policy_target / inactive_policy / invalid_sku
  eligible: boolean;
  reason: string | null;
  as_part_id: number;
  policy: {
    id: number;
    code: string;
    name: string;
    policy_type: string;
    max_free_as_count: number;
    coverage_scope: string;
  } | null;
  matched_purchase: {
    order_id: number | null;
    order_no: string | null;
    target_sku: string | null;
    policy_sku_qty: number | null;
  } | null;
  usage: {
    used: number;
    remaining: number;
    allowed: number;
    max_per_unit: number;
  } | null;
  pricing: {
    part_sku: string | null;
    original_part_price: number;
    free_part_price: number;
    waived_amount: number;
    labor_fee_policy: string;
  } | null;
}

export function getWarrantyEligibility(
  asId: number,
  asPartId: number,
): Promise<WarrantyEligibilityResponse> {
  return request<WarrantyEligibilityResponse>(
    `/as/${asId}/warranty-eligibility?as_part_id=${asPartId}`,
  );
}

export function applyWarrantyFree(
  asId: number,
  body: { as_part_id: number; policy_id: number; confirm?: boolean },
): Promise<AsRequest> {
  return request<AsRequest>(`/as/${asId}/warranty-apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: true, ...body }),
  });
}

export function voidWarrantyFree(
  asId: number,
  body: { usage_id?: number; as_part_id?: number },
): Promise<AsRequest> {
  return request<AsRequest>(`/as/${asId}/warranty-void`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function upsertAsCost(body: {
  as_request_id: number;
  parts_cost?: number;
  labor_cost?: number;
  shipping_cost?: number;
  extra_cost?: number;
  customer_charge?: number;
  memo?: string;
}): Promise<AsCostItem> {
  return request<AsCostItem>("/as/costs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 중국 구매/입고 관리 ──────────────────────────────────────────────────────

export const PO_STATUS_LABELS: Record<string, string> = {
  requested: "발주진행중",
  ordered: "발주완료",
  received_in_china: "중국창고입고",
  ready_for_forwarding: "포워딩준비",
  shipped_to_korea: "한국행발송",
  arrived_in_korea: "국내도착",
  partial_received: "부분입고",
  stocked_in: "입고완료",
  cancelled: "취소",
};

export const PO_STATUS_COLORS: Record<string, string> = {
  requested: "bg-gray-100 text-gray-700",
  ordered: "bg-blue-100 text-blue-800",
  received_in_china: "bg-yellow-100 text-yellow-800",
  ready_for_forwarding: "bg-orange-100 text-orange-800",
  shipped_to_korea: "bg-purple-100 text-purple-800",
  arrived_in_korea: "bg-indigo-100 text-indigo-800",
  partial_received: "bg-amber-100 text-amber-800",
  stocked_in: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

// 19차 — 중국 수입 PO 운영 4단계 (DB status는 9개 그대로 유지, UI 매핑만)
export type ChinaOperationalStage =
  | "order"             // 발주 (requested/ordered/received_in_china/ready_for_forwarding)
  | "shipped"           // 한국행발송 (shipped_to_korea)
  | "inbound_progress"  // 샵입고진행중 (arrived_in_korea)
  | "inbound_done";     // 샵입고완료 (stocked_in)

export const CHINA_STAGE_ORDER: ChinaOperationalStage[] = [
  "order",
  "shipped",
  "inbound_progress",
  "inbound_done",
];

export const CHINA_STAGE_LABELS: Record<ChinaOperationalStage, string> = {
  order: "발주",
  shipped: "한국행발송",
  inbound_progress: "샵입고진행중",
  inbound_done: "샵입고완료",
};

export const CHINA_STAGE_COLORS: Record<ChinaOperationalStage, string> = {
  order: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  inbound_progress: "bg-indigo-100 text-indigo-800",
  inbound_done: "bg-green-100 text-green-800",
};

// DB 에 들어간 9개 status 중 어느 것이 어느 운영 단계에 해당하는지
export const CHINA_STAGE_STATUS_MAP: Record<ChinaOperationalStage, string[]> = {
  order: ["requested", "ordered", "received_in_china", "ready_for_forwarding"],
  shipped: ["shipped_to_korea"],
  inbound_progress: ["arrived_in_korea", "partial_received"],
  inbound_done: ["stocked_in"],
};

export function statusToChinaStage(status: string): ChinaOperationalStage | null {
  for (const stage of CHINA_STAGE_ORDER) {
    if (CHINA_STAGE_STATUS_MAP[stage].includes(status)) return stage;
  }
  return null;
}

export const SHIPMENT_STATUS_LABELS: Record<string, string> = {
  pending: "대기",
  forwarded: "포워딩완료",
  in_transit: "운송중",
  arrived: "도착",
  completed: "완료",
};

/** [PURCHASE-001][ERP-PURCHASE-SKU-OPERATION-DEV-20260818]
 *  이 SKU 가 구성품으로 포함된 세트(번들). backend used_in_bundles_by_sku
 *  (= GET /products/{sku}/used-in-bundles 와 동일 SoT) 의 bulk 응답. */
export interface PurchaseOrderItemBundleRef {
  bundle_sku: string;
  bundle_name: string | null;
  quantity: number;
}

// [PURCHASE-002-PHASE1] 원가 팩터 등급/상태 (D14 / AC-20). backend 판정값을 그대로 쓴다.
//   excellent 200 미만 / good 200~250 / normal 250 초과~270
//   inefficient 270 초과~300 / warning 300 초과
export type PoCostFactorGrade =
  | "excellent" | "good" | "normal" | "inefficient" | "warning";
//   ok               정상 산출
//   not_computable   총상품금액(위안) 0 → 계산 불가
//   estimated        비용 일부만 확정 → 예상 팩터
//   costs_incomplete 비용 입력 미완료
export type PoCostFactorState =
  | "ok" | "not_computable" | "estimated" | "costs_incomplete";

export interface PurchaseOrderItem {
  id: number;
  purchase_order_id: number;
  sku: string | null;
  jan_code: string | null;
  product_name: string;
  // [PURCHASE-001][ERP-PURCHASE-SKU-OPERATION-DEV-20260818] SKU 운영 컨텍스트(표시 전용).
  listed_platforms?: string[];
  listing_urls?: Record<string, string> | null;
  used_in_bundles?: PurchaseOrderItemBundleRef[];
  product_url: string | null;
  quantity_ordered: number;
  quantity_received_china: number;
  quantity_received_korea: number;
  unit_price_cny: number;
  unit_price_krw: number;
  total_price_krw: number;
  china_tracking_no: string | null;
  memo: string | null;
  // [ERP-PO-ITEM-ORDERED-MARK-DEV-20260723-001]
  // 제조사/공급처 주문 넣음 표시(2상태). 없거나 null 이면 미표시(false) 로만 해석한다.
  is_ordered?: boolean;
  // [ERP-PO-...-111] SKU 전역 자유 메모 (Product.purchase_note 조인, 표시 전용).
  //   [PURCHASE-002-PHASE3][D2] 화면 라벨은 `기존 SKU 매입 메모` 다 — 아래 `provenance`(발주 참고 비고)와
  //   **다른 값**이며 서로 대체·이관하지 않는다. 이 Project 는 이 값을 건드리지 않는다.
  purchase_note?: string | null;
  // [PURCHASE-002-PHASE3][D1] 발주 참고 비고 = 이 품목이 왜 이 PO 에 있는지(시스템 파생 출처).
  //   이력이 없으면 backend 가 `legacy`("기존 데이터 · 출처 기록 없음")로 내려준다 — 프론트가 추정하지 않는다.
  provenance?: PoItemProvenanceSummary | null;
  // [PURCHASE-002-PHASE1] 화면 표시용 바코드 상품명/옵션 (D11 / AC-15). Product 조인, 표시 전용.
  // backend 가 공백·"null"·"undefined" 를 걸러 null 로 정규화해 내려준다.
  // **이지타오 Export 의 영문명·중국어 옵션·통관 필드 계약과 무관**하다.
  barcode_label_name?: string | null;
  barcode_option_name?: string | null;
  // 부분입고 집계 (shimano/daiwa)
  quantity_partial_received: number;
  quantity_remaining: number;
  receipt_status: "pending" | "partial" | "complete";
  // 26차(2026-04-24) — 제조사 공유 양식 copy 용 (Product 조인 값)
  brand: string | null;
  name_cn: string | null;
  option1: string | null;
  option2: string | null;
  option1_cn: string | null;
  option2_cn: string | null;
  // 26차 후속 #3(2026-04-24) — 제조사 공유 양식 "价格" SoT (Product.cost_price_cny, 위안화).
  cost_price_cny: number | null;
  // [Phase1 2026-06-15] 발주 품목 썸네일 표시용 (Product.image_url 조인, 표시 전용)
  image_url?: string | null;
  // [ERP-PO-BARCODE-PRINT-JOBS-PHASE-B-...-20260616-001] SKU 기본 바코드 스타일(Product 조인).
  // Phase B 바코드 모달 자동선택용. 표시 전용.
  barcode_label_size?: BarcodeLabelSize | null;  // small | large | null
  barcode_code_type?: BarcodeCodeType | null;    // qr | code128 | null
  // [ERP-PO-ITEM-COST-SNAPSHOT-CURRENT-STOCK-LOCATION-UPDATE-DEV-20260623-001]
  // 발주 snapshot 단가 vs 상품DB 최신 원가 비교 + 현재고/위치 (모두 표시 전용).
  current_cost_price_cny?: number | null;
  current_cost_price_krw?: number | null;
  current_purchase_url?: string | null;
  cost_diff_exists?: boolean;
  cost_diff_reason?: string | null;
  cost_update_allowed?: boolean;
  current_stock_total?: number;
  current_stock_locations?: { location_code: string; label: string; quantity: number }[];
  safety_stock?: number;
  stock_status?: "ok" | "out_of_stock" | "below_order" | "below_safety" | "unknown";
  stock_status_label?: string | null;
  // [PURCHASE-002-PHASE4][D4] soft delete 상태 + 삭제 차단 사유.
  //   `delete_block_reasons` 는 **backend 실행 가드와 같은 판정**이다 — 화면이 따로 추측하지 않는다.
  //   비어 있으면 실제로 삭제되고, 값이 있으면 그 문구를 그대로 사용자에게 보여준다.
  is_deleted?: boolean;
  deletable?: boolean;
  delete_block_reasons?: string[];
  // [FW-042] 미주문 — 행은 남고 원래 발주수량·단가는 기록으로만 보인다. PO 합계에서는 backend 가 이미 뺐다.
  is_not_ordered?: boolean;
  not_ordered_at?: string | null;
  not_ordered_reason?: string | null;
  // [PURCHASE-002-PHASE4][D7] 실제 입고수량 기반 **파생** 표시 단위원가(저장 단가는 불변).
  //   발주수량과 실제 입고수량이 같으면 backend 가 null 을 내려준다 → 기존 단가를 그대로 쓴다.
  inbound_quantity_actual?: number | null;
  derived_unit_price_cny?: number | null;
  derived_unit_price_krw?: number | null;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrder {
  id: number;
  po_no: string;
  vendor_type: string;       // china_import | shimano | daiwa | consumables
  supplier_name: string;
  supplier_type: string;
  status: string;
  // [PURCHASE-004 Phase 3] 공급사 Excel 자동 입고 PO provenance(수기 PO 는 null).
  origin?: string | null;
  supplier_batch_key?: string | null;
  arrived_at?: string | null;
  receiving_flow?: string | null;
  receiving_flow_label?: string | null;
  import_method: string | null;       // ecommerce | sea | lcl
  forwarder: string | null;           // 배대지명 (이지타오 등)
  forwarder_order_no: string | null;  // 배대지 주문번호
  order_date: string | null;
  expected_arrival_date: string | null;
  ezytao_order_no: string | null;
  ezytao_tracking_no: string | null;
  china_warehouse_address: string | null;
  domestic_courier: string | null;
  domestic_tracking_no: string | null;
  domestic_shipping_type: string | null;
  box_count: number;
  has_invoice: boolean;
  has_packing_list: boolean;
  has_cost_estimate: boolean;
  cost_shipping: number;
  cost_handling: number;
  cost_extra_options: number;
  cost_lcl_customs: number;
  cost_lcl_origin_cert: number;
  cost_bl: number;
  cost_origin_label: number;
  cost_new_box: number;
  cost_misc: number;
  cost_customs_duty: number;
  cost_vat: number;
  cost_domestic_freight: number;
  cost_domestic_logistics: number;
  forwarder_costs_confirmed: boolean;
  customs_costs_confirmed: boolean;
  // 26차 후속 #16(2026-04-24) — china_import PO 공통 CNY→KRW 환율.
  // [PURCHASE-002-PHASE1] 신규 PO 는 **210** 으로 저장된다(D12/AC-19).
  // 기존 PO 저장값(190·210·220·225·230)은 불변이며 backfill 하지 않는다.
  exchange_rate_cny_krw: number | null;
  total_item_count: number;
  total_quantity: number;
  total_price_krw: number;
  created_by: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  items: PurchaseOrderItem[];
  // [PURCHASE-002-PHASE1] 비용 요약 6항목 + 원가 팩터 (D13·D14 / AC-18·AC-20).
  // **backend 가 저장 금액에서 파생**한다. frontend 는 표시만 하고 같은 산식을 다시 만들지 않는다.
  //   cost_forwarder_total / cost_customs_total — 확정 플래그가 꺼져 있으면 0(기존 grand total 계약).
  //   total_amount_krw  = total_price_krw (기존 품목 원화합계 SoT 그대로)
  //   grand_total_krw   = 상품(원) + 배대지 + 통관/국내화물
  //   cost_factor       = grand_total_krw ÷ total_amount_cny (반올림 전 원값)
  cost_forwarder_total?: number;
  cost_customs_total?: number;
  total_amount_cny?: number;
  total_amount_krw?: number;
  grand_total_krw?: number;
  cost_factor?: number | null;
  cost_factor_grade?: PoCostFactorGrade | null;
  cost_factor_state?: PoCostFactorState;
  // [FW-042] 미주문 품목 — 위 총상품금액·총수량·원가 팩터에서 **이미 빠져 있다**.
  //   declared_amount_* = 미주문 품목까지 포함한 신고 기준 상품금액(대조 표시 전용 · 미주문 없으면 null).
  not_ordered_item_count?: number;
  declared_amount_cny?: number | null;
  declared_amount_krw?: number | null;
  // [PURCHASE-002-PHASE5][D9] 미등록 품목. 위 총상품금액·총결제금액·원가 팩터에는
  //   **이미 포함**돼 있고 아래는 그중 미등록 몫 내역이다(중복 합산 아님).
  unregistered_items?: UnregisteredItem[];
  unregistered_total_cny?: number;
  unregistered_total_krw?: number;
  unregistered_count?: number;
  // 26차 후속 #8(2026-04-24) — 샵입고완료 관리자 승인 이력
  stocked_in_approved_at?: string | null;
  stocked_in_approved_by_user_id?: number | null;
  stocked_in_approved_by_user_name?: string | null;
}

export interface PurchaseOrderListItem {
  id: number;
  po_no: string;
  vendor_type: string;
  supplier_name: string;
  supplier_type: string;
  status: string;
  // [PURCHASE-004 Phase 3] 자동 입고 PO 배지
  origin?: string | null;
  arrived_at?: string | null;
  receiving_flow?: string | null;
  receiving_flow_label?: string | null;
  order_date: string | null;
  expected_arrival_date: string | null;
  total_item_count: number;
  total_quantity: number;
  total_price_krw: number;
  total_received: number;   // shimano/daiwa 누적 입고 수량
  total_remaining: number;  // shimano/daiwa 미입고 수량
  created_by: string | null;
  updated_at: string;
}

// 부분입고 (shimano/daiwa)
export interface PartialInboundItem {
  id: number;
  partial_inbound_id: number;
  po_item_id: number;
  quantity_received: number;
  created_at: string;
}

export interface PartialInbound {
  id: number;
  inbound_no: string;
  purchase_order_id: number;
  inbound_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items: PartialInboundItem[];
  // 26차 후속 #21 — B2B 엑셀 업로드로 생성된 배치 메타 (수기 등록은 null)
  source_file_name?: string | null;
  source_uploaded_at?: string | null;
}

// 26차 후속 #21 — 시마노/다이와 B2B 엑셀 업로드 preview 응답
export interface B2BInboundPreviewMatched {
  raw_identifier: string;
  match_key: "jan_code" | "sku";
  po_item_id: number;
  sku: string | null;
  jan_code: string | null;
  product_name: string | null;
  quantity_in_file: number;
  quantity_ordered: number;
  quantity_already_received: number;
  quantity_remaining: number;
  over_receipt: boolean;
}

export interface B2BInboundPreviewUnmatched {
  raw_identifier: string;
  quantity_in_file: number;
  reason: string;
}

export interface B2BInboundPreviewAmbiguous {
  raw_identifier: string;
  quantity_in_file: number;
  candidate_po_item_ids: number[];
}

export interface B2BInboundPreviewResponse {
  po_id: number;
  po_no: string;
  vendor_type: string;
  file_name: string;
  total_rows: number;
  matched: B2BInboundPreviewMatched[];
  unmatched: B2BInboundPreviewUnmatched[];
  ambiguous: B2BInboundPreviewAmbiguous[];
  warnings: string[];
}

// 안전재고 추천
export interface SafetyStockSuggestion {
  sku: string;
  product_name: string;
  current_qty: number;
  safety_stock_qty: number;
  sold_3months: number;
  suggested_order_qty: number;
  // 26차 후속 #14(2026-04-24) — 중국 수입 PO 문맥에서 브랜드 기반 필터에 사용.
  brand?: string | null;
}

export interface PurchaseOrderListResponse {
  total: number;
  items: PurchaseOrderListItem[];
}

export interface InboundShipment {
  id: number;
  shipment_no: string;
  po_ids: number[];
  status: string;
  ezytao_order_no: string | null;
  ezytao_box_count: number | null;
  ezytao_total_weight_kg: number | null;
  ezytao_shipping_method: string;
  ezytao_departure_date: string | null;
  ezytao_arrival_china: string | null;
  ezytao_forwarding_date: string | null;
  ezytao_tracking_no: string | null;
  domestic_courier: string | null;
  domestic_tracking_no: string | null;
  arrival_date_korea: string | null;
  stocked_date: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface InboundShipmentListItem {
  id: number;
  shipment_no: string;
  po_ids: number[];
  status: string;
  ezytao_shipping_method: string;
  ezytao_forwarding_date: string | null;
  ezytao_tracking_no: string | null;
  domestic_tracking_no: string | null;
  arrival_date_korea: string | null;
  updated_at: string;
}

export interface InboundShipmentListResponse {
  total: number;
  items: InboundShipmentListItem[];
}

export function getPurchaseOrders(params?: {
  vendor_type?: string;
  status?: string;
  q?: string;
  // [PURCHASE-004 가시성 보완] "supplier_excel" = 공급사 Excel 자동 입고 PO 만(조회 필터)
  origin?: "supplier_excel";
  // [가시성 UX 2차] origin 전용 — 단계 기준일 범위(YYYY-MM-DD) · q 는 하위 품목(SKU·JAN·상품명)까지 검색
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}): Promise<PurchaseOrderListResponse> {
  const qs = new URLSearchParams();
  if (params?.vendor_type) qs.set("vendor_type", params.vendor_type);
  if (params?.status) qs.set("status", params.status);
  if (params?.q) qs.set("q", params.q);
  if (params?.origin) qs.set("origin", params.origin);
  if (params?.date_from) qs.set("date_from", params.date_from);
  if (params?.date_to) qs.set("date_to", params.date_to);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<PurchaseOrderListResponse>(`/purchase/orders?${qs}`);
}

export function getPurchaseOrder(id: number): Promise<PurchaseOrder> {
  return request<PurchaseOrder>(`/purchase/orders/${id}`);
}

export function createPurchaseOrder(body: {
  vendor_type?: string;
  supplier_name?: string;
  supplier_type?: string;
  import_method?: string;
  forwarder?: string;
  forwarder_order_no?: string;
  order_date?: string;
  expected_arrival_date?: string;
  ezytao_order_no?: string;
  china_warehouse_address?: string;
  created_by?: string;
  memo?: string;
  // 26차 후속 #16(2026-04-24) — china_import 전용 PO 공통 환율(미전달 시 BE 기본 190).
  exchange_rate_cny_krw?: number;
  items?: Array<{
    product_name: string;
    sku?: string;
    jan_code?: string;
    product_url?: string;
    quantity_ordered?: number;
    unit_price_cny?: number;
    unit_price_krw?: number;
  }>;
}): Promise<PurchaseOrder> {
  return request<PurchaseOrder>("/purchase/orders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updatePurchaseOrder(
  id: number,
  body: Partial<{
    vendor_type: string;
    supplier_name: string;
    status: string;
    import_method: string;
    forwarder: string;
    forwarder_order_no: string;
    order_date: string;
    expected_arrival_date: string;
    ezytao_order_no: string;
    ezytao_tracking_no: string;
    china_warehouse_address: string;
    domestic_courier: string;
    domestic_tracking_no: string;
    domestic_shipping_type: string;
    box_count: number;
    has_invoice: boolean;
    has_packing_list: boolean;
    has_cost_estimate: boolean;
    cost_shipping: number;
    cost_handling: number;
    cost_extra_options: number;
    cost_lcl_customs: number;
    cost_lcl_origin_cert: number;
    cost_bl: number;
    cost_domestic_logistics: number;
    cost_misc: number;
    created_by: string;
    memo: string;
    // 26차 후속 #16(2026-04-24) — 환율 변경 시 품목 원화단가 cascade 재계산 트리거(BE).
    exchange_rate_cny_krw: number;
  }>
): Promise<PurchaseOrder> {
  return request<PurchaseOrder>(`/purchase/orders/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPurchaseStatusCounts(vendorType?: string): Promise<Record<string, number>> {
  const qs = new URLSearchParams();
  if (vendorType) qs.set("vendor_type", vendorType);
  return request<Record<string, number>>(`/purchase/status-counts?${qs}`);
}

export function addPurchaseOrderItemWithTracking(poId: number, body: {
  product_name: string;
  sku?: string;
  jan_code?: string;
  product_url?: string;
  quantity_ordered?: number;
  unit_price_cny?: number;
  unit_price_krw?: number;
  china_tracking_no?: string;
  memo?: string;
}): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(`/purchase/order-items?po_id=${poId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// 부분입고 API (shimano/daiwa)
export function createPartialInbound(body: {
  purchase_order_id: number;
  inbound_date?: string;
  notes?: string;
  created_by?: string;
  items: Array<{ po_item_id: number; quantity_received: number }>;
}): Promise<PartialInbound> {
  return request<PartialInbound>("/purchase/partial-inbounds", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPartialInbounds(poId: number): Promise<PartialInbound[]> {
  return request<PartialInbound[]>(`/purchase/partial-inbounds?po_id=${poId}`);
}

export function deletePartialInbound(id: number): Promise<void> {
  return request<void>(`/purchase/partial-inbounds/${id}`, { method: "DELETE" });
}

// 26차 후속 #21 — 시마노/다이와 B2B 엑셀 업로드 preview
export function previewPartialInboundFromB2bExcel(
  poId: number,
  file: File,
): Promise<B2BInboundPreviewResponse> {
  const fd = new FormData();
  fd.append("file", file);
  return request<B2BInboundPreviewResponse>(
    `/purchase/orders/${poId}/partial-inbounds/from-b2b-excel/preview`,
    { method: "POST", body: fd },
  );
}

// 26차 후속 #21 — 시마노/다이와 B2B 엑셀 업로드 → 실입고 배치 생성
export function createPartialInboundFromB2bExcel(
  poId: number,
  file: File,
  opts: { notes?: string; inbound_date?: string; created_by?: string } = {},
): Promise<PartialInbound> {
  const fd = new FormData();
  fd.append("file", file);
  if (opts.notes !== undefined) fd.append("notes", opts.notes);
  if (opts.inbound_date !== undefined) fd.append("inbound_date", opts.inbound_date);
  if (opts.created_by !== undefined) fd.append("created_by", opts.created_by);
  return request<PartialInbound>(
    `/purchase/orders/${poId}/partial-inbounds/from-b2b-excel`,
    { method: "POST", body: fd },
  );
}

// 안전재고 추천
export function getSafetyStockSuggestions(opts?: {
  excludeBrands?: string[];
  excludeSkus?: string[];
}): Promise<SafetyStockSuggestion[]> {
  const qs = new URLSearchParams();
  if (opts?.excludeBrands && opts.excludeBrands.length > 0) {
    qs.set("exclude_brands", opts.excludeBrands.join(","));
  }
  if (opts?.excludeSkus && opts.excludeSkus.length > 0) {
    qs.set("exclude_skus", opts.excludeSkus.join(","));
  }
  const query = qs.toString();
  return request<SafetyStockSuggestion[]>(
    query ? `/purchase/safety-stock-suggestions?${query}` : "/purchase/safety-stock-suggestions",
  );
}

// 발주 서류 파일 관리
export interface PoDocument {
  filename: string;
  size: number;
  modified: number;
}

export function listPoDocuments(poId: number): Promise<{ po_id: number; files: PoDocument[] }> {
  return request(`/purchase/orders/${poId}/documents`);
}

export async function uploadPoDocument(poId: number, file: File): Promise<{ ok: boolean; filename: string; size: number }> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_URL}/purchase/orders/${poId}/documents`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "업로드 실패" }));
    throw new Error(err.detail || "업로드 실패");
  }
  return res.json();
}

export function deletePoDocument(poId: number, filename: string): Promise<void> {
  return request(`/purchase/orders/${poId}/documents/${encodeURIComponent(filename)}`, { method: "DELETE" });
}

export function getPoDocumentUrl(poId: number, filename: string): string {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : "";
  return `${API_URL}/purchase/orders/${poId}/documents/${encodeURIComponent(filename)}`;
}

export function addPurchaseOrderItem(poId: number, body: {
  product_name: string;
  sku?: string;
  jan_code?: string;
  product_url?: string;
  quantity_ordered?: number;
  unit_price_cny?: number;
  unit_price_krw?: number;
  china_tracking_no?: string;
  memo?: string;
}): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(`/purchase/order-items?po_id=${poId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// [PURCHASE-001-FEATURE4-MISSING-ITEM-ADD-DEV] 중국발주 샵입고진행중 누락 품목 추가(관리자 전용, 별도 정정 경로).
export interface MissingInboundItemResult {
  ok: boolean;
  po_id: number;
  po_no: string;
  purchase_order_item_id: number;
  po_inbound_item_id: number;
  sku: string;
  quantity_ordered: number;
  quantity_actual: number;
  unit_price_cny: number | null;
  unit_price_krw: number;
  incoming_before: number | null;
  incoming_after: number | null;
}
export function addMissingInboundItem(poId: number, body: {
  sku: string;
  product_name?: string | null;
  quantity_ordered: number;
  quantity_actual: number;
  unit_price_cny?: number | null;
  reason: string;
}): Promise<MissingInboundItemResult> {
  return request<MissingInboundItemResult>(`/purchase/orders/${poId}/missing-item`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// [PURCHASE-001-FEATURE4C-REUSE-ITEM-PICKER-DEV] 복수 SKU 누락 품목 atomic batch(전건 성공/전건 rollback).
export interface MissingInboundItemLineInput {
  sku: string;
  product_name?: string | null;
  quantity_ordered: number;
  quantity_actual: number;
  unit_price_cny?: number | null;
}
export interface MissingInboundItemsBatchResult {
  ok: boolean;
  po_id: number;
  po_no: string;
  total_created: number;
  created: MissingInboundItemResult[];
}
export function addMissingInboundItems(poId: number, body: {
  items: MissingInboundItemLineInput[];
  reason: string;
}): Promise<MissingInboundItemsBatchResult> {
  return request<MissingInboundItemsBatchResult>(`/purchase/orders/${poId}/missing-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updatePurchaseOrderItem(
  itemId: number,
  body: Partial<{
    product_name: string;
    sku: string;
    quantity_ordered: number;
    quantity_received_china: number;
    quantity_received_korea: number;
    unit_price_cny: number;
    unit_price_krw: number;
    memo: string;
  }>
): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(`/purchase/order-items/${itemId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// [ERP-PO-ITEM-ADMIN-QUANTITY-EDIT-20260616-001]
// 관리자 전용 발주수량 정정. backend 에서 require_admin + 입고수량 가드 + audit 기록.
// quantity_ordered 만 수정. 성공 시 호출자가 PO 상세를 reload 해 합계를 갱신한다.
export interface PoItemQuantityUpdateResult {
  ok: boolean;
  item_id: number;
  purchase_order_id: number;
  sku: string | null;
  old_quantity_ordered: number;
  new_quantity_ordered: number;
}

export function updatePurchaseOrderItemQuantity(
  itemId: number,
  quantityOrdered: number,
  reason?: string,
): Promise<PoItemQuantityUpdateResult> {
  return request<PoItemQuantityUpdateResult>(`/purchase/order-items/${itemId}/quantity`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ quantity_ordered: quantityOrdered, reason: reason || null }),
  });
}

// [ERP-PO-ITEM-ORDERED-MARK-DEV-20260723-001]
// 발주 품목 "주문함" 표시 토글. backend 는 require_level("lv2") + is_ordered 단일 필드만 변경.
// 수량/단가/합계/입고수량/발주상태는 이 API 로 절대 변경되지 않는다.
export interface PoItemOrderedUpdateResult {
  item_id: number;
  is_ordered: boolean;
}

export function updatePurchaseOrderItemOrdered(
  poId: number,
  itemId: number,
  isOrdered: boolean,
): Promise<PoItemOrderedUpdateResult> {
  return request<PoItemOrderedUpdateResult>(
    `/purchase/orders/${poId}/items/${itemId}/ordered`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_ordered: isOrdered }),
    },
  );
}

// [PURCHASE-002-PHASE4][D4] 발주 품목 **soft delete**. 물리삭제가 아니며 **사유가 필수**다.
//   차단 조건(관세 처리·Export 확정·입고·연결 업무이력)은 backend 가 최종 판정해 구체적 사유를 400 으로 돌려준다.
//   복원 API 는 존재하지 않는다(이번 범위 아님).
export interface PoItemDeleteResult {
  ok: boolean;
  item_id: number;
  purchase_order_id: number;
  sku: string | null;
  delete_reason: string;
  po_total_price_krw_before: number;
  po_total_price_krw_after: number;
  inbound_rows_removed: number;
}

export function deletePurchaseOrderItem(itemId: number, reason: string): Promise<PoItemDeleteResult> {
  return request<PoItemDeleteResult>(`/purchase/order-items/${itemId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
}

// [ERP-PO-DETAIL-SELECTED-ITEMS-BULK-QTY-TRANSFER-DEV-20260625-001]
// 선택 품목 수량 일괄 변경 / 기존 PO 이동 / 새 PO 분리. backend require_admin + 입고/lock 가드 + audit.
export interface PoBulkQuantityResult {
  ok: boolean;
  purchase_order_id: number;
  updated: { item_id: number; sku: string | null; old_quantity_ordered: number; new_quantity_ordered: number }[];
  po_total_quantity: number;
  po_total_price_krw: number;
}
export function bulkUpdatePoItemQuantity(poId: number, body: { item_ids: number[]; mode: "set" | "delta" | "per_item"; quantity?: number; delta?: number; items?: { item_id: number; quantity: number }[]; reason?: string }): Promise<PoBulkQuantityResult> {
  return request<PoBulkQuantityResult>(`/purchase/orders/${poId}/items/bulk-quantity`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface PoItemsMoveResult {
  ok: boolean;
  source_po_id: number;
  target_po_id: number;
  moved_count: number;
  merged_count: number;
  relocated_count: number;
  source_total_quantity: number;
  target_total_quantity: number;
}
// [PURCHASE-002-PHASE1] 중국 내 운송장 개별·일괄 입력/삭제 (D16 / AC-14).
//
// 단건도 이 함수로 처리한다(itemIds 길이 1). **순차 호출로 부분성공을 만들지 않는다** —
// backend 가 전건 선검증 후 단일 트랜잭션으로 반영한다.
// trackingNo 를 null 또는 빈 문자열로 보내면 선택 품목의 운송장을 **삭제**한다(NULL 저장).
// 이지타오 Export U(트래킹번호) 컬럼 출력은 기존 매핑 그대로이며 여기서 재구현하지 않는다.
export interface PoItemsChinaTrackingResult {
  ok: boolean;
  po_id: number;
  updated_count: number;
  china_tracking_no: string | null;
  item_ids: number[];
}
export function updatePoItemsChinaTracking(
  poId: number,
  body: { item_ids: number[]; china_tracking_no: string | null },
): Promise<PoItemsChinaTrackingResult> {
  return request<PoItemsChinaTrackingResult>(`/purchase/orders/${poId}/items/china-tracking`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// [PURCHASE-002-PHASE3][D1] PO 이동은 **사유가 필수**다. 코드 집합은 여기에 박지 않고
//   서버(`getOrderItemProvenance().move_reason_options`)가 내려주는 값을 그대로 쓴다.
//   `other` 는 공백이 아닌 상세사유가 필수이며, 최종 판정은 backend 가 다시 한다.
export interface PoMoveReason {
  move_reason_code: string;
  move_reason_detail?: string | null;
}

export function movePoItemsToPo(
  sourcePoId: number,
  body: { item_ids: number[]; target_po_id: number } & PoMoveReason
): Promise<PoItemsMoveResult> {
  return request<PoItemsMoveResult>(`/purchase/orders/${sourcePoId}/items/move`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PoItemsSplitResult {
  ok: boolean;
  source_po_id: number;
  new_po_id: number;
  new_po_no: string;
  moved_count: number;
  source_total_quantity: number;
}
export function splitPoItemsToNewPo(
  sourcePoId: number,
  body: { item_ids: number[]; supplier_name?: string; memo?: string } & PoMoveReason
): Promise<PoItemsSplitResult> {
  return request<PoItemsSplitResult>(`/purchase/orders/${sourcePoId}/items/split-to-new-po`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// [ERP-PO-ITEM-COST-SNAPSHOT-CURRENT-STOCK-LOCATION-UPDATE-DEV-20260623-001]
// 발주 단가를 상품DB 최신 원가로 명시 갱신. backend 에서 require_admin + 상태(requested|ordered) 가드 + audit.
// 개별: 성공 시 갱신된 품목 응답 반환. 호출자가 PO 상세 reload 로 합계 갱신.
export function refreshPurchaseOrderItemCost(
  poId: number,
  itemId: number,
): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(
    `/purchase/orders/${poId}/items/${itemId}/refresh-cost`,
    { method: "POST" },
  );
}

export interface RefreshItemCostsResult {
  ok: boolean;
  po_id: number;
  updated_item_ids: number[];
  skipped: { item_id: number; sku: string | null; reason: string }[];
  total_price_krw: number;
}

// 선택(scope=selected, item_ids) 또는 전체(scope=all) 품목 일괄 갱신.
export function refreshPurchaseOrderItemCosts(
  poId: number,
  body: { scope: "selected" | "all"; item_ids?: number[] },
): Promise<RefreshItemCostsResult> {
  return request<RefreshItemCostsResult>(
    `/purchase/orders/${poId}/items/refresh-costs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export function deletePurchaseOrder(poId: number): Promise<void> {
  return request<void>(`/purchase/orders/${poId}`, { method: "DELETE" });
}

export function getInboundShipments(params?: {
  status?: string;
  limit?: number;
}): Promise<InboundShipmentListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  return request<InboundShipmentListResponse>(`/purchase/shipments?${qs}`);
}

export function createInboundShipment(body: {
  po_ids?: number[];
  ezytao_order_no?: string;
  ezytao_box_count?: number;
  ezytao_total_weight_kg?: number;
  ezytao_shipping_method?: string;
  ezytao_forwarding_date?: string;
  ezytao_tracking_no?: string;
  domestic_courier?: string;
  domestic_tracking_no?: string;
  memo?: string;
}): Promise<InboundShipment> {
  return request<InboundShipment>("/purchase/shipments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateInboundShipment(
  id: number,
  body: Partial<{
    status: string;
    po_ids: number[];
    ezytao_order_no: string;
    ezytao_box_count: number;
    ezytao_total_weight_kg: number;
    ezytao_shipping_method: string;
    ezytao_departure_date: string;
    ezytao_arrival_china: string;
    ezytao_forwarding_date: string;
    ezytao_tracking_no: string;
    domestic_courier: string;
    domestic_tracking_no: string;
    arrival_date_korea: string;
    stocked_date: string;
    memo: string;
  }>
): Promise<InboundShipment> {
  return request<InboundShipment>(`/purchase/shipments/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 이지타오 배송신청서 Export ──────────────────────────────────

export interface EzytaoExportPreviewRow {
  번호: number;
  PO번호: string;
  상품명: string;
  "상품 URL": string;
  "SKU/JAN코드": string;
  수량: number;
  "단가(CNY)": number | string;
  "소계(CNY)": number | string;
  수령인: string;
  전화번호: string;
  주소: string;
  운송방법: string;
  비고: string;
}

// [ERP-PO-EASYTAO-REQUIRED-FIELDS-PREVIEW-20260615-001]
// 필수값(영문명/통관품목번호/이미지 URL) 누락 품목 단위 행.
// missing_fields 코드: english_name | customs_hs_code | image_url | product_match
export interface EzytaoMissingRow {
  sku: string | null;
  product_name: string;
  brand: string | null;
  option1: string | null;
  option2: string | null;
  missing_fields: string[];
}

export interface EzytaoExportPreview {
  shipment_no: string;
  total_pos: number;
  total_rows: number;
  preview_rows: EzytaoExportPreviewRow[];
  missing_fields: string[];
  // [ERP-PO-EASYTAO-REQUIRED-FIELDS-PREVIEW-20260615-001] PO 단위 preview 만 채워짐.
  total_items?: number;
  missing_count?: number;
  missing_rows?: EzytaoMissingRow[];
}

export async function getEzytaoExportPreview(
  shipmentId: number
): Promise<EzytaoExportPreview> {
  return request<EzytaoExportPreview>(
    `/purchase/shipments/${shipmentId}/export-ezytao/preview`
  );
}

export async function downloadEzytaoExport(shipmentId: number): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/shipments/${shipmentId}/export-ezytao`,
    { headers }
  );
  if (!res.ok) throw new Error(`Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_${shipmentId}.xlsx`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// 19차 — PO 단독 이지타오 배송신청서 다운로드 (shipment 미생성 상태에서 발주 단계 샘플)
export async function getPoEzytaoExportPreview(
  poId: number
): Promise<EzytaoExportPreview> {
  return request<EzytaoExportPreview>(
    `/purchase/orders/${poId}/export-ezytao/preview`
  );
}

export async function downloadPoEzytaoExport(poId: number): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/orders/${poId}/export-ezytao`,
    { headers }
  );
  if (!res.ok) throw new Error(`Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_PO${poId}.xlsx`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// [ERP-PO-EASYTAO-EXPORT-30-ITEM-SPLIT-ZIP-20260616-001]
// PO 이지타오 export 를 품목 30개 단위로 분할한 xlsx 묶음 ZIP 다운로드.
// 품목이 30개 이하인 경우에도 ZIP 1개로 내려받는다.
export async function downloadPoEzytaoExportSplit(poId: number): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/orders/${poId}/export-ezytao-split`,
    { headers }
  );
  if (!res.ok) throw new Error(`분할 Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_PO${poId}_분할.zip`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// [ERP-PO-...-111] SKU 발주 참고 비고 저장 (전용 명시적 저장).
export function updateProductPurchaseNote(sku: string, purchaseNote: string | null): Promise<Product> {
  return request<Product>(`/products/${encodeURIComponent(sku)}/purchase-note`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ purchase_note: purchaseNote }),
  });
}

// [ERP-PO-...-111] 다른 PO 등록 이력 (SKU 별 count + 최신 PO). 참고용 경고.
export interface PoHistoryEntry {
  sku: string;
  history_count: number;
  latest_po_id: number | null;
  latest_po_no: string | null;
  latest_po_status: string | null;
  latest_created_at: string | null;
  latest_ordered_qty: number | null;
  latest_received_qty: number | null;
}
export function getPoItemHistory(skus: string[], excludePoId?: number): Promise<{ entries: Record<string, PoHistoryEntry> }> {
  return request(`/purchase/order-items/po-history`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ skus, exclude_po_id: excludePoId ?? null }),
  });
}

// [ERP-PO-...-111] 선택 품목만 이지타오 Export.
export async function downloadPoEzytaoExportSelected(poId: number, itemIds: number[]): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/orders/${poId}/export-ezytao/selected`,
    { method: "POST", headers, body: JSON.stringify({ item_ids: itemIds }) }
  );
  if (!res.ok) throw new Error(`선택 Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_PO${poId}_선택.xlsx`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── 감사 로그 ─────────────────────────────────────────────────

export interface AuditLogListItem {
  id: number;
  entity_type: string;
  entity_id: string | null;
  action_type: string;
  changed_by: string | null;
  changed_at: string;
  summary: string | null;
}

export interface AuditLogDetail extends AuditLogListItem {
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditLogListResponse {
  total: number;
  items: AuditLogListItem[];
}

export function getAuditLogs(params?: {
  entity_type?: string;
  action_type?: string;
  changed_by?: string;
  entity_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}): Promise<AuditLogListResponse> {
  const qs = new URLSearchParams();
  if (params?.entity_type) qs.set("entity_type", params.entity_type);
  if (params?.action_type) qs.set("action_type", params.action_type);
  if (params?.changed_by) qs.set("changed_by", params.changed_by);
  if (params?.entity_id) qs.set("entity_id", params.entity_id);
  if (params?.date_from) qs.set("date_from", params.date_from);
  if (params?.date_to) qs.set("date_to", params.date_to);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<AuditLogListResponse>(`/audit-logs?${qs}`);
}

export function getAuditLog(id: number): Promise<AuditLogDetail> {
  return request<AuditLogDetail>(`/audit-logs/${id}`);
}

// ── 승인 요청 ─────────────────────────────────────────────────

export const APPROVAL_REQUEST_TYPE_LABELS: Record<string, string> = {
  inventory_adjustment: "재고 조정",
  purchase_approval: "구매 발주 승인",
  manual_price_change: "수동 가격 변경",
  return_discard: "폐기 처리",
  as_charge_confirm: "AS 청구 확정",
  cost_factor_apply: "원가팩터 적용",
  deposit_confirm: "입금 확인",
  as_deposit_confirm: "AS 입금 확인",
  shipment_hold_release: "출고중지 해제",
  return_restock_as_new: "반품 새제품 재등록",
  // design_approval_workflow.md Tier 1 확장
  inventory_bulk_import: "재고 일괄 업로드",
  product_change: "상품 필드 변경",
  product_bulk_import: "상품 일괄 업로드",
  order_cancellation: "주문 취소",
  master_data_change: "마스터 데이터 변경",
};

export const APPROVAL_TYPE_ENTITY_GUIDE: Record<string, { entity_type: string; placeholder: string }> = {
  deposit_confirm:         { entity_type: "order",          placeholder: "주문번호 (예: 2026-XXXX)" },
  as_deposit_confirm:      { entity_type: "as_request",     placeholder: "AS ID (숫자)" },
  shipment_hold_release:   { entity_type: "return_receipt", placeholder: "출고중지 ID (숫자)" },
  return_restock_as_new:   { entity_type: "return_receipt", placeholder: "반품 접수 ID (숫자)" },
  inventory_adjustment:    { entity_type: "inventory",      placeholder: "SKU" },
  purchase_approval:       { entity_type: "purchase_order", placeholder: "발주 ID (숫자)" },
  manual_price_change:     { entity_type: "product",        placeholder: "SKU" },
  return_discard:          { entity_type: "return_receipt", placeholder: "반품 접수 ID (숫자)" },
  as_charge_confirm:       { entity_type: "as_request",     placeholder: "AS ID (숫자)" },
  cost_factor_apply:       { entity_type: "product",        placeholder: "SKU" },
  inventory_bulk_import:   { entity_type: "inventory",      placeholder: "업로드 배치 ID" },
  product_change:          { entity_type: "product",        placeholder: "SKU" },
  product_bulk_import:     { entity_type: "product",        placeholder: "업로드 배치 ID" },
  order_cancellation:      { entity_type: "order",          placeholder: "주문번호" },
  master_data_change:      { entity_type: "product",        placeholder: "대상 키" },
};

export const APPROVAL_STATUS_LABELS: Record<string, string> = {
  pending: "대기중",
  approved: "승인",
  rejected: "반려",
  cancelled: "취소",
};

export const APPROVAL_STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export interface ApprovalRequestListItem {
  id: number;
  request_type: string;
  entity_type: string;
  entity_id: string | null;
  requested_by: string | null;
  requested_at: string;
  status: string;
  approver: string | null;
  reason: string | null;
  memo: string | null;
  payload_json: Record<string, unknown> | null;
  auto_approved: boolean;
  updated_at: string;
}

export interface ApprovalRequestDetail extends ApprovalRequestListItem {
  approved_at: string | null;
  rejected_at: string | null;
  memo: string | null;
  created_at: string;
}

export interface ApprovalRequestListResponse {
  total: number;
  items: ApprovalRequestListItem[];
}

export function getApprovalRequests(params?: {
  status?: string;
  request_type?: string;
  requested_by?: string;
  entity_type?: string;
  limit?: number;
  offset?: number;
}): Promise<ApprovalRequestListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.request_type) qs.set("request_type", params.request_type);
  if (params?.requested_by) qs.set("requested_by", params.requested_by);
  if (params?.entity_type) qs.set("entity_type", params.entity_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<ApprovalRequestListResponse>(`/approvals?${qs}`);
}

export function getApprovalRequest(id: number): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>(`/approvals/${id}`);
}

export function createApprovalRequest(body: {
  request_type: string;
  entity_type: string;
  entity_id?: string;
  requested_by?: string;
  memo?: string;
  // [FW-005-COST-PHASE2] cost_factor_apply 전용 구조화 요청값 — 승인 시 이 값으로만 원가 계약 적용(memo 파싱 폐지)
  payload_json?: { sku: string; cost_factor: number; cost_price_cny?: number };
}): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>("/approvals", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function approveRequest(id: number, body: { approver?: string; reason?: string }): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>(`/approvals/${id}/approve`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function rejectRequest(id: number, body: { approver?: string; reason?: string }): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>(`/approvals/${id}/reject`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 출고 검수 (Picking) ───────────────────────────────────────────────────────

export interface HoldInfo {
  id: number;
  request_type: string;
  status: string;
  request_reason: string | null;
  requested_by: string;
  requested_at: string;
}

// [ERP-PACKING-BUNDLE-SET-COMPONENT-SCAN-CHECK-20260618-002]
export interface SetComponent {
  component_sku: string;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  required_qty: number;
  scanned_qty: number;
  is_scanned_complete: boolean;
}

export interface PickItemResponse {
  id: number;
  order_item_id: number;
  sku: string;
  product_name: string | null;
  option_name: string | null;
  required_qty: number;
  scanned_qty: number;
  status: string; // waiting | partial | completed | blocked
  // [ERP-PACKING-BUNDLE-SET-COMPONENT-SCAN-CHECK-20260618-002] 세트 구성품 스캔 진행
  is_set_item?: boolean;
  set_components?: SetComponent[];
}

export interface ScanLogResponse {
  id: number;
  scanned_value: string;
  scan_result: string;
  scanned_by: string | null;
  created_at: string;
}

export interface ShipmentPickDetail {
  id: number;
  order_no: string;
  location_code: string;
  status: string; // waiting | picking | picked | packed | shipped | blocked
  picker_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  invoice_no: string | null;
  carrier_name: string | null;
  shipped_at: string | null;
  memo: string | null;
  receiver_name: string | null;
  platform: string | null;
  is_daiwa_direct_ship: boolean;
  active_holds: HoldInfo[];
  items: PickItemResponse[];
  recent_scan_logs: ScanLogResponse[];
  created_at: string;
  updated_at: string;
}

export interface ShipmentPickListItem {
  id: number;
  order_no: string;
  location_code: string;
  status: string;
  picker_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  invoice_no: string | null;
  carrier_name: string | null;
  shipped_at: string | null;
  receiver_name: string | null;
  platform: string | null;
  item_count: number;
  scanned_count: number;
  has_active_hold: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShipmentPickListResponse {
  total: number;
  items: ShipmentPickListItem[];
}

export interface ScanResponse {
  scan_result: string; // matched | over_scan | sku_not_in_order | cancelled_order | blocked_by_hold
  message: string;
  scanned_value: string;
  matched_sku: string | null;
  scanned_qty: number | null;
  required_qty: number | null;
  item_status: string | null;
  pick_status: string;
  // 26차 후속 #82 Phase 1 — 안정적 분기용 에러 코드 (없으면 null).
  // MATCHED | SHIPMENT_HOLD_ACTIVE | PACKING_ALREADY_COMPLETED |
  // WRONG_BARCODE | OVER_SCAN | SET_PARENT_SCAN | SET_COMPONENT_MATCHED | SET_COMPONENT_OVER
  error_code?: string | null;
  // [ERP-PACKING-BUNDLE-SET-COMPONENT-SCAN-CHECK-20260618-002] 세트 구성품 스캔 시 대표 SKU
  set_bundle_sku?: string | null;
}

export function startPick(body: {
  order_no: string;
  location_code?: string;
  picker_name?: string;
}): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>("/picking/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPickList(params?: {
  status?: string;
  order_no?: string;
  limit?: number;
  offset?: number;
}): Promise<ShipmentPickListResponse> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.order_no) q.set("order_no", params.order_no);
  if (params?.limit !== undefined) q.set("limit", String(params.limit));
  if (params?.offset !== undefined) q.set("offset", String(params.offset));
  const qs = q.toString();
  return request<ShipmentPickListResponse>(`/picking${qs ? "?" + qs : ""}`);
}

export function getPickDetail(order_no: string): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>(`/picking/${encodeURIComponent(order_no)}`);
}

export function scanSku(
  order_no: string,
  body: { scanned_value: string; scanned_by?: string }
): Promise<ScanResponse> {
  return request<ScanResponse>(`/picking/${encodeURIComponent(order_no)}/scan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function completePick(
  order_no: string,
  body?: { picker_name?: string }
): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>(`/picking/${encodeURIComponent(order_no)}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

// ── 운영 대시보드 ─────────────────────────────────────────────────────────────

export interface DashboardOrderStats {
  count: number;
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  fee_amount: number;
  settlement_amount: number;
}

export interface DashboardPickingStats {
  started_today: number;
  shipped_today: number;
  hold_pending: number;
  blocked_picks: number;
}

export interface DashboardReturnStats {
  awaiting_inspection: number;
  completed_today: number;
  quarantine_total: number;
}

export interface DashboardLocationItem {
  location_code: string;
  label: string;
  sku_count: number;
  total_quantity: number;
}

export interface DashboardInventoryStats {
  total_skus: number;
  total_quantity: number;
  quarantine_quantity: number;
  rocket_fc_quantity: number;
  by_location: DashboardLocationItem[];
}

export interface DashboardAsStats {
  active_count: number;
  repairing: number;
  completed_today: number;
  customer_charge_total: number;
}

export interface DashboardPurchaseStats {
  in_progress_count: number;
  waiting_arrival_count: number;
  stocked_in_today: number;
}

export interface DashboardSummary {
  date: string;
  orders: DashboardOrderStats;
  picking: DashboardPickingStats;
  returns: DashboardReturnStats;
  inventory: DashboardInventoryStats;
  as_requests: DashboardAsStats;
  purchase: DashboardPurchaseStats;
}

export function getDashboardSummary(date?: string): Promise<DashboardSummary> {
  const q = date ? `?date=${date}` : "";
  return request<DashboardSummary>(`/dashboard/summary${q}`);
}

export function getDailyReport(date?: string): Promise<DashboardSummary> {
  const q = date ? `?date=${date}` : "";
  return request<DashboardSummary>(`/dashboard/daily-report${q}`);
}

export function shipPick(
  order_no: string,
  body: { invoice_no: string; carrier_name?: string; shipped_by?: string }
): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>(`/picking/${encodeURIComponent(order_no)}/ship`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ─── Work Assignments ────────────────────────────────────────

export interface WorkAssignment {
  id: number;
  task_type: string;
  entity_type: string;
  entity_id: string | null;
  title: string;
  status: string;
  assignee: string | null;
  assigned_by: string | null;
  assigned_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  priority: string;
  due_date: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  entity_link: string | null;
  assignee_on_leave: boolean;
}

export interface WorkAssignmentListResponse {
  items: WorkAssignment[];
  total: number;
  counts: Record<string, number>;
}

export interface WorkAssignmentCreate {
  task_type: string;
  entity_type: string;
  entity_id?: string;
  title: string;
  status?: string;
  assignee?: string;
  assigned_by?: string;
  priority?: string;
  due_date?: string;
  memo?: string;
}

export interface WorkAssignmentUpdate {
  assignee?: string | null;
  assigned_by?: string;
  priority?: string;
  status?: string;
  due_date?: string;
  memo?: string;
}

export function getWorkAssignments(params?: {
  assignee?: string;
  status?: string;
  task_type?: string;
  priority?: string;
}): Promise<WorkAssignmentListResponse> {
  const q = new URLSearchParams();
  if (params?.assignee) q.set("assignee", params.assignee);
  if (params?.status) q.set("status", params.status);
  if (params?.task_type) q.set("task_type", params.task_type);
  if (params?.priority) q.set("priority", params.priority);
  const qs = q.toString() ? `?${q.toString()}` : "";
  return request<WorkAssignmentListResponse>(`/work-assignments${qs}`);
}

export function createWorkAssignment(body: WorkAssignmentCreate): Promise<WorkAssignment> {
  return request<WorkAssignment>("/work-assignments", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateWorkAssignment(id: number, body: WorkAssignmentUpdate): Promise<WorkAssignment> {
  return request<WorkAssignment>(`/work-assignments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function startWorkAssignment(id: number, started_by?: string): Promise<WorkAssignment> {
  return request<WorkAssignment>(`/work-assignments/${id}/start`, {
    method: "PATCH",
    body: JSON.stringify({ started_by }),
  });
}

export function completeWorkAssignment(id: number, completed_by?: string): Promise<WorkAssignment> {
  return request<WorkAssignment>(`/work-assignments/${id}/complete`, {
    method: "PATCH",
    body: JSON.stringify({ completed_by }),
  });
}

export function seedWorkAssignmentSamples(): Promise<{ message: string; created: number }> {
  return request("/work-assignments/seed-samples", { method: "POST" });
}

// ─── AS 2차 ──────────────────────────────────────────────────

export function confirmAsCharge(asId: number, confirmed_by?: string): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${asId}/confirm-charge`, {
    method: "POST",
    body: JSON.stringify({ confirmed_by }),
  });
}

export function createAsShipment(
  asId: number,
  body: { requested_by?: string; memo?: string }
): Promise<AsShipmentRequest> {
  return request<AsShipmentRequest>(`/as/requests/${asId}/create-shipment`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getAsShipment(asId: number): Promise<AsShipmentRequest | null> {
  return request<AsShipmentRequest | null>(`/as/requests/${asId}/shipment`);
}

// ─── Message Templates ────────────────────────────────────────

export interface MessageTemplate {
  id: number;
  template_type: string;
  title: string;
  body: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function getMessageTemplates(params?: {
  template_type?: string;
  active_only?: boolean;
}): Promise<MessageTemplate[]> {
  const q = new URLSearchParams();
  if (params?.template_type) q.set("template_type", params.template_type);
  if (params?.active_only) q.set("active_only", "true");
  const qs = q.toString() ? `?${q}` : "";
  return request<MessageTemplate[]>(`/message-templates${qs}`);
}

export function createMessageTemplate(body: {
  template_type: string;
  title: string;
  body: string;
  is_active?: boolean;
}): Promise<MessageTemplate> {
  return request<MessageTemplate>("/message-templates", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateMessageTemplate(
  id: number,
  body: { title?: string; body?: string; is_active?: boolean }
): Promise<MessageTemplate> {
  return request<MessageTemplate>(`/message-templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function seedMessageTemplates(): Promise<{ message: string; created: number }> {
  return request("/message-templates/seed", { method: "POST" });
}



// ─── Calendar & Leave ─────────────────────────

export interface CalendarEvent {
  id: number;
  title: string;
  description: string | null;
  event_type: string;
  start_date: string;
  end_date: string;
  is_all_day: boolean;
  owner_name: string | null;
  visibility: string;
  created_by: string | null;
  // [FW-036] 휴가 연동 일정('vacation') 표시 · 로그인 사용자 기준 수정·삭제 가능 여부(서버 계산)
  source_type?: string | null;
  can_manage?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CalendarEventCreate {
  title: string;
  description?: string;
  event_type?: string;
  start_date: string;
  end_date: string;
  is_all_day?: boolean;
  owner_name?: string;
  visibility?: string;
}

export interface LeaveRequest {
  id: number;
  employee_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  is_half_day: boolean;
  half_day_type: string | null;
  reason: string | null;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  // [FW-036] 로그인 사용자 기준 수정·취소 가능 여부(본인·관리자, 서버 계산)
  can_manage?: boolean;
  created_at: string;
  updated_at: string;
}

// [FW-036] 신청자는 서버가 로그인 사용자로 정한다(신청자 이름을 보내지 않는다).
export interface LeaveRequestCreate {
  leave_type?: string;
  start_date: string;
  end_date: string;
  is_half_day?: boolean;
  half_day_type?: string;
  reason?: string;
}

export interface LeaveListResponse {
  total: number;
  items: LeaveRequest[];
}

// 26차 후속 — 운영대시보드 위젯 1차: 금주 등록 신상품.
export interface RecentProduct {
  sku: string;
  product_name: string;
  brand: string | null;
  category: string | null;
  created_at: string | null;
  total_stock: number;
  is_active: boolean;
  /** product_platform_meta 의 smartstore listing_url (없으면 null). */
  naver_url?: string | null;
}
export interface RecentProductsResponse {
  week_start: string;
  week_end: string;
  total: number;
  items: RecentProduct[];
}
export function getRecentProducts(limit = 50): Promise<RecentProductsResponse> {
  return request(`/dashboard/recent-products?limit=${limit}`);
}

// 26차 후속 — 운영대시보드 위젯 1차 보정: 내 주요 로그인 (DB SoT).
export interface MyExternalLink {
  id: number;
  title: string;
  url: string;
  sort_order: number;
  memo: string | null;
  created_at: string;
  updated_at: string;
}
export function listMyExternalLinks(): Promise<MyExternalLink[]> {
  return request(`/me/external-links`);
}
export function createMyExternalLink(body: {
  title: string;
  url: string;
  memo?: string | null;
}): Promise<MyExternalLink> {
  return request(`/me/external-links`, { method: "POST", body: JSON.stringify(body) });
}
export function updateMyExternalLink(
  id: number,
  body: Partial<{ title: string; url: string; memo: string | null; sort_order: number }>,
): Promise<MyExternalLink> {
  return request(`/me/external-links/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteMyExternalLink(id: number): Promise<void> {
  return request(`/me/external-links/${id}`, { method: "DELETE" });
}

// 26차 후속 — 운영대시보드 위젯 1차 보정: 소싱 업사이드 상품 (공유 SoT, soft delete).
export interface SourcingUpsideItem {
  id: number;
  planned_product_name: string;
  purchase_url: string | null;
  benchmark_url: string | null;
  expected_cost_krw: number | null;
  is_active: boolean;
  created_by_user_id: number;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}
export function listSourcingUpside(includeInactive = false): Promise<SourcingUpsideItem[]> {
  const qs = includeInactive ? "?include_inactive=true" : "";
  return request(`/dashboard/sourcing-upside-products${qs}`);
}
export function createSourcingUpside(body: {
  planned_product_name: string;
  purchase_url?: string | null;
  benchmark_url?: string | null;
  expected_cost_krw?: number | null;
}): Promise<SourcingUpsideItem> {
  return request(`/dashboard/sourcing-upside-products`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function updateSourcingUpside(
  id: number,
  body: Partial<{
    planned_product_name: string;
    purchase_url: string | null;
    benchmark_url: string | null;
    expected_cost_krw: number | null;
    is_active: boolean;
  }>,
): Promise<SourcingUpsideItem> {
  return request(`/dashboard/sourcing-upside-products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
export function softDeleteSourcingUpside(id: number): Promise<SourcingUpsideItem> {
  return request(`/dashboard/sourcing-upside-products/${id}`, { method: "DELETE" });
}

export function getCalendarEvents(params: {
  year?: number;
  month?: number;
  start?: string;
  end?: string;
  event_type?: string;
  visibility?: string;
}): Promise<CalendarEvent[]> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
  return request<CalendarEvent[]>(`/calendar/events?${qs}`);
}

export function createCalendarEvent(body: CalendarEventCreate): Promise<CalendarEvent> {
  return request<CalendarEvent>("/calendar/events", { method: "POST", body: JSON.stringify(body) });
}

// [FW-037] 보낸 필드만 반영된다. 설명·담당자는 null 로 보내면 비워진다.
export interface CalendarEventUpdate {
  title?: string;
  description?: string | null;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  owner_name?: string | null;
  visibility?: string;
}

export function updateCalendarEvent(id: number, body: CalendarEventUpdate): Promise<CalendarEvent> {
  return request<CalendarEvent>(`/calendar/events/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteCalendarEvent(id: number): Promise<void> {
  return request<void>(`/calendar/events/${id}`, { method: "DELETE" });
}

export function getLeaves(params: {
  status?: string;
  employee_name?: string;
  leave_type?: string;
  year?: number;
}): Promise<LeaveListResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
  return request<LeaveListResponse>(`/calendar/leaves?${qs}`);
}

export function createLeave(body: LeaveRequestCreate): Promise<LeaveRequest> {
  return request<LeaveRequest>("/calendar/leaves", { method: "POST", body: JSON.stringify(body) });
}

// [FW-036] 승인·반려·취소 처리자는 서버가 로그인 사용자로 기록한다(처리자 이름을 보내지 않는다).
export function approveLeave(id: number): Promise<LeaveRequest> {
  return request<LeaveRequest>(`/calendar/leaves/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function rejectLeave(id: number, reject_reason?: string): Promise<LeaveRequest> {
  return request<LeaveRequest>(`/calendar/leaves/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ reject_reason }),
  });
}

export function cancelLeave(id: number): Promise<LeaveRequest> {
  return request<LeaveRequest>(`/calendar/leaves/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export interface LeaveBalance {
  accrued: number;
  used: number;
  balance: number;
  hire_date: string | null;
  year_index: number | null;
  period_start: string | null;
  period_end: string | null;
  allocation: number;
}

export function getLeaveBalance(): Promise<LeaveBalance> {
  return request<LeaveBalance>("/calendar/leaves/balance");
}

// [ERP-VACATION-CALENDAR-SYNC-AND-REMAINING-DAYS-20260606-001] 전 직원 잔여 휴가 (admin)
export interface AllLeaveBalanceItem {
  user_id: number;
  name: string;
  role: string | null;
  hire_date: string | null;
  accrued: number;
  used: number;
  balance: number;
  pending: number;
  allocation: number;
  period_start: string | null;
  period_end: string | null;
  year_index: number | null;
}
export function getAllLeaveBalances(): Promise<{ items: AllLeaveBalanceItem[] }> {
  return request<{ items: AllLeaveBalanceItem[] }>("/calendar/leaves/balance/all");
}


// ─── Today Summary ─────────────────────────

export interface TodaySummaryEvent {
  id: number;
  title: string;
  event_type: string;
  start_date: string;
  end_date: string;
  visibility: string;
  owner_name: string | null;
}

export interface TodaySummary {
  today: string;
  today_events: TodaySummaryEvent[];
  today_leave_employees: string[];
  today_leave_count: number;
}

export function getTodaySummary(): Promise<TodaySummary> {
  return request<TodaySummary>("/calendar/today-summary");
}

// [ERP-OPERATIONS-DASHBOARD-ROUTINE-20260519-001] 운영대시보드 "오늘 해야 할 업무"
// 섹션용 종합 카운트. pending_accounts / pending_leaves 는 admin 일 때만 값,
// 그 외 직원에게는 null 로 응답 — 화면에서 숨김.
export interface RoutineSummary {
  pending_accounts: number | null;
  pending_leaves: number | null;
  shipment_holds_pending: number;
  barcode_missing_products: number;
}

export function getRoutineSummary(): Promise<RoutineSummary> {
  return request<RoutineSummary>("/dashboard/routine-summary");
}

export function getOnLeaveToday(): Promise<{ on_leave: string[] }> {
  return request<{ on_leave: string[] }>("/work-assignments/on-leave-today");
}


// ─── Period Report ────────────────────────

export interface PeriodOrderStats {
  count: number;
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  fee_amount: number;
  settlement_amount: number;
}

export interface PeriodOpStats {
  shipped_count: number;
  hold_requested: number;
  return_received: number;
  return_completed: number;
  as_received: number;
  as_completed: number;
  as_customer_charge: number;
  stocked_in: number;
}

export interface PeriodSummary {
  date_from: string;
  date_to: string;
  orders: PeriodOrderStats;
  ops: PeriodOpStats;
}

export interface TrendRow {
  period_label: string;
  date_from: string;
  date_to: string;
  order_count: number;
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  fee_amount: number;
  settlement_amount: number;
  shipped_count: number;
  hold_requested: number;
  return_received: number;
  return_completed: number;
  as_received: number;
  as_completed: number;
  stocked_in: number;
}

export interface TrendsResponse {
  date_from: string;
  date_to: string;
  group_by: string;
  rows: TrendRow[];
  totals: TrendRow;
}

export function getPeriodSummary(date_from: string, date_to: string): Promise<PeriodSummary> {
  return request<PeriodSummary>(`/dashboard/period-summary?date_from=${date_from}&date_to=${date_to}`);
}

export function getTrends(date_from: string, date_to: string, group_by: string): Promise<TrendsResponse> {
  return request<TrendsResponse>(`/dashboard/trends?date_from=${date_from}&date_to=${date_to}&group_by=${group_by}`);
}


// ─── Period Targets ───────────────────────

export const SUPPORTED_METRICS: Record<string, string> = {
  order_count:            "주문건수",
  paid_amount:            "결제총액",
  settlement_amount:      "정산금액",
  shipped_count:          "출고완료",
  as_completed_count:     "AS완료건수",
  return_completed_count: "반품검수완료",
  stocked_in_count:       "구매입고완료",
};

export interface TargetItem {
  id: number;
  year: number;
  month: number;
  metric: string;
  metric_label: string;
  target_value: number;
}

export interface TargetActual {
  metric: string;
  metric_label: string;
  target: number;
  actual: number;
  rate: number;      // 0~∞, 1.0 = 100%
  status: "green" | "yellow" | "red";
}

export function getPeriodTargets(year: number, month: number): Promise<TargetItem[]> {
  return request<TargetItem[]>(`/dashboard/period-targets?year=${year}&month=${month}`);
}

export function upsertPeriodTarget(body: {
  year: number; month: number; metric: string; target_value: number;
}): Promise<TargetItem> {
  return request<TargetItem>("/dashboard/period-targets", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updatePeriodTarget(id: number, target_value: number): Promise<TargetItem> {
  return request<TargetItem>(`/dashboard/period-targets/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ target_value }),
  });
}

export function getTargetActuals(
  date_from: string,
  date_to: string,
  year: number,
  month: number,
): Promise<TargetActual[]> {
  return request<TargetActual[]>(
    `/dashboard/period-target-actuals?date_from=${date_from}&date_to=${date_to}&year=${year}&month=${month}`,
  );
}

export interface TargetAlertResponse {
  at_risk_count: number;
  items: TargetActual[];
}

export function getTargetAlerts(year: number, month: number): Promise<TargetAlertResponse> {
  return request<TargetAlertResponse>(`/dashboard/target-alerts?year=${year}&month=${month}`);
}

// ─── 단건 주문접수 ────────────────────────────────────────────────

export interface CustomerSearchResult {
  id: number;
  customer_name: string;
  phone: string | null;
  address1: string | null;
  zipcode: string | null;
  total_order_count: number;
  last_order_date: string | null;
  // [20260816] 최근 주문 요약(with_recent_order=true 일 때만, 통합고객은 group scope 최신)
  recent_order_no?: string | null;
  recent_order_date?: string | null;
  recent_platform?: string | null;
  recent_platform_store_name?: string | null;
  recent_product_name?: string | null;
  recent_product_other_count?: number | null;
  recent_receiver_name?: string | null;
  recent_receiver_phone?: string | null;
  recent_address?: string | null;
}

export function searchCustomers(q: string, opts?: { withRecentOrder?: boolean }): Promise<CustomerSearchResult[]> {
  const rec = opts?.withRecentOrder ? "&with_recent_order=true" : "";
  return request<CustomerSearchResult[]>(`/orders/customers/search?q=${encodeURIComponent(q)}${rec}`);
}

export interface CustomerListItem {
  id: number;
  customer_name: string;
  phone: string | null;
  address1: string | null;
  zipcode: string | null;
  total_order_count: number;
  last_order_date: string | null;
  as_count: number;
  memo: string | null;
  // [ERP-CUSTOMER-DB-SEARCH-ORDERER-RECIPIENT-20260527-001]
  // 검색어가 있을 때만 backend 가 매칭된 필드 집합을 채워서 반환. q 없으면 null/생략.
  // 가능 값: "customer_name" | "customer_phone" | "recipient_name" | "recipient_phone"
  match_sources?: string[] | null;
}

export async function getCustomers(
  params: { q?: string; page?: number; page_size?: number } = {},
): Promise<{ total: number; items: CustomerListItem[]; page: number; page_size: number; total_pages: number }> {
  // 26차 후속 #67 — page/page_size 페이지네이션
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  return request(`/orders/customers?${qs}`);
}

export async function getCustomerDetail(id: number): Promise<unknown> {
  // 반환 스키마는 고객 360 뷰 (customer_360_view.md) 기준으로 `app/customers/page.tsx` 의
  // CustomerDetail 인터페이스에서 타입 정의함. 여기선 unknown 으로 남겨 중복 방지.
  return request(`/orders/customers/${id}`);
}

export function getNextManualOrderNo(): Promise<{ order_no: string }> {
  return request<{ order_no: string }>("/orders/manual/next-no");
}

export interface ManualOrderItemIn {
  sku?: string | null;
  product_name: string;
  option_name?: string | null;
  quantity: number;
  unit_price: number;
  discount_rate?: number;
  discount_amount?: number;
  // [ERP-OFFLINE-POS-ITEM-LEVEL-STOCK-LOCATION-20260527-001]
  // POS(order_source='offline_store') 품목별 재고 차감 위치 — 'office' | 'warehouse'.
  // 미전달 시 body.stock_location → 'office' fallback. 비-POS 흐름에서는 무시.
  stock_location?: "office" | "warehouse" | null;
}

export interface ManualOrderRequest {
  company_code: string;
  customer_id?: number | null;
  customer_name: string;
  customer_phone?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  payment_method: string;
  shipping_fee: number;
  /**
   * 주문 레벨 할인 (원 단위 정수).
   * [ERP-STORE-POS-ADDITIONAL-DISCOUNT-PERSIST-DEV-20260922-002]
   *   · 비-POS: 기존 "전체 할인" — 라인별 discount_rate/discount_amount 와 **동시 사용 금지**(422).
   *   · POS(order_source='offline_store'): 화면의 "추가 할인" — 행 할인과 **동시 사용 가능**하며
   *     orders.discount_amount 에 이 값만 저장된다(행 할인은 order_items.line_discount_* 에 별도).
   */
  order_discount_amount?: number;
  items: ManualOrderItemIn[];
  memo?: string | null;
  order_source?: string | null;
  // [ERP-OFFLINE-POS-ONSITE-SALE-POLICY-FIX-20260527-001]
  // POS(order_source='offline_store') 재고 차감 위치 — 'office' | 'warehouse'.
  stock_location?: "office" | "warehouse" | null;
  // [ERP-STORE-POS-ADDITIONAL-DISCOUNT-PERSIST-DEV-20260922-002]
  // POS 최종 결제금액 — **대조 전용**이다(더 이상 frontend SoT 가 아니다).
  //   backend 가 상품합계·행할인·추가할인·올림조정·배송비를 독립 계산해 paid_amount 를 만들고,
  //   이 값과 다르면 저장하지 않고 422 로 거부한다.
  final_paid_amount?: number | null;
  // 표시·감사용 참고값 — 서버 저장·판정에 쓰이지 않는다.
  auto_discount_amount?: number | null;
  rounding_adjust?: number | null;
  // [ORDER-003 Phase 5] POS 수령 방법 + 배송 요청사항(주문 snapshot). POS 전용 — 비-POS 는 보내지 않는다.
  //   courier 필수 배송정보 검증은 backend(order_shipping_edit) 가 최종 판정한다.
  receipt_method?: ReceiptMethod | null;
  delivery_request_note?: string | null;
}

export interface ManualOrderResponse {
  order_no: string;
  customer_id: number;
  total_product_amount: number;
  discount_amount: number;
  shipping_fee: number;
  paid_amount: number;
  item_count: number;
  payment_method: string;
  order_source?: string | null;
}

export function createManualOrder(body: ManualOrderRequest): Promise<ManualOrderResponse> {
  return request<ManualOrderResponse>("/orders/manual", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getNextPosOrderNo(): Promise<{ order_no: string }> {
  return request<{ order_no: string }>("/orders/pos/next-no");
}

// ─── 견적서 (26차 후속 #33) ──────────────────────────────────────

export type QuoteStatus = "quote_pending" | "quote_converted" | "quote_cancelled";

export interface QuoteItemIn {
  sku?: string | null;
  product_name: string;
  option_name?: string | null;
  quantity: number;
  unit_price: number;
  discount_amount?: number;
}

export interface QuoteCreateRequest {
  company_code: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  shipping_fee: number;
  // [ERP-INST-20260513-010] 추가배송비 별도 항목 (선택).
  additional_shipping_fee?: number;
  memo?: string | null;
  items: QuoteItemIn[];
}

export interface QuoteItemOut {
  id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  line_amount: number | null;
}

export interface QuoteResponse {
  id: number;
  order_no: string;
  order_status: QuoteStatus;
  customer_name: string | null;
  customer_phone: string | null;
  shipping_fee: number | null;
  // [ERP-INST-20260513-010] 추가배송비 (additive, optional).
  additional_shipping_fee?: number | null;
  total_product_amount: number | null;
  paid_amount: number | null;
  item_count: number;
  total_qty: number;
  memo: string | null;
  converted_order_no: string | null;
  created_at: string;
  items: QuoteItemOut[];
}

export interface QuoteListResponse {
  total: number;
  items: QuoteResponse[];
}

export interface QuoteConvertRequest {
  customer_name: string;
  customer_phone?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  payment_method?: string;
  shipping_fee?: number | null;
  memo?: string | null;
}

export function getNextQuoteNo(): Promise<{ order_no: string }> {
  return request<{ order_no: string }>("/orders/quotes/next-no");
}

export function createQuote(body: QuoteCreateRequest): Promise<QuoteResponse> {
  return request<QuoteResponse>("/orders/quotes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listQuotes(params?: {
  status?: QuoteStatus | "all";
  limit?: number;
  offset?: number;
}): Promise<QuoteListResponse> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  return request<QuoteListResponse>(`/orders/quotes?${q.toString()}`);
}

export function convertQuoteToOrder(
  quoteId: number,
  body: QuoteConvertRequest,
): Promise<ManualOrderResponse> {
  return request<ManualOrderResponse>(`/orders/quotes/${quoteId}/convert`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function cancelQuote(quoteId: number): Promise<QuoteResponse> {
  return request<QuoteResponse>(`/orders/quotes/${quoteId}/cancel`, {
    method: "POST",
  });
}

// ─── 토글 Export ──────────────────────────────────────────────────

export interface ExportItem {
  id: number;
  source: string;
  order_no: string | null;
  as_no: string | null;
  customer_name: string;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  sku: string | null;
  product_name: string;
  // [ERP-TOGGLE-EXPORT-BARCODE-NAME-OPTIONS-AND-XLSX-DEV-20260712-054]
  // 화면/XLSX 공용 표시 상품명(바코드용 → 일반 → SKU) + 옵션. 기존 product_name 은 원본 유지.
  display_product_name?: string | null;
  option1?: string | null;
  option2?: string | null;
  quantity: number | null;
  // [ORDER-002 DIFF6] 최종 실매출 = 원 플랫폼 결제 + 활성 SKU 변경 diff 합(원 orders.paid_amount 무수정).
  // 그룹 첫 행에만 값, 이후 행 0. order_total_amount 와 동일하며 실제 XLSX 결제금액과도 정합.
  paid_amount: number | null;
  payment_method: string | null;
  created_at: string;
  exported_at: string | null;
  as_status: string | null;   // AS 5단계 상태 (as_completed_shipment 행에만 존재 — 보조 표시용)
  // 26차 후속 #30 — 결제 흐름 / 배송 흐름 명시 분리
  payment_status: "paid" | "unpaid" | "not_required" | null;
  shipping_status: "pending" | "shipping" | "delivered" | null;
  // [ERP-TOGGLE-EXPORT-ORDER-TYPE-LABEL-FIX-20260528-001]
  // 업무 유형 구분 (memo prefix / source 기반 정규화)
  export_kind?: "reissue" | "quote" | "manual" | "as" | "invoice_split" | null;
  export_kind_label?: string | null;
  // [ERP-TOGGLE-EXPORT-FLAT-TABLE-WIDTH-AND-ORDER-AMOUNT-COLUMNS-DEV-20260712-061]
  // 주문 단위 금액(우측 3컬럼). manual 계열만 값 존재, AS 행은 모두 null. 프론트 재계산 금지.
  // [ORDER-002 DIFF6] order_total_amount = 원 orders.paid_amount 가 아니라 최종 실매출
  // (원 플랫폼 결제 + 활성 SKU 변경 diff 합, 원결제 보존). SKU 변경 없으면 원결제와 동일. null 유지.
  shipping_fee?: number | null;             // 기본배송비
  additional_shipping_fee?: number | null;  // 도서산간비
  order_total_amount?: number | null;       // 최종 실매출(원 paid + 활성 diff)
  // [ERP-EXCHANGE-RETURN-TOGGLE-EXPORT-LINKED-STATUS-UI-PHASE2-20260712-075]
  // 교환 재출고 연결정보(additive). is_exchange_resend!==true 면 일반 행으로 기존 렌더링.
  is_exchange_resend?: boolean;
  exchange_receipt_id?: number | null;
  exchange_reason?: string | null;
  exchange_reason_label?: string | null;
  original_order_id?: number | null;
  original_order_no?: string | null;
  exchange_collection_status?: string | null;
  exchange_collection_status_label?: string | null;
  replacement_hold?: boolean;
  exchange_payment_status?: string | null;
  exchange_payment_status_label?: string | null;
  exchange_expected_payment_amount?: number | null;
  exchange_collection_item_count?: number;
  exchange_collection_summary?: string | null;
  // [AS-001-AS-BILLING-EXPORT-CUSTOMER-MESSAGE-DEV-20260828] 단건 AS Export 안전 게이트.
  // 청구액 확정 전(또는 유상 미입금) AS 행은 export_blocked=true + 사유. 프론트 체크박스 비활성화용.
  export_blocked?: boolean;
  export_block_reason?: string | null;
  // [ORDER-003 Phase 5] 표시용 출처·수령 방법(read-only · additive). AS 행은 null.
  order_source?: string | null;
  receipt_method?: ReceiptMethod | null;
}

// [ERP-EXCHANGE-RETURN-TOGGLE-EXPORT-LINKED-STATUS-UI-PHASE2-20260712-075]
export interface HeldExchangeItem {
  order_id: number;
  order_no: string;
  exchange_receipt_id: number;
  exchange_reason?: string | null;
  exchange_reason_label?: string | null;
  original_order_id?: number | null;
  original_order_no?: string | null;
  exchange_collection_status?: string | null;
  exchange_collection_status_label?: string | null;
  exchange_payment_status?: string | null;
  exchange_payment_status_label?: string | null;
  exchange_expected_payment_amount?: number | null;
  exchange_collection_item_count?: number;
  exchange_collection_summary?: string | null;
  customer_name?: string | null;
  receiver_name?: string | null;
  created_at: string;
}

export interface HeldExchangeListResponse {
  total: number;
  items: HeldExchangeItem[];
}

export function getHeldExchanges(): Promise<HeldExchangeListResponse> {
  return request<HeldExchangeListResponse>("/orders/export/toggle/held-exchanges");
}

// 재출고 보류(replacement_hold) 관리자 수동 변경. reason(memo) 필수. 해제만 사용(false).
export function updateReplacementHold(
  receiptId: number,
  body: { replacement_hold: boolean; memo: string },
): Promise<unknown> {
  return request(`/returns/receipts/${receiptId}/replacement-hold`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// [ERP-EXCHANGE-RETURN-INTAKE-UI-PHASE3-20260713-076]
// 교환 접수 통합(원자) — 회수 대기건 + 재출고 주문 동시 생성. POST /returns/exchange-intakes.
// 필드는 OpenAPI(ExchangeIntakeCreate) 기준 정확히 일치. replacement_items[].sku 필수.
export interface ExchangeCollectionItemIn {
  original_order_item_id: number;
  quantity: number;
}
export interface ExchangeReplacementItemIn {
  sku: string;
  product_name?: string | null;
  option_name?: string | null;
  quantity?: number;
  unit_price?: number;
}
export interface ExchangeIntakeCreate {
  order_no: string;
  original_order_id?: number | null;
  exchange_reason: "wrong_delivery" | "wrong_order" | "option_exchange" | "damaged_exchange";
  intake_idempotency_key?: string | null;
  collection_items?: ExchangeCollectionItemIn[] | null;
  replacement_items: ExchangeReplacementItemIn[];
  recovery_type?: string;
  recovery_courier?: string | null;
  return_invoice_no?: string | null;
  received_by?: string | null;
  memo?: string | null;
  expected_payment_amount?: number | null;
  payment_reason?: string | null;
  replacement_hold_override?: boolean | null;
}
export interface ExchangeCollectionItemOut {
  id: number;
  original_order_item_id: number | null;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  requested_quantity: number;
  received_quantity: number;
  inspected_quantity: number;
}
export interface ExchangeIntakeResponse {
  created: boolean;
  receipt_id: number;
  return_type: string;
  exchange_reason: string | null;
  original_order_id: number | null;
  original_order_no: string | null;
  collection_status: string;
  collection_items: ExchangeCollectionItemOut[];
  replacement_hold: boolean;
  exchange_resend_order_id: number | null;
  replacement_order_no: string | null;
  expected_payment_amount: number | null;
  payment_status: string;
  payment_reason?: string | null;
}
export function createExchangeIntake(data: ExchangeIntakeCreate): Promise<ExchangeIntakeResponse> {
  return request<ExchangeIntakeResponse>("/returns/exchange-intakes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface ExportListResponse {
  total: number;
  items: ExportItem[];
  // [ERP-TOGGLE-EXPORT-TAB-COUNT-BY-ORDER-NO-20260528-001]
  // total_orders: distinct order_no 기준. 탭 카운트 등 "주문건수" 표시에 사용.
  // total: item-level (outerjoin row 수) — 기존 화면 호환.
  total_orders?: number | null;
  // [ERP-TOGGLE-EXPORT-ORDER-TYPE-LABEL-FIX-20260528-001]
  // export_kind 별 distinct 주문 카운트 (chip 카운트 표시).
  kind_counts?: { reissue?: number; quote?: number; manual?: number; as?: number; invoice_split?: number } | null;
  // [ERP-TOGGLE-EXPORT-QUOTE-REGISTER-COUNT-AND-VISIBILITY-DEV-20260622-001]
  // 견적서 등록건(quote_pending) 수 — chip "견적서" 카운트용. export_status 무관 항상 동일.
  quote_register_count?: number;
  // [ORDER-003 Phase 5] 택배 지정 POS 주문 중 Export 대상에서 빠진 주문 + 사유(read-only · additive).
  pos_courier_attention?: PosCourierAttention[];
}

export function getExportList(params?: {
  source?: string;
  unexported_only?: boolean;
  // [ERP-TOGGLE-EXPORT-COMPLETED-TAB-20260528-001] export_status: pending | exported | all
  export_status?: "pending" | "exported" | "all";
  // [ERP-TOGGLE-EXPORT-ORDER-TYPE-LABEL-FIX-20260528-001]
  // 업무 유형 필터 — reissue | quote | manual | as | invoice_split | all
  kind?: "reissue" | "quote" | "manual" | "as" | "invoice_split" | "all";
  limit?: number;
  offset?: number;
}): Promise<ExportListResponse> {
  const q = new URLSearchParams();
  if (params?.source) q.set("source", params.source);
  if (params?.unexported_only) q.set("unexported_only", "true");
  if (params?.export_status) q.set("export_status", params.export_status);
  if (params?.kind) q.set("kind", params.kind);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  return request<ExportListResponse>(`/orders/export/toggle?${q.toString()}`);
}

// [ERP-TOGGLE-EXPORT-COMPLETED-TAB-20260528-001]
// Export 완료 처리한 주문을 미출고 상태로 되돌리기.
export interface RevertExportedResponse {
  reverted_orders: number;
  reverted_shipments: number;
  blocked: Array<{ id: number; order_no?: string; reason: string }>;
}

export function revertExported(body: {
  order_ids?: number[];
  shipment_ids?: number[];
}): Promise<RevertExportedResponse> {
  return request<RevertExportedResponse>("/orders/export/toggle/revert-exported", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// [AS-001-...-MIN-HARDENING-20260828 #4] typed ID 분리 전달 — order_ids / as_ids 를 각각 넘겨
// backend 가 같은 숫자의 Order.id / AsRequest.id 를 섞지 않게 한다.
export async function exportToggleXlsx(
  orderIds: number[],
  asIds: number[],
): Promise<void> {
  const token = getToken();
  const res = await fetch(
    `${API_URL}/orders/export/toggle/xlsx`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ order_ids: orderIds, as_ids: asIds }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "xlsx export 실패" }));
    const d = err?.detail;
    throw new Error(
      (typeof d === "string" ? d : d?.message) || "xlsx export 실패",
    );
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disp = res.headers.get("Content-Disposition") || "";
  const match = disp.match(/filename="(.+?)"/);
  a.download = match ? match[1] : "toggle_export.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

// ── 채팅/댓글 (업무 객체별) ──────────────────────────────────────

export interface ChatMessageAttachment {
  id: number;
  filename: string;
  kind: "image" | "file";
  mime_type: string;
  size_bytes: number;
  download_url: string;
  thumbnail_url: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface ChatMessage {
  id: number;
  thread_id: number;
  // Phase A — user_id 우선 노출 (직원별 색상 매핑 안정 키). 시스템/외부봇은 null.
  sender_user_id?: number | null;
  sender_name: string;
  sender_role: string | null;
  message: string;
  is_system: boolean;
  created_at: string;
  attachments?: ChatMessageAttachment[];
  // [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
  // 읽음 표시(발신자 제외 active user 기준) + soft delete. 구버전 백엔드는 미반환 → optional.
  read_count?: number;
  read_user_ids?: number[];
  read_user_names?: string[];
  // [ERP-TEAMCHAT-UNREAD-COUNT-KAKAOTALK-STYLE-FIX-DEV-20260703-031B]
  // 카카오톡식 "아직 안 읽은 사람 수" — UI 는 이 값만 숫자로 표시(0이면 미표시).
  unread_count?: number;
  unread_user_names?: string[];
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by_user_id?: number | null;
  deleted_by_name?: string | null;
}

export interface ChatMessagesResponse {
  thread_id: number;
  entity_type: string;
  entity_id: string;
  message_count: number;
  messages: ChatMessage[];
  // 26차 후속 #61 — 페이지네이션 메타. 구버전 백엔드는 미반환할 수 있어 optional.
  has_more?: boolean;
  oldest_message_id?: number | null;
  newest_message_id?: number | null;
  limit?: number;
}

export function getChatMessages(
  entity_type: string,
  entity_id: string,
  options: { since_id?: number; before_id?: number; limit?: number } = {},
): Promise<ChatMessagesResponse> {
  const params = new URLSearchParams({
    entity_type,
    entity_id,
  });
  if (options.since_id) params.set("since_id", String(options.since_id));
  if (options.before_id) params.set("before_id", String(options.before_id));
  if (options.limit) params.set("limit", String(options.limit));
  return request<ChatMessagesResponse>(`/chat/messages?${params.toString()}`);
}

export function postChatMessage(body: {
  entity_type: string;
  entity_id: string;
  sender_name: string;
  sender_role?: string;
  message: string;
  is_system?: boolean;
}): Promise<ChatMessage> {
  return request<ChatMessage>("/chat/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
// 본인이 보낸 메시지 soft delete. 삭제된 메시지 응답(masked) 반환. 권한 없으면 403.
export function deleteChatMessage(messageId: number): Promise<ChatMessage> {
  return request<ChatMessage>(`/chat/messages/${messageId}`, { method: "DELETE" });
}

export function postChatMessageWithAttachments(opts: {
  entity_type: string;
  entity_id: string;
  sender_name: string;
  sender_role?: string;
  message?: string;
  files: File[];
}): Promise<ChatMessage> {
  const fd = new FormData();
  fd.append("entity_type", opts.entity_type);
  fd.append("entity_id", opts.entity_id);
  fd.append("sender_name", opts.sender_name);
  if (opts.sender_role) fd.append("sender_role", opts.sender_role);
  fd.append("message", opts.message ?? "");
  for (const f of opts.files) fd.append("files", f, f.name);
  return request<ChatMessage>("/chat/messages/with-attachments", {
    method: "POST",
    body: fd,
  });
}

export function markExported(body: {
  order_ids?: number[];
  shipment_ids?: number[];
}): Promise<{ marked_orders: number; marked_shipments: number }> {
  return request("/orders/export/toggle/mark-exported", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 수기주문 soft delete (토글 Export 화면 행 단위 삭제)
export function deleteManualOrder(orderId: number): Promise<{
  ok: boolean;
  order_id: number;
  order_no: string;
  is_cancelled: boolean;
  order_status: string;
}> {
  return request(`/orders/manual/${orderId}`, { method: "DELETE" });
}

// [ERP-TOGGLE-EXPORT-MANUAL-ORDER-RECREATE-FLOW-20260528-001]
// 토글 Export 화면 행 단위 "재작성" — 기존 주문 무효화 + prefill payload 반환.
export interface RecreateManualOrderPrefill {
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  items: Array<{
    sku: string | null;
    product_name: string;
    option_name: string | null;
    quantity: number;
    unit_price: number;
  }>;
  memo_prefix: string;
  reference_order_no: string;
}

export interface PrepareRecreateManualOrderResponse {
  ok: boolean;
  old_order_no: string;
  old_order_id: number;
  prefill: RecreateManualOrderPrefill;
}

export function prepareRecreateManualOrder(
  orderNo: string,
  reason?: string | null,
): Promise<PrepareRecreateManualOrderResponse> {
  return request<PrepareRecreateManualOrderResponse>(
    `/orders/${encodeURIComponent(orderNo)}/prepare-recreate-manual-order`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    },
  );
}

// ── 알림 이벤트 (방문 고객 호출) ─────────────────────────────

export interface NotificationEvent {
  id: number;
  event_type: string;
  title: string;
  message: string | null;
  source_location: string | null;
  is_acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
}

export function createNotificationEvent(body: {
  event_type?: string;
  title: string;
  message?: string;
  source_location?: string;
}): Promise<NotificationEvent> {
  return request<NotificationEvent>("/notifications/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event_type: "customer_visit", ...body }),
  });
}

export function getActiveNotificationEvents(): Promise<NotificationEvent[]> {
  return request<NotificationEvent[]>("/notifications/events/active");
}

export function acknowledgeNotificationEvent(
  id: number,
  acknowledged_by?: string
): Promise<NotificationEvent> {
  return request<NotificationEvent>(`/notifications/events/${id}/acknowledge`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ acknowledged_by }),
  });
}

// ── 채팅 메시지 운영 확인 (Acknowledge) ──────────────────────

export interface ChatAcknowledgement {
  id: number;
  message_id: number;
  acknowledged_by: string;
  acknowledged_at: string;
  note: string | null;
}

export interface DashboardUnackedItem {
  entity_type: string;
  entity_id: string;
  thread_id: number;
  message_id: number;
  sender_name: string;
  sender_role: string | null;
  message: string;
  created_at: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  detail_url: string;
}

export function getDashboardUnacked(limit = 20): Promise<DashboardUnackedItem[]> {
  return request<DashboardUnackedItem[]>(`/chat/dashboard-unacked?limit=${limit}`);
}

export function acknowledgeChatMessage(
  messageId: number,
  acknowledged_by: string,
  note?: string
): Promise<ChatAcknowledgement> {
  return request<ChatAcknowledgement>(`/chat/messages/${messageId}/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ acknowledged_by, note }),
  });
}

export function getChatAcknowledgements(messageId: number): Promise<ChatAcknowledgement[]> {
  return request<ChatAcknowledgement[]>(`/chat/messages/${messageId}/acknowledgements`);
}


// ── 확인필요 주문건 (운영대시보드 카드, 26차 후속 #57 Phase 2) ──────────────

export interface AttentionItemRow {
  id: number;
  entity_type: string;
  entity_id: string;
  work_type: string | null;
  order_id: number | null;
  order_no: string | null;
  target_no: string | null;
  customer_name: string | null;
  summary: string | null;
  last_message_text: string | null;
  last_message_at: string | null;
  status: "active" | "completion_requested" | "completed" | "rejected" | string;
  created_by_user_id: number | null;
  created_by_name: string | null;
  requested_by_user_id: number | null;
  requested_by_name: string | null;
  approved_by_user_id: number | null;
  approved_by_name: string | null;
  rejected_by_user_id: number | null;
  rejected_by_name: string | null;
  requested_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  detail_url: string | null;
  product_summary: string | null;
}

export interface AttentionListResponse {
  active: AttentionItemRow[];
  completion_requested: AttentionItemRow[];
  recent_old: AttentionItemRow[];
  counts: { active: number; completion_requested: number; completed_total: number };
}

export interface AttentionDetailMessage {
  id: number;
  thread_id: number;
  sender_user_id: number | null;
  sender_name: string;
  sender_role: string | null;
  message: string;
  is_system: boolean;
  created_at: string | null;
}

export interface AttentionDetailResponse {
  item: AttentionItemRow;
  messages: AttentionDetailMessage[];
}

export function getAttentionOrders(): Promise<AttentionListResponse> {
  return request<AttentionListResponse>(`/work/attention-orders`);
}

export function getAttentionOrderDetail(id: number): Promise<AttentionDetailResponse> {
  return request<AttentionDetailResponse>(`/work/attention-orders/${id}`);
}

export function requestAttentionComplete(id: number): Promise<AttentionItemRow> {
  return request<AttentionItemRow>(`/work/attention-orders/${id}/request-complete`, {
    method: "POST",
  });
}

export function approveAttentionComplete(id: number): Promise<AttentionItemRow> {
  return request<AttentionItemRow>(`/work/attention-orders/${id}/approve-complete`, {
    method: "POST",
  });
}

export function rejectAttentionComplete(
  id: number,
  reason?: string,
): Promise<AttentionItemRow> {
  return request<AttentionItemRow>(`/work/attention-orders/${id}/reject-complete`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}


// ── 계좌입금 실행 큐 (승인요청과 분리) ─────────────────────────
//   pending : 입금확인 필요
//   confirmed: 완료 (출고 전 + 3일 이내만 큐에 유지)

export interface PendingDepositItem {
  // 26차 후속 — Phase 1: "return_exchange" 추가 (반품/교환 입금)
  // [ERP-ORDER-DETAIL-ADDON-COMBINE-SHIPMENT-PAYMENT-QUEUE-DEV-20260703-032] "order_addon" 추가
  kind: "order" | "as" | "return_exchange" | "order_addon" | "sku_change";
  // 26차 후속 #25 / Phase 1 — 통합 입금확인 큐 출처 구분
  // additional_charge | exchange_diff | round_trip_shipping |
  // return_shipping | reshipping_fee | other 가 새로 추가됨.
  source_type:
    | "manual_order"
    | "offline_store"
    | "as_repair"
    | "additional_charge"
    | "exchange_diff"
    | "round_trip_shipping"
    | "return_shipping"
    | "reshipping_fee"
    | "other"
    | string;
  source_label: string;
  state: "pending" | "confirmed";
  order_no: string | null;
  as_id: number | null;
  as_no: string | null;
  /** 26차 후속 — Phase 1: ReturnReceipt 식별자 */
  return_receipt_id?: number | null;
  /** [ERP-ORDER-DETAIL-ADDON-COMBINE-SHIPMENT-PAYMENT-QUEUE-DEV-20260703-032] 추가 합배송 item id (큐 직접 입금확인) */
  addon_item_id?: number | null;
  /** [DASH-SKU-CHANGE-DEPOSIT] 구매상품 변경 추가금 order_item_sku_changes.id (큐 직접 입금확인 = settleSkuChange) */
  sku_change_id?: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  amount: number | null;
  ref_date: string | null;
  confirmed_at: string | null;
  memo: string | null;
  detail_url: string | null;
}

export interface PendingDepositsResponse {
  pending_total: number;
  confirmed_total: number;
  pending_amount_total: number;
  items: PendingDepositItem[];
  total: number;
}

export function getPendingDeposits(limit = 50): Promise<PendingDepositsResponse> {
  return request<PendingDepositsResponse>(`/dashboard/pending-deposits?limit=${limit}`);
}

// 관리자 전용 — 비-관리자가 호출하면 403. requested_by/is_admin 파라미터 제거 (JWT 기반).
export function confirmDeposit(
  order_no: string,
): Promise<{ result: string; order_no: string; order_status?: string; deposit_confirmed_at?: string }> {
  return request(`/orders/${encodeURIComponent(order_no)}/confirm-deposit`, { method: "POST" });
}

export function confirmAsDeposit(
  as_id: number,
): Promise<{ result: string; as_id: number; as_no: string; deposit_confirmed_at: string }> {
  return request(`/as/requests/${as_id}/confirm-deposit`, { method: "POST" });
}

// [ERP-ORDER-DETAIL-ADDON-COMBINE-SHIPMENT-PAYMENT-QUEUE-DEV-20260703-032]
// 추가 주문 합배송 (order_addon_items) — 등록/조회/취소/입금확인.
export interface OrderAddonItem {
  id: number;
  order_id: number;
  order_no: string;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  payment_status: "pending" | "paid" | "cancelled";
  payment_method: string;
  packed_at: string | null;
  memo: string | null;
  created_by_user_id: number | null;
  paid_at: string | null;
  cancelled_at: string | null;
  created_at: string;
}

export function getOrderAddonItems(orderNo: string): Promise<OrderAddonItem[]> {
  return request<OrderAddonItem[]>(`/orders/${encodeURIComponent(orderNo)}/addon-items`);
}

export function createOrderAddonItem(
  orderNo: string,
  body: {
    sku?: string | null;
    product_name?: string | null;
    option_name?: string | null;
    quantity: number;
    unit_price: number;
    memo?: string | null;
  },
): Promise<OrderAddonItem> {
  return request<OrderAddonItem>(`/orders/${encodeURIComponent(orderNo)}/addon-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function cancelOrderAddonItem(itemId: number): Promise<OrderAddonItem> {
  return request<OrderAddonItem>(`/orders/addon-items/${itemId}`, { method: "DELETE" });
}

export function confirmOrderAddonDeposit(itemId: number): Promise<OrderAddonItem> {
  return request<OrderAddonItem>(`/orders/addon-items/${itemId}/confirm-deposit`, { method: "POST" });
}

// 26차 후속 #42 — AS 비용 내역 초기화 (repaired/payment_confirmed → recovered)
export function resetAsCost(asId: number): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${asId}/reset-cost`, { method: "POST" });
}

// [AS-001-MULTI-AS-ZERO-BILLING-REOPEN-SEARCH-STATE-DEV] 청구 확정 취소·비용 수정(안전).
// 파츠·AS권·재고·수리완료 불변. repaired→청구확정만 해제 / payment_confirmed→관리자·repaired 복귀.
export function reopenAsCharge(asId: number): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${asId}/reopen-charge`, { method: "POST" });
}

// ─────────────────────────────────────────────
// 상품 대량등록
// ─────────────────────────────────────────────

export function downloadBulkTemplate(): void {
  window.open(`${API_URL}/products/bulk-import/template`, "_blank");
}

// ─────────────────────────────────────────────
// 포장 출고 (POS)
// ─────────────────────────────────────────────

export interface PackingOrderItem {
  id: number;
  // [ERP-PACKING-MULTI-ORDER-SAME-INVOICE-FIX-20260528-001]
  // 합포장 (multi_order) 시 각 item 의 실제 order_no — frontend 가 reduce-inventory /
  // mark-packed 호출 시 currentOrder.order_no 대신 이 값을 사용해야 함.
  order_no?: string;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  line_amount?: number | null;
  // [ERP-DAIWA-BARCODE-SCAN-20260519-001] products.barcode 동봉.
  // null/빈문자열이면 미등록 상태 — 포장 스캔 흐름에서 등록 모달 트리거 후보.
  barcode?: string | null;
  // [ERP-PACKING-OUTBOUND-IPAD-ORDER-INFO-UX-AND-COMPLETE-SCAN-FIX-DEV-20260626-001]
  // 주문내역 카드 표시용 — barcode_product_name 은 실제 DB Product.barcode_label_name 값.
  // last_inbound_at: SKU별 최근 입고일 ISO (없으면 null → 배지 숨김).
  barcode_product_name?: string | null;
  last_inbound_at?: string | null;
  // [PACKING-001][Feature A1 최소보정] Product canonical 옵션1·옵션2(각각 표시).
  // option_name(플랫폼 옵션문구)은 canonical option1/2 가 없을 때만 fallback.
  option1?: string | null;
  option2?: string | null;
  // [ERP-ORDER-INVOICE-SPLIT-ITEM-MOVE-REDESIGN-20260529-001]
  // moved_out 항목은 분할자식으로 이동된 이력 → 원주문 송장 스캔/재고차감 제외.
  split_status?: string;            // none | moved_out
  split_child_order_no?: string | null;
  scan_required?: boolean;          // false 면 포장 스캔 대상 아님 (moved_out / 재고미관리 부품)
  stock_deduction_required?: boolean;
  // [ERP-ORDER-ADDON-PICKING-STOCK-DEDUCTION-PHASE-C-DEV-20260703-032C]
  // 추가 주문 합배송 item — find-by-invoice 가 order_items 와 함께 반환. 일반 상품은 false/null.
  is_addon?: boolean;
  addon_item_id?: number | null;
  source_type?: string | null;
  source_label?: string | null;
  // [ERP-QTY-SPLIT-PICKING-BADGE-VISIBILITY-FIX-DEV-20260707-033C]
  // 송장 수량분할 — 원송장 잔여수량 반영. quantity=잔여(현재 송장 포장수량), original_quantity=원수량,
  // qty_split_allocated=다른 송장으로 분할된 합계. has_qty_split=분할 있음(배지 표시용).
  original_quantity?: number | null;
  qty_split_allocated?: number | null;
  has_qty_split?: boolean;
  // [ERP-PICKING-NON-STOCK-PART-SCAN-BYPASS-HOTFIX-20260619-001]
  is_non_stock_part?: boolean;      // true 면 재고미관리 부품 — 스캔/차감 불필요, 완료 차단 없음
  display_status?: string | null;   // "송장쪼개기됨" 등 표시 라벨
  // [ERP-INVOICE-SPLIT-ORIGINAL-ORDER-MOVED-OUT-VISIBILITY-20260601-001]
  // moved_out 항목의 분할자식 송장/Export 상태 (원송장 포장 화면 표시용).
  split_child_invoice_no?: string | null;
  split_child_export_status?: string | null; // "별도송장 {inv}" | "Export 완료 · 송장 대기" | "Export 대기"
}

// [ERP-PACKING-MULTI-ORDER-SAME-INVOICE-FIX-20260528-001]
// 합포장 (같은 invoice_no 공유 N주문) 정보 — find-by-invoice 응답에 동봉.
export interface PackingOrderSibling {
  order_no: string;
  order_id?: number;
  // [FW-040] 이 주문의 출고 구성 토큰 — mark-packed 에 그대로 되돌려 보낸다.
  sku_change_token?: string | null;
  receiver_name?: string | null;
  order_status?: string | null;
  packing_request_note?: string | null;
  has_packing_request?: boolean;
  packing_request_updated_at?: string | null;
  packing_request_updated_by_name?: string | null;
  packing_request_completed_at?: string | null;
  packing_request_completed_by_name?: string | null;
}

export interface PackingOrder {
  order_no: string;
  invoice_no: string | null;
  order_status: string | null;
  order_type: string;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  paid_amount: number | null;
  shipping_fee: number | null;
  platform: string | null;
  platform_store_name: string | null;
  is_exchange: boolean;
  memo: string | null;
  is_daiwa_direct_ship: boolean;
  daiwa_fulfillment_choice?: string | null;
  // [ORDER-003 Phase 5] 표시용 출처·수령 방법(read-only · additive).
  order_source?: string | null;
  receipt_method?: ReceiptMethod | null;
  // 26차 후속 #83 Phase 2 — ShipmentPick 기반 포장 완료 분리 (optional/additive).
  packing_status?: "pending" | "completed" | null;
  packing_completed_at?: string | null;
  // [PACKING-TODAY-INVOICE-REGISTERED-DATE-INCLUSION-20260922] additive.
  //   포장출고 목록은 `주문 생성일 OR 송장 등록일`이 선택일인 주문을 담는다.
  //   *_date_kst 는 서버가 KST 로 확정한 YYYY-MM-DD (클라이언트 timezone 해석 차이 제거).
  created_at?: string | null;
  invoice_registered_at?: string | null;
  created_date_kst?: string | null;
  invoice_registered_date_kst?: string | null;
  // [ERP-PACKING-REQUEST-NOTE-COLUMNS-AND-HIGHLIGHT-20260527-001]
  packing_request_note?: string | null;
  has_packing_request?: boolean;
  packing_request_updated_at?: string | null;
  packing_request_updated_by_name?: string | null;
  // [ERP-PACKING-REQUEST-TOP-BANNER-COMPLETE-AND-MANUAL-FINISH-20260528-001]
  packing_request_completed_at?: string | null;
  packing_request_completed_by_name?: string | null;
  // [ERP-PACKING-MULTI-ORDER-SAME-INVOICE-FIX-20260528-001]
  // 같은 invoice_no 를 공유하는 합포장 주문이 N건이면 multi_order=true + orders[N] 동봉.
  multi_order?: boolean;
  orders?: PackingOrderSibling[];
  // [FW-040] 조회 시점 출고 구성 토큰(활성 SKU 변경 지문). 포장 완료 시 mark-packed 로 보내 서버 현재값과 대조.
  sku_change_token?: string | null;
  // [ERP-ORDER-COMBINE-SHIPPING-PICKING-INTEGRATION-20260602-001] 합배송 그룹 연동(Phase 2).
  combined_shipping?: CombinedShippingPackingInfo | null;
  items: PackingOrderItem[];
}

export interface CombinedShippingPackingMember {
  order_no: string;
  role: string;
  payment_required: boolean;
  payment_confirmed: boolean;
  receiver_match_status: string;
  include_in_packing: boolean;
}
export interface CombinedShippingPackingInfo {
  is_combined: boolean;
  group_no: string;
  status: string;
  representative_order_no: string;
  representative_invoice_no: string | null;
  shipping_fee_policy: string;
  warnings: string[];
  members: CombinedShippingPackingMember[];
}

export function getPackingTodayOrders(params?: { review_date?: string }): Promise<{
  date: string;
  /** 목록에 담긴 **주문 수**(송장 수 아님). */
  total: number;
  /**
   * [PACKING-TODAY-INVOICE-REGISTERED-DATE-INCLUSION-20260922] additive.
   * 선택일에 **송장이 등록된** 주문들의 고유·비어있지 않은 송장번호 수.
   *   · 한 송장 2주문(합배송) = 송장 1건
   *   · 송장 없는 AS 자가수리 = 제외
   *   · 포장완료 주문도 선택일 등록이면 포함(대기 주문 수에는 미포함)
   */
  registered_invoice_count?: number;
  orders: PackingOrder[];
  /** [ORDER-003 Phase 5] 택배 지정 POS 주문 중 포장 대상에서 빠진 주문 + 사유(read-only · additive). */
  pos_courier_attention?: PosCourierAttention[];
}> {
  const qs = new URLSearchParams();
  if (params?.review_date) qs.set("review_date", params.review_date);
  return request(`/orders/packing/today${qs.toString() ? "?" + qs : ""}`);
}

export function findPackingOrder(q: string): Promise<PackingOrder> {
  return request(`/orders/packing/find?q=${encodeURIComponent(q)}`);
}

/** 송장번호로 주문 조회 */
export function findPackingOrderByInvoice(invoiceNo: string): Promise<PackingOrder> {
  return request(`/orders/packing/find-by-invoice?invoice_no=${encodeURIComponent(invoiceNo)}`);
}

export interface PackingSkuLocationInfo {
  location_code: string;
  quantity: number;
}

export function getPackingSkuLocations(skus: string[]): Promise<Record<string, PackingSkuLocationInfo[]>> {
  if (skus.length === 0) return Promise.resolve({});
  return request(`/orders/packing/sku-locations?skus=${skus.join(",")}`);
}

// 26차 후속 #82 회귀 보강 — 전량 스캔 완료 시 서버에 포장완료를 마킹한다.
// idempotent 하므로 같은 주문에 중복 호출되어도 안전.
// [PACKING-COMPLETION-SAFETY-GUARDS-DEV-20260922]
//   `invoiceNo` 는 레거시 호환 파라미터일 뿐 서버가 저장/판정에 쓰지 않는다.
//   포장 기록 송장은 항상 서버가 DB 의 Order.invoice_no 를 다시 읽어 결정하며,
//   송장 미등록이면 409 PACKING_INVOICE_NOT_REGISTERED 로 거부된다.
//   재고 부족이면 409 PACKING_STOCK_SHORTAGE(부족 품목 전량 동봉)로 전체 거부된다.
export function markPackedForOrder(
  orderNo: string,
  actor?: string,
  invoiceNo?: string,
  // [FW-040] 포장 화면이 조회한 출고 구성 토큰 {order_no: token} — 합포장이면 같은 송장의 모든 주문.
  //   하나라도 서버 현재값과 다르면 어느 주문도 쓰기 전에 409 PACKING_COMPOSITION_CHANGED.
  skuChangeTokens?: Record<string, string>,
): Promise<{
  ok: boolean;
  order_no: string;
  packing_status: "completed";
  packing_completed_at: string | null;
}> {
  const qs = new URLSearchParams();
  if (actor) qs.set("actor", actor);
  if (invoiceNo) qs.set("invoice_no", invoiceNo);
  if (skuChangeTokens && Object.keys(skuChangeTokens).length > 0) {
    qs.set("sku_change_tokens", JSON.stringify(skuChangeTokens));
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return request(`/orders/${encodeURIComponent(orderNo)}/mark-packed${suffix}`, {
    method: "POST",
  });
}

export function reduceInventoryForPacking(params: {
  order_no: string;
  sku: string;
  location_code: string;
  quantity?: number;
  actor?: string;
}): Promise<{ ok: boolean; sku: string; location_code: string; quantity_deducted: number; remaining_qty: number; skipped_inventory?: boolean; skipped_reason?: string | null; skipped_sku?: string | null }> {
  const qs = new URLSearchParams();
  qs.set("sku", params.sku);
  qs.set("location_code", params.location_code);
  if (params.quantity) qs.set("quantity", String(params.quantity));
  if (params.actor) qs.set("actor", params.actor);
  return request(`/orders/${encodeURIComponent(params.order_no)}/reduce-inventory?${qs}`, {
    method: "POST",
  });
}

// [ERP-PICKING-SET-PARENT-SKU-SCAN-NOT-FOUND-HOTFIX-20260619-001]
// 세트상품 완료 차감 — 대표 SKU 가 아니라 구성품 SKU 를 SELLABLE 위치 자동선택·합산 차감.
// 구성품 스캔이 모두 완료(required 충족)된 후에만 호출.
export function reduceSetComponentsForPacking(params: {
  order_no: string;
  bundle_sku: string;
  quantity?: number;
  actor?: string;
}): Promise<{
  ok: boolean;
  bundle_sku: string;
  quantity: number;
  is_set: boolean;
  components: number;
  deductions: { sku: string; location: string; quantity: number; before: number; after: number }[];
}> {
  const qs = new URLSearchParams();
  qs.set("bundle_sku", params.bundle_sku);
  if (params.quantity) qs.set("quantity", String(params.quantity));
  if (params.actor) qs.set("actor", params.actor);
  return request(`/orders/${encodeURIComponent(params.order_no)}/reduce-inventory-set?${qs}`, {
    method: "POST",
  });
}

// [ERP-PACKING-BARCODE-REGISTRATION-POPUP-BLOCK-20260527-001]
// 포장출고 스캔 fallback — 스캔값이 ERP 상품DB 에 등록되어 있는지 판정.
// exact match (barcode OR sku). DB 무변경 read-only.
export interface ProductBarcodeLookupResponse {
  found: boolean;
  sku: string | null;
  product_name: string | null;
  barcode: string | null;
  // [ERP-STORE-BARCODE-SCAN-AUTO-LISTUP-DEV-20260626-001] /store 자동 리스트업 보강(옵셔널, 하위호환).
  match_count?: number;          // exact 매칭 건수 (0/1/2+)
  conflict?: boolean;            // 2건 이상이면 true → 자동추가 차단
  product_id?: number;
  barcode_product_name?: string | null; // = Product.barcode_label_name
  option1?: string | null;
  option2?: string | null;
  option3?: string | null; // [FW-032] additive
  selling_price?: number | null; // smartstore 기준 판매가
  is_active?: boolean;
}

export function lookupProductByBarcode(code: string): Promise<ProductBarcodeLookupResponse> {
  const qs = new URLSearchParams({ code });
  return request<ProductBarcodeLookupResponse>(`/products/lookup-barcode?${qs}`, {
    method: "GET",
  });
}

// [ERP-PRODUCT-BULK-INVENTORY-ADJUSTMENT-20260527-001]
// 선택 상품 일괄 재고 조정 (set_absolute) — Preview + Apply 두 단계.
export interface BulkInventoryAdjustItem {
  sku: string;
  product_name: string | null;
  location_code: string;
  current_quantity: number;
  target_quantity: number;
  delta: number;
  action: "update" | "no_change" | "blocked";
  warnings: string[];
}

export interface BulkInventoryAdjustSummary {
  total: number;
  will_update: number;
  no_change: number;
  blocked: number;
}

export interface BulkInventoryAdjustPreviewResponse {
  items: BulkInventoryAdjustItem[];
  summary: BulkInventoryAdjustSummary;
}

export interface BulkInventoryAdjustApplyResponse {
  items: BulkInventoryAdjustItem[];
  summary: BulkInventoryAdjustSummary;
  applied_count: number;
}

export function previewBulkInventoryAdjustment(body: {
  skus: string[];
  location_code: string;
  target_quantity: number;
  reason: string;
}): Promise<BulkInventoryAdjustPreviewResponse> {
  return request<BulkInventoryAdjustPreviewResponse>("/products/inventory-adjustments/preview", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function applyBulkInventoryAdjustment(body: {
  skus: string[];
  location_code: string;
  target_quantity: number;
  reason: string;
}): Promise<BulkInventoryAdjustApplyResponse> {
  return request<BulkInventoryAdjustApplyResponse>("/products/inventory-adjustments/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function previewBulkImport(file: File): Promise<{
  count: number;
  headers: string[];
  rows: Record<string, string>[];
}> {
  const fd = new FormData();
  fd.append("file", file);
  // FormData 는 Content-Type 을 브라우저가 boundary 와 함께 붙여야 해서 request() 를 쓰지 않는다.
  // [ETC-001-PHASE4B3A] 다만 실패 status 를 잃지 않도록 ApiError 로 변환한다 —
  // 이전 구현은 응답 본문만 Error 로 감싸서 403 을 파싱 오류와 구분할 수 없었다.
  const res = await fetch(`${API_URL}/products/bulk-import/preview`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!res.ok) {
    const raw = await res.text();
    let detail: unknown = raw;
    let msg = raw;
    try {
      const parsed = JSON.parse(raw);
      detail = parsed?.detail ?? parsed;
      if (typeof detail === "string") msg = detail;
      else if (detail && typeof (detail as { message?: unknown }).message === "string") {
        msg = String((detail as { message: string }).message);
      }
    } catch {
      /* JSON 이 아니면 원문을 그대로 쓴다 */
    }
    throw new ApiError(`[${res.status}] ${msg}`, res.status, null, detail);
  }
  return res.json();
}

export async function confirmBulkImport(rows: Record<string, string>[]): Promise<{
  created: number;
  skus: string[];
  errors: { row: number; sku?: string; error: string }[];
}> {
  return request("/products/bulk-import/confirm", {
    method: "POST",
    body: JSON.stringify(rows),
  });
}

// ─────────────────────────────────────────────
// [ETC-001-PHASE4B3A] 본인 업무(action) 권한 조회
// 표시 전용 · DB write 0. enforcement_active=true 인 code 에만 UI 를 맞춘다.
// ─────────────────────────────────────────────

export type MyActionPermission = {
  code: string;
  label: string;
  group: string;
  risk: string;
  recommended_level: string | null;
  override: "grant" | "deny" | null;
  resolved_allowed: boolean;
  source: "admin" | "grant" | "deny" | "level";
  /** 이 code 가 실제 차단에 쓰이는가. false 면 UI 를 막으면 안 된다. */
  enforcement_active: boolean;
};

export type MyActionPermissions = {
  user_id: number;
  role: string;
  permission_level: string | null;
  is_admin: boolean;
  enforcement_active: boolean;
  enforced_codes: string[];
  actions: MyActionPermission[];
};

export function getMyActionPermissions(): Promise<MyActionPermissions> {
  return request<MyActionPermissions>("/me/action-permissions");
}

// ─────────────────────────────────────────────
// 번들 구성
// ─────────────────────────────────────────────

export interface BundleComponent {
  id: number;
  bundle_sku: string;
  component_sku: string;
  component_name: string;
  quantity: number;
  component_type: "set_part" | "free_gift";
  memo?: string | null;
  // 26차 후속 #39 — 구성품 상세 (백엔드 응답 확장)
  image_url?: string | null;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
  product_status?: string | null;
  sale_price?: number | null;
  stock_total?: number | null;
  // [PACKING-001][Feature A1] 세트 구성품 bulk 표시 보강(포장출고 화면).
  //   available_stock: 가용재고(SELLABLE_LOCATIONS 합) — 전 위치 합(stock_total)과 다름.
  //   last_inbound_at: SKU별 최근입고일 ISO (없으면 null → 배지 숨김).
  //   barcode_product_name: 구성품 포장상품명(Product.barcode_label_name) — 표시 우선.
  available_stock?: number | null;
  last_inbound_at?: string | null;
  barcode_product_name?: string | null;
}

export function getBundleComponents(sku: string): Promise<BundleComponent[]> {
  return request<BundleComponent[]>(`/products/${sku}/bundle-components`);
}

// [PACKING-001][Feature A1] 세트(대표) SKU 목록의 구성품 정보 bulk 조회(구성품 N+1 금지).
// 반환 = {bundle_sku: BundleComponent[]}. 각 구성품에 available_stock/last_inbound_at/barcode_product_name 포함.
export function getPackingSetComponentInfo(
  skus: string[],
): Promise<Record<string, BundleComponent[]>> {
  if (skus.length === 0) return Promise.resolve({});
  return request<Record<string, BundleComponent[]>>(
    `/orders/packing/set-component-info?skus=${skus.map(encodeURIComponent).join(",")}`,
  );
}

// ─────────────────────────────────────────────
// [PACKING-001][Phase B1/B2/B3] SKU 입고요청 — 상태 판정 + 원장
// ─────────────────────────────────────────────
// [PACKING-002] 미종결 PO(모든 vendor, 완료·취소 제외)는 수량·SKU별 입고승인과 무관하게 전부 차단(is_blocking=true).
export interface InboundRelatedPo {
  po_id: number;
  po_no: string;
  status: string;
  status_label: string;
  vendor_type: string;
  // [PURCHASE-004 2026-10-21] PO 출처 — "supplier_excel" = 공급사 Excel 자동 PO · null = 수기 PO.
  origin?: string | null;
  quantity_ordered: number;
  is_blocking: boolean;
}
// [PACKING-002] 실시간 가용재고(SELLABLE 합, 세트=구성품 기준) + 위치별 수량 — 재고 SoT 조회 시점 값.
export interface InboundStockLocation {
  location_code: string;
  label: string;
  quantity: number;
  sellable: boolean;
}
export interface InboundStockDetail {
  available_total: number;
  is_bundle: boolean;
  by_location: InboundStockLocation[];
}
// [PACKING-002] SKU 입고이력 이벤트 — 재고 SoT(stock_movements) 반영 시각·수량, 정정은 원기록과 분리.
export interface InboundHistoryAdjustment {
  at: string;
  source_type: string;
  label: string;
  delta: number;
  memo: string | null;
}
export interface InboundHistoryEvent {
  key: string;
  kind: "po" | "receipt";
  ref: string | null;
  po_no: string | null;
  po_status: string | null;
  vendor_type: string | null;
  // [PURCHASE-004 2026-10-21] PO 출처 — "supplier_excel" = 공급사 Excel 자동 PO · null = 수기 PO(표시 전용).
  po_origin?: string | null;
  // [PACKING-002] 실제 입고 이벤트(purchase_order/purchase_receipt)일 때만 inbound_at·source_type 존재.
  is_inbound: boolean;
  source_type: string | null;
  inbound_at: string | null;
  event_at: string;
  original_qty: number;
  adjustments: InboundHistoryAdjustment[];
  net_qty: number;
  corrected: boolean;
  fully_reversed: boolean;
  placement: { quantity: number; last_at: string | null; locations: Record<string, number> } | null;
  record_deleted: boolean;
  placement_only: boolean;
}
export interface InboundHistoryPage {
  sku: string;
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  items: InboundHistoryEvent[];
}
export interface InboundActiveRequest {
  id: number;
  status: string;
  requested_at: string | null;
  requested_by: string | null;
  request_source: string;
}
// [PACKING-001][반려·지속차단]
export interface InboundRejectionBlock {
  id: number;
  rejection_code: string | null;
  rejection_label: string | null;
  reason: string | null;
  rejected_by: number | null;
  rejected_at: string | null;
}
export interface InboundBlockStatus {
  sku: string;
  can_request: boolean;
  blocked: boolean;
  block_reason: string | null;
  vendor_scope: string;
  active_request: InboundActiveRequest | null;
  related_pos: InboundRelatedPo[];
  rejection_block: InboundRejectionBlock | null;
  // [PURCHASE-004 2026-10-21] 서버 canonical 공급사 관리 SKU 판정(브랜드 윤성 시마노·선라인/다이와 ∪ 공급사 Excel 매칭 이력) —
  //   입고 요청은 가능 · PO추가(수기 PO)만 불가. 두 공급사 동시 = supplier_conflict(공급사 미결정).
  supplier_vendor?: string | null;
  supplier_label?: string | null;
  supplier_conflict?: boolean;
  // [PACKING-002] opt-in(include_stock / include_history) 일 때만 존재.
  stock?: InboundStockDetail | null;
  inbound_history?: InboundHistoryPage;
}
// 반려 사유코드 옵션(요청함 UI 셀렉트). backend REJECTION_CODES 와 동일.
export const INBOUND_REJECTION_OPTIONS: { code: string; label: string; autoBlock: boolean }[] = [
  { code: "discontinued", label: "단종", autoBlock: true },
  { code: "supply_ended", label: "공급처 취급 종료", autoBlock: true },
  { code: "not_orderable", label: "발주 대상 아님", autoBlock: true },
  { code: "duplicate", label: "중복·오요청", autoBlock: false },
  { code: "other", label: "기타", autoBlock: false },
];
export type InboundRequestSource = "packing" | "products";
export interface SkuInboundRequestRow {
  id: number;
  sku: string;
  status: string;
  request_source: string;
  requested_qty: number | null;
  requested_by_name_snapshot: string | null;
  requested_at: string | null;
  linked_po_id: number | null;
  linked_po_no: string | null;
}

// 표시 중인 SKU 들의 [입고 요청] 가능/비활성 상태 bulk 1회 조회(버튼별 N+1 금지).
export function getInboundBlockStatus(
  skus: string[],
): Promise<Record<string, InboundBlockStatus>> {
  if (skus.length === 0) return Promise.resolve({});
  return request<Record<string, InboundBlockStatus>>(
    `/sku-inbound-requests/block-status?skus=${skus.map(encodeURIComponent).join(",")}`,
  );
}

// [PACKING-002] SKU 1개의 입고요청 상태 + 실시간 재고(위치별) + 입고이력(최근 N건, offset 더보기).
//   입고요청함·포장출고·상품및재고가 같은 조회 계약(block-status opt-in)을 쓴다.
export function getSkuInboundInsight(
  sku: string,
  opts?: { historyLimit?: number; historyOffset?: number },
): Promise<InboundBlockStatus | undefined> {
  const qs = new URLSearchParams({
    skus: sku,
    include_stock: "true",
    include_history: "true",
    history_limit: String(opts?.historyLimit ?? 20),
    history_offset: String(opts?.historyOffset ?? 0),
  });
  return request<Record<string, InboundBlockStatus>>(
    `/sku-inbound-requests/block-status?${qs}`,
  ).then((m) => m[sku]);
}

// 단건 입고요청 생성. 이번 단계(B3)는 수량·PO 를 묻지 않음(source 만 구분).
export function createInboundRequest(body: {
  sku: string;
  request_source: InboundRequestSource;
  requested_qty?: number | null;
  memo?: string | null;
}): Promise<SkuInboundRequestRow> {
  return request<SkuInboundRequestRow>(`/sku-inbound-requests`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// [PACKING-001][Phase B4] 입고 요청함 — 목록/요약/PO 반영.
export interface SkuInboundRequestFull {
  id: number;
  sku: string;
  product_name_snapshot: string | null;
  option1_snapshot: string | null;
  option2_snapshot: string | null;
  barcode_label_snapshot: string | null;
  current_stock_snapshot: number | null;
  request_source: string;
  requested_qty: number | null;
  requested_by_name_snapshot: string | null;
  requested_at: string | null;
  memo: string | null;
  status: string;
  linked_po_id: number | null;
  linked_po_no: string | null;
  // [PACKING-001][반려·지속차단]
  rejection_code: string | null;
  rejection_label: string | null;
  block_active: boolean;
  unblocked_by_user_id: number | null;
  unblocked_at: string | null;
  unblock_reason: string | null;
  process_memo: string | null;
  processed_at: string | null;
  // [PURCHASE-004 2026-10-21] 공급사 관리 SKU metadata(서버 canonical) — 입고 요청함 공급사 영역 분리·배지용.
  supplier_vendor?: string | null;
  supplier_label?: string | null;
  supplier_conflict?: boolean;
  // [PACKING-002] enrich=true 일 때만 — 현재 상품 마스터 / 조회 시점 재고 SoT(snapshot 아님).
  product?: {
    exists: boolean;
    brand: string | null;
    image_url: string | null;
    product_name: string | null;
    barcode_label_name: string | null;
    option1: string | null;
    option2: string | null;
  };
  stock?: InboundStockDetail | null;
}
export interface InboundRequestSummary {
  pending: number;
  in_progress: number;
  open_total: number;
  today_completed: number;
}
export function listInboundRequests(params?: {
  status?: string; sku?: string; request_source?: string; block_active?: boolean; limit?: number; offset?: number;
  // [PACKING-002] 상품정보·실시간 재고 동봉 / 브랜드 → SKU 자연순(서버 공용 정렬).
  enrich?: boolean; sort?: "brand_sku";
  // [PURCHASE-004 2026-10-21] supplier = 공급사 관리 SKU 요청만 · general = 중국·일반 요청만(서버 canonical 판정).
  supplier_flow?: "supplier" | "general";
}): Promise<{ items: SkuInboundRequestFull[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.supplier_flow) qs.set("supplier_flow", params.supplier_flow);
  if (params?.enrich) qs.set("enrich", "true");
  if (params?.sort) qs.set("sort", params.sort);
  if (params?.status) qs.set("status", params.status);
  if (params?.sku) qs.set("sku", params.sku);
  if (params?.request_source) qs.set("request_source", params.request_source);
  if (params?.block_active != null) qs.set("block_active", String(params.block_active));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<{ items: SkuInboundRequestFull[]; total: number }>(
    `/sku-inbound-requests${qs.toString() ? "?" + qs : ""}`,
  );
}
// [PURCHASE-004 2026-10-21] 공급사 관리 요청 동기화(명시 write) — 공급사 발주 미입고에 같은 SKU 가 있으면 자동 완료.
// [FW-043] 실행 권한 = 업무 권한(공급사 요청 동기화) 보유자 또는 관리자 — 서버가 강제.
export function syncSupplierInboundRequests(): Promise<{
  count: number; completed: { request_id: number; sku: string; vendor_code: string; supplier_order_line_id: number }[];
}> {
  return request(`/sku-inbound-requests/supplier-sync`, { method: "POST" });
}
// [PURCHASE-004 2026-10-21] 미등록 SKU 재매칭(새로고침 · 로그인 직원) — 원래 자동 PO 에만 연결 · 안전하지 않으면 blocked 사유.
export interface SupplierRematchResult {
  vendor_code: string; matched_lines: number; matched_direct: number; still_unmatched: number;
  attached: { shipment_id: number; sku: string; qty: number; basis_date: string; po_id: number; po_no: string }[];
  blocked: { shipment_id: number; sku: string; qty: number; basis_date: string; po_no: string | null; po_status?: string | null; reason: string }[];
  completed_requests: { request_id: number; sku: string; vendor_code: string; supplier_order_line_id: number }[];
}
export function rematchSupplierSkus(vendorCode: string): Promise<SupplierRematchResult> {
  return request(`/purchase/supplier-excel/rematch-skus`, { method: "POST", body: JSON.stringify({ vendor_code: vendorCode }) });
}
export function getInboundRequestSummary(): Promise<InboundRequestSummary> {
  return request<InboundRequestSummary>(`/sku-inbound-requests/summary`);
}
// [PACKING-001][반려·지속차단] OPEN 요청 반려(처리자 = 업무 권한 보유자 또는 관리자 · FW-043). 'other' 는 persist_block 명시.
export function rejectInboundRequest(id: number, body: {
  rejection_code: string; reason?: string | null; persist_block?: boolean | null;
}): Promise<SkuInboundRequestFull> {
  return request<SkuInboundRequestFull>(`/sku-inbound-requests/${id}/reject`, {
    method: "POST", body: JSON.stringify(body),
  });
}
// 지속차단 반려의 재요청 차단 해제(처리자 = 업무 권한 보유자 또는 관리자 · FW-043). 반려 이력 보존.
export function unblockInboundRequest(id: number, body?: { reason?: string | null }): Promise<SkuInboundRequestFull> {
  return request<SkuInboundRequestFull>(`/sku-inbound-requests/${id}/unblock`, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}
// OPEN 요청 다건을 처리자(업무 권한 보유자 또는 관리자 · FW-043)가 명시 선택한 하나의 PO 에 SKU별 확정수량으로 atomic 반영.
export function assignInboundRequestsToPo(body: {
  po_id: number;
  items: { request_id: number; quantity: number }[];
}): Promise<{
  po_id: number; po_no: string; created_item_count: number;
  created_items: { sku: string; quantity_ordered: number; unit_price_cny: number; unit_price_krw: number; total_price_krw: number }[];
  completed_requests: SkuInboundRequestFull[];
  po_totals: { total_item_count: number; total_quantity: number; total_price_krw: number };
}> {
  return request(`/sku-inbound-requests/assign-to-po`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface UsedInBundle {
  bundle_sku: string;
  bundle_name: string;
  quantity: number;
  component_type: "set_part" | "free_gift";
  memo?: string | null;
}

export function getUsedInBundles(sku: string): Promise<UsedInBundle[]> {
  return request<UsedInBundle[]>(`/products/${sku}/used-in-bundles`);
}

export function upsertBundleComponent(
  sku: string,
  body: { component_sku: string; quantity: number; component_type: string; memo?: string }
): Promise<{ ok: boolean }> {
  return request(`/products/${sku}/bundle-components`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteBundleComponent(
  sku: string,
  component_sku: string
): Promise<{ ok: boolean }> {
  return request(`/products/${sku}/bundle-components/${component_sku}`, {
    method: "DELETE",
  });
}

// 현재 로그인 유저 정보
export async function getCurrentUser() {
  return request<{
    id: number;
    name: string;
    role: string;
    permission_level?: string;
    company_id?: number | null;
    company_code?: string | null;
    company_name?: string | null;
  }>("/auth/me");
}

// [ERP-PO-SHARED-MUST-ADD-MEMO-CHECKLIST-AUDIT-20260616-001]
// role/permission_level → 유효 권한등급(lv2 이상 여부). backend effective_level 미러.
const _ROLE_DEFAULT_LEVEL: Record<string, string> = {
  admin: "lv3",
  inventory: "lv2",
  shipping: "lv2",
  as: "lv2",
  repair: "lv2",
  sales: "lv2",
  marketing: "lv1",
  store: "lv1",
  viewer: "lv1",
};
const _LEVEL_ORDER: Record<string, number> = { lv1: 1, lv2: 2, lv3: 3 };

export function userHasLevel(
  user: { role?: string; permission_level?: string } | null | undefined,
  minLevel: "lv1" | "lv2" | "lv3",
): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  const lv =
    user.permission_level && _LEVEL_ORDER[user.permission_level]
      ? user.permission_level
      : _ROLE_DEFAULT_LEVEL[user.role || ""] || "lv1";
  return (_LEVEL_ORDER[lv] || 1) >= (_LEVEL_ORDER[minLevel] || 1);
}

// [ERP-PRODUCT-LIST-SEARCH-STATE-PRESERVE-AFTER-EDIT-20260619-001]
// 상품 목록 복귀 URL(returnTo/from) 보안 검증 — 내부 /products 목록 경로만 허용.
// 허용: "/products", "/products?..." (검색/필터/페이지 query 포함)
// 차단: 외부 URL(http/https), protocol-relative(//), javascript: 등 scheme, 다른 내부 경로(/admin 등)
export function safeProductsReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let v: string;
  try {
    v = decodeURIComponent(raw);
  } catch {
    return null;
  }
  v = v.trim();
  if (!v) return null;
  if (v.startsWith("//")) return null;        // protocol-relative
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return null; // http:, https:, javascript:, data: 등 scheme
  if (v === "/products" || v.startsWith("/products?")) return v;
  return null;
}

// [PURCHASE-002-PHASE3][D1] 발주참고비고 = **시스템 파생 출처 이력**(자유 텍스트 아님).
//   `Product.purchase_note`(기존 SKU 매입 메모)와는 완전히 다른 값이며 서로 대체하지 않는다.
//   append-only — 수정·삭제 API 는 존재하지 않는다.
export interface PoItemProvenanceSummary {
  source_type: string;
  source_label: string;
  /** 화면 1줄 표시용. 이력이 없으면 "기존 데이터 · 출처 기록 없음"(추정하지 않음). */
  summary_text: string;
  history_count: number;
  quantity: number | null;
  source_po_no: string | null;
  move_reason_code: string | null;
  /** 사유 코드의 한글 라벨. 코드가 없으면 null. 화면은 이 값을 쓰고 코드를 해석하지 않는다. */
  move_reason_label: string | null;
  move_reason_detail: string | null;
  actor_user_id: number | null;
  occurred_at: string | null;
}

export interface PoItemProvenanceRow {
  id: number;
  source_type: string;
  source_label: string;
  quantity: number;
  sku_snapshot: string | null;
  source_po_id: number | null;
  source_po_no: string | null;
  source_po_item_id: number | null;
  source_request_id: number | null;
  move_reason_code: string | null;
  move_reason_label: string | null;
  move_reason_detail: string | null;
  created_by_user_id: number | null;
  created_at: string | null;
}

// [PURCHASE-002-PHASE3][D1] 품목 출처 **전체 이력** 조회(읽기 전용).
//   여러 번 이동한 품목은 PO 응답의 요약(현재 이유) 외에 전체 흐름을 볼 수 있어야 한다.
export interface PoMoveReasonOption {
  code: string;
  label: string;
  requires_detail: boolean;
}

export function getOrderItemProvenance(itemId: number): Promise<{
  item_id: number;
  sku: string | null;
  summary: PoItemProvenanceSummary;
  history: PoItemProvenanceRow[];
  /** 이동 사유 선택지(서버 SoT). 신규 endpoint 를 늘리지 않으려고 이 응답에 additive 로 얹었다. */
  move_reason_options: PoMoveReasonOption[];
}> {
  return request(`/purchase/order-items/${itemId}/provenance`);
}

export interface PoInboundItem {
  id: number;
  po_id: number;
  po_no: string;
  sku: string;
  product_name: string;
  // 26차 후속 #8(2026-04-24) — 한글 옵션(중문 아님) + SKU 입고완료 체크 상태
  option1?: string | null;
  option2?: string | null;
  // [Phase1 2026-06-15] 입고검수장 브랜드 정렬 + 썸네일 표시용 (Product 조인, 표시 전용)
  brand?: string | null;
  image_url?: string | null;
  // [PURCHASE-002-PHASE1] 입고검수장 바코드 상품명/옵션 (D11 / AC-15). Product 조인, 표시 전용.
  barcode_label_name?: string | null;
  barcode_option_name?: string | null;
  quantity_ordered: number;
  quantity_actual: number;
  quantity_moved: number;
  quantity_remaining: number;
  inbound_checked_at: string | null;
  inbound_checked_by_user_id: number | null;
  inbound_checked_by_user_name: string | null;
  // [ERP-PO-INBOUND-PARTIAL-STOCK-APPROVAL-DEV-20260702-018] SKU별 부분 승인 상태.
  // stock_approved_at 이 있으면 해당 SKU 는 ERP 재고 반영 완료(중복 승인 차단).
  stock_approved_at?: string | null;
  stock_approved_by_user_id?: number | null;
  stock_approved_by_user_name?: string | null;
  // 26차 후속 #9(2026-04-24) — 스태프가 지정한 이동 목적지(계획값).
  // 실제 재고 이동은 관리자 approve-stocked-in 시점에 커밋.
  planned_target_location_code: string | null;
  // [ERP-PO-INBOUND-STATION-SKU-MEMO-DEV-20260702-010] SKU별 입고 현장 메모.
  receiving_memo?: string | null;
  // 26차 후속 #10(2026-04-24) — 다중 목적지 분배 계획(allocations). 잔량을 쪼개서 보낼 때 사용.
  allocations: PoInboundAllocation[];
  allocated_qty: number;
  // [PURCHASE-001-FEATURE4-INLINE-CORRECTION-UX-DEV] 행별 정정 가능 여부 canonical 판정(additive read).
  //   프론트 추측 금지 — backend 가 정정 endpoint 와 동일 불변식으로 판정. 없을 수도 있어 optional.
  correction?: InboundCorrectionJudgment;
  // [FW-042] 미주문 표시(PC·iPad 공통) + [미주문 처리] 가능 여부 — backend 실행 가드와 같은 판정.
  not_ordered?: boolean;
  not_ordered_reason?: string | null;
  not_ordered_check?: { allowed: boolean; block_reasons: string[] };
  // [PURCHASE-002-PHASE3][D15] SKU 기본 입고 위치.
  //   default_location_code      = 이 SKU 를 **마지막으로 정상 승인**한 위치(= 목적지 prefill 값).
  //                                기록이 없으면 null — 프론트가 임의 추정하지 않는다.
  //   next_default_location_code = 분할 입고에서 사용자가 고른 "다음 기본 위치".
  //                                분배가 2곳 이상이면 이 값이 있어야 승인된다(backend 가 400 으로 차단).
  default_location_code?: string | null;
  next_default_location_code?: string | null;
  // [PURCHASE-003 Phase 1] iPad 입고장 additive read.
  //   po_vendor_type              = 소속 PO 공급사 구분(china_import | shimano | daiwa | consumables).
  //   version                     = 판단에 쓰는 품목·분배 상태의 토큰. `정상 입고` 전제조건으로 그대로 돌려보낸다.
  //   unresolved_exception_count  = 미해결(open·in_progress) 입고 예외 수. 1 이상이면 어떤 경로로도 재고 승인되지 않는다.
  po_vendor_type?: string | null;
  // [PURCHASE-004 Phase 3] 입고 흐름(china_import | supplier_excel:shimano | supplier_excel:daiwa) · provenance 배지
  po_receiving_flow?: string | null;
  po_receiving_flow_label?: string | null;
  po_arrived_at?: string | null;
  version?: string | null;
  unresolved_exception_count?: number;
  // [PURCHASE-003 Phase 2] iPad 검수 제출 상태. null = 미제출(기존 PC 흐름).
  //   submitted = 제출됨(관리자 포함 직접 수정 불가 — 반려로만 재개) · rejected = 반려됨(직원 재검수·재제출).
  inspection_status?: "submitted" | "rejected" | null;
  /** 미해결 예외 유형(damaged · hold · shortage 등) — 표시용. */
  unresolved_exception_types?: string[];
  submitted_at?: string | null;
  submitted_by_user_name?: string | null;
  last_reject_reason?: string | null;
  last_rejected_at?: string | null;
  last_rejected_by_name?: string | null;
  // [PURCHASE-003 현장 UX] 검수 경로(tablet = iPad 경로 업무 write 있음 → 검수 제출 뒤에만 승인) · 담당자 인계 · 바코드.
  inspection_channel?: "tablet" | null;
  submit_required_for_approval?: boolean;
  open_handoff?: InboundHandoff | null;
  recent_handoffs?: InboundHandoff[];
  barcode?: string | null;
}

/** [PURCHASE-003 현장 UX] 문제 발견·담당자 인계(수량·예외·재고 불변). */
export type InboundHandoffReason = "shortage" | "overage" | "wrong_item" | "damaged" | "hold" | "other";
export interface InboundHandoff {
  id: number;
  po_inbound_item_id: number | null;
  po_id: number;
  po_no: string;
  sku: string;
  reason_code: InboundHandoffReason;
  reason_label: string;
  memo: string | null;
  status: "open" | "resolved" | "cancelled";
  resolution: "exception_registered" | "confirmed_normal" | "cancelled" | null;
  linked_exception_id: number | null;
  created_by_user_id: number | null;
  created_by_name: string;
  created_at: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
}

export interface InboundCorrectionJudgment {
  gate_ok: boolean;               // china_import + arrived_in_korea (정정/예외 대상 여부)
  correctable: boolean;           // 직접 정정 가능(완전 미처리) — 분배·체크·이동·승인 이력 없음
  block_reasons: string[];        // correctable=false 일 때 정확한 차단 사유(한글)
  po_item_match_count: number;    // 동일 PO 내 SKU 발주품목 수(0/2+ 이면 발주수량·SKU 정정 모호)
  /** [PURCHASE-003 현장 UX] 막는 사유가 목적지 계획뿐 — 확인창 동의(clear_plan_confirmed) 후 계획 정리 + 예외 등록 가능. */
  plan_clearable?: boolean;
}

export interface PoInboundAllocation {
  id: number;
  target_location_code: string;
  planned_qty: number;
}

export function getPoInboundItems(
  poId?: number,
): Promise<{ items: PoInboundItem[]; unregistered_items?: UnregisteredItem[] }> {
  const qs = poId ? `?po_id=${poId}` : "";
  return request(`/purchase/inbound-items${qs}`);
}

export function adjustInboundQuantity(itemId: number, quantityActual: number): Promise<{ ok: boolean }> {
  return request(`/purchase/inbound-items/${itemId}/adjust`, {
    method: "PATCH",
    body: JSON.stringify({ quantity_actual: quantityActual }),
  });
}

export function moveInboundItem(
  itemId: number,
  body: { to_location_code: string; quantity: number; moved_by?: string }
): Promise<{ ok: boolean; all_moved: boolean }> {
  return request(`/purchase/inbound-items/${itemId}/move`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ── [PURCHASE-001-CHINA-INBOUND-CORRECTION-EXCEPTION-DEV] ────────────────
// 중국발주(china_import) 샵입고진행중 입고 정정 · 예외 관리 (관리자 전용).
export interface InboundCorrectionResult {
  ok: boolean;
  inbound_item_id: number;
  po_id: number;
  sku: string | null;
  field: string;
  old_value: unknown;
  new_value: unknown;
  incoming_before: number | null;
  incoming_after: number | null;
}

export interface InboundException {
  id: number;
  po_id: number;
  po_no: string;
  po_inbound_item_id: number | null;
  // [FW-042] 실제 값 집합(backend EXCEPTION_TYPES · DB CHECK ck_po_inbound_exceptions_type)과 맞춘다 — 종전 2종 선언은 stale.
  exception_type: "wrong_item" | "shortage" | "not_shipped" | "overage" | "qty_misentry"
    | "content_correction" | "damaged" | "hold" | "not_ordered";
  expected_sku: string | null;
  actual_sku: string | null;
  resolution_path: "return_to_vendor" | "compensation" | "additional_inbound" | "accept_wrong_item" | "discard" | null;
  status: "open" | "in_progress" | "resolved";
  problem_qty: number;
  resolved_qty: number;
  remaining_qty: number;
  memo: string | null;
  created_by_user_id: number | null;
  resolved_by_user_id: number | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

// 1. 실제 입고수량 정정 (수량 오입고) — 발주수량 불변.
export function correctInboundActualQty(itemId: number, quantityActual: number, reason: string): Promise<InboundCorrectionResult> {
  return request(`/purchase/inbound-items/${itemId}/correct-actual-qty`, {
    method: "POST",
    body: JSON.stringify({ quantity_actual: quantityActual, reason }),
  });
}

// 2. ERP 발주수량 오입력 정정 — 발주품목·입고장·입고대기재고 동기.
export function correctInboundOrderedQty(itemId: number, quantityOrdered: number, reason: string): Promise<InboundCorrectionResult> {
  return request(`/purchase/inbound-items/${itemId}/correct-ordered-qty`, {
    method: "POST",
    body: JSON.stringify({ quantity_ordered: quantityOrdered, reason }),
  });
}

// 3. ERP SKU 오입력 정정 — 구 SKU 차감 · 신 SKU 증가.
export function correctInboundSku(itemId: number, newSku: string, reason: string): Promise<InboundCorrectionResult> {
  return request(`/purchase/inbound-items/${itemId}/correct-sku`, {
    method: "POST",
    body: JSON.stringify({ new_sku: newSku, reason }),
  });
}

// 4. 예외 등록 (오입고/누락) — expected SKU 실입고수량·재고 감산.
export function createInboundException(
  itemId: number,
  body: { exception_type: "wrong_item" | "shortage"; problem_qty: number; actual_sku?: string | null; memo?: string | null; reason: string }
): Promise<InboundException> {
  return request(`/purchase/inbound-items/${itemId}/exceptions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 5. 예외 목록.
export function listInboundExceptions(poId: number): Promise<InboundException[]> {
  return request(`/purchase/orders/${poId}/exceptions`);
}

// ══════════════════════════════════════════════════════════════════
// [PURCHASE-002-PHASE5][D9] 미등록 품목 — SKU 를 만들지 않는 PO 전용 품목
//
// 조회는 별도 endpoint 가 없다 — PO 상세(`getPurchaseOrder`)와 입고검수장
// (`getPoInboundItems`) 응답에 additive 로 실려 온다.
// write 4종은 전부 action code 로 강제된다(403).
// ⚠️ 권한 code 문자열은 화면 컴포넌트에만 두고 이 파일에는 넣지 않는다(ETC-001 규칙).
// ══════════════════════════════════════════════════════════════════

export interface UnregisteredItem {
  id: number;
  purchase_order_id: number;
  unregistered_no: string;
  display_name: string;
  ezytao_name: string | null;
  english_name: string | null;
  customs_hs_code: string | null;
  option_color: string | null;
  option_size: string | null;
  spec_note: string | null;
  quantity_ordered: number;
  unit_label: string | null;
  unit_price_cny: number;
  /** backend 산출값 — 프론트가 다시 계산하지 않는다. */
  total_price_cny: number;
  total_price_krw: number;
  image_url: string | null;
  purchase_url: string | null;
  supplier_name: string | null;
  china_tracking_no: string | null;
  memo: string | null;
  /** 입고 기록. ⚠️ ERP 재고와 무관하다. */
  quantity_actual: number;
  received_location_code: string | null;
  /** backend 가 사실로 내려준다 — 화면이 추측하지 않는다. 항상 false. */
  affects_inventory: boolean;
  /** 이지타오 Export 필수값 중 비어 있는 것. 있으면 Export 가 차단된다. */
  export_missing_fields: string[];
  export_blocked: boolean;
  delete_block_reasons: string[];
  deletable: boolean;
  created_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  /** 입고검수장 응답에서만 채워진다. */
  po_id?: number;
  po_no?: string;
}

export interface UnregisteredItemPayload {
  display_name?: string;
  quantity_ordered?: number;
  unit_price_cny?: number;
  ezytao_name?: string | null;
  english_name?: string | null;
  customs_hs_code?: string | null;
  option_color?: string | null;
  option_size?: string | null;
  spec_note?: string | null;
  unit_label?: string | null;
  image_url?: string | null;
  purchase_url?: string | null;
  supplier_name?: string | null;
  china_tracking_no?: string | null;
  memo?: string | null;
  /** 입고 기록 — 재고를 바꾸지 않는다. */
  quantity_actual?: number;
  received_location_code?: string | null;
  /** 내용 필드를 바꿀 때 필수. 입고 기록만 바꿀 때는 불필요. */
  reason?: string;
}

export interface UnregisteredItemResult {
  ok: boolean;
  purchase_order_id: number;
  po_no: string;
  item: UnregisteredItem;
  po_total_price_krw: number;
  po_total_quantity: number;
  /** 내용 정정으로 생성된 content_correction 예외 id. */
  exception_id: number | null;
}

export interface UnregisteredItemDeleteResult {
  ok: boolean;
  unregistered_item_id: number;
  purchase_order_id: number;
  unregistered_no: string;
  delete_reason: string;
  po_total_price_krw_before: number;
  po_total_price_krw_after: number;
}

export function createUnregisteredItem(
  poId: number, payload: UnregisteredItemPayload,
): Promise<UnregisteredItemResult> {
  return request(`/purchase/orders/${poId}/unregistered-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** 내용 정정 또는 입고 기록. 전달한 필드만 바뀐다. */
export function updateUnregisteredItem(
  itemId: number, payload: UnregisteredItemPayload,
): Promise<UnregisteredItemResult> {
  return request(`/purchase/unregistered-items/${itemId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** soft delete. 사유 필수 · 처리 이력이 있으면 backend 가 400 으로 막는다. */
export function deleteUnregisteredItem(
  itemId: number, reason: string,
): Promise<UnregisteredItemDeleteResult> {
  return request(`/purchase/unregistered-items/${itemId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
}

/** 다른 PO 로 이동. 사유는 등록 품목과 같은 고정 6종이며 backend 가 다시 검증한다. */
export function moveUnregisteredItem(
  itemId: number,
  body: { target_po_id: number; move_reason_code: string; move_reason_detail?: string | null },
): Promise<UnregisteredItemResult> {
  return request(`/purchase/unregistered-items/${itemId}/move`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ══════════════════════════════════════════════════════════════════
// [PURCHASE-002-PHASE4] 통합 정정·예외 + 수입신고 조정 참고액
//
// 화면은 [정정·예외] 버튼 하나와 모달 하나다. 비삭제 5유형은 **API 도 하나**이고
// backend 가 한 트랜잭션에서 처리한다 — 프론트가 여러 호출을 순서대로 보내
// 원자성을 흉내 내지 않는다(예외·재고·재등록·조정 참고액은 전부 성사되거나 전부 사라진다).
// ══════════════════════════════════════════════════════════════════

export type PoCorrectionKind =
  | "not_shipped"    // 미발송
  | "shortage"       // 수량 누락
  | "overage"        // 수량 과다
  | "wrong_item"     // 다른 옵션 오입고
  | "qty_misentry"   // 수량 오입력
  | "damaged"        // [PURCHASE-003 Phase 2] 파손 — 수량 분리 · 미해결
  | "hold"           // [PURCHASE-003 Phase 2] 보류 — 수량 분리 · 미해결
  | "not_ordered";   // [FW-042] 미주문 — 발주하지 않은 품목 정리(PO 합계 제외 · 재고 변동 0 승인)

export interface PoItemCorrectionPayload {
  exception_type: PoCorrectionKind;
  reason: string;
  memo?: string | null;
  problem_qty?: number | null;               // shortage · wrong_item(원 SKU 미입고분)
  overage_qty?: number | null;               // overage
  actual_sku?: string | null;                // wrong_item
  actual_qty?: number | null;                // wrong_item
  actual_unit_price_cny?: number | null;     // wrong_item
  reorder_target_po_id?: number | null;      // wrong_item — 사용자가 고를 때만. 자동 선택 금지.
  reorder_qty?: number | null;
  corrected_quantity_ordered?: number | null; // qty_misentry
  // [PURCHASE-003 현장 UX] 계획 정리 동의 · 함께 종료할 담당자 인계 · iPad 경로 표시.
  clear_plan_confirmed?: boolean;
  handoff_id?: number | null;
  via_tablet?: boolean;
}

export interface PoImportAdjustment {
  id: number;
  po_id: number;
  po_no: string;
  purchase_order_item_id: number | null;
  unregistered_item_id: number | null;
  sku_snapshot: string | null;
  exception_type: string;
  direction: "deduct" | "add";
  direction_label: string;
  quantity: number;
  unit_price_cny: number;
  amount_cny: number;
  formula_note: string | null;
  status: "pending" | "done";
  status_label: string;
  memo: string | null;
  source_exception_id: number | null;
  processed_by_user_id: number | null;
  processed_at: string | null;
  created_by_user_id: number | null;
  created_at: string | null;
  events: {
    id: number;
    event_type: string;
    event_label: string;
    from_status: string | null;
    to_status: string | null;
    memo: string | null;
    created_by_user_id: number | null;
    created_at: string | null;
  }[];
}

export interface PoItemCorrectionResult {
  ok: boolean;
  po_id: number;
  po_no: string;
  inbound_item_id: number;
  sku: string | null;
  exception_type: PoCorrectionKind;
  exception_id: number;
  // 비삭제 5유형 공통 불변식 — 이 두 값은 같아야 한다(D5).
  po_total_price_krw_before: number;
  po_total_price_krw_after: number;
  adjustment: PoImportAdjustment | null;
  reorder: {
    target_po_id: number; target_po_no: string;
    purchase_order_item_id: number; sku: string | null;
    quantity: number; unit_price_cny: number;
  } | null;
  incoming_changes: { sku: string; delta: number; before: number; after: number }[];
  // [FW-042] 미주문 처리 결과 — 함께 종결한 미발송·누락 예외 · 정리한 목적지 계획 · 샵입고완료 자동 전환.
  closed_exception_ids?: number[];
  plan_cleared?: Record<string, unknown> | null;
  po_stocked_in?: boolean;
}

export function applyPoItemCorrection(
  inboundItemId: number,
  payload: PoItemCorrectionPayload,
): Promise<PoItemCorrectionResult> {
  return request(`/purchase/inbound-items/${inboundItemId}/correction`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

//: 차감 예정 / 추가 반영 예정 **분리** 합계. 상계한 순액은 backend 가 내려주지 않는다(자동 상계 금지).
export interface PoImportAdjustmentSummary {
  deduct_total_cny: number;
  add_total_cny: number;
  deduct_pending_cny: number;
  add_pending_cny: number;
  deduct_count: number;
  add_count: number;
  pending_count: number;
  done_count: number;
  total_count: number;
}

export function listPoImportAdjustments(
  poId: number,
): Promise<{ po_id: number; summary: PoImportAdjustmentSummary; adjustments: PoImportAdjustment[] }> {
  return request(`/purchase/orders/${poId}/import-adjustments`);
}

// 처리완료 / 재오픈 / 메모 변경. 셋 다 append-only 이벤트가 남는다(발생 근거는 변경 불가).
export function updatePoImportAdjustment(
  adjustmentId: number,
  action: "mark_done" | "reopen" | "update_memo",
  memo?: string | null,
): Promise<PoImportAdjustment> {
  return request(`/purchase/import-adjustments/${adjustmentId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, memo: memo ?? null }),
  });
}

// 6. 예외 해결 진행 (부분 가능).
export function fulfillInboundException(
  excId: number,
  body: { resolution_path: "return_to_vendor" | "compensation" | "additional_inbound" | "accept_wrong_item" | "discard"; quantity: number; reason: string }
): Promise<InboundException> {
  return request(`/purchase/inbound-exceptions/${excId}/fulfill`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 7. 예외 해결완료 (resolved_qty == problem_qty 도달 시).
export function resolveInboundException(excId: number): Promise<InboundException> {
  return request(`/purchase/inbound-exceptions/${excId}/resolve`, { method: "POST" });
}

// 26차 후속 #8(2026-04-24) — SKU별 입고완료 체크 + 관리자 샵입고완료 승인.
export function checkInboundItem(itemId: number): Promise<{
  ok: boolean;
  id: number;
  inbound_checked_at: string | null;
  inbound_checked_by_user_id: number | null;
  inbound_checked_by_user_name: string | null;
}> {
  return request(`/purchase/inbound-items/${itemId}/check`, { method: "POST" });
}

export function uncheckInboundItem(itemId: number): Promise<{ ok: boolean; id: number }> {
  return request(`/purchase/inbound-items/${itemId}/check`, { method: "DELETE" });
}

// 26차 후속 #9(2026-04-24) — SKU 이동 목적지(계획) 저장. 실제 이동은 발생하지 않음.
// 26차 후속 #10(2026-04-24) — 레거시 backcompat 용. 신규 입력은 allocations API 사용.
export function planInboundTarget(
  itemId: number,
  toLocationCode: string | null
): Promise<{ ok: boolean; id: number; planned_target_location_code: string | null }> {
  return request(`/purchase/inbound-items/${itemId}/plan-target`, {
    method: "POST",
    body: JSON.stringify({ to_location_code: toLocationCode }),
  });
}

// [ERP-PO-INBOUND-STATION-SKU-MEMO-DEV-20260702-010] SKU별 입고 현장 메모 저장(blur).
export function updatePoInboundMemo(
  itemId: number,
  memo: string | null
): Promise<{ id: number; receiving_memo: string | null }> {
  return request(`/purchase/inbound-items/${itemId}/memo`, {
    method: "PATCH",
    body: JSON.stringify({ memo }),
  });
}

// [PURCHASE-002-PHASE3][D15] 분할 입고의 "다음 기본 위치" 지정.
//   분배가 2곳 이상이면 대표 위치를 추정할 수 없어 사용자가 직접 고른다. 이 호출 자체는
//   재고·기본위치를 바꾸지 않고(입고장 행에만 기록), **승인 트랜잭션에서** 기본 위치로 승격된다.
//   null 을 보내면 선택 해제(= 다시 승인 차단 상태로 되돌린다).
export function setNextDefaultLocation(
  itemId: number,
  code: string | null
): Promise<{ ok: boolean; item_id: number; next_default_location_code: string | null }> {
  return request(`/purchase/inbound-items/${itemId}/next-default-location`, {
    method: "POST",
    body: JSON.stringify({ next_default_location_code: code }),
  });
}

// 26차 후속 #10(2026-04-24) — SKU 분배 계획(다중 목적지) CRUD.
// allocations 합계 == 입고장 잔량 일 때만 입고완료 체크 / 승인 가능.
export function listInboundAllocations(itemId: number): Promise<{
  items: PoInboundAllocation[];
  remaining: number;
  allocated_qty: number;
}> {
  return request(`/purchase/inbound-items/${itemId}/allocations`);
}

export function createInboundAllocation(
  itemId: number,
  body: { target_location_code: string; planned_qty: number }
): Promise<{
  ok: boolean;
  id: number;
  target_location_code: string;
  planned_qty: number;
  allocated_qty: number;
  remaining: number;
}> {
  return request(`/purchase/inbound-items/${itemId}/allocations`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateInboundAllocation(
  allocId: number,
  body: { target_location_code?: string; planned_qty?: number }
): Promise<{
  ok: boolean;
  id: number;
  target_location_code: string;
  planned_qty: number;
}> {
  return request(`/purchase/inbound-item-allocations/${allocId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteInboundAllocation(allocId: number): Promise<{ ok: boolean }> {
  return request(`/purchase/inbound-item-allocations/${allocId}`, {
    method: "DELETE",
  });
}

export function approveStockedIn(poId: number): Promise<PurchaseOrder> {
  return request(`/purchase/orders/${poId}/approve-stocked-in`, { method: "POST" });
}

// [ERP-PO-INBOUND-PARTIAL-STOCK-APPROVAL-DEV-20260702-018] SKU 단건 부분 승인.
// 해당 입고장 품목만 ERP 재고에 반영. 전 SKU 승인 완료 시 PO 자동 stocked_in.
export interface ApproveInboundItemStockResult {
  success: boolean;
  item_id: number;
  approved_qty: number;
  movements_created: number;
  po_stocked_in: boolean;
}

export function approveInboundItemStock(
  itemId: number,
): Promise<ApproveInboundItemStockResult> {
  return request(`/purchase/inbound-items/${itemId}/approve-stock`, { method: "POST" });
}

// ── [PURCHASE-003 Phase 1] iPad 터치 입고장 ─────────────────────────────
// 별도 원장 없음 — 기존 입고장(po_inbound_items·분배·입고완료 체크)을 그대로 쓴다. 재고 반영은 위 승인 API 뿐이다.
export interface TabletReceiveAllocation {
  location_code: string;
  qty: number;
}

export interface TabletNormalReceiveResult {
  ok: boolean;
  /** 이미 같은 결과가 반영돼 있어 아무것도 쓰지 않았다(중복 터치·재전송). */
  idempotent: boolean;
  item_id: number;
  inbound_checked_at: string | null;
  allocations: PoInboundAllocation[];
  next_default_location_code: string | null;
  quantity_actual?: number;
  inspection_status?: "submitted" | "rejected" | null;
  version: string;
}

/** `정상 입고 N개` — 확인수량=예상수량 · 목적지 · 입고완료를 한 트랜잭션으로. 화면이 본 뒤 바뀌었으면 409. */
export function tabletNormalReceive(
  itemId: number,
  body: {
    expected_version: string;
    expected_qty: number;
    allocations: TabletReceiveAllocation[];
    next_default_location_code?: string | null;
    // [PURCHASE-003 Phase 2] 정상 + 파손 + 보류 = 확인수량(expected_qty). 0 이 아니면 정정·예외 권한 + 사유 필요.
    damaged_qty?: number;
    hold_qty?: number;
    problem_reason?: string | null;
    problem_memo?: string | null;
    // [PURCHASE-003 현장 UX] 기존 목적지 계획과 다른 위치 — 화면 확인 후에만 true(아니면 409 INBOUND_PLAN_CHANGE_UNCONFIRMED).
    plan_change_confirmed?: boolean;
  },
): Promise<TabletNormalReceiveResult> {
  return request(`/purchase/inbound-items/${itemId}/tablet-normal-receive`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface TabletInboundSubmitResult {
  ok: boolean;
  idempotent?: boolean;
  submitted_count?: number;
  po_id: number;
  po_no: string;
  total: number;
  approved: number;
  submitted?: number;
  rejected?: number;
  awaiting_approval: number;
  exception_items: number;
  unregistered_items: number;
  /** [PURCHASE-003 현장 UX] 담당자 인계 대기(제출 대상 제외 · 별도 집계). */
  handoff_waiting?: number;
}

/**
 * 검수 제출 — 대상(미승인 · 미제출/반려) 품목 전체를 submitted 로 전환 + 이력.
 * 미확인 품목이 있으면 400(INBOUND_TABLET_UNCONFIRMED), 화면이 본 버전과 다르면 409. 재고 write 0.
 */
export function tabletInboundSubmit(
  poId: number,
  expectedVersions: Record<number, string>,
): Promise<TabletInboundSubmitResult> {
  return request(`/purchase/orders/${poId}/tablet-inbound-submit`, {
    method: "POST",
    body: JSON.stringify({ expected_versions: expectedVersions }),
  });
}

export interface TabletInboundRejectResult extends TabletInboundSubmitResult {
  rejected_item_ids: number[];
}

/** [PURCHASE-003 Phase 2] 승인권자 반려 — 고른 품목만 rejected(사유 필수). 재고 write 0. */
export function tabletInboundReject(
  poId: number,
  items: { item_id: number; expected_version: string }[],
  reason: string,
): Promise<TabletInboundRejectResult> {
  return request(`/purchase/orders/${poId}/tablet-inbound-reject`, {
    method: "POST",
    body: JSON.stringify({ items, reason }),
  });
}

// ── [PURCHASE-003 현장 UX] 목적지 일괄 지정 · 검수 되돌리기 · 문제 인계 ───────────────
export interface TabletPlanChange {
  item_id: number;
  sku: string;
  from: TabletReceiveAllocation[];
  to: TabletReceiveAllocation[];
}

/** 고른 미처리 품목 전부를 한 위치로(전부 성공 또는 전부 rollback). 기존 계획 변경은 plan_change_confirmed 필요(아니면 409 + changes). */
export function tabletBulkDestination(
  poId: number,
  items: { item_id: number; expected_version: string }[],
  locationCode: string,
  planChangeConfirmed = false,
): Promise<{ ok: boolean; idempotent: boolean; item_ids: number[]; location_code: string; changed_plan_item_ids: number[] }> {
  return request(`/purchase/orders/${poId}/tablet-bulk-destination`, {
    method: "POST",
    body: JSON.stringify({ items, location_code: locationCode, plan_change_confirmed: planChangeConfirmed }),
  });
}

/** 검수 되돌리기 — 확인만 해제 · 목적지 계획 유지. 제출·승인 품목은 409. */
export function tabletUndo(itemId: number, expectedVersion: string): Promise<TabletNormalReceiveResult> {
  return request(`/purchase/inbound-items/${itemId}/tablet-undo`, {
    method: "POST",
    body: JSON.stringify({ expected_version: expectedVersion }),
  });
}

/** 문제 발견·담당자 인계 등록(로그인 직원) — 수량·예외·재고 불변. 확인돼 있던 품목은 확인 해제. */
export function createInboundHandoff(
  itemId: number,
  body: { reason_code: InboundHandoffReason; memo?: string | null; expected_version: string },
): Promise<{ ok: boolean; idempotent: boolean; handoff: InboundHandoff }> {
  return request(`/purchase/inbound-items/${itemId}/handoffs`, { method: "POST", body: JSON.stringify(body) });
}

/** 인계 종료 — confirmed_normal(권한자) · cancelled(등록자 본인 또는 권한자). 예외 전환은 applyPoItemCorrection(handoff_id). */
export function closeInboundHandoff(
  itemId: number,
  handoffId: number,
  action: "confirmed_normal" | "cancelled",
  note?: string | null,
): Promise<{ ok: boolean; idempotent: boolean; handoff: InboundHandoff }> {
  return request(`/purchase/inbound-items/${itemId}/handoffs/${handoffId}/close`, {
    method: "POST",
    body: JSON.stringify({ action, note: note ?? null }),
  });
}

// ── 26차 후속 #47: SKU 바코드 라벨 PDF ────────────────────────────────────
// POST /barcode-labels/pdf → application/pdf Blob → 새 탭으로 오픈.
// 단건/다건 공용 (현재는 상품 상세 단건만 사용).
export type BarcodeLabelSize = "small" | "large";
// [ERP-BARCODE-20260513-013] 코드 형식 선택 (QR / Code128).
// 미지정 시 backend 가 label_size 기준 기본값 적용 (small→qr, large→code128).
export type BarcodeCodeType = "qr" | "code128";
export type BarcodeLabelItemRequest = { sku: string; quantity: number };

export async function openBarcodeLabelsPdf(body: {
  label_size: BarcodeLabelSize;
  code_type?: BarcodeCodeType;
  items: BarcodeLabelItemRequest[];
}): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_URL}/barcode-labels/pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "바코드 PDF 생성 실패" }));
    throw new Error(err.detail || "바코드 PDF 생성 실패");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  // 새 탭으로 열기 — Mac 인쇄 대화상자 흐름. 일부 브라우저 팝업 차단 시 a click fallback.
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }
  // 새 탭에서 PDF 가 로드된 뒤 revoke — 60s 후 안전 해제.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── [ERP-PO-BARCODE-PRINT-JOBS-PHASE-B-...-20260616-001] PO 바코드 출력 이력 ──
// PDF export 1건 = job 1건(style 그룹 1개). "출력됨" 표시는 printed_keys 기준.
export interface PoBarcodePrintJobItem {
  id: number;
  job_id: number;
  purchase_order_id: number;
  purchase_order_item_id: number | null;
  sku: string;
  product_name: string | null;
  quantity: number;
  label_size: BarcodeLabelSize;
  code_type: BarcodeCodeType;
  created_at: string;
}

export interface PoBarcodePrintJob {
  id: number;
  purchase_order_id: number;
  label_size: BarcodeLabelSize;
  code_type: BarcodeCodeType;
  total_sku_count: number;
  total_label_count: number;
  printed_by_user_id: number | null;
  printed_at: string;
  created_at: string;
  items: PoBarcodePrintJobItem[];
}

export interface PoBarcodePrintedKey {
  sku: string;
  label_size: BarcodeLabelSize;
  code_type: BarcodeCodeType;
  total_quantity: number;
  job_count: number;
  last_printed_at: string;
}

export interface PoBarcodePrintHistory {
  purchase_order_id: number;
  jobs: PoBarcodePrintJob[];
  printed_keys: PoBarcodePrintedKey[];
}

export function getPoBarcodePrintJobs(poId: number): Promise<PoBarcodePrintHistory> {
  return request<PoBarcodePrintHistory>(`/purchase/orders/${poId}/barcode-print-jobs`);
}

// style 그룹 1개(label_size+code_type) 의 SKU 들을 한 PDF 로 export.
// 성공 시 PDF 를 새 탭으로 열고, job/items 이력이 저장됨.
export async function exportPoBarcodePrintJob(
  poId: number,
  body: {
    label_size: BarcodeLabelSize;
    code_type: BarcodeCodeType;
    items: { sku: string; quantity: number }[];
    include_reprint?: boolean;
  },
): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_URL}/purchase/orders/${poId}/barcode-print-jobs/export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "바코드 PDF 생성 실패" }));
    throw new Error(err.detail || "바코드 PDF 생성 실패");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── 개인 메모장 (26차 후속 #58 Phase 4, 2026-05-06) ─────────────────
export interface LinkedOrderRef {
  order_id: number;
  order_no: string;
  customer_name: string | null;
}

export interface PersonalNoteRow {
  id: number;
  user_id: number;
  title: string;
  content: string;
  note_date: string;        // YYYY-MM-DD
  is_pinned: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  linked_orders: LinkedOrderRef[];
  unmatched_order_refs: string[];
}

export interface PersonalNoteListResponse {
  items: PersonalNoteRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PersonalNoteCreate {
  title?: string | null;
  content?: string | null;
  note_date?: string | null;   // YYYY-MM-DD
  is_pinned?: boolean;
}

export interface PersonalNoteUpdate {
  title?: string | null;
  content?: string | null;
  note_date?: string | null;
  is_pinned?: boolean | null;
  is_archived?: boolean | null;
}

export interface PersonalNoteListParams {
  q?: string;
  date_from?: string;
  date_to?: string;
  is_archived?: boolean;
  limit?: number;
  offset?: number;
}

export function getPersonalNotes(params: PersonalNoteListParams = {}): Promise<PersonalNoteListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (typeof params.is_archived === "boolean") qs.set("is_archived", String(params.is_archived));
  if (typeof params.limit === "number") qs.set("limit", String(params.limit));
  if (typeof params.offset === "number") qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<PersonalNoteListResponse>(`/personal-notes${suffix}`);
}

export function createPersonalNote(payload: PersonalNoteCreate): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>("/personal-notes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getPersonalNote(id: number): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}`);
}

export function updatePersonalNote(id: number, payload: PersonalNoteUpdate): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** 영구 삭제 — 휴지통(is_archived=true) 안의 메모만 삭제 가능. 휴지통이 아니면 백엔드 400. */
export function permanentDeletePersonalNote(
  id: number,
): Promise<{ ok: boolean; id: number }> {
  return request<{ ok: boolean; id: number }>(
    `/personal-notes/${id}/permanent`,
    { method: "DELETE" },
  );
}

export function deletePersonalNote(id: number): Promise<PersonalNoteRow> {
  // 백엔드는 hard delete 가 아니라 soft delete (is_archived=true) 후 row 반환.
  return request<PersonalNoteRow>(`/personal-notes/${id}`, { method: "DELETE" });
}

export function togglePersonalNotePin(id: number): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}/toggle-pin`, { method: "POST" });
}

export function restorePersonalNote(id: number): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}/restore`, { method: "POST" });
}

// ── ERP 개선요청 (26차 후속 #59, 2026-05-06) ─────────────────────

export type ErpRequestStatus =
  | "open"
  | "reviewing"
  | "planned"
  | "in_progress"
  | "completed"
  | "rejected";
export type ErpRequestCategory =
  | "bug"
  | "ux"
  | "feature"
  | "data"
  | "speed"
  | "other";
export type ErpRequestPriority = "low" | "normal" | "high" | "urgent";

export interface ErpImprovementRow {
  id: number;
  title: string;
  content: string;
  category: ErpRequestCategory | string;
  status: ErpRequestStatus | string;
  priority: ErpRequestPriority | string;
  page_url: string | null;
  reporter_user_id: number;
  reporter_name: string | null;
  assignee_user_id: number | null;
  assignee_name: string | null;
  admin_comment: string | null;
  completed_by_user_id: number | null;
  completed_by_name: string | null;
  completed_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_by_name: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface ErpImprovementListResponse {
  items: ErpImprovementRow[];
  total: number;
  limit: number;
  offset: number;
  counts_by_status: Array<{ status: string; count: number }>;
}

export interface ErpImprovementCreate {
  title?: string;
  content?: string;
  category?: ErpRequestCategory | string;
  priority?: ErpRequestPriority | string;
  page_url?: string | null;
}

export interface ErpImprovementUpdate {
  title?: string;
  content?: string;
  category?: ErpRequestCategory | string;
  priority?: ErpRequestPriority | string;
  page_url?: string | null;
}

export interface ErpImprovementAdminUpdate {
  status?: ErpRequestStatus | string;
  priority?: ErpRequestPriority | string;
  admin_comment?: string | null;
  assignee_user_id?: number | null;
}

export interface ErpImprovementListParams {
  q?: string;
  status?: string;
  category?: string;
  priority?: string;
  reporter_user_id?: number;
  mine?: boolean;
  /** 기본 false = 일반 목록(휴지통 제외). true = 휴지통 보기. */
  is_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export function getErpImprovements(
  params: ErpImprovementListParams = {},
): Promise<ErpImprovementListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.category) qs.set("category", params.category);
  if (params.priority) qs.set("priority", params.priority);
  if (params.reporter_user_id != null)
    qs.set("reporter_user_id", String(params.reporter_user_id));
  if (params.mine) qs.set("mine", "true");
  if (params.is_deleted) qs.set("is_deleted", "true");
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<ErpImprovementListResponse>(`/erp-improvements${suffix}`);
}

export function getErpImprovement(id: number): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}`);
}

export function createErpImprovement(
  payload: ErpImprovementCreate,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateErpImprovement(
  id: number,
  payload: ErpImprovementUpdate,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function adminUpdateErpImprovement(
  id: number,
  payload: ErpImprovementAdminUpdate,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/admin-update`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function completeErpImprovement(
  id: number,
  admin_comment?: string,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ admin_comment: admin_comment ?? null }),
  });
}

export function rejectErpImprovement(
  id: number,
  admin_comment?: string,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ admin_comment: admin_comment ?? null }),
  });
}

/** 휴지통 이동 (soft delete). admin 전체, 직원은 본인 + open/reviewing 한정. */
export function softDeleteErpImprovement(
  id: number,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}`, {
    method: "DELETE",
  });
}

/** 휴지통 복원. admin 전체, 직원은 본인 한정. */
export function restoreErpImprovement(
  id: number,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/restore`, {
    method: "POST",
  });
}

// ── [ERP-PO-SHARED-MUST-ADD-MEMO-CHECKLIST-AUDIT-20260616-001] ────────
// 발주 공유 체크리스트 — "다음 발주에 꼭 추가해야 할 것".
// 발주건과 무관하게 유지되는 공유 메모/체크리스트.
// 읽기: 로그인 전체 / 작성·수정·완료·삭제: lv2 이상 (백엔드 게이트).
export type PoChecklistScope =
  | "china_import"
  | "shimano"
  | "daiwa"
  | "global";
export type PoChecklistStatus = "active" | "done";

export interface PoSharedChecklistRow {
  id: number;
  scope: PoChecklistScope | string;
  content: string;
  sku: string | null;
  product_name: string | null;
  status: PoChecklistStatus | string;
  sort_order: number;
  created_by_user_id: number;
  created_by_name: string | null;
  updated_by_user_id: number | null;
  updated_by_name: string | null;
  completed_by_user_id: number | null;
  completed_by_name: string | null;
  completed_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_by_name: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface PoSharedChecklistListResponse {
  items: PoSharedChecklistRow[];
  total: number;
  limit: number;
  offset: number;
  counts_by_status: Array<{ status: string; count: number }>;
}

export interface PoSharedChecklistCreate {
  scope?: PoChecklistScope | string;
  content: string;
  sku?: string | null;
  product_name?: string | null;
}

export interface PoSharedChecklistUpdate {
  content?: string;
  sku?: string | null;
  product_name?: string | null;
  sort_order?: number;
}

export interface PoSharedChecklistListParams {
  scope?: PoChecklistScope | string;
  q?: string;
  status?: PoChecklistStatus | string;
  /** 기본 false = 일반 목록(휴지통 제외). true = 휴지통 보기. */
  is_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export function getPoSharedChecklist(
  params: PoSharedChecklistListParams = {},
): Promise<PoSharedChecklistListResponse> {
  const qs = new URLSearchParams();
  if (params.scope) qs.set("scope", params.scope);
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.is_deleted) qs.set("is_deleted", "true");
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<PoSharedChecklistListResponse>(`/po-shared-checklist${suffix}`);
}

export function createPoSharedChecklist(
  payload: PoSharedChecklistCreate,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updatePoSharedChecklist(
  id: number,
  payload: PoSharedChecklistUpdate,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** 완료/완료해제 토글. done=true 면 완료(done), false 면 미완료(active) 복귀. */
export function toggleCompletePoSharedChecklist(
  id: number,
  done: boolean,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ done }),
  });
}

/** 휴지통 이동 (soft delete). lv2 이상. */
export function softDeletePoSharedChecklist(
  id: number,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}`, {
    method: "DELETE",
  });
}

/** 휴지통 복원. lv2 이상. */
export function restorePoSharedChecklist(
  id: number,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}/restore`, {
    method: "POST",
  });
}

// ── [ERP-INBOUND-20260513-002] 모바일 입고검수 세션 ─────────────────
// 본 API 는 재고 SoT 가 아니다. reviewed 도 단순 관리자 확인 표식.
export type MobileInboundSessionStatus =
  | "checking"
  | "submitted"
  | "cancelled"
  | "reviewed"
  | "rejected";
export type MobileInboundIssueType =
  | "normal"
  | "shortage"
  | "overage"
  | "damaged"
  | "wrong_item"
  | "hold";

export interface MobileInboundCheckItem {
  id: number;
  po_item_id: number | null;
  sku: string | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  expected_qty: number;
  checked_qty: number;
  issue_type: MobileInboundIssueType;
  memo: string | null;
  checked_at: string | null;
}

export interface MobileInboundCheckSessionSummary {
  id: number;
  purchase_order_id: number;
  po_no: string;
  status: MobileInboundSessionStatus;
  checked_by_user_id: number;
  checked_by_name: string | null;
  memo: string | null;
  started_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by_user_id: number | null;
  rejected_at: string | null;
  rejected_reason: string | null;
  total_items: number;
  checked_items: number;
  issue_items: number;
}

export interface MobileInboundCheckSessionDetail extends MobileInboundCheckSessionSummary {
  items: MobileInboundCheckItem[];
}

export interface MobileInboundCheckListResponse {
  total: number;
  items: MobileInboundCheckSessionSummary[];
}

export function listMobileInboundChecks(params?: {
  status?: MobileInboundSessionStatus;
  po_id?: number;
  limit?: number;
  offset?: number;
}): Promise<MobileInboundCheckListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.po_id != null) qs.set("po_id", String(params.po_id));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<MobileInboundCheckListResponse>(`/mobile/inbound-checks${suffix}`);
}

export function getMobileInboundCheck(id: number): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(`/mobile/inbound-checks/${id}`);
}

export function createMobileInboundCheck(
  purchase_order_id: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(`/mobile/inbound-checks`, {
    method: "POST",
    body: JSON.stringify({ purchase_order_id }),
  });
}

export function updateMobileInboundCheckItem(
  sessionId: number,
  itemId: number,
  body: { checked_qty?: number; issue_type?: MobileInboundIssueType; memo?: string | null },
): Promise<MobileInboundCheckItem> {
  return request<MobileInboundCheckItem>(
    `/mobile/inbound-checks/${sessionId}/items/${itemId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function updateMobileInboundCheckMemo(
  sessionId: number,
  memo: string | null,
): Promise<MobileInboundCheckSessionSummary> {
  return request<MobileInboundCheckSessionSummary>(
    `/mobile/inbound-checks/${sessionId}/memo`,
    { method: "PATCH", body: JSON.stringify({ memo }) },
  );
}

export function submitMobileInboundCheck(
  sessionId: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/submit`,
    { method: "POST" },
  );
}

export function cancelMobileInboundCheck(
  sessionId: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/cancel`,
    { method: "POST" },
  );
}

export function reviewMobileInboundCheck(
  sessionId: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/review`,
    { method: "POST" },
  );
}

export function rejectMobileInboundCheck(
  sessionId: number,
  reason?: string,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/reject`,
    { method: "POST", body: JSON.stringify({ rejected_reason: reason ?? null }) },
  );
}

// ─── 송장쪼개기 (item-move) ─────────────────────────────────────
// [ERP-ORDER-INVOICE-SPLIT-ITEM-MOVE-REDESIGN-20260529-001]
// 송장쪼개기 = 원주문 항목 일부를 분할자식 주문(order_type='invoice_split_order')으로 "이동".
//   - 원주문 항목은 삭제하지 않고 split_status='moved_out' 으로 이력 보존.
//   - moved_out 항목은 원주문 송장 포장 스캔/재고차감 대상에서 제외.
//   - 분할자식은 자신의 order_no/order_items 를 가진 정상 주문 → 포장/스캔/재고차감/Export 대상.
//   - 재고차감은 분할자식 포장 스캔 시점에만 발생 (이중 차감 없음).
//   - Phase 1 은 "항목 행 전체 이동" 만 지원 (부분 수량 이동 범위 밖).

export interface InvoiceSplitMovableItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  // [ERP-INVOICE-SPLIT-QTY-UX-AMOUNT-DISPLAY-DEV-20260818]
  // allocation SoT 잔여수량(=quantity−active배정). backend InvoiceSplitMovableItem 이 제공.
  remaining_quantity?: number | null;
  split_status: string; // none | moved_out
  split_child_order_no: string | null;
  movable: boolean;
  block_reason: string | null;
}

export interface InvoiceSplitItemsPreviewResponse {
  original_order_no: string;
  original_invoice_no: string | null;
  order_status: string | null;
  order_status_blocked: boolean;
  block_reason: string | null;
  customer_name: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  existing_child_count: number;
  items: InvoiceSplitMovableItem[];
  notice: string;
}

export interface InvoiceSplitChildItem {
  order_item_id: number;
  source_order_item_id?: number | null;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
}

export interface InvoiceSplitChildResponse {
  child_order_no: string;
  original_order_no: string;
  order_status: string | null;
  invoice_no: string | null;
  exported_at: string | null;
  created_at: string;
  item_count: number;
  items: InvoiceSplitChildItem[];
}

export interface InvoiceSplitChildListResponse {
  total: number;
  items: InvoiceSplitChildResponse[];
}

export function previewInvoiceSplitItems(
  orderNo: string,
): Promise<InvoiceSplitItemsPreviewResponse> {
  return request<InvoiceSplitItemsPreviewResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items/preview`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function createInvoiceSplitItems(
  orderNo: string,
  orderItemIds: number[],
  splitReason?: string | null,
  // [ERP-INVOICE-SPLIT-ALLOCATION-SOT-DEV-20260724] 멱등키 — 버튼 연타/네트워크 재전송 시
  // 백엔드가 동일 요청을 1건으로 처리(중복 자식주문 방지). 미전달 시 백엔드 멱등 게이트가 무력화됨.
  idempotencyKey?: string,
): Promise<InvoiceSplitChildResponse> {
  return request<InvoiceSplitChildResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items`,
    {
      method: "POST",
      body: JSON.stringify({
        order_item_ids: orderItemIds,
        split_reason: splitReason ?? null,
        idempotency_key: idempotencyKey ?? null,
      }),
    },
  );
}

// [ERP-INVOICE-SPLIT-UNIFIED-MULTI-QTY-AND-FULFILLMENT-GUARD-DEV-20260825]
// 통합 플로우: 상품별 이동수량(items) + 자식 전체 송장번호(선택) 1개로 한 번에 제출.
// 한 번 제출 = 자식 주문 1개 + 상품별 allocation N개(1행마다 별도 자식 금지). 같은 endpoint 재사용.
export function createInvoiceSplitUnified(
  orderNo: string,
  items: { order_item_id: number; quantity: number }[],
  opts?: { splitReason?: string | null; targetInvoiceNo?: string | null; idempotencyKey?: string },
): Promise<InvoiceSplitChildResponse> {
  return request<InvoiceSplitChildResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items`,
    {
      method: "POST",
      body: JSON.stringify({
        items,
        split_reason: opts?.splitReason ?? null,
        target_invoice_no: opts?.targetInvoiceNo ?? null,
        idempotency_key: opts?.idempotencyKey ?? null,
      }),
    },
  );
}

export function listOrderInvoiceSplitItems(
  orderNo: string,
): Promise<InvoiceSplitChildListResponse> {
  return request<InvoiceSplitChildListResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items`,
  );
}

export function listAllInvoiceSplitChildren(params: {
  page?: number;
  page_size?: number;
} = {}): Promise<InvoiceSplitChildListResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));
  const q = qs.toString();
  return request<InvoiceSplitChildListResponse>(`/orders/invoice-splits${q ? `?${q}` : ""}`);
}

// [ERP-INVOICE-SPLIT-VISIBILITY-AND-CANCEL-FIX-20260601-002] 송장쪼개기 취소(분할 원복)
export interface InvoiceSplitCancelItem {
  order_item_id: number;
  sku?: string | null;
  product_name?: string | null;
  option_name?: string | null;
  quantity?: number | null;
}

export interface InvoiceSplitCancelPreviewResponse {
  can_cancel: boolean;
  child_order_no: string;
  original_order_no?: string | null;
  child_order_status?: string | null;
  child_invoice_no?: string | null;
  child_exported: boolean;
  restore_items: InvoiceSplitCancelItem[];
  child_items: InvoiceSplitCancelItem[];
  block_reasons: string[];
  warnings: string[];
  notice: string;
}

export interface InvoiceSplitCancelResponse {
  ok: boolean;
  child_order_no: string;
  original_order_no?: string | null;
  child_order_status: string;
  restored_order_item_ids: number[];
  cancelled_child_item_ids: number[];
}

export function previewCancelInvoiceSplit(
  childOrderNo: string,
): Promise<InvoiceSplitCancelPreviewResponse> {
  return request<InvoiceSplitCancelPreviewResponse>(
    `/orders/${encodeURIComponent(childOrderNo)}/invoice-split-items/cancel-preview`,
    { method: "POST" },
  );
}

export function cancelInvoiceSplit(
  childOrderNo: string,
): Promise<InvoiceSplitCancelResponse> {
  return request<InvoiceSplitCancelResponse>(
    `/orders/${encodeURIComponent(childOrderNo)}/invoice-split-items/cancel`,
    { method: "POST" },
  );
}

// ── 송장 수량분할 [ERP-TOGGLE-EXPORT-INVOICE-SPLIT-QUANTITY-SPLIT-DEV-20260703-033] ──
// 한 order_item(quantity>1)의 수량을 여러 송장에 수량 단위로 배정. 원 order_items.quantity 무수정.
// Phase 1: allocation CRUD + export 표시 반영. 포장출고/재고차감은 Phase 2.
export interface QtySplitMovableItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  original_quantity: number;
  allocated_quantity: number;
  remaining_quantity: number;
  splittable: boolean;
  block_reason: string | null;
}

export interface QtySplitPreviewResponse {
  order_no: string;
  invoice_no: string | null;
  items: QtySplitMovableItem[];
  notice: string;
}

export interface QtySplitAllocation {
  id: number;
  original_order_item_id: number;
  parent_order_no: string;
  parent_invoice_no: string | null;
  child_order_no: string | null;
  target_invoice_no: string | null;
  allocated_quantity: number;
  is_active: boolean;
  cancelled_at: string | null;
  created_at: string;
}

export function previewQtySplit(orderNo: string): Promise<QtySplitPreviewResponse> {
  return request<QtySplitPreviewResponse>(`/orders/${encodeURIComponent(orderNo)}/qty-split/preview`);
}

export function createQtySplit(
  orderNo: string,
  // [ERP-INVOICE-SPLIT-ALLOCATION-SOT-DEV-20260724] idempotency_key 추가 —
  // 연타/재전송 시 중복 배정 방지. 미전달 시 백엔드 멱등 게이트 무력화됨.
  body: { order_item_id: number; allocated_quantity: number; target_invoice_no?: string | null; child_order_no?: string | null; idempotency_key?: string | null },
): Promise<QtySplitAllocation> {
  return request<QtySplitAllocation>(`/orders/${encodeURIComponent(orderNo)}/qty-split`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function listQtySplit(orderNo: string): Promise<QtySplitAllocation[]> {
  return request<QtySplitAllocation[]>(`/orders/${encodeURIComponent(orderNo)}/qty-split`);
}

export function cancelQtySplit(allocationId: number): Promise<QtySplitAllocation> {
  return request<QtySplitAllocation>(`/orders/qty-split/${allocationId}/cancel`, { method: "POST" });
}

// ── 가격 조정 후보 (read-only) [ERP-PRICE-ADJUSTMENT-CANDIDATE-READONLY-VIEW-20260601-001] ──
export interface PriceCandidate {
  sku: string;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  option3?: string | null; // [FW-032] additive
  brand: string | null;
  category: string | null;
  supplier: string | null;
  platform: string;
  current_price: number | null;
  base_price_source: string;
  cost_price: number | null;
  cost_currency: string | null;
  margin_rate: number | null;
  margin_quality: string;
  total_stock: number;
  available_stock: number;
  office_stock: number;
  warehouse_stock: number;
  rocket_stock: number;
  sales_qty_7d: number;
  sales_qty_30d: number;
  sales_qty_90d: number;
  sales_amount_30d: number;
  last_sold_at: string | null;
  daiwa_policy_price: number | null;
  daiwa_naver_price: number | null;
  policy_status: string;
  recommended_action: string;
  suggested_delta_amount: number | null;
  suggested_delta_percent: number | null;
  suggested_price: number | null;
  reason_type: string;
  reason_text: string;
  confidence: string;
  warning_flags: string[];
  // [ERP-COMMON-SKU-MARKET-LINKS-REUSE-DEV-20260629-001] 마켓 등록 플랫폼/링크(read-only).
  listed_platforms?: string[];
  listing_urls?: Record<string, string>;
}

export interface PriceCandidateResponse {
  total: number;
  returned: number;
  summary: Record<string, number>;
  notice: string;
  items: PriceCandidate[];
}

export function getPriceAdjustmentCandidates(params: {
  reason_type?: string;
  brand?: string;
  category?: string;
  supplier?: string;
  platform?: string;
  min_stock?: number;
  max_stock?: number;
  min_sales_30d?: number;
  max_sales_90d?: number;
  max_margin_rate?: number;
  q?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<PriceCandidateResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  const s = qs.toString();
  return request<PriceCandidateResponse>(`/price-adjustment/candidates${s ? "?" + s : ""}`);
}

// ── 품절 처리 요청 큐 [ERP-PACKING-SOLDOUT-REQUEST-QUEUE-20260601-001] ──
export interface SoldoutPreviewResult {
  can_create: boolean;
  block_reasons: string[];
  existing_open_request_id: number | null;
  sku: string;
  product_name: string | null;
  option_name: string | null;
  product_status: string | null;
  stock: { total: number; office: number; warehouse: number; rocket: number };
  order_no: string | null;
  invoice_no: string | null;
  notice: string;
}
export interface SoldoutRequestRow {
  id: number;
  sku: string;
  product_name_snapshot: string | null;
  option_name_snapshot: string | null;
  order_no: string | null;
  invoice_no: string | null;
  requested_by_name_snapshot: string | null;
  requested_at: string;
  request_source: string;
  reason: string;
  memo: string | null;
  current_stock_snapshot: number | null;
  office_stock_snapshot: number | null;
  warehouse_stock_snapshot: number | null;
  rocket_stock_snapshot: number | null;
  product_status_snapshot: string | null;
  status: string;
  processed_by_user_id: number | null;
  processed_at: string | null;
  process_memo: string | null;
  action_taken: string | null;
}
export interface SoldoutSummary {
  pending_count: number;
  in_progress_count: number;
  today_completed_count: number;
  stale_count: number;
}

export function previewSoldoutRequest(body: { sku: string; order_no?: string | null; invoice_no?: string | null; order_item_id?: number | null }): Promise<SoldoutPreviewResult> {
  return request<SoldoutPreviewResult>(`/packing/soldout-requests/preview`, { method: "POST", body: JSON.stringify(body) });
}
export function createSoldoutRequest(body: { sku: string; order_no?: string | null; invoice_no?: string | null; order_item_id?: number | null; reason?: string; memo?: string | null; request_source?: string }): Promise<SoldoutRequestRow> {
  return request<SoldoutRequestRow>(`/packing/soldout-requests`, { method: "POST", body: JSON.stringify(body) });
}
export function listSoldoutRequests(params: { status?: string; sku?: string; order_no?: string; limit?: number; offset?: number } = {}): Promise<{ total: number; items: SoldoutRequestRow[] }> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
  const s = qs.toString();
  return request(`/packing/soldout-requests${s ? "?" + s : ""}`);
}
export function getSoldoutSummary(): Promise<SoldoutSummary> {
  return request<SoldoutSummary>(`/packing/soldout-requests/summary`);
}
export function updateSoldoutStatus(id: number, body: { status: string; process_memo?: string | null; action_taken?: string | null }): Promise<SoldoutRequestRow> {
  return request<SoldoutRequestRow>(`/packing/soldout-requests/${id}/status`, { method: "PATCH", body: JSON.stringify(body) });
}

// ── 가격 조정 작업 큐 (저장형) [ERP-PRICE-ADJUSTMENT-TASK-QUEUE-SCHEMA-20260601-001] ──
export interface PriceTask {
  id: number;
  sku: string;
  product_name_snapshot: string | null;
  option1_snapshot: string | null;
  option2_snapshot: string | null;
  brand_snapshot: string | null;
  category_snapshot: string | null;
  supplier_snapshot: string | null;
  platform: string;
  current_price_snapshot: number | null;
  cost_price_snapshot: number | null;
  cost_currency_snapshot: string | null;
  margin_rate_snapshot: number | null;
  total_stock_snapshot: number | null;
  available_stock_snapshot: number | null;
  sales_qty_7d_snapshot: number | null;
  sales_qty_30d_snapshot: number | null;
  sales_qty_90d_snapshot: number | null;
  sales_amount_30d_snapshot: number | null;
  last_sold_at_snapshot: string | null;
  adjustment_type: string | null;
  adjustment_value: number | null;
  target_price: number | null;
  delta_amount: number | null;
  delta_percent: number | null;
  reason_type: string;
  reason_text: string | null;
  memo: string | null;
  status: string;
  // [ERP-PRICE-ADJUSTMENT-ROUNDING-UNIT-SNAPSHOT-PERSISTENCE-DEV-20260624-001]
  // 플랫폼 적용 완료(applied) 시점에 못박힌 올림단위(10/100/1000). null = 미적용/없음.
  platform_rounding_unit_snapshot: number | null;
  created_by_name_snapshot: string | null;
  created_at: string | null;
  applied_at: string | null;
  confirmed_at: string | null;
  cancelled_at: string | null;
  apply_memo: string | null;
  confirm_memo: string | null;
  cancel_reason: string | null;
  // [ERP-COMMON-SKU-MARKET-LINKS-REUSE-DEV-20260629-001] 마켓 등록 플랫폼/링크(read-only).
  listed_platforms?: string[];
  listing_urls?: Record<string, string>;
}
export interface PriceTaskListResponse {
  total: number;
  summary: Record<string, number>;
  items: PriceTask[];
  notice: string;
}

export function listPriceTasks(params: { status?: string; sku?: string; q?: string; platform?: string; include_closed?: boolean; limit?: number; offset?: number } = {}): Promise<PriceTaskListResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
  const s = qs.toString();
  return request(`/price-adjustment/tasks${s ? "?" + s : ""}`);
}
export function createPriceTask(body: { sku: string; platform?: string; adjustment_type?: string | null; adjustment_value?: number | null; target_price?: number | null; reason_type?: string; reason_text?: string | null; memo?: string | null }): Promise<PriceTask> {
  return request<PriceTask>(`/price-adjustment/tasks`, { method: "POST", body: JSON.stringify(body) });
}
export function patchPriceTask(id: number, body: { adjustment_type?: string | null; adjustment_value?: number | null; target_price?: number | null; status?: string; memo?: string | null; apply_memo?: string | null; confirm_memo?: string | null; cancel_reason?: string | null }): Promise<PriceTask> {
  return request<PriceTask>(`/price-adjustment/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

// 가격 조정 작업 큐 일괄 수정 [ERP-PRICE-ADJUSTMENT-BULK-SELECT-UPDATE-20260602-001]
export function bulkPatchPriceTasks(body: { task_ids: number[]; adjustment_type?: string | null; adjustment_value?: number | null; target_price?: number | null; status?: string | null; memo?: string | null; apply_memo?: string | null; confirm_memo?: string | null; cancel_reason?: string | null; overwrite_memo?: boolean; task_rounding_units?: Record<number, number> }): Promise<{ updated_count: number; updated_task_ids: number[]; status_summary: Record<string, number>; message: string }> {
  return request(`/price-adjustment/tasks/bulk`, { method: "PATCH", body: JSON.stringify(body) });
}

// [ERP-PRICE-ADJUSTMENT-ERP-APPLY-WITH-HISTORY-AND-ROLLBACK-20260608-001]
// Phase 3.1 — ERP 가격 반영 Preview (read-only, DB write 0). 실제 Apply 는 별도 차수.
export interface ErpApplyPlatformDiff {
  platform: string;
  label: string;
  multiplier: number;
  before: number | null;
  after: number;
  delta: number | null;
  changed: boolean;
}
export interface ErpApplyPreviewItem {
  task_id: number;
  sku?: string;
  product_name?: string | null;
  option1?: string | null;
  option2?: string | null;
  status?: string;
  target_price?: number | null;
  current_sale_price?: number | null;
  proposed_sale_price?: number | null;
  sale_price_changed?: boolean;
  platform_prices?: ErpApplyPlatformDiff[];
  warnings: string[];
  can_apply: boolean;
}
export interface ErpApplyPreviewResponse {
  rounding_unit: number;
  total: number;
  appliable_count: number;
  items: ErpApplyPreviewItem[];
  note: string;
}
export function erpApplyPreview(body: { task_ids: number[]; rounding_unit?: 10 | 100 | 1000 }): Promise<ErpApplyPreviewResponse> {
  return request(`/price-adjustment/tasks/erp-apply-preview`, { method: "POST", body: JSON.stringify(body) });
}

// [ERP-PRICE-ADJUSTMENT-ERP-APPLY-ACTIVATE-WITH-AUDIT-20260609-001]
// 실제 ERP 가격 반영 Apply (관리자 전용). product_prices + products.sale_price UPDATE 발생.
export interface ErpApplyResponse {
  applied_count: number;
  applied: { task_id: number; sku: string; target_price: number;
    before: { sale_price: number | null; product_prices: Record<string, number | null> };
    after: { sale_price: number; product_prices: Record<string, number> } }[];
  rounding_unit: number;
  message: string;
}
export function erpApply(body: { task_ids: number[]; rounding_unit?: 10 | 100 | 1000; confirm: boolean }): Promise<ErpApplyResponse> {
  return request(`/price-adjustment/tasks/erp-apply`, { method: "POST", body: JSON.stringify(body) });
}

// ── 합배송 그룹 [ERP-ORDER-COMBINE-SHIPPING-GROUP-SCHEMA-20260602-001] ──
export interface CombineGroupOrder {
  order_no: string;
  role: string;
  payment_required: boolean;
  payment_confirmed: boolean;
  amount_expected: number | null;
  receiver_match_status: string;
  include_in_packing: boolean;
}
export interface CombineGroup {
  group_no: string;
  status: string;
  representative_order_no: string;
  representative_invoice_no: string | null;
  receiver_name_snapshot: string | null;
  receiver_phone_snapshot: string | null;
  receiver_address1_snapshot: string | null;
  shipping_fee_policy: string;
  shipping_fee_note: string | null;
  memo: string | null;
  created_by_name_snapshot: string | null;
  created_at: string | null;
  cancelled_at: string | null;
  orders: CombineGroupOrder[];
}
export interface CombinePreviewResult {
  can_create: boolean;
  block_reasons: string[];
  warnings: string[];
  representative_order: Record<string, unknown> | null;
  additional_orders: Record<string, unknown>[];
  receiver_match_status: Record<string, string>;
  shipping_fee_policy: string;
  suggested_group_status: string;
  message: string;
}

export function previewCombinedShipping(body: { representative_order_no: string; additional_order_nos: string[]; shipping_fee_policy?: string; shipping_fee_note?: string | null; memo?: string | null }): Promise<CombinePreviewResult> {
  return request<CombinePreviewResult>(`/orders/combined-shipping/preview`, { method: "POST", body: JSON.stringify(body) });
}
export function createCombinedShipping(body: { representative_order_no: string; additional_order_nos: string[]; shipping_fee_policy?: string; shipping_fee_note?: string | null; memo?: string | null; confirm_warnings?: boolean }): Promise<CombineGroup> {
  return request<CombineGroup>(`/orders/combined-shipping`, { method: "POST", body: JSON.stringify(body) });
}
export function listCombinedShippingByOrder(orderNo: string): Promise<{ total: number; items: CombineGroup[] }> {
  return request(`/orders/combined-shipping?order_no=${encodeURIComponent(orderNo)}`);
}
export function cancelCombinedShipping(groupNo: string): Promise<{ ok: boolean; group_no: string; status: string }> {
  return request(`/orders/combined-shipping/${encodeURIComponent(groupNo)}/cancel`, { method: "POST" });
}

// ─── 고객통합ID (Customer Identity) — Phase 1-B ─────────────────────
// [ERP-CUSTOMER-IDENTITY-PHASE-1B-CANDIDATE-MERGE-UI-20260617-001]
// 자동 통합 아님 — 조회는 로그인 사용자, 생성/수정/alias 변경은 admin 만(백엔드 require_admin).
export type CustomerIdentityAliasType =
  | "phone_real" | "phone_coupang_safe" | "phone_past"
  | "name_orderer" | "name_receiver" | "email" | "manual_note";
export type CustomerIdentityAliasSource =
  | "naver" | "coupang" | "as" | "order" | "manual" | "import";
export type CustomerIdentityConfidence = "high" | "medium" | "low" | "manual";

export interface CustomerIdentityAlias {
  id: number;
  group_id: number | null;
  alias_type: string;
  value: string;
  value_normalized: string | null;
  source: string | null;
  linked_customer_id: number | null;
  linked_order_id: number | null;
  linked_as_request_id: number | null;
  confidence: string | null;
  verified_by_user_id: number | null;
  verified_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerIdentityGroup {
  id: number;
  display_name: string | null;
  memo: string | null;
  is_active: boolean;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerIdentityGroupDetail extends CustomerIdentityGroup {
  aliases: CustomerIdentityAlias[];
}

export interface CustomerIdentityGroupListResponse {
  total: number;
  page: number;
  page_size: number;
  items: CustomerIdentityGroup[];
}

export interface CustomerIdentityCandidate {
  source_table: string;
  source_id: number;
  customer_name: string | null;
  phone_masked: string | null;
  phone?: string | null;          // [AS-001] 업무 식별용 원문
  phone_normalized: string | null;
  product_name: string | null;
  platform: string | null;
  confidence: string;
  reasons: string[];
  // [20260816] 판단용 집계(customers 후보만)
  address_summary?: string | null;
  order_count?: number | null;
  total_amount?: number | null;
  as_count?: number | null;
  current_group_id?: number | null;
}

export interface CustomerIdentityBase {
  customer_id: number;
  customer_name: string | null;
  phone_masked: string | null;
  phone?: string | null;          // [AS-001] 업무 식별용 원문
  address_summary: string | null;
  order_count: number | null;
  total_amount: number | null;
  as_count: number | null;
}

export interface CustomerIdentityCandidateResponse {
  query: string;
  candidates: CustomerIdentityCandidate[];
  base?: CustomerIdentityBase | null;
  note: string;
}

export function getCustomerIdentityGroups(params: {
  q?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
} = {}): Promise<CustomerIdentityGroupListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.is_active != null) qs.set("is_active", String(params.is_active));
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<CustomerIdentityGroupListResponse>(`/customer-identity/groups${suffix}`);
}

export function createCustomerIdentityGroup(payload: {
  display_name?: string | null;
  memo?: string | null;
}): Promise<CustomerIdentityGroup> {
  return request<CustomerIdentityGroup>(`/customer-identity/groups`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getCustomerIdentityGroup(groupId: number): Promise<CustomerIdentityGroupDetail> {
  return request<CustomerIdentityGroupDetail>(`/customer-identity/groups/${groupId}`);
}

export function updateCustomerIdentityGroup(groupId: number, payload: {
  display_name?: string | null;
  memo?: string | null;
  is_active?: boolean;
}): Promise<CustomerIdentityGroup> {
  return request<CustomerIdentityGroup>(`/customer-identity/groups/${groupId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function addCustomerIdentityAlias(groupId: number, payload: {
  alias_type: string;
  value: string;
  source?: string | null;
  confidence?: string | null;
  linked_customer_id?: number | null;
  linked_order_id?: number | null;
  linked_as_request_id?: number | null;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/groups/${groupId}/aliases`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCustomerIdentityAlias(aliasId: number, payload: {
  group_id?: number | null;
  alias_type?: string;
  value?: string;
  source?: string | null;
  confidence?: string | null;
  is_active?: boolean;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/aliases/${aliasId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function getCustomerIdentityCandidates(params: {
  q?: string;
  phone?: string;
  name?: string;
  customer_id?: number;
  order_id?: number;
  as_request_id?: number;
  limit?: number;
} = {}): Promise<CustomerIdentityCandidateResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.phone) qs.set("phone", params.phone);
  if (params.name) qs.set("name", params.name);
  if (params.customer_id != null) qs.set("customer_id", String(params.customer_id));
  if (params.order_id != null) qs.set("order_id", String(params.order_id));
  if (params.as_request_id != null) qs.set("as_request_id", String(params.as_request_id));
  if (params.limit != null) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<CustomerIdentityCandidateResponse>(`/customer-identity/candidates${suffix}`);
}

// ─── Phase 1-C: 업무 화면 연결 (lookup / link) ─────────────────────
// [ERP-CUSTOMER-IDENTITY-PHASE-1C-CUSTOMER360-AS-LINK-20260617-001]
export interface CustomerIdentityLookupResponse {
  linked_groups: CustomerIdentityGroup[];
  aliases: CustomerIdentityAlias[];
  candidates: CustomerIdentityCandidate[];
  warning_notes: string[];
}

export function lookupCustomerIdentity(params: {
  customer_id?: number;
  order_id?: number;
  as_request_id?: number;
  phone?: string;
  name?: string;
  include_candidates?: boolean;
} = {}): Promise<CustomerIdentityLookupResponse> {
  const qs = new URLSearchParams();
  if (params.customer_id != null) qs.set("customer_id", String(params.customer_id));
  if (params.order_id != null) qs.set("order_id", String(params.order_id));
  if (params.as_request_id != null) qs.set("as_request_id", String(params.as_request_id));
  if (params.phone) qs.set("phone", params.phone);
  if (params.name) qs.set("name", params.name);
  if (params.include_candidates != null) qs.set("include_candidates", String(params.include_candidates));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<CustomerIdentityLookupResponse>(`/customer-identity/lookup${suffix}`);
}

export function linkCustomerToIdentityGroup(groupId: number, payload: {
  customer_id: number;
  alias_type?: string | null;
  memo?: string | null;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/groups/${groupId}/link-customer`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function linkAsRequestToIdentityGroup(groupId: number, payload: {
  as_request_id: number;
  alias_type?: string | null;
  memo?: string | null;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/groups/${groupId}/link-as-request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// [ERP-CUSTOMER-IDENTITY-CUSTOMER-INFO-CENTERED-UX-DEV-20260806-046]
// 고객정보 중심 고객통합 UX — identity-summary / 360 / merge-preview / confirm-members.
export interface IdentityEntitlementSummary {
  group_id: number;
  group_code: string | null;
  group_name: string | null;
  issued: number;
  consumed: number;
  restored: number;
  revoked: number;
  available_balance: number;
  expired_balance: number;
  total_balance: number;
  nearest_expires_at: string | null;
}
export interface IdentitySummary {
  customer_id: number;
  customer_name: string;
  identity_group_id: number | null;
  group_display_name: string | null;
  group_is_active: boolean | null;
  member_count: number;
  candidates_count: number;
  has_conflict: boolean;
  status: "single" | "grouped" | "candidates" | "inactive_group";
  is_grouped: boolean;
  entitlement_summary: IdentityEntitlementSummary[];
}
export function getIdentitySummary(customerId: number): Promise<IdentitySummary> {
  return request<IdentitySummary>(`/customer-identity/customers/${customerId}/identity-summary`);
}

// [ERP-AS-INTAKE-UNIFIED-ID-CUSTOMER-CONTEXT-INTEGRATION-DEV-20260816]
// 검색결과 identity 상태 batch 조회(N+1 회피).
export interface IdentityStatusItem {
  customer_id: number;
  identity_group_id: number | null;
  is_grouped: boolean;
  member_count: number;
}
export function getIdentityStatusBatch(customer_ids: number[]): Promise<{ items: IdentityStatusItem[] }> {
  return request<{ items: IdentityStatusItem[] }>(`/customer-identity/identity-status-batch`, {
    method: "POST",
    body: JSON.stringify({ customer_ids }),
  });
}

export interface Identity360Member {
  customer_id: number;
  customer_name: string;
  phone_masked: string | null;
  address_summary: string | null;
  source: string | null;
  first_order_date: string | null;
  last_order_date: string | null;
  total_order_count: number;
  active_group_id: number | null;
}
export interface Identity360History {
  ledger_id: number;
  event_type: string;
  delta: number;
  group_id: number;
  group_code: string | null;
  source_order_id: number | null;
  source_order_item_id: number | null;
  as_request_id: number | null;
  sku: string | null;
  expires_at: string | null;
  reason: string | null;
  created_at: string | null;
}
export interface Identity360 {
  group: { group_id: number; display_name: string | null; is_active: boolean; member_count: number; memo: string | null };
  members: Identity360Member[];
  orders: {
    total: number; total_amount: number;
    recent: Array<{ order_id: number; order_no: string; paid_amount: number; order_date: string | null; customer_id: number }>;
    items: Array<{ order_id: number; order_no: string; sku: string | null; product_name: string | null; option_name: string | null; quantity: number | null }>;
  };
  as_requests: {
    total: number; in_progress: number; done: number;
    recent: Array<{ as_request_id: number; as_no: string; status: string; product_name: string | null; received_date: string | null; customer_id: number }>;
  };
  entitlement_balances: IdentityEntitlementSummary[];
  entitlement_history: Identity360History[];
}
export function getCustomer360(groupId: number): Promise<Identity360> {
  return request<Identity360>(`/customer-identity/groups/${groupId}/360`);
}

export interface IdentityMergePreview {
  base_customer_id: number;
  base_customer_name: string;
  base_group_id: number | null;
  action: "create_group" | "add_to_group";
  selected: Array<{ customer_id: number; customer_name: string; phone_masked: string | null; current_group_id: number | null; order_count: number; as_count: number }>;
  conflicts: Array<{ customer_id: number; reason: string; group_id?: number }>;
  warnings: string[];
  can_confirm: boolean;
}
export function getIdentityMergePreview(base_customer_id: number, selected_customer_ids: number[]): Promise<IdentityMergePreview> {
  return request<IdentityMergePreview>(`/customer-identity/merge-preview`, {
    method: "POST",
    body: JSON.stringify({ base_customer_id, selected_customer_ids }),
  });
}
export function confirmIdentityMembers(payload: {
  base_customer_id: number;
  selected_customer_ids: number[];
  reason?: string;
  expected_base_group_id?: number | null;
}): Promise<{ group_id: number; created_group: boolean; added_customer_ids: number[]; member_count: number }> {
  return request(`/customer-identity/confirm-members`, {
    method: "POST",
    body: JSON.stringify({ confirm: true, ...payload }),
  });
}

// [ERP-AS-OWNERSHIP-TRANSFER-LEDGER-DISPLAY-ONLY-DEV-20260809-050A]
// AS 권리 소유권 이전 — 표시·이력 전용(consume scope 미반영). transfer/reverse append-only.
export interface OwnershipCustomerBrief { customer_id: number; customer_name: string | null }
export interface OwnershipHistoryItem {
  transfer_id: number;
  event_type: "transfer" | "reverse_transfer";
  from_customer: OwnershipCustomerBrief | null;
  to_customer: OwnershipCustomerBrief | null;
  related_transfer_id: number | null;
  reason: string | null;
  created_by_user_id: number | null;
  created_at: string | null;
}
export interface OwnershipPreview {
  issue_ledger_id: number;
  entitlement_group_id: number | null;
  entitlement_group_name: string | null;
  sku: string | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  option3?: string | null; // [FW-032] additive
  purchase_order_no: string | null;
  validity_type: string | null;
  expires_at: string | null;
  transferable: boolean;
  block_reasons: string[];
  original_buyer: OwnershipCustomerBrief | null;
  current_owner: OwnershipCustomerBrief | null;
  target_owner: OwnershipCustomerBrief | null;
  remaining_quantity: number;
  transfer_history: OwnershipHistoryItem[];
  consume_scope_applied: boolean;
  note: string;
}
export interface OwnershipLot {
  issue_ledger_id: number;
  entitlement_group_id: number | null;
  entitlement_group_name: string | null;
  sku: string | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  option3?: string | null; // [FW-032] additive
  purchase_order_no: string | null;
  validity_type: string | null;
  expires_at: string | null;
  current_owner: OwnershipCustomerBrief | null;
  original_buyer: OwnershipCustomerBrief | null;
}
export interface CustomerOwnership {
  customer_id: number;
  owned_via_transfer: OwnershipLot[];
  given_away: OwnershipLot[];
  consume_scope_applied: boolean;
}
export function previewOwnershipTransfer(issue_ledger_id: number, target_customer_id: number): Promise<OwnershipPreview> {
  return request<OwnershipPreview>(`/as-entitlements/ownership/preview`, {
    method: "POST",
    body: JSON.stringify({ issue_ledger_id, target_customer_id }),
  });
}
export function confirmOwnershipTransfer(payload: {
  issue_ledger_id: number;
  target_customer_id: number;
  expected_owner_customer_id?: number | null;
  reason?: string;
}): Promise<{ transfer_id: number; issue_ledger_id: number; from_customer_id: number; to_customer_id: number; current_owner: OwnershipCustomerBrief | null; consume_scope_applied: boolean }> {
  return request(`/as-entitlements/ownership/confirm`, {
    method: "POST",
    body: JSON.stringify({ confirm: true, ...payload }),
  });
}
export function reverseOwnershipTransfer(transferId: number, reason: string): Promise<{ transfer_id: number; reversed_transfer_id: number; issue_ledger_id: number; current_owner: OwnershipCustomerBrief | null }> {
  return request(`/as-entitlements/ownership/${transferId}/reverse`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}
export function getOwnershipHistory(issueLedgerId: number): Promise<{ issue_ledger_id: number; original_buyer: OwnershipCustomerBrief | null; current_owner: OwnershipCustomerBrief | null; transfer_count: number; history: OwnershipHistoryItem[]; consume_scope_applied: boolean }> {
  return request(`/as-entitlements/ownership/history?issue_ledger_id=${issueLedgerId}`);
}
export function getCustomerOwnership(customerId: number): Promise<CustomerOwnership> {
  return request<CustomerOwnership>(`/as-entitlements/ownership/customer/${customerId}`);
}

// [AS-002-ORDER-DETAIL-SIMPLE-ENTITLEMENT-TRANSFER] 주문 상세 단순 승계(whole-lot 재사용)
export interface AsSuccessionLot {
  issue_ledger_id: number;
  entitlement_group_id: number | null;
  entitlement_group_name: string | null;
  sku: string | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  option3?: string | null; // [FW-032] additive
  purchase_order_no: string | null;
  validity_type: string | null;
  expires_at: string | null;
  order_item_id: number | null;
  remaining_quantity: number;
  original_owner: OwnershipCustomerBrief | null;
  current_owner: OwnershipCustomerBrief | null;
  // [AS-002] 현재 소유자 연락처·주소(주문 상세 '현재 소유자' 표시·AS 접수 기본값, additive)
  current_owner_contact?: {
    customer_id: number;
    customer_name: string | null;
    phone: string | null;
    zipcode: string | null;
    address1: string | null;
    address2: string | null;
  } | null;
  transferable: boolean;
  block_reasons: string[];
  transfer_history: OwnershipHistoryItem[];
}
export interface AsOrderTransferableLots {
  order_no: string;
  order_id: number;
  original_buyer: OwnershipCustomerBrief | null;
  lots: AsSuccessionLot[];
  transferable_count: number;
  has_transferable: boolean;
}
export function getOrderTransferableLots(orderNo: string): Promise<AsOrderTransferableLots> {
  return request<AsOrderTransferableLots>(`/as-entitlements/ownership/order/${encodeURIComponent(orderNo)}/lots`);
}
export interface AsSuccessionNewCustomerResult {
  // 성공(승계) 응답
  transfer_id?: number;
  issue_ledger_id?: number;
  current_owner?: OwnershipCustomerBrief | null;
  created_customer_id?: number;
  created_customer?: OwnershipCustomerBrief | null;
  consume_scope_applied?: boolean;
  // 전화 중복 후보(자동 생성 안 함)
  needs_customer_decision?: boolean;
  message?: string;
  duplicate_candidates?: { customer_id: number; customer_name: string; phone: string | null; zipcode: string | null; address1: string | null; address2: string | null }[];
}
export function confirmSuccessionNewCustomer(payload: {
  issue_ledger_id: number;
  expected_owner_customer_id?: number | null;
  new_customer: { customer_name: string; phone?: string | null; zipcode?: string | null; address1?: string | null; address2?: string | null };
  allow_duplicate?: boolean;
  reason?: string | null;
}): Promise<AsSuccessionNewCustomerResult> {
  return request<AsSuccessionNewCustomerResult>(`/as-entitlements/ownership/confirm-new-customer`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ─── [AS-002 Phase 2b-2D] 제품단위 승계 공개 계약 ─────────────────────
// 승계의 본질 = 실물 제품 소유권 이전. AS권 유무·잔여와 무관하게 진입 가능하다.
// 실물 식별 = (order_item_id, unit_seq). Preview 는 완전 READ-ONLY(서버 write 0).
export interface ProductUnitRef { order_item_id: number; unit_seq: number }

/** 제품단위 소유자 요약 — 연락처·주소 포함(AS 접수 기본값·회수지/배송지 파생용). */
export interface ProductUnitOwnerBrief {
  customer_id: number;
  customer_name: string | null;
  phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
}

export interface ProductUnitRow extends ProductUnitRef {
  product_unit_id: number | null;
  materialized: boolean;
  current_owner: ProductUnitOwnerBrief | null;
  current_owner_customer_id: number | null;
  source: "materialized" | "legacy" | "original";
  legacy_ambiguous: boolean;
  selectable: boolean;
}

export interface OrderUnitEntitlementLot {
  issue_ledger_id: number;
  entitlement_group_id: number | null;
  entitlement_per_unit?: number | null;
  lot_remaining?: number;
  balances?: { customer_id: number; quantity: number }[];
  block_reason?: { code: string; message: string } | null;
}

export interface OrderUnitItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number;
  valid_unit_count: number | null;
  units: ProductUnitRow[];
  entitlement_lots: OrderUnitEntitlementLot[];
  // SKU 변경 등으로 실물 수를 확정할 수 없으면 추측하지 않고 사유를 표시한다(409 STOP).
  block_reason: { code: string; message: string } | null;
  selectable: boolean;
}

export interface OrderUnitsResponse {
  order_no: string;
  order_id: number;
  original_buyer: ProductUnitOwnerBrief | null;
  items: OrderUnitItem[];
  unit_count: number;
  distinct_owner_count: number;
  has_selectable_unit: boolean;
  // [AS-002-FOLLOWUP] 완료된 제품 승계 + 건별 AS권 추가 승계 상태(backend SoT).
  //   계산 불가 시 목록은 비고 `completed_successions_error` 에 사유가 온다(조용한 숨김 아님).
  completed_successions?: CompletedSuccession[];
  completed_successions_error?: { code: string; message: string } | null;
  has_entitlement_followup?: boolean;
}

export function getOrderUnits(orderNo: string): Promise<OrderUnitsResponse> {
  return request<OrderUnitsResponse>(`/as-entitlements/ownership/order/${encodeURIComponent(orderNo)}/units`);
}

export interface EntitlementSelection { issue_ledger_id: number; quantity: number }

export interface UnitsPreviewResponse {
  products: (ProductUnitRef & { sku: string | null; product_name: string | null; option_name: string | null })[];
  product_count: number;
  current_owner: ProductUnitOwnerBrief | null;
  new_owner: (ProductUnitOwnerBrief & { is_new?: boolean }) | { is_new: true; customer_name?: string } | null;
  entitlement_lots: {
    issue_ledger_id: number;
    entitlement_group_id: number | null;
    transfer_quantity: number;
    max_selectable: number;
    lot_remaining: number;
    before: { customer_id: number; quantity: number }[];
    after: { customer_id: number; quantity: number }[];
    // [2b-2D 보완] 신규 고객은 DB id 가 없으므로 after 목록에 넣지 않고 별도 필드로 제공한다.
    //   항등식: Σ after(기존 고객) + new_owner_after ≡ lot_remaining
    current_owner_before: number;
    current_owner_after: number;
    new_owner_before: number | null;
    new_owner_after: number | null;
    new_owner_is_new: boolean;
  }[];
  entitlement_total_selected: number;
  product_only: boolean;
  new_owner_is_new: boolean;
  write_performed: false;
}

export function previewUnitsSuccession(payload: {
  units: ProductUnitRef[];
  expected_owner_customer_id: number;
  target_customer_id?: number | null;
  new_customer?: { customer_name: string; phone?: string | null; zipcode?: string | null; address1?: string | null; address2?: string | null } | null;
  entitlements?: EntitlementSelection[] | null;
}): Promise<UnitsPreviewResponse> {
  return request<UnitsPreviewResponse>(`/as-entitlements/ownership/units/preview`, {
    method: "POST", body: JSON.stringify(payload),
  });
}

export interface UnitsConfirmResponse {
  transfers?: { product_unit_id: number; transfer_id: number; order_item_id: number; unit_seq: number; idempotent?: boolean }[];
  entitlement_transfers?: { entitlement_qty_transfer_id: number; issue_ledger_id: number; quantity: number; unit_seq?: number }[];
  to_customer_id?: number;
  idempotent?: boolean;
  created_customer?: { customer_id: number; customer_name: string } | null;
  new_owner?: ProductUnitOwnerBrief | null;
  // 전화 중복 후보(자동 생성 안 함)
  needs_customer_decision?: boolean;
  message?: string;
  duplicate_candidates?: { customer_id: number; customer_name: string; phone: string | null; zipcode: string | null; address1: string | null; address2: string | null }[];
}

export function confirmUnitsSuccession(payload: {
  units: ProductUnitRef[];
  expected_owner_customer_id: number;
  idempotency_key: string;
  target_customer_id?: number | null;
  new_customer?: { customer_name: string; phone?: string | null; zipcode?: string | null; address1?: string | null; address2?: string | null } | null;
  allow_duplicate?: boolean;
  entitlements?: EntitlementSelection[] | null;
  reason?: string | null;
}): Promise<UnitsConfirmResponse> {
  return request<UnitsConfirmResponse>(`/as-entitlements/ownership/units/confirm`, {
    method: "POST", body: JSON.stringify(payload),
  });
}

/** 제품 이전 + 동반 권리 이전을 정확히 한 번 되돌린다(admin). */
export function reverseUnitsSuccession(productUnitTransferId: number, reason: string) {
  return request(`/as-entitlements/ownership/units/${productUnitTransferId}/reverse`, {
    method: "POST", body: JSON.stringify({ reason }),
  });
}

export interface UnitHistoryResponse {
  order_item_id: number;
  unit_seq: number;
  product_unit_id: number | null;
  materialized: boolean;
  source: string;
  current_owner: ProductUnitOwnerBrief | null;
  events: {
    transfer_id: number; event_type: string;
    from: ProductUnitOwnerBrief | null; to: ProductUnitOwnerBrief | null;
    is_legacy_adoption: boolean; reason: string | null; created_at: string;
    entitlement_transfers: { id: number; issue_ledger_id: number; quantity: number }[];
  }[];
}

export function getUnitHistory(orderItemId: number, unitSeq: number): Promise<UnitHistoryResponse> {
  return request<UnitHistoryResponse>(`/as-entitlements/ownership/units/${orderItemId}/${unitSeq}/history`);
}

// ─── [AS-002-FOLLOWUP] 제품 승계 완료 후 **AS권 추가 승계** ─────────────────
// 제품만 승계되고 남은 미사용 AS권이 원소유자에 남아 있을 때, 기존 승계 기록(연결키 =
// product_unit_transfer_id)을 근거로 잔여 권리만 뒤늦게 이전한다.
//   · 신규 발급·잔액 초기화·사용/소멸분 복원 0 (잔여만 이동)
//   · 버튼 라벨·활성 판정은 backend SoT(state/label) — frontend 추정 금지
//   · Preview 는 완전 READ-ONLY, Confirm 은 단일 트랜잭션·멱등
export type EntitlementFollowupState = "eligible" | "already_done" | "no_remaining" | "blocked";

export interface EntitlementFollowupLot {
  issue_ledger_id: number;
  entitlement_group_id: number | null;
  group_code_snapshot?: string | null;
  expires_at?: string | null;
  entitlement_per_unit: number | null;
  already_transferred_quantity: number;
  already_transferred_ids: number[];
  from_balance: number | null;
  to_balance?: number | null;
  lot_remaining: number | null;
  max_selectable: number;
  block_reason: { code: string; message: string } | null;
}

export interface CompletedSuccession {
  product_unit_transfer_id: number;
  product_unit_id: number;
  order_item_id: number;
  unit_seq: number;
  order_id: number | null;
  order_no: string | null;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  event_type: string;
  transferred_at: string | null;
  created_by_user_id: number | null;
  reason: string | null;
  is_legacy_adoption: boolean;
  /** 원소유자(= 잔여 AS권 보유자). 승계 기록에서만 확정된다. */
  from_customer: ProductUnitOwnerBrief | null;
  /** 새 소유자(= 제품을 승계받은 고객). */
  to_customer: ProductUnitOwnerBrief | null;
  reversed: boolean;
  is_latest: boolean;
  current_owner_customer_id: number | null;
  lots: EntitlementFollowupLot[];
  transferable_total: number;
  already_transferred_total: number;
  state: EntitlementFollowupState;
  label: string;
  block_reason: { code: string; message: string } | null;
  needs_user_decision: boolean;
}

export interface EntitlementFollowupPreview {
  product_unit_transfer_id: number;
  state: EntitlementFollowupState;
  label: string;
  product: {
    order_item_id: number; unit_seq: number; sku: string | null;
    product_name: string | null; option_name: string | null; order_no: string | null;
  };
  from_customer: ProductUnitOwnerBrief | null;
  to_customer: ProductUnitOwnerBrief | null;
  transferred_at: string | null;
  entitlement_lots: {
    issue_ledger_id: number;
    entitlement_group_id: number | null;
    transfer_quantity: number;
    max_selectable: number;
    lot_remaining: number;
    entitlement_per_unit: number | null;
    before: { customer_id: number; quantity: number }[];
    after: { customer_id: number; quantity: number }[];
    from_owner_before: number; from_owner_after: number;
    to_owner_before: number; to_owner_after: number;
  }[];
  entitlement_total_selected: number;
  already_transferred_total: number;
  needs_user_decision: boolean;
  reason: string;
  write_performed: false;
}

export interface EntitlementFollowupConfirmResponse {
  already_complete: boolean;
  idempotent: boolean;
  state: EntitlementFollowupState;
  label: string;
  product_unit_transfer_id: number;
  from_customer_id: number;
  to_customer_id: number;
  entitlement_transfers: {
    entitlement_qty_transfer_id: number; issue_ledger_id: number;
    entitlement_group_id: number | null; quantity: number;
    product_unit_transfer_id: number; unit_seq?: number;
  }[];
  already_transferred: {
    issue_ledger_id: number; quantity: number; entitlement_qty_transfer_ids: number[];
  }[];
  needs_user_decision: boolean;
  reason?: string;
}

/** 완료된 제품 승계 목록은 `getOrderUnits()` 응답의 `completed_successions` 로 함께 내려온다. */

// ⚠️ 추가 승계는 **기존 제품 승계 endpoint 를 그대로** 쓴다(신규 우회 API 없음).
//    `entitlement_followup_transfer_id`(= 완료된 제품 승계 연결키)가 모드를 결정하고,
//    확정은 제품 승계 확정과 **동일한 업무 권한 게이트**를 그대로 통과한다(권한 code 는
//    backend registry·UI hook 이 SoT — API client 는 code 문자열을 들고 있지 않는다).
export interface EntitlementFollowupPayload {
  /** 연결키 = 이미 완료된 제품 승계(product_unit_transfers.id) */
  product_unit_transfer_id: number;
  expected_from_customer_id?: number | null;
  expected_to_customer_id?: number | null;
  /** 생략하면 이전 가능한 잔여 전량(서버가 lot 별 상한으로 산출) */
  entitlements?: EntitlementSelection[] | null;
}

function followupBody(p: EntitlementFollowupPayload) {
  return {
    entitlement_followup_transfer_id: p.product_unit_transfer_id,
    expected_from_customer_id: p.expected_from_customer_id ?? null,
    expected_to_customer_id: p.expected_to_customer_id ?? null,
    entitlements: p.entitlements ?? null,
  };
}

export function previewEntitlementFollowup(p: EntitlementFollowupPayload): Promise<EntitlementFollowupPreview> {
  return request<EntitlementFollowupPreview>(`/as-entitlements/ownership/units/preview`, {
    method: "POST", body: JSON.stringify(followupBody(p)),
  });
}

export function confirmEntitlementFollowup(p: EntitlementFollowupPayload): Promise<EntitlementFollowupConfirmResponse> {
  return request<EntitlementFollowupConfirmResponse>(`/as-entitlements/ownership/units/confirm`, {
    method: "POST", body: JSON.stringify(followupBody(p)),
  });
}

// ─── 무상 AS 정책 / 대상 SKU (Phase 1.5-B) ─────────────────────────
// [ERP-WARRANTY-POLICY-PHASE-1.5B-MANAGEMENT-UI-20260617-001]
// 조회=로그인 사용자, 정책 생성/수정=admin, SKU add/deactivate=lv2+ (백엔드 require_level("lv2")).
export type WarrantyPolicyType = "lifetime_once" | "period_once" | "period_unlimited" | "manual";
export type WarrantyCoverageScope = "parts_repair_only" | "parts_repair_and_shipping" | "full";

export interface WarrantyPolicy {
  id: number;
  policy_code: string;
  policy_name: string;
  policy_type: string;
  coverage_period_days: number | null;
  max_free_as_count: number;
  coverage_scope: string;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  memo: string | null;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface WarrantyPolicyProduct {
  id: number;
  policy_id: number;
  sku: string;
  product_name_snapshot: string | null;
  option1_snapshot: string | null;
  option2_snapshot: string | null;
  is_active: boolean;
  added_by_user_id: number | null;
  removed_by_user_id: number | null;
  removed_at: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface WarrantyPolicyWithProducts extends WarrantyPolicy {
  products: WarrantyPolicyProduct[];
}

export interface WarrantyPolicyListResponse {
  total: number;
  page: number;
  page_size: number;
  items: WarrantyPolicy[];
}

export interface EligibleWarrantySku {
  policy_id: number;
  policy_code: string;
  sku: string;
  product_name_snapshot: string | null;
}

export interface EligibleWarrantySkuResponse {
  total: number;
  items: EligibleWarrantySku[];
}

export function getWarrantyPolicies(params: {
  q?: string;
  is_active?: boolean;
  policy_type?: string;
  page?: number;
  page_size?: number;
} = {}): Promise<WarrantyPolicyListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.is_active != null) qs.set("is_active", String(params.is_active));
  if (params.policy_type) qs.set("policy_type", params.policy_type);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<WarrantyPolicyListResponse>(`/warranty-policies${suffix}`);
}

export function createWarrantyPolicy(payload: {
  policy_code: string;
  policy_name: string;
  policy_type: string;
  coverage_period_days?: number | null;
  max_free_as_count?: number;
  coverage_scope?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  memo?: string | null;
}): Promise<WarrantyPolicy> {
  return request<WarrantyPolicy>(`/warranty-policies`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getWarrantyPolicy(policyId: number): Promise<WarrantyPolicyWithProducts> {
  return request<WarrantyPolicyWithProducts>(`/warranty-policies/${policyId}`);
}

export function updateWarrantyPolicy(policyId: number, payload: {
  policy_name?: string;
  memo?: string | null;
  is_active?: boolean;
  ends_at?: string | null;
  policy_type?: string;
  coverage_period_days?: number | null;
  max_free_as_count?: number;
  coverage_scope?: string;
}): Promise<WarrantyPolicy> {
  return request<WarrantyPolicy>(`/warranty-policies/${policyId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function addWarrantyPolicyProduct(policyId: number, payload: {
  sku: string;
  product_name_snapshot?: string | null;
  option1_snapshot?: string | null;
  option2_snapshot?: string | null;
  memo?: string | null;
  allow_unknown_sku?: boolean;
}): Promise<WarrantyPolicyProduct> {
  return request<WarrantyPolicyProduct>(`/warranty-policies/${policyId}/products`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateWarrantyPolicyProduct(policyProductId: number, payload: {
  is_active?: boolean;
  memo?: string | null;
}): Promise<WarrantyPolicyProduct> {
  return request<WarrantyPolicyProduct>(`/warranty-policies/products/${policyProductId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function getEligibleWarrantySkus(params: { policy_id?: number } = {}): Promise<EligibleWarrantySkuResponse> {
  const qs = new URLSearchParams();
  if (params.policy_id != null) qs.set("policy_id", String(params.policy_id));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<EligibleWarrantySkuResponse>(`/warranty-policies/eligible-skus${suffix}`);
}

// [ERP-DASHBOARD-STAFF-WORK-INSTRUCTIONS-MVP-20260622-001]
// 운영대시보드 업무지시 카드 — 대표→직원 지시 (공통/강원준/강동준).
export interface WorkInstructionSection {
  title: string;
  content: string;
  updated_at: string;
  updated_by: string;
}
export interface WorkInstructionsResponse {
  can_edit: boolean;
  // 사용자가 볼 수 있는 구역만 (key → 내용)
  sections: Record<string, WorkInstructionSection>;
}
export function getWorkInstructions() {
  return request<WorkInstructionsResponse>("/dashboard/work-instructions");
}
export function saveWorkInstructions(body: {
  common: { title: string; content: string };
  kang_wonjun: { title: string; content: string };
  kang_dongjun: { title: string; content: string };
}) {
  return request<WorkInstructionsResponse>("/dashboard/work-instructions", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// [ERP-DASHBOARD-WORK-INSTRUCTIONS-BOARD-CHAT-COMPLETE-FLOW-DEV-20260622-001]
// 업무지시 보드 (타이틀 목록 → 상세 + 채팅 → 종료체크/승인/완료이력)
export interface DwiItem {
  id: number;
  section_key: string;
  title: string;
  content: string;
  status: string; // open | needs_check | done_requested | approved | archived
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  message_count: number;
  done_requested_by_name?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
}
export interface DwiSection {
  key: string;
  label: string;
  active: DwiItem[];
  completed: DwiItem[];
}
export interface DwiBoard {
  can_manage: boolean;
  own_section: string | null;
  sections: DwiSection[];
}
export interface DwiMessage {
  id: number;
  user_id: number | null;
  sender_name: string;
  sender_role: string | null;
  message: string;
  created_at: string;
}
export interface DwiDetail {
  instruction: DwiItem;
  messages: DwiMessage[];
  can_edit: boolean;
  can_request_done: boolean;
  can_approve: boolean;
}

export function getWorkInstructionBoard() {
  return request<DwiBoard>("/dashboard/work-instructions/board");
}
export function createWorkInstruction(body: { section_key: string; title: string; content: string }) {
  return request<DwiItem>("/dashboard/work-instructions", { method: "POST", body: JSON.stringify(body) });
}
export function getWorkInstructionDetail(id: number) {
  return request<DwiDetail>(`/dashboard/work-instructions/${id}`);
}
export function patchWorkInstruction(id: number, body: { title?: string; content?: string }) {
  return request<DwiItem>(`/dashboard/work-instructions/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteWorkInstruction(id: number) {
  return request<{ ok: boolean; id: number }>(`/dashboard/work-instructions/${id}`, { method: "DELETE" });
}
export function postWorkInstructionMessage(id: number, message: string) {
  return request<DwiMessage>(`/dashboard/work-instructions/${id}/messages`, { method: "POST", body: JSON.stringify({ message }) });
}
export function requestWorkInstructionDone(id: number) {
  return request<DwiItem>(`/dashboard/work-instructions/${id}/request-done`, { method: "POST", body: "{}" });
}
export function approveWorkInstruction(id: number) {
  return request<DwiItem>(`/dashboard/work-instructions/${id}/approve`, { method: "POST", body: "{}" });
}

// [ERP-AS-CHECKLIST-TEMPLATE-PHASE1-DEV-20260630-001] AS 체크리스트 마스터
export interface AsChecklistNode {
  id: number;
  parent_id: number | null;
  level: "group" | "category" | "title" | "item";
  label: string;
  sort_order: number;
  applies_to: string;
  checklist_type: "intake" | "repair_check" | "both";
  is_active: boolean;
  children: AsChecklistNode[];
}
export function getAsChecklistTree(params?: { applies_to?: string; checklist_type?: string; include_inactive?: boolean }): Promise<{ tree: AsChecklistNode[]; levels: string[]; types: string[] }> {
  const qs = new URLSearchParams();
  if (params?.applies_to) qs.set("applies_to", params.applies_to);
  if (params?.checklist_type) qs.set("checklist_type", params.checklist_type);
  if (params?.include_inactive) qs.set("include_inactive", "true");
  const s = qs.toString();
  return request(`/as-checklist/tree${s ? "?" + s : ""}`);
}
export function createAsChecklistItem(body: { parent_id?: number | null; level: string; label: string; sort_order?: number; applies_to?: string; checklist_type?: string }): Promise<AsChecklistNode> {
  return request(`/as-checklist`, { method: "POST", body: JSON.stringify(body) });
}
export function patchAsChecklistItem(id: number, body: { label?: string; sort_order?: number; applies_to?: string; checklist_type?: string; is_active?: boolean; parent_id?: number }): Promise<AsChecklistNode> {
  return request(`/as-checklist/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deactivateAsChecklistItem(id: number): Promise<{ deactivated: boolean; id: number }> {
  return request(`/as-checklist/${id}/deactivate`, { method: "POST", body: "{}" });
}
export function reorderAsChecklistItems(orders: { id: number; sort_order: number }[]): Promise<{ updated: number }> {
  return request(`/as-checklist/reorder`, { method: "PATCH", body: JSON.stringify({ orders }) });
}

// [ERP-AS-CHECKLIST-TEMPLATE-PHASE2-DEV-20260630-002] AS 체크 결과
export interface AsChecklistCheckedItem { item_id: number | null; label_snapshot: string; }
export interface AsChecklistResultSet { checked: AsChecklistCheckedItem[]; memo: string | null; }
export interface AsChecklistResults { as_request_id: number; results: { intake: AsChecklistResultSet; repair_check: AsChecklistResultSet }; }
export function getAsChecklistResults(asRequestId: number): Promise<AsChecklistResults> {
  return request(`/as-checklist/results/${asRequestId}`);
}
export function saveAsChecklistResults(asRequestId: number, checklistType: "intake" | "repair_check", body: { item_ids: number[]; memo?: string | null }): Promise<{ saved: number }> {
  return request(`/as-checklist/results/${asRequestId}/${checklistType}`, { method: "PUT", body: JSON.stringify(body) });
}

// ══════════════════════════════════════════════════════════════
// [ERP-DEV-FRONTEND-PENDING-UI-PHASE1-20260722-001]
// Phase 2 포장 이력 / Phase 3 교환 미회수 큐 — 조회 전용 클라이언트.
// 두 endpoint 모두 GET(read-only). 화면 진입만으로 DB write 가 발생하지 않는다.
// ══════════════════════════════════════════════════════════════

/** 포장 기록 송장 표시 상태 — [PACKING-COMPLETION-SAFETY-GUARDS-DEV-20260922] #3.
 *  registered                = 실제 송장이 기록됨(invoice_no 표시)
 *  not_registered_at_packing = 포장 당시 송장 미등록(주문번호 placeholder 포함)
 *  unverified_legacy         = 백필 기록 — 당시 송장 유무 확인 근거 없음
 */
export type PackingEventInvoiceStatus =
  | "registered"
  | "not_registered_at_packing"
  | "unverified_legacy";

/** 포장 처리 이벤트 1건 (append-only 원장 packing_events). */
export interface PackingEvent {
  id: number;
  /** 실제 송장번호. placeholder(주문번호)/미등록이면 null — 주문번호로 fallback 하지 말 것. */
  invoice_no: string | null;
  invoice_status: PackingEventInvoiceStatus;
  /** 서버 한글 라벨(포장 당시 송장 미등록 / 기존 기록 · 송장 확인 필요). registered 면 null */
  invoice_status_label: string | null;
  /** packed | cancelled | repacked */
  action: string;
  /** 서버가 내려주는 한글 라벨(포장완료/포장취소/재처리) */
  action_label: string;
  /** scan | manual (null=기록 없음 — 과거 데이터) */
  packing_method: string | null;
  /** 서버 한글 라벨(송장 스캔/수동 처리). null 이면 기록 없음 */
  packing_method_label: string | null;
  performed_by_user_id: number | null;
  /** 서버 인증 사용자 이름. null 이면 확인 불가 */
  performed_by_name: string | null;
  /** ISO8601(서버 시각) */
  performed_at: string | null;
  reason: string | null;
  /** [FW-004] 포장 완료 취소 이벤트의 재고 원복 합계(그 외 이벤트는 null). */
  restored_quantity?: number | null;
}

/** [FW-004] 포장 완료 취소 가능 여부(업무 상태만 — 권한은 /me/action-permissions 로 따로 판정). */
export interface PackingCancelEligibility {
  allowed: boolean;
  /** 차단 코드(PACKING_HANDED_OVER 등). 허용이면 null */
  code: string | null;
  /** 차단 사유(서버 문구). 허용이면 null */
  message: string | null;
  /** 취소 대상 포장 이벤트(packed/repacked) id — 취소 요청의 expected_event_id */
  pack_event_id: number | null;
}

export interface PackingEventsResponse {
  order_no: string;
  /** 현재 주문에 등록된 실제 송장(별도 정보). 포장 당시 송장과 혼동 표시 금지. */
  current_order_invoice_no: string | null;
  events: PackingEvent[];
  cancel_eligibility?: PackingCancelEligibility;
}

export interface PackingCancelResult {
  ok: boolean;
  /** 같은 포장 이벤트를 이미 취소한 재요청이면 true(추가 처리 없음) */
  idempotent: boolean;
  order_no: string;
  cancel_event_id: number;
  order_status: string | null;
  restored: { sku: string; location_code: string; quantity: number }[];
}

/** [FW-004] 포장 완료 취소 — 사유 필수. 작업자·시각은 서버가 정한다. */
export function cancelPackingCompletion(
  orderNo: string,
  body: { reason: string; expected_event_id: number | null },
): Promise<PackingCancelResult> {
  return request<PackingCancelResult>(
    `/orders/${encodeURIComponent(orderNo)}/packing-cancel`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** 주문의 포장 처리 이력(시간 역순). read-only. */
export function getPackingEvents(orderNo: string): Promise<PackingEventsResponse> {
  return request<PackingEventsResponse>(
    `/orders/${encodeURIComponent(orderNo)}/packing-events`,
  );
}

/** 교환 선재출고 후 기존상품 미회수 건 1행. */
export interface UncollectedExchange {
  receipt_id: number;
  original_order_no: string | null;
  customer_name: string | null;
  receiver_name: string | null;
  product_name: string | null;
  exchange_reason: string | null;
  replacement_hold: boolean;
  /** ship_first(선재출고) | recover_then_ship(회수 후 출고) */
  flow_type: string;
  flow_label: string;
  replacement_shipped: boolean;
  replacement_order_no: string | null;
  replacement_invoice_no: string | null;
  replacement_shipped_at: string | null;
  recovery_status: string;
  recovery_status_label: string;
  recovery_invoice_no: string | null;
  recovery_type: string | null;
  collected_at: string | null;
  received_at: string | null;
  assignee: string | null;
  /** 교환품 발송 후 경과일 */
  delay_days: number | null;
}

export interface UncollectedExchangeListResponse {
  count: number;
  items: UncollectedExchange[];
}

/** 교환품 발송완료 + 기존상품 미회수 큐. read-only(진입만으로 상태 변경 없음). */
export function getUncollectedExchanges(): Promise<UncollectedExchangeListResponse> {
  return request<UncollectedExchangeListResponse>("/returns/exchange/uncollected");
}

// ── Phase 1: 다품목 반품 접수 (return_receipt_items 기반) ──────────────

/** 반품 접수 화면용 주문상품 1행. remaining_quantity 가 이번에 반품 가능한 최대치. */
export interface ReturnableItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  order_quantity: number;
  already_returned_quantity: number;
  remaining_quantity: number;
  blocked_reason: string | null;
}

export interface ReturnableItemListResponse {
  order_no: string;
  order_status: string | null;
  items: ReturnableItem[];
}

/** 주문의 반품 가능 상품·잔여수량 조회. read-only. */
export function getReturnableItems(orderNo: string): Promise<ReturnableItemListResponse> {
  return request<ReturnableItemListResponse>(
    `/returns/orders/${encodeURIComponent(orderNo)}/returnable-items`,
  );
}

/** 회수건에 속한 반품 품목 명세(수량 흐름 + 확장 축). */
export interface ReturnReceiptItem {
  id: number;
  order_item_id: number | null;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  requested_quantity: number;
  received_quantity: number;
  inspected_quantity: number;
  restocked_quantity: number;
  defective_quantity: number;
  inspection_result: string | null;
  restock_decision: string | null;
  is_resellable: boolean | null;
  refund_amount: number | null;
  refund_status: string | null;
  memo: string | null;
}

export interface ReturnIntakeItemIn {
  order_item_id: number;
  quantity: number;
}

/**
 * 순수반품 다품목 접수 — 1 회수건 + N 품목(원자).
 * quantity>0 인 품목만 담아 보낸다. 서버가 품목별 1..잔여 범위를 재검증한다.
 * intake_idempotency_key 로 중복 클릭/재시도 시 회수건 중복 생성을 막는다.
 */
export function createReturnIntake(data: {
  order_no: string;
  items: ReturnIntakeItemIn[];
  recovery_type?: RecoveryType;
  recovery_courier?: string | null;
  return_invoice_no?: string | null;
  visit_date?: string | null;
  received_by?: string | null;
  memo?: string | null;
  expected_payment_amount?: number | null;
  payment_reason?: ReturnPaymentReason | null;
  intake_idempotency_key?: string | null;
}): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>("/returns/intakes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ─────────────────────────────────────────────────────────────────
// [ERP-AS-ENTITLEMENT-LEDGER-PHASE2A-MANUAL-RIGHT-SKU-ADMIN-DEV-20260729-009]
// AS 권리(Entitlement) 정책 관리 — 관리자 화면 /as-entitlements.
// ⚠️ 정책 관리만. Ledger 발급/compute_balance/자동생성 없음.
// ─────────────────────────────────────────────────────────────────
export interface EntitlementListItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  policy_id: number;
  active_version_id: number | null;
  active_version_no: number | null;
  rule_sku_count: number;
  created_at: string;
  updated_at: string;
}
export interface EntitlementListResponse {
  items: EntitlementListItem[];
  total: number;
}
// 권리 유효기간 유형(Version 레벨). null=미설정.
export type EntitlementValidityType = "lifetime" | "days" | "months" | "years";
export interface EntitlementVersionSummary {
  id: number;
  version_no: number;
  is_active: boolean;
  note: string | null;
  effective_from: string | null;
  effective_to: string | null;
  validity_type: EntitlementValidityType | null;
  validity_value: number | null;
  rule_count: number;
  created_at: string;
}
export interface EntitlementRuleOut {
  id: number;
  sku: string;
  product_id: number | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  option3?: string | null; // [FW-032] additive
  entitlement_per_unit: number;
  valid_from: string | null;
  valid_to: string | null;
  priority: number;
  is_active: boolean;
}
export interface EntitlementDetail {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  policy_id: number;
  versions: EntitlementVersionSummary[];
  active_version_id: number | null;
  active_version_no: number | null;
  active_validity_type: EntitlementValidityType | null;
  active_validity_value: number | null;
  rules: EntitlementRuleOut[];
  created_at: string;
  updated_at: string;
}
export interface EntitlementRuleInput {
  sku: string;
  entitlement_per_unit: number;
  valid_from?: string | null;
  valid_to?: string | null;
  priority?: number;
  product_id?: number | null;
}
export interface EntitlementPreviewIssue {
  level: "error" | "warning";
  code: string;
  sku: string | null;
  message: string;
}
export interface EntitlementPreviewResult {
  selected_sku_count: number;
  valid_rule_count: number;
  planned_row_count: number;
  issues: EntitlementPreviewIssue[];
  error_count: number;
  warning_count: number;
  blocked: boolean;
  db_write: boolean;
}
export interface EntitlementVersionCreateResponse {
  entitlement_id: number;
  new_version_id: number;
  new_version_no: number;
  rule_count: number;
  preview: EntitlementPreviewResult;
}

export function getEntitlements(params?: {
  q?: string;
  is_active?: boolean;
  sort?: string;
}): Promise<EntitlementListResponse> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.is_active !== undefined) qs.set("is_active", String(params.is_active));
  if (params?.sort) qs.set("sort", params.sort);
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<EntitlementListResponse>(`/as-entitlements${suffix}`);
}
export function createEntitlement(body: {
  name: string;
  code: string;
  description?: string | null;
  is_active?: boolean;
}): Promise<EntitlementDetail> {
  return request<EntitlementDetail>("/as-entitlements", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function getEntitlement(id: number): Promise<EntitlementDetail> {
  return request<EntitlementDetail>(`/as-entitlements/${id}`);
}
export function updateEntitlement(
  id: number,
  body: { name?: string; description?: string | null; is_active?: boolean },
): Promise<EntitlementDetail> {
  return request<EntitlementDetail>(`/as-entitlements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
export function previewEntitlementRules(
  id: number,
  rules: EntitlementRuleInput[],
): Promise<EntitlementPreviewResult> {
  return request<EntitlementPreviewResult>(`/as-entitlements/${id}/preview`, {
    method: "POST",
    body: JSON.stringify({ rules }),
  });
}
export function createEntitlementVersion(
  id: number,
  rules: EntitlementRuleInput[],
  opts?: {
    note?: string | null;
    confirm_warnings?: boolean;
    base_version_id?: number | null;
    // 권리 유효기간(-029). null=미설정, lifetime=평생무상(value 무시), days/months/years=value 양수.
    validity_type?: EntitlementValidityType | null;
    validity_value?: number | null;
  },
): Promise<EntitlementVersionCreateResponse> {
  return request<EntitlementVersionCreateResponse>(`/as-entitlements/${id}/versions`, {
    method: "POST",
    body: JSON.stringify({
      rules,
      note: opts?.note ?? null,
      confirm_warnings: opts?.confirm_warnings ?? false,
      // stale editor 방지(-012): 편집 시작 시점의 활성 Version id.
      base_version_id: opts?.base_version_id ?? null,
      validity_type: opts?.validity_type ?? null,
      validity_value: opts?.validity_value ?? null,
    }),
  });
}

// ─────────────────────────────────────────────────────────────────
// [ERP-AS-ENTITLEMENT-LEDGER-PHASE3-ISSUE-CONSUME-CORE-DEV-20260729-017]
// AS 권리 Ledger 조회 (read-only). 발급/차감/회수는 관리자 write API(별도).
// ─────────────────────────────────────────────────────────────────
export interface EntitlementLedgerRow {
  id: number;
  entitlement_group_id: number;
  group_code_snapshot: string | null;
  event_type: string;
  delta: number;
  source_order_id: number | null;
  source_order_item_id: number | null;
  source_sku_snapshot: string | null;
  as_request_id: number | null;
  as_warranty_usage_id: number | null;
  policy_rule_id: number | null;
  related_ledger_entry_id: number | null;
  reason: string | null;
  idempotency_key: string | null;
  created_by_user_id: number | null;
  created_at: string;
  // 유효기간 snapshot(-041)
  purchase_date_snapshot: string | null;
  validity_type_snapshot: string | null;
  validity_value_snapshot: number | null;
  expires_at: string | null;
  is_expired: boolean;
}
export interface EntitlementLedgerListResponse {
  total: number;
  items: EntitlementLedgerRow[];
}
export interface EntitlementBalanceItem {
  group_id: number;
  group_code: string | null;
  group_name: string | null;
  balance: number;
  // 만료 분리 잔액(-041, additive)
  available_balance: number | null;
  expired_balance: number | null;
  total_balance: number | null;
  nearest_expires_at: string | null;
}

export function getEntitlementLedger(params?: {
  group_id?: number;
  event_type?: string;
  order_id?: number;
  order_item_id?: number;
  as_request_id?: number;
  customer_id?: number;
  limit?: number;
  offset?: number;
}): Promise<EntitlementLedgerListResponse> {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<EntitlementLedgerListResponse>(`/as-entitlements/ledger${suffix}`);
}
export function getEntitlementBalances(): Promise<{ balances: EntitlementBalanceItem[] }> {
  return request<{ balances: EntitlementBalanceItem[] }>(`/as-entitlements/balances`);
}
export interface EntitlementCustomerBalances {
  customer_id: number;
  balances: EntitlementBalanceItem[];
  identity_group_id: number | null;
  representative_customer_id: number | null;
  member_customer_ids: number[];
  is_grouped: boolean;
}
export function getCustomerEntitlementBalances(
  customerId: number,
): Promise<EntitlementCustomerBalances> {
  return request<EntitlementCustomerBalances>(
    `/as-entitlements/balances/customer/${customerId}`,
  );
}

// [ERP-AS-ENTITLEMENT-MANUAL-ORDER-ISSUE-PREVIEW-CONFIRM-DEV-20260805-042]
// 관리자 개별 주문 AS 권리 발급 — Preview(read-only) + Confirm(admin).
export type OrderIssuePreviewStatus =
  | "ready"
  | "already_issued"
  | "policy_not_found"
  | "sku_missing"
  | "product_not_found"
  | "ambiguous_policy"
  | "excluded_split_child"
  | "excluded_exchange"
  | "excluded_quote"
  | "cancelled"
  | "purchase_date_missing";

export interface OrderIssuePreviewItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  quantity: number;
  status: OrderIssuePreviewStatus;
  eligible: boolean;
  already_issued: boolean;
  reason: string | null;
  matched_group_id: number | null;
  matched_group_name: string | null;
  policy_version_id: number | null;
  policy_rule_id: number | null;
  entitlement_per_unit: number | null;
  expected_quantity: number;
  validity_type: string | null;
  validity_value: number | null;
  expires_at: string | null;
  issued_ledger_id: number | null;
  issued_delta: number | null;
  issued_expires_at: string | null;
}

export interface OrderIssuePreviewResponse {
  order_id: number;
  order_no: string;
  order_type: string | null;
  order_source: string | null;
  order_status: string | null;
  customer_id: number | null;
  customer_name: string | null;
  identity_group_id: number | null;
  purchase_date: string | null;
  order_eligible: boolean;
  order_block_reasons: string[];
  already_issued_count: number;
  ready_count: number;
  expected_issue_count: number;
  policy_not_found_count: number;
  attention_count: number;
  ready_order_item_ids: number[];
  items: OrderIssuePreviewItem[];
}

export interface IssueOrderResult {
  order_id: number;
  created_count: number;
  created: Array<{
    ledger_id: number;
    order_item_id: number;
    group_id: number;
    delta: number;
    expires_at: string | null;
  }>;
  skipped: Array<{ order_item_id: number; sku: string | null; reason: string }>;
  reason: string | null;
  balances: EntitlementBalanceItem[] | null;
}

/** 주문 단위 AS 권리 발급 Preview (read-only, 로그인 사용자). */
export function getOrderIssuePreview(orderId: number): Promise<OrderIssuePreviewResponse> {
  return request<OrderIssuePreviewResponse>(
    `/as-entitlements/ledger/issue-preview/order/${orderId}`,
  );
}

/** 주문 단위 AS 권리 발급 확정 (admin). expected_order_item_ids 로 stale 방지. */
export function confirmOrderIssue(
  orderId: number,
  body: { expected_order_item_ids?: number[]; reason?: string } = {},
): Promise<IssueOrderResult> {
  return request<IssueOrderResult>(
    `/as-entitlements/ledger/issue-order/${orderId}`,
    { method: "POST", body: JSON.stringify({ confirm: true, ...body }) },
  );
}

// [ERP-AS-ENTITLEMENT-LEDGER-PHASE4C-PART-MAPPING-CONSUME-UX-DEV-20260729-022]
export interface EntitlementPartRule {
  id: number;
  entitlement_group_id: number;
  group_code: string | null;
  group_name: string | null;
  part_type: string | null;
  part_sku: string | null;
  brand: string | null;
  line_name: string | null;
  priority: number;
  is_active: boolean;
  memo: string | null;
  updated_at: string | null;
  // 표시용 Product 파생(additive, -035)
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  product_status: string | null;
  product_found: boolean;
}
export interface EntitlementPartRuleList { total: number; items: EntitlementPartRule[]; }
// SKU 전용 일괄 등록(-035)
export interface PartRuleBulkConflict {
  sku: string;
  existing_group_id: number;
  existing_group_code: string | null;
  existing_group_name: string | null;
}
export interface PartRuleBulkResponse {
  created: number;
  reactivated: number;
  skipped_existing: number;
  items: EntitlementPartRule[];
  conflicts: PartRuleBulkConflict[];
}
export function bulkCreateEntitlementPartRules(
  entitlement_group_id: number,
  skus: string[],
): Promise<PartRuleBulkResponse> {
  return request<PartRuleBulkResponse>(`/as-entitlements/part-rules/bulk`, {
    method: "POST",
    body: JSON.stringify({ entitlement_group_id, skus }),
  });
}
// 관리자 규칙 매칭 미리보기(잔액 무시, exact part_sku 기준). -035
export function previewEntitlementPartRuleMatch(
  part_sku: string,
): Promise<{ items: ApplicableEntitlementGroup[] }> {
  const qs = new URLSearchParams({ part_sku });
  return request<{ items: ApplicableEntitlementGroup[] }>(
    `/as-entitlements/part-rules/preview-match?${qs}`,
    { method: "POST" },
  );
}
export function listEntitlementPartRules(params?: {
  group_id?: number; part_sku?: string; line_name?: string; is_active?: boolean; q?: string;
  limit?: number; offset?: number;
}): Promise<EntitlementPartRuleList> {
  const qs = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<EntitlementPartRuleList>(`/as-entitlements/part-rules${suffix}`);
}
export function createEntitlementPartRule(body: {
  entitlement_group_id: number; part_type?: string | null; part_sku?: string | null;
  brand?: string | null; line_name?: string | null; priority?: number; memo?: string | null;
}): Promise<EntitlementPartRule> {
  return request<EntitlementPartRule>(`/as-entitlements/part-rules`, { method: "POST", body: JSON.stringify(body) });
}
export function updateEntitlementPartRule(id: number, body: Partial<{
  part_type: string | null; part_sku: string | null; brand: string | null; line_name: string | null;
  priority: number; is_active: boolean; memo: string | null;
}>): Promise<EntitlementPartRule> {
  return request<EntitlementPartRule>(`/as-entitlements/part-rules/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export interface ApplicableEntitlementGroup {
  entitlement_group_id: number;
  entitlement_name: string;
  group_code: string | null;
  available_quantity: number;
  matched_rule: Record<string, unknown>;
  reason: string;
}
export function getApplicableEntitlementGroups(params: {
  customer_id?: number; part_sku?: string; line_name?: string; part_type?: string;
}): Promise<{ items: ApplicableEntitlementGroup[] }> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<{ items: ApplicableEntitlementGroup[] }>(`/as-entitlements/part-consume/applicable${suffix}`);
}
export interface PartConsumePreview {
  as_request_id: number; part_id: number; part_name: string | null; part_sku: string | null;
  group_id: number; group_name: string | null; applicable: boolean; reasons: string[];
  would_consume: number; available_before: number; available_after_if_consumed: number;
  free_part_id: number | null; note: string;
}
export function previewPartConsume(body: { as_request_id: number; part_id: number; group_id: number; }): Promise<PartConsumePreview> {
  return request<PartConsumePreview>(`/as-entitlements/part-consume/preview`, { method: "POST", body: JSON.stringify(body) });
}

// [ERP-AS-ENTITLEMENT-LEDGER-PHASE4D-AS-RIGHT-CONSUME-CONFIRM-DEV-20260729-023]
export interface PartConsumeConfirmResult {
  consume_ledger_id: number; entitlement_group_id: number; applied_part: { id: number; part_sku: string | null; part_name: string | null };
  balance_before: number; balance_after: number; original_part_price: number | null; charged_part_price: number; consumed: boolean;
}
export function confirmPartConsume(body: {
  as_request_id: number; as_part_used_id: number; entitlement_group_id: number; expected_balance?: number | null; note?: string | null;
}): Promise<PartConsumeConfirmResult> {
  return request<PartConsumeConfirmResult>(`/as-entitlements/part-consume/confirm`, { method: "POST", body: JSON.stringify(body) });
}
export interface PartConsumeRestoreResult {
  restore_ledger_id: number; consume_ledger_id: number; restored_quantity: number; restored_part_price: number | null; balance_after: number | null; restored: boolean;
}
export function restorePartConsume(body: { consume_ledger_id: number; restore_ref: string; reason?: string | null; }): Promise<PartConsumeRestoreResult> {
  return request<PartConsumeRestoreResult>(`/as-entitlements/part-consume/restore`, { method: "POST", body: JSON.stringify(body) });
}
export interface PartConsumeStatusItem {
  consume_ledger_id: number; entitlement_group_id: number; group_name: string | null; as_part_used_id: number | null;
  part_name: string | null; part_sku: string | null; original_part_price: number | null; charged_part_price: number | null;
  balance_after: number | null; status: string; actor_user_id: number | null; created_at: string | null;
}
export function getPartConsumeStatus(asRequestId: number): Promise<{ as_request_id: number; items: PartConsumeStatusItem[] }> {
  return request<{ as_request_id: number; items: PartConsumeStatusItem[] }>(`/as-entitlements/part-consume/status?as_request_id=${asRequestId}`);
}

// ══════════════════════════════════════════════════════════════════
// [PURCHASE-002-PHASE6][D3] 글로벌 SKU 메모
//
// `Product.memo`(상품 메모)·`Product.purchase_note`(기존 SKU 매입 메모)와 **다른 SoT** 다.
// 화면 라벨을 반드시 구분한다 — 같은 "메모" 로 보이면 운영자가 엉뚱한 곳에 쓴다.
//
// 조회는 **batch 하나**다. SKU 별 호출을 만들지 않는다(N+1 금지 — 확정 명세 7).
// write 3종은 action code 로 강제된다(403).
// ⚠️ 권한 code 문자열은 화면 컴포넌트에만 두고 이 파일에는 넣지 않는다(ETC-001 규칙).
// ══════════════════════════════════════════════════════════════════

export type SkuMemoCategory = "order" | "handling" | "packing";
export type SkuMemoSeverity = "info" | "warning" | "critical";
export type SkuMemoChangeType = "create" | "update" | "deactivate" | "reactivate";

export interface SkuMemo {
  id: number;
  sku: string;
  category: SkuMemoCategory;
  /** 한글 라벨은 **서버가 준다** — 화면마다 다른 문구가 생기지 않게. */
  category_label: string;
  severity: SkuMemoSeverity;
  severity_label: string;
  /** 정렬 우선순위(클수록 먼저). 화면이 다시 계산하지 않는다. */
  severity_rank: number;
  body: string;
  is_active: boolean;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SkuMemoRevision {
  id: number;
  memo_id: number;
  revision_no: number;
  change_type: SkuMemoChangeType;
  category: SkuMemoCategory;
  category_label: string;
  severity: SkuMemoSeverity;
  severity_label: string;
  body: string;
  is_active: boolean;
  changed_by_user_id: number | null;
  changed_at: string | null;
}

export interface SkuMemoBatchResponse {
  memos_by_sku: Record<string, SkuMemo[]>;
  count: number;
}

export interface SkuMemoResult {
  ok: boolean;
  memo: SkuMemo;
  /** 실제로 바뀐 게 없으면 null(빈 revision 을 만들지 않는다). */
  revision: SkuMemoRevision | null;
  sku: string;
  /** 활성만 — 화면 batch cache 를 추가 조회 없이 갱신한다. */
  sku_memos: SkuMemo[];
  /** 비활성 포함 — 관리 UI 용. */
  sku_memos_all: SkuMemo[];
}

/** SKU 목록 → 메모 맵. 중복·빈 값은 서버가 정리한다. 전 직원 조회 가능. */
export function getSkuMemosBatch(
  skus: string[],
  opts?: { includeInactive?: boolean },
): Promise<SkuMemoBatchResponse> {
  return request(`/products/sku-memos/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ skus, include_inactive: !!opts?.includeInactive }),
  });
}

/** 수정 이력(append-only). 관리 UI 에서만 연다. */
export function getSkuMemoRevisions(
  memoId: number,
): Promise<{ memo: SkuMemo; revisions: SkuMemoRevision[] }> {
  return request(`/products/sku-memos/${memoId}/revisions`);
}

export function createSkuMemo(body: {
  sku: string;
  category: SkuMemoCategory;
  severity: SkuMemoSeverity;
  body: string;
}): Promise<SkuMemoResult> {
  return request(`/products/sku-memos`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateSkuMemo(
  memoId: number,
  body: { category?: SkuMemoCategory; severity?: SkuMemoSeverity; body?: string },
): Promise<SkuMemoResult> {
  return request(`/products/sku-memos/${memoId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** 비활성화 / 재활성화. **물리삭제 경로는 없다.** */
export function setSkuMemoActive(memoId: number, isActive: boolean): Promise<SkuMemoResult> {
  return request(`/products/sku-memos/${memoId}/active`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_active: isActive }),
  });
}

// ─── [CUSTOMER-002] 전 직원 공유 입고 알람 ──────────────────────
// 검색어·전화번호 같은 개인정보는 URL query 에 싣지 않는다(목록·중복 확인 = POST body).
export type RestockIntakeChannel = "phone" | "order_soldout" | "platform_inquiry";
export type RestockCompletionMethod = "sms" | "phone_consult";
export type RestockStatusFilter = "open" | "completed" | "all" | "deleted";
export type RestockSort = "created_desc" | "created_asc" | "completed_desc" | "completed_asc";

export interface RestockAlertItem {
  position: number;
  product_id: number;
  sku: string;
  display_name: string | null;
  display_option: string | null;
  quantity: number;
}

export interface RestockAlert {
  id: number;
  customer_name: string | null;
  phone: string | null;
  address: string | null;
  memo: string | null;
  intake_channels: RestockIntakeChannel[];
  status: "open" | "completed";
  completion_methods: RestockCompletionMethod[] | null;
  completed_at: string | null;
  completed_by_name: string | null;
  created_at: string;
  created_by_name: string | null;
  updated_at: string;
  updated_by_name: string | null;
  deleted_at: string | null;
  deleted_by_name: string | null;
  version: number;
  shipping_fee: number;
  items: RestockAlertItem[];
}

export interface RestockAlertRow extends RestockAlert {
  duplicate_alert_ids: number[];
  message: RestockMessage;
}

export interface RestockAlertEvent {
  id: number;
  event_type: "create" | "update" | "complete" | "reopen" | "delete" | "restore";
  actor_name: string | null;
  occurred_at: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  version_after: number;
}

export interface RestockMessageWarning {
  position: number | null;
  sku: string | null;
  code: string;
  message: string;
}

export interface RestockMessageItem {
  sku: string | null;
  name: string | null;
  option: string | null;
  quantity: number;
  naver_price: number | null;
  naver_url: string | null;
  coupang_url: string | null;
}

export interface RestockTotals {
  available: boolean;
  subtotal: number | null;
  shipping_fee: number;
  total: number | null;
}

export interface RestockMessage {
  text: string;
  warnings: RestockMessageWarning[];
  items: RestockMessageItem[];
  totals: RestockTotals;
}

export interface RestockDuplicate {
  id: number;
  customer_name: string | null;
  phone: string | null;
  created_at: string;
  created_by_name: string | null;
  overlapping_skus: string[];
}

export interface RestockAlertDetail {
  alert: RestockAlert;
  events: RestockAlertEvent[];
  message: RestockMessage;
  duplicates: RestockDuplicate[];
}

export interface RestockAlertSearchParams {
  q?: string;
  status?: RestockStatusFilter;
  intake_channels?: RestockIntakeChannel[];
  completion_methods?: RestockCompletionMethod[];
  created_from?: string;
  created_to?: string;
  completed_from?: string;
  completed_to?: string;
  sort?: RestockSort;
  page?: number;
  page_size?: number;
}

export interface RestockAlertSearchResponse {
  items: RestockAlertRow[];
  total: number;
  page: number;
  page_size: number;
  open_count: number;
}

export interface RestockAlertInput {
  customer_name?: string | null;
  phone?: string | null;
  address?: string | null;
  memo?: string | null;
  intake_channels?: RestockIntakeChannel[];
  /** Phase 1 형식(각 수량 1) — items 와 동시에 보내면 422. */
  skus?: string[];
  items?: { sku: string; quantity: number }[];
  shipping_fee?: number;
}

export interface RestockTemplate {
  id: number | null;
  header_text: string;
  item_text: string;
  total_text: string;
  total_is_default: boolean;
  footer_text: string;
  is_default: boolean;
  created_at: string | null;
  created_by_name: string | null;
}

export interface RestockTemplateResponse {
  template: RestockTemplate;
  default: { header_text: string; item_text: string; total_text: string; footer_text: string };
  tokens: string[];
  can_edit: boolean;
}

export function searchRestockAlerts(params: RestockAlertSearchParams): Promise<RestockAlertSearchResponse> {
  return request<RestockAlertSearchResponse>("/restock-alerts/search", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export function checkRestockDuplicates(
  phone: string | null, skus: string[], excludeId?: number | null,
): Promise<{ duplicates: RestockDuplicate[] }> {
  return request<{ duplicates: RestockDuplicate[] }>("/restock-alerts/duplicate-check", {
    method: "POST",
    body: JSON.stringify({ phone, skus, exclude_id: excludeId ?? null }),
  });
}

export function createRestockAlert(payload: RestockAlertInput): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>("/restock-alerts", { method: "POST", body: JSON.stringify(payload) });
}

export function getRestockAlert(id: number): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>(`/restock-alerts/${id}`);
}

export function updateRestockAlert(
  id: number, expectedVersion: number,
  payload: RestockAlertInput & { completion_methods?: RestockCompletionMethod[] },
): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>(`/restock-alerts/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...payload, expected_version: expectedVersion }),
  });
}

export function completeRestockAlert(
  id: number, expectedVersion: number, methods: RestockCompletionMethod[],
): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>(`/restock-alerts/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ expected_version: expectedVersion, completion_methods: methods }),
  });
}

export function reopenRestockAlert(id: number, expectedVersion: number): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>(`/restock-alerts/${id}/reopen`, {
    method: "POST",
    body: JSON.stringify({ expected_version: expectedVersion }),
  });
}

/** 논리 삭제(복구 가능). 물리 삭제 경로는 없다. */
export function deleteRestockAlert(id: number, expectedVersion: number): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>(`/restock-alerts/${id}?expected_version=${expectedVersion}`, { method: "DELETE" });
}

export function restoreRestockAlert(id: number, expectedVersion: number): Promise<RestockAlertDetail> {
  return request<RestockAlertDetail>(`/restock-alerts/${id}/restore`, {
    method: "POST",
    body: JSON.stringify({ expected_version: expectedVersion }),
  });
}

export function getRestockTemplate(): Promise<RestockTemplateResponse> {
  return request<RestockTemplateResponse>("/restock-alerts/message-template");
}

/** 전역 문자 템플릿 저장 — backend 가 관리자(role=admin)만 허용한다. */
export function saveRestockTemplate(
  expectedTemplateId: number | null,
  tpl: { header_text: string; item_text: string; total_text: string | null; footer_text: string },
): Promise<RestockTemplateResponse> {
  return request<RestockTemplateResponse>("/restock-alerts/message-template", {
    method: "PUT",
    body: JSON.stringify({ ...tpl, expected_template_id: expectedTemplateId }),
  });
}

// ─── [CUSTOMER-002 Phase 2] SKU 검색(전체 · 페이지) · 일괄 처리 ─────────────
export interface RestockProductHit {
  product_id: number;
  sku: string;
  barcode: string | null;
  display_name: string | null;
  display_option: string | null;
  // [FW-032] additive — 검색 결과 보조 표시용(문자용 display_option 과 별개)
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
  product_status: string;
  is_active: boolean;
  exact_match: boolean;
  naver_price: number | null;
}

export interface RestockProductSearchResponse {
  items: RestockProductHit[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

/** 입고 알람 SKU 검색 — 검색어·페이지는 body(입고 알람 모듈 관례). 기본 50건씩. */
export function searchRestockProducts(q: string, page = 1, pageSize = 50): Promise<RestockProductSearchResponse> {
  return request<RestockProductSearchResponse>("/restock-alerts/product-search", {
    method: "POST",
    body: JSON.stringify({ q, page, page_size: pageSize }),
  });
}

export type RestockBatchAction = "complete" | "reopen" | "delete" | "restore";

export interface RestockBatchFailure {
  id: number;
  code: string;
  message: string;
  current_version?: number;
  current?: RestockAlert;
}

/** 일괄 처리 — 전부 적용되거나 전부 미적용(409 시 detail.failures). 최대 100건. */
export function batchRestockAlerts(
  action: RestockBatchAction,
  items: { id: number; expected_version: number }[],
  completionMethods?: RestockCompletionMethod[],
): Promise<{ action: RestockBatchAction; count: number; alerts: RestockAlert[] }> {
  return request("/restock-alerts/batch", {
    method: "POST",
    body: JSON.stringify({ action, items, ...(action === "complete" ? { completion_methods: completionMethods } : {}) }),
  });
}


// ──────────────────────────────────────────────────────────────
// [PURCHASE-004 Phase 3] 공급사 Excel 발주·입고 자동화 (윤성시마노 · 한국다이와)
//   업로드·운송장 = purchase.supplier_excel.upload · 예외·직배송·선택 PO 전환 = purchase.supplier_exception.resolve
//   (backend 가 강제 — UI 버튼 비활성은 표시용). 원본 Excel 은 서버에 저장되지 않는다.
// ──────────────────────────────────────────────────────────────
export const SUPPLIER_EXCEL_UPLOAD_CODE = "purchase.supplier_excel.upload";
export const SUPPLIER_EXCEPTION_RESOLVE_CODE = "purchase.supplier_exception.resolve";

export interface SupplierPreviewRow { row: number; action: string; [k: string]: unknown }
export interface SupplierPreview {
  ok: boolean;
  errors: string[];
  vendor_code: string | null;
  file_kind: string | null;
  adapter_code?: string;
  sha256: string;
  duplicate_import_file_id?: number | null;
  would_be_baseline?: boolean | null;
  row_kinds?: Record<string, number>;
  actions?: Record<string, number>;
  exception_candidates?: Record<string, number>;
  rows?: SupplierPreviewRow[];
  sku_unmatched?: string[];
  disappeared_line_ids?: number[];
}
export interface SupplierApplyResult {
  import_file_id: number;
  duplicate: boolean;
  is_baseline: boolean;
  vendor_code: string;
  file_kind: string;
  counts: Record<string, number>;
  pos: { id: number; po_no: string; total_quantity: number }[];
  exception_count: number;
  uploaded_at: string;
}
export interface SupplierOpenLine {
  id: number; vendor_code: string; vendor_label: string; order_date: string; supplier_order_no: string | null;
  sku: string | null; product_code: string; product_name: string | null; qty_ordered: number; qty_shipped: number;
  qty_open: number; qty_cancelled: number; status: string; supplier_status: string | null;
  supplier_side_received_qty: number | null; exception_types: string[]; sku_unmatched: boolean;
  // [가시성 보완] 누적 입고(확정 · 입고장 기준 파생) · 기준선 이력 여부
  qty_received: number; is_baseline: boolean;
  // [가시성 UX 2차] include_stock=true 응답 전용 — 표시값(null = 미매칭 '—' / JAN 마스터 정보 없음)
  hitcamp_stock?: number | null; daiwa_stock?: number | null; daiwa_stock_updated_at?: string | null;
  // [PURCHASE-004 후속] include_price=true 응답 전용 — Excel Apply 당시 저장 가격(Decimal 문자열 · null = 가격 정보 없음)
  excel_unit_price?: string | null; excel_price_currency?: string | null; excel_amount?: string | null;
  excel_amount_currency?: string | null; excel_line_kind?: string | null;
  // [UX 3차] include_price=true 응답 전용 — 시마노 VAT 제외 단가(저장값 · 화면만 원 단위 반올림)·공급가·부가세 · 영문 품명(없으면 null)
  excel_unit_price_ex_vat?: string | null; excel_supply_amount?: string | null; excel_vat_amount?: string | null;
  product_name_en?: string | null;
}
export interface SupplierExceptionTarget {
  kind: "order_line" | "shipment"; id: number; key: string; sku: string | null; product_code: string | null;
  product_name: string | null; qty_open?: number; qty?: number; status?: string; basis_date?: string;
  destination?: string; has_po?: boolean; is_baseline?: boolean; implied?: boolean;
}
export interface SupplierExceptionRow {
  id: number; vendor_code: string; exception_type: string; status: string; quantity: number | null;
  created_at: string; resolved_at: string | null; resolution_note: string | null; candidate_count: number | null;
  target: SupplierExceptionTarget | null;
}
export interface SupplierDirectShipment {
  id: number; vendor_code: string; supplier_order_no: string | null; sku: string | null; product_code: string | null;
  product_name: string | null; qty: number; ship_date: string | null; recipient_name: string | null;
  tracking_nos: string[]; link_status: string | null; order_id: number | null; order_item_id: number | null;
  is_baseline: boolean; voided: boolean;
}
export interface SupplierDirectCandidate { order_id: number; order_item_id: number; order_no: string; order_date: string | null }
export interface SupplierPaymentGroup {
  label: string; ship_from: string; ship_to: string; pay_from: string | null; pay_to: string | null;
  amount: string; count: number; direct_amount: string; adjustment: boolean; shipment_ids: number[];
}
export interface SupplierPaymentSchedule { vendor_code: string; groups: SupplierPaymentGroup[]; total_amount: string }
export interface SupplierPoTracking { tracking_no: string; carrier_code: string | null; quantity: number; source: "file" | "manual" }
export interface SupplierPoInfo {
  po_id: number; po_no: string; vendor_code: string | null; vendor_label: string | null; status: string;
  arrived_at: string | null; batch_key: string | null; manual_tracking_input: boolean; basis_dates: string[];
  source_files: { id: number; file_kind: string; uploaded_at: string; is_baseline: boolean }[];
  shipments: { id: number; sku: string | null; qty: number; basis_date: string; ship_date: string | null;
               supplier_order_no: string | null; baseline_converted: boolean; voided: boolean }[];   // baseline_converted = legacy/reserved(PD-12 개정 이후 신규 true 0)
  skus: { sku: string; product_name: string; shipped: number; assigned: number; trackings: SupplierPoTracking[] }[];
}
export interface SupplierTrackingInput { sku: string; carrier_code: string | null; tracking_no: string; quantity: number }
export interface SupplierTrackingResult {
  po_id: number; po_no: string;
  results: { row: number; sku: string; tracking_no: string; carrier_code: string | null; quantity: number; status: string }[];
  sku_totals: Record<string, { shipped: number; assigned: number }>;
}

export function supplierExcelPreview(file: File): Promise<SupplierPreview> {
  const fd = new FormData();
  fd.append("file", file);
  return request<SupplierPreview>("/purchase/supplier-excel/preview", { method: "POST", body: fd });
}
export function supplierExcelApply(file: File): Promise<SupplierApplyResult> {
  const fd = new FormData();
  fd.append("file", file);
  return request<SupplierApplyResult>("/purchase/supplier-excel/apply", { method: "POST", body: fd });
}
// scope: open = 메인 미출고(기본) · history = 기준선·완료 이력(조회 전용)
export function getSupplierOpenLines(p: { vendor?: string; status?: string; has_exception?: boolean; q?: string;
                                          scope?: "open" | "history"; limit?: number; offset?: number;
                                          // [가시성 UX 2차] 서버 필터 · 현재 페이지 재고 표시
                                          sku_match?: "matched" | "unmatched"; baseline?: boolean;
                                          date_from?: string; date_to?: string; include_stock?: boolean;
                                          include_price?: boolean;
                                          // [UX 3차] 공급사 원문 상태(시마노 요청·승인·완결·종결 · 다이와 등록·확정·입고중)
                                          supplier_status?: string } = {}):
  Promise<{ total: number; scope?: string; items: SupplierOpenLine[] }> {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v)); });
  return request(`/purchase/supplier-excel/open-lines?${qs.toString()}`);
}
export function getSupplierExceptions(p: { vendor?: string; exception_type?: string; status?: string; limit?: number } = {}):
  Promise<{ total: number; items: SupplierExceptionRow[] }> {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== "") qs.set(k, String(v)); });
  return request(`/purchase/supplier-excel/exceptions?${qs.toString()}`);
}
export function resolveSupplierException(id: number, action: "acknowledge" | "dismiss" | "cancel_remaining", note: string) {
  return request<{ exception_id: number; status: string }>(`/purchase/supplier-excel/exceptions/${id}/resolve`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action, note }),
  });
}
export function convertSupplierShipmentsToPo(vendor_code: string, shipment_ids: number[]) {
  return request<{ converted: number; already: number[]; po_ids: number[]; pos: { id: number; po_no: string }[] }>(
    "/purchase/supplier-excel/shipments/convert-to-po", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ vendor_code, shipment_ids }),
    });
}
export function getSupplierDirectShipments(link_status?: string): Promise<{ total: number; items: SupplierDirectShipment[] }> {
  return request(`/purchase/supplier-excel/direct-shipments${link_status ? `?link_status=${link_status}` : ""}`);
}
export function getSupplierDirectCandidates(id: number): Promise<{ shipment_id: number; candidates: SupplierDirectCandidate[] }> {
  return request(`/purchase/supplier-excel/direct-shipments/${id}/candidates`);
}
export function linkSupplierDirectShipment(id: number, order_id: number, order_item_id: number) {
  return request<{ shipment_id: number; already_linked: boolean }>(`/purchase/supplier-excel/direct-shipments/${id}/link`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ order_id, order_item_id }),
  });
}
export function getSupplierPaymentSchedule(vendor: string, ship_from: string, ship_to: string): Promise<SupplierPaymentSchedule> {
  return request(`/purchase/supplier-excel/payment-schedule?vendor=${vendor}&ship_from=${ship_from}&ship_to=${ship_to}`);
}
export function getSupplierPoInfo(poId: number): Promise<SupplierPoInfo> {
  return request(`/purchase/supplier-excel/orders/${poId}/supplier-info`);
}
export function arriveSupplierPo(poId: number) {
  return request<{ po_id: number; already_arrived: boolean; arrived_at: string | null }>(
    `/purchase/supplier-excel/orders/${poId}/arrive`, { method: "POST" });
}
export function saveSupplierPoTrackings(poId: number, entries: SupplierTrackingInput[]): Promise<SupplierTrackingResult> {
  return request(`/purchase/supplier-excel/orders/${poId}/trackings`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries }),
  });
}

export interface SupplierShipmentRow {
  id: number; vendor_code: string; vendor_label: string; supplier_order_no: string | null; sku: string | null;
  product_code: string | null; product_name: string | null; qty: number; basis_date: string; ship_date: string | null;
  // baseline_converted = legacy/reserved(PD-12 개정 이후 신규 true 0) · 목록은 기준선 출고를 반환하지 않는다.
  is_baseline: boolean; has_po: boolean; baseline_converted: boolean; po_id: number | null; po_no: string | null;
  // scope=history 응답 전용(완료·과거 출고 이력 — 기준선 + 재고승인 완료 입고분 + 직배송)
  destination_kind?: "hitcamp" | "customer_direct"; line_kind?: string; voided?: boolean;
  // [UX 3차] scope=history 응답 전용 — 마감배송 원본 금액 5종·배송비(저장 통화) · 수취인 · 송장 · 영문 품명
  unit_price?: string | null; unit_price_vat_incl?: string | null; supply_amount?: string | null;
  vat_amount?: string | null; total_amount?: string | null; amount_currency?: string | null;
  shipping_fee?: string | null; recipient_name?: string | null; tracking_nos?: string[]; product_name_en?: string | null;
  // [2026-10-21 후속] scope=planned/yard 응답 전용 — 연결 자동 PO 상태·도착 시각 · 그 품목 입고장 상태(검수·제출·재고 승인)
  po_status?: string | null; arrived_at?: string | null; inbound_registered?: boolean; inbound_checked?: boolean;
  inbound_submitted?: boolean; inbound_approved?: boolean;
}
// scope: convert = 전환 후보(기본 · 기준선 제외) · history = 완료·과거 출고 이력(조회 전용)
//        planned = ② 출고됨·입고예정 · yard = ③ 도착·검수대기(그 품목 재고 승인 전) — 품목(출고) 기준 · 조회 전용
export function getSupplierShipments(p: { vendor?: string; pending_po?: boolean; baseline?: boolean;
                                          scope?: "convert" | "history" | "planned" | "yard"; limit?: number; offset?: number;
                                          // [2026-10-21 후속] convert 전용 — 보류 출고 원인(SKU 매칭 필요 / PO 전환 대기)
                                          sku_match?: "matched" | "unmatched";
                                          // [가시성 UX 2차] history 전용 — 수취인·운송장 제외 검색 · 구분 · 기준일
                                          q?: string; destination?: "hitcamp" | "customer_direct";
                                          date_from?: string; date_to?: string } = {}):
  Promise<{ total: number; scope?: string; direct_total?: number; items: SupplierShipmentRow[] }> {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
  return request(`/purchase/supplier-excel/shipments?${qs.toString()}`);
}
